import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';
import { stringify, parse } from 'flatted';
import md5 from 'crypto-js/md5';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { printViaIframe, printLabelViaIframe } from '../../lib/printHelper';
import { getSaleDisplayTitle } from '../../lib/saleDisplayTitle';
import { isInvoiceSaleReadyForEmail, invoiceSaleEmailSentDate } from '../../lib/invoiceEmailReady';
import { isForeignSale, saleCompanyName, switchToSaleCustomerAndOpen, sharedSalesEnabled } from '../../lib/crossCustomerSales';
import { duettRowState, duettSentState, duettOrderStatusLabel, qualifiesForDuettBulkSend } from '../../lib/duettSaleStatus';
import { formatTripletexVoucherNo } from '../../lib/tripletexVoucherNo';
import { buildReminderRows as buildReminderRowsFromSales } from '../../lib/reminderRows';
import { dhpErrorMessage } from '../../lib/dhpErrorMessage';

import FitTextOneLine from '../gui/fitTextOneLine';
import FitTextSvg from '../gui/fitTextSvg';
import TripletexComparison from './tripletexComparison';
import TripletexOpenPosts from './tripletexOpenPosts';
import TripletexVoucherReporting from './tripletexVoucherReporting';
import PowerofficeVoucherReporting from './powerofficeVoucherReporting';
import DuettVoucherReporting from './duettVoucherReporting';
import DuettComparison from './duettComparison';
import DyreIdReportBatchButton from './dyreIdReportBatchButton';
import DyrejournalSaleSearch from './search';
import DyrejournalSaleReceiptPrint from './saleReceipt';
import CollectiveInvoiceProgress from './collectiveInvoiceProgress';
import AccountingTransferProgress, { transferLabel } from './accountingTransferProgress';
import { appAlert, appConfirm, appPrompt } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';
import { conveneErrorMessage } from '../../lib/conveneErrorMessage';
import { tripletexSendReasonLabel, isPartiallyPaidSale } from '../../lib/tripletexSendReason';
import {
    COLLECTIVE_INVOICE_METHOD_INVOICE,
    COLLECTIVE_INVOICE_METHOD_CONVENE,
    canCreateCollectiveInvoiceViaConvene,
    collectiveInvoiceSaleProps,
    isConveneCollectiveInvoice,
} from '../../lib/collectiveInvoiceConvene';

function hasJournalWithDiagnoses(visit) {
    // Check if the visit has journals and it's an array
    if (!visit.journals || !Array.isArray(visit.journals)) {
       return false;
    }
    // Use the some() method to check if any journal has diagnoses
    return visit.journals.some(
        (journal) =>
            Array.isArray(journal.diagnoses) && journal.diagnoses.length > 0
    );
}

function hasJournalWithMedicines(visit) {
    // Check if the visit has journals and it's an array
    if (!visit.journals || !Array.isArray(visit.journals)) {
       return false;
    }
    // Use the some() method to check if any journal has medicines
    return visit.journals.some(
        (journal) =>
            Array.isArray(journal.medicine) && journal.medicine.length > 0
    );
}

// Companion species (dog, cat, rodents, ...) are never reported to DHP — their journals
// use small-animal (DyreID) diagnosis codes that DHP rejects. A sale can be flagged
// isProductionAnimal just because the OWNER has a producerNumber (e.g. a sheep farmer
// getting dogs vaccinated — TICKET-01020), so DHP nagging/reporting must also check the
// animal species. Unknown/unset species counts as DHP-relevant (conservative).
// NOTE: Keep in sync with companionSpeciesIds in backend/lib/dhp-species.js
const DHP_COMPANION_SPECIES_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 38, 39, 40, 41, 51, 52, 58, 63, 64, 66, 71];

// Default SMS purring text — mirrors DEFAULTS.no in backend post-sale-sms-reminder.js.
const REMINDER_DEFAULT_SMS = 'Hei {{firstname}}. Vi minner om ubetalt faktura {{saleNumber}} på {{amount}} kr fra '
    + '{{clinicName}}. Betal enkelt her: {{payLink}}. Vennlig hilsen {{clinicName}}';

function isDhpRelevantAnimal(animal) {
    return !DHP_COMPANION_SPECIES_IDS.includes(parseInt(animal?.speciesId, 10));
}

function hasDhpRelevantAnimals(visit) {
    if (!Array.isArray(visit?.animals) || visit.animals.length === 0) {
        return false;
    }
    return visit.animals.some(isDhpRelevantAnimal);
}

function getDhpRelevantJournals(visit) {
    if (!Array.isArray(visit?.journals) || !Array.isArray(visit?.animals)) {
        return [];
    }
    return visit.journals.filter(journal => isDhpRelevantAnimal(visit.animals.find(a => a.id === journal.animal)));
}

function hasJournalWithPrescriptions(visit) {
    // Check if the visit has journals and it's an array
    if (!visit.journals || !Array.isArray(visit.journals)) {
         return false;
    }

    // Count the number of prescriptions in each journal
    const prescriptionCount = visit.journals.reduce((count, journal) => {
        // Check if the journal has prescriptions and it's an array
        if (Array.isArray(journal.prescriptions)) {
            // Add the number of prescriptions in this journal to the count
            return count + journal.prescriptions.length;
        }
        // If the journal doesn't have prescriptions, return the current count
        return count;
    }, 0);

    return prescriptionCount;

    // // Use the some() method to check if any journal has prescriptions
    // return visit.journals.some(
    //     (journal) =>
    //         Array.isArray(journal.prescriptions) && journal.prescriptions.length > 0
    // );
}

function getInitials(name) {
    if (!name) return '';
    return name
        .split(' ')  // Split the name into an array of words
        .filter(word => word.length > 0)  // Filter out any empty strings
        .map(word => word[0].toUpperCase())  // Map each word to its first character and convert to uppercase
        .join('');  // Join the initials into a single string
}

function visitorHasEmail(visitors) {
    if (!Array.isArray(visitors)) {
        return false;
    }
    return visitors.some(visitor => visitor.email);
}

function visitorHasCellphone(visitors) {
    if (!Array.isArray(visitors)) {
        return false;
    }
    return visitors.some(visitor => visitor.cellphone);
}

function visitorHasEmailOrCellphone(visitors) {
    if (!Array.isArray(visitors)) {
        return false;
    }
    return visitors.some(visitor => visitor.email || visitor.cellphone);
}

function visitorHasValidEmail(visitors) {
    if (!Array.isArray(visitors)) {
        return false;
    }
    return visitors.some(visitor => util.validateEmail(visitor.email));
}

function validateCellphone(cellphone) {
    if (!cellphone) {
        return false;
    }
    // Check for valid Norwegian cellphone number
    return /^(\+\d{2})?[489]\d{7}$/.test(cellphone);
}

function visitorHasValidCellphone(visitors) {
    if (!Array.isArray(visitors)) {
        return false;
    }
    return visitors.some(visitor => validateCellphone(visitor.cellphone));
}

/**
 * Analyzes tripletexApiResponses and returns a readable summary for end users.
 * @param {Array} responses - The tripletexApiResponses array from a sale
 * @returns {Object} - { summary: string, errors: string[], hasErrors: boolean, invoiceNumber: string|null }
 */
function analyzeTripletexResponses(responses, liveInvoice = null) {
    if (!Array.isArray(responses) || responses.length === 0) {
        return { summary: [], fullSummary: [], hiddenSummaryCount: 0, errors: [], fullErrors: [], hiddenErrorsCount: 0, hasErrors: false, invoiceNumber: null };
    }

    const result = {
        messages: [], // Each message is a complete line (e.g., "Kunde: X | Faktura #Y | Utestående: kr Z")
        errors: [],
        hasErrors: false,
        invoiceNumber: null,
        customerCreated: false,
        customerFound: false,
        customerUpdated: false,
        productsFound: 0,
        productsCreated: 0,
        invoiceCreated: false,
        amountOutstanding: null,
    };

    for (const response of responses) {
        const messageParts = []; // Parts for this single response/message

        // Check for customer query/lookup
        if (response.tripletexCustomers) {
            const customers = response.tripletexCustomers;
            if (customers.count > 0 && customers.values?.length > 0) {
                result.customerFound = true;
                const customer = customers.values[0];
                if (customer?.name) {
                    messageParts.push(`Kunde: ${customer.name}`);
                }
            }
        }

        // Check for customer creation (tripletexCustomerCreated)
        if (response.tripletexCustomerCreated) {
            const created = response.tripletexCustomerCreated;
            if (created.id) {
                result.customerCreated = true;
                messageParts.push(`Kunde opprettet: ${created.name || created.id}`);
            } else if (created.status === 422 && created.validationMessages) {
                result.hasErrors = true;
                for (const msg of created.validationMessages) {
                    result.errors.push(`Kunde: ${msg.message}`);
                }
            }
        }

        // Check for customer update via apiResponse (when queryUpdateCustomer exists)
        if (response.queryUpdateCustomer && response.apiResponse) {
            const apiResp = response.apiResponse;
            if (apiResp.statusCode === 200 && apiResp.value?.id) {
                result.customerUpdated = true;
            } else if (apiResp.statusCode >= 400) {
                result.hasErrors = true;
                if (apiResp.validationMessages) {
                    for (const msg of apiResp.validationMessages) {
                        result.errors.push(`Kunde: ${msg.message}`);
                    }
                }
            }
        }

        // Check for products found
        if (response.tripletexProducts || response.tripletexProductsFinal) {
            const products = response.tripletexProducts || response.tripletexProductsFinal;
            if (products.count > 0) {
                result.productsFound = products.count;
            }
        }

        // Check for product creation
        if (response.tripletexProductCreated) {
            const product = response.tripletexProductCreated;
            if (product.id) {
                result.productsCreated++;
            }
        }

        // Check for invoice creation (apiResponse with queryInvoiceCreate)
        if (response.queryInvoiceCreate && response.apiResponse) {
            const apiResp = response.apiResponse;
            const invoice = apiResp.value;
            if (invoice?.invoiceNumber) {
                result.invoiceCreated = true;
                result.invoiceNumber = invoice.invoiceNumber;
                // The creation response is a snapshot from before any payment was
                // registered. Prefer the live amount on sale.tripletexInvoice (updated
                // by the payment registration and the status sync) when it is the same
                // invoice, so a part-paid sale does not show the pre-payment balance.
                let { amountOutstanding } = invoice;
                if (liveInvoice?.invoiceNumber === invoice.invoiceNumber
                    && typeof liveInvoice.amountOutstanding === 'number') {
                    amountOutstanding = liveInvoice.amountOutstanding;
                }
                result.amountOutstanding = amountOutstanding;
                messageParts.push(`Faktura #${invoice.invoiceNumber}`);
                if (amountOutstanding !== undefined && amountOutstanding !== null) {
                    if (amountOutstanding === 0) {
                        messageParts.push('Betalt');
                    } else {
                        messageParts.push(`Utestående: kr ${amountOutstanding.toFixed(2)}`);
                    }
                }
            } else if (apiResp.statusCode === 422 || apiResp.statusCode >= 400) {
                result.hasErrors = true;
                if (apiResp.validationMessages) {
                    for (const msg of apiResp.validationMessages) {
                        result.errors.push(`Faktura: ${msg.message}`);
                    }
                } else if (apiResp.message) {
                    result.errors.push(`Faktura: ${apiResp.message}`);
                }
            }
        }

        // Check for any validation errors in the response itself
        if (response.validationMessages && Array.isArray(response.validationMessages)) {
            result.hasErrors = true;
            for (const msg of response.validationMessages) {
                const field = msg.field ? `${msg.field}: ` : '';
                result.errors.push(`${field}${msg.message}`);
            }
        }

        // Check for status errors in direct response
        if (response.status >= 400 && response.message) {
            result.hasErrors = true;
            result.errors.push(response.message);
        }

        // Add this response as a complete message line
        if (messageParts.length > 0) {
            result.messages.push(messageParts.join(' | '));
        }
    }

    // Add products created as a separate message
    if (result.productsCreated > 0) {
        result.messages.push(`${result.productsCreated} produkt(er) opprettet`);
    }

    // Limit messages to last 2 to keep it readable
    const MAX_MESSAGES = 2;
    const fullSummary = result.messages;
    let limitedSummary = fullSummary;
    let hiddenSummaryCount = 0;

    if (result.messages.length > MAX_MESSAGES) {
        hiddenSummaryCount = result.messages.length - MAX_MESSAGES;
        limitedSummary = result.messages.slice(-MAX_MESSAGES);
    }

    // Limit errors to first 2 (root cause errors come first from Tripletex)
    const fullErrors = result.errors;
    let limitedErrors = result.errors;
    let hiddenErrorsCount = 0;
    if (result.errors.length > MAX_MESSAGES) {
        hiddenErrorsCount = result.errors.length - MAX_MESSAGES;
        limitedErrors = result.errors.slice(0, MAX_MESSAGES);
    }

    return {
        summary: limitedSummary,
        fullSummary,
        hiddenSummaryCount,
        errors: limitedErrors,
        fullErrors,
        hiddenErrorsCount,
        hasErrors: result.hasErrors,
        invoiceNumber: result.invoiceNumber,
        amountOutstanding: result.amountOutstanding,
        customerCreated: result.customerCreated,
        customerFound: result.customerFound,
        customerUpdated: result.customerUpdated,
        invoiceCreated: result.invoiceCreated,
    };
}

/**
 * Analyzes fikenApiResponses and returns a readable summary for end users.
 * @param {Array} responses - The fikenApiResponses array from a sale
 * @returns {Object} - { summary: string, errors: string[], hasErrors: boolean, invoiceNumber: string|null }
 */
function analyzeFikenResponses(responses) {
    if (!Array.isArray(responses) || responses.length === 0) {
        return { summary: [], fullSummary: [], hiddenSummaryCount: 0, errors: [], fullErrors: [], hiddenErrorsCount: 0, hasErrors: false, invoiceNumber: null };
    }

    const result = {
        errors: [],
        hasErrors: false,
        invoiceNumber: null,
        saleNumber: null,
        customerCreated: false,
        invoiceCreated: false,
        saleCreated: false,
        latestSaleStatus: null,
    };

    // Track seen messages to avoid duplicates
    const seenInvoices = new Set();

    for (const response of responses) {
        // Check for new type/status fields first
        if (response.type && response.status) {
            if (response.status === 'success') {
                if (response.type === 'saleCreate') {
                    result.saleCreated = true;
                } else if (response.type === 'invoiceCreate') {
                    result.invoiceCreated = true;
                } else if (response.type === 'customerCreate') {
                    result.customerCreated = true;
                }
            } else if (response.status === 'error') {
                result.hasErrors = true;
                const typeLabel = {
                    'saleCreate': 'Salg',
                    'invoiceCreate': 'Faktura',
                    'customerCreate': 'Kunde',
                    customerResolutionFailed: 'Kunde',
                    'creditNote': 'Kreditnota',
                }[response.type] || response.type;
                const errorMsg = response.errorMessage || response.apiResponse?.error_description || 'Ukjent feil';
                result.errors.push(`${typeLabel}: ${errorMsg}`);
            }
            // 'simulated' status - don't mark as success/error, just record it
        }

        // Check for customer creation (fikenCustomerCreated) - only show if actually created
        if (response.fikenCustomerCreated) {
            const created = response.fikenCustomerCreated;
            // Check if it's a success response (has location header)
            if (created.location) {
                result.customerCreated = true;
            } else if (Array.isArray(created)) {
                // Fiken validation errors come as [{ logref, message }], e.g.
                // "Invalid e-mail: x@y.no." — the reason the contact was never
                // created (TICKET-01750).
                const messages = created.map(entry => entry?.message).filter(m => m);
                if (messages.length > 0) {
                    result.hasErrors = true;
                    result.errors.push(`Kunde: ${messages.join('. ')}`);
                }
            } else if (created.statusCode >= 400 || created.error) {
                result.hasErrors = true;
                result.errors.push(`Kunde: ${created.error_description || created.message || 'Ukjent feil'}`);
            }
        }

        // Check for customer lookup errors
        if (response.fikenCustomers?.error) {
            result.hasErrors = true;
            result.errors.push(`API: ${response.fikenCustomers.error_description || response.fikenCustomers.error}`);
        }

        // Check for invoice creation (queryInvoiceCreate with apiResponse) - fallback for old format
        if (response.queryInvoiceCreate && response.apiResponse && !response.type) {
            const apiResp = response.apiResponse;
            // Success is indicated by location header
            if (apiResp.location) {
                result.invoiceCreated = true;
            } else if (apiResp.statusCode >= 400 || apiResp[0]?.message) {
                result.hasErrors = true;
                const errorMsg = apiResp.error_description || apiResp[0]?.message || apiResp.message || 'Ukjent feil';
                result.errors.push(`Faktura: ${errorMsg}`);
            }
        }

        // Check for sale creation (querySaleCreate with apiResponse) - fallback for old format
        if (response.querySaleCreate && response.apiResponse && !response.type) {
            const apiResp = response.apiResponse;
            if (apiResp.location || apiResp.saleNumber) {
                result.saleCreated = true;
            } else if (apiResp.statusCode >= 400 || apiResp[0]?.message) {
                result.hasErrors = true;
                const errorMsg = apiResp.error_description || apiResp[0]?.message || apiResp.message || 'Ukjent feil';
                result.errors.push(`Salg: ${errorMsg}`);
            }
        }

        // Check for invoice sync response (fikenSaleResponse) - use the latest one
        if (response.fikenSaleResponse) {
            const fikenSale = response.fikenSaleResponse;
            if (fikenSale.saleNumber) {
                result.saleNumber = fikenSale.saleNumber;
                result.latestSaleStatus = {
                    saleNumber: fikenSale.saleNumber,
                    settled: fikenSale.settled,
                    outstandingBalance: fikenSale.outstandingBalance || 0,
                };
            }
        }

        // Check for credit note or other error responses (with error field)
        if (response.error) {
            result.hasErrors = true;
            const errorPrefix = response.type === 'creditNote' ? 'Kreditnota: ' : '';
            result.errors.push(`${errorPrefix}${response.error}`);
        }

        // Check for direct error responses
        if (response.statusCode >= 400) {
            result.hasErrors = true;
            result.errors.push(response.error_description || response.message || 'Ukjent feil');
        }
    }

    // Build summary messages (deduplicated and meaningful)
    const messages = [];

    if (result.customerCreated) {
        messages.push('Kunde opprettet');
    }

    if (result.latestSaleStatus) {
        const s = result.latestSaleStatus;
        const status = s.settled ? 'Betalt' : `Utestående: kr ${(s.outstandingBalance / 100).toFixed(2)}`;
        messages.push(`Faktura #${s.saleNumber} | ${status}`);
    } else if (result.invoiceCreated) {
        messages.push('Faktura opprettet');
    } else if (result.saleCreated) {
        messages.push('Salg opprettet');
    }

    // Limit messages to last 2 to keep it readable
    const MAX_MESSAGES = 2;
    const fullSummary = messages;
    let limitedSummary = fullSummary;
    let hiddenSummaryCount = 0;

    if (messages.length > MAX_MESSAGES) {
        hiddenSummaryCount = messages.length - MAX_MESSAGES;
        limitedSummary = messages.slice(-MAX_MESSAGES);
    }

    // Deduplicate errors
    const uniqueErrors = [...new Set(result.errors)];
    const fullErrors = uniqueErrors;
    let limitedErrors = uniqueErrors;
    let hiddenErrorsCount = 0;
    if (uniqueErrors.length > MAX_MESSAGES) {
        hiddenErrorsCount = uniqueErrors.length - MAX_MESSAGES;
        limitedErrors = uniqueErrors.slice(0, MAX_MESSAGES);
    }

    return {
        summary: limitedSummary,
        fullSummary,
        hiddenSummaryCount,
        errors: limitedErrors,
        fullErrors,
        hiddenErrorsCount,
        hasErrors: result.hasErrors,
        invoiceNumber: result.invoiceNumber,
        saleNumber: result.saleNumber,
        customerCreated: result.customerCreated,
        invoiceCreated: result.invoiceCreated,
        saleCreated: result.saleCreated,
    };
}

// Entry types written by backend/lib/duett-{customer,invoice,payment-sync}.js.
const DUETT_LOG_LABELS = {
    customerSearch: 'Kundesøk',
    customerRegisterSearch: 'Kundesøk (ekstern id)',
    customerCreate: 'Kunde opprettet',
    customerVerify: 'Kundeverifisering',
    orderCreate: 'Faktura sendt',
    creditOrderCreate: 'Kreditnota sendt',
    orderSearch: 'Ordre hentet',
    paymentSync: 'Statusoppdatering fra Duett',
    paymentSyncRevert: 'Betalt-markering tilbakestilt',
    customerUpdate: 'Kunde oppdatert',
    attachmentGenerateError: 'PDF-vedlegg feilet',
};

/**
 * Describes a single duettApiResponses entry for the log view.
 * @param {Object} entry - One entry: { createdDate, type, request, response } or
 *                         { createdDate, type: 'paymentSync', duettOrderResponse }.
 * @returns {Object} - { label, outcome, summaryMessage, errors, isError }
 */
function describeDuettLogEntry(entry) {
    const type = entry.type || 'ukjent';
    const label = DUETT_LOG_LABELS[type] || type;
    const response = (type === 'paymentSync' ? entry.duettOrderResponse : entry.response) || {};
    const errors = [];
    let outcome = '';
    let summaryMessage = null;

    const orderParts = (order) => {
        const parts = [];
        if (order.orderNumber) parts.push(`Ordre #${order.orderNumber}`);
        if (order.invoiceNumber) parts.push(`Faktura #${order.invoiceNumber}`);
        if (order.status) parts.push(duettOrderStatusLabel(order.status));
        return parts;
    };

    if (type === 'customerSearch' || type === 'customerRegisterSearch') {
        const found = Array.isArray(response.items) && response.items.length > 0 ? response.items[0] : null;
        outcome = found ? `Treff: ${found.name || found.id}` : 'Ingen treff';
        if (found && found.name) summaryMessage = `Kunde: ${found.name}`;
    } else if (type === 'customerCreate') {
        const newId = response.ids && response.ids[0];
        if (newId && !(response.errorMessages && response.errorMessages.length > 0)) {
            outcome = `Duett-kunde ${newId} opprettet`;
            summaryMessage = `Kunde opprettet: ${entry.request?.name || newId}`;
        } else {
            outcome = 'Opprettelse feilet';
            (response.errorMessages || ['Ukjent feil']).forEach(msg => errors.push(`Kunde: ${msg}`));
        }
    } else if (type === 'customerVerify') {
        const zipKey = response.items?.[0]?.address?.zipCode?.key;
        outcome = zipKey ? `Postnummer ${zipKey} bekreftet` : 'Sjekk av opprettet kunde';
    } else if (type === 'orderCreate' || type === 'creditOrderCreate') {
        const word = type === 'creditOrderCreate' ? 'Kreditnota' : 'Faktura';
        const newId = response.ids && response.ids[0];
        if (newId && !(response.errorMessages && response.errorMessages.length > 0)) {
            outcome = `Ordre-id ${newId} opprettet i Duett`;
            summaryMessage = `${word} sendt til Duett (ordre-id ${newId})`;
        } else {
            outcome = `${word} avvist av Duett`;
            (response.errorMessages || [`HTTP ${response.statusCode || 'ukjent'}`]).forEach(msg => errors.push(`${word}: ${msg}`));
        }
    } else if (type === 'orderSearch') {
        const parts = orderParts(response);
        outcome = parts.length > 0 ? parts.join(' | ') : 'Ingen ordredata';
        if (parts.length > 0) summaryMessage = parts.join(' | ');
    } else if (type === 'paymentSync') {
        const parts = orderParts(response);
        outcome = parts.length > 0 ? parts.join(' | ') : 'Ingen ordredata';
        if (parts.length > 0) summaryMessage = `Status fra Duett: ${parts.join(' | ')}`;
    }

    if (errors.length === 0 && response.statusCode >= 400) {
        errors.push(`${label}: HTTP ${response.statusCode}${response.title ? ` — ${response.title}` : ''}`);
    }

    return { label, outcome, summaryMessage, errors, isError: errors.length > 0 };
}

/**
 * Analyzes duettApiResponses and returns a readable summary for end users.
 * @param {Array} responses - The duettApiResponses array from a sale
 * @returns {Object} - Same shape as analyzeTripletexResponses/analyzeFikenResponses.
 */
function analyzeDuettResponses(responses) {
    if (!Array.isArray(responses) || responses.length === 0) {
        return { summary: [], fullSummary: [], hiddenSummaryCount: 0, errors: [], fullErrors: [], hiddenErrorsCount: 0, hasErrors: false };
    }

    const messages = [];
    const errors = [];
    const sorted = [...responses].sort((a, b) => new Date(a.createdDate || 0) - new Date(b.createdDate || 0));
    for (const entry of sorted) {
        const described = describeDuettLogEntry(entry);
        if (described.summaryMessage) messages.push(described.summaryMessage);
        errors.push(...described.errors);
    }

    // Limit messages to last 2 to keep it readable (same as Tripletex/Fiken)
    const MAX_MESSAGES = 2;
    const fullSummary = messages;
    let limitedSummary = fullSummary;
    let hiddenSummaryCount = 0;
    if (messages.length > MAX_MESSAGES) {
        hiddenSummaryCount = messages.length - MAX_MESSAGES;
        limitedSummary = messages.slice(-MAX_MESSAGES);
    }

    // Deduplicate errors, limit to first 2 (root cause first)
    const uniqueErrors = [...new Set(errors)];
    const fullErrors = uniqueErrors;
    let limitedErrors = uniqueErrors;
    let hiddenErrorsCount = 0;
    if (uniqueErrors.length > MAX_MESSAGES) {
        hiddenErrorsCount = uniqueErrors.length - MAX_MESSAGES;
        limitedErrors = uniqueErrors.slice(0, MAX_MESSAGES);
    }

    return {
        summary: limitedSummary,
        fullSummary,
        hiddenSummaryCount,
        errors: limitedErrors,
        fullErrors,
        hiddenErrorsCount,
        hasErrors: uniqueErrors.length > 0,
    };
}

@withText(props => ({
    saleTitle: <Text id='dyrejournal.sale-title'>Search for sale</Text>,
    searchTitle: <Text id='dyrejournal.search-title'>Search for sale</Text>,
    deleteAreYouSure: <Text id='dyrejournal.sale-delete-are-you-sure'>Are you sure you want to delete this sale?</Text>,
    deleteReason: <Text id='dyrejournal.sale-delete-reason'>Please provide a reason for deleting this sale:</Text>,
    deleteReasonError: <Text id='dyrejournal.sale-delete-reason-error'>You must provide a reason for deleting this sale.</Text>,
    animalTitle: <Text id='animal.title'>Animal</Text>,
    journalTitle: <Text id='dyrejournal.journal-title'>Journal</Text>,
    prescriptionTitle: <Text id='dyrejournal.prescription-title'>Prescription</Text>,
    medicinesTitle: <Text id='dyrejournal.medicines-title'>Medicines</Text>,
    diagnosesTitle: <Text id='dyrejournal.diagnoses-title'>Diagnoses</Text>,
}))
@observer
class AllVisitsTable extends Component {
	constructor(props) {
        super(props);
        this.state = {
            displayAnimaliaError: {},
            displayFikenError: {},
            displayNetsError: {},
            animaliaLoading: {},
            fikenLoading: {},
            fikenSent: {},
            tripletexLoading: {},
            tripletexError: {},
            tripletexSent: {},
            tripletexCreateError: {},
            tripletexExpandedSummary: {},
            tripletexExpandedErrors: {},
            powerofficeLoading: {},
            powerofficeError: {},
            powerofficeSent: {},
            powerofficeExpandedSummary: {},
            powerofficeExpandedErrors: {},
            fikenExpandedSummary: {},
            fikenExpandedErrors: {},
            displayDuettError: {},
            duettExpandedSummary: {},
            duettExpandedErrors: {},
            saleUserUpdating: {},
            saleUserIsUpdated: {},
            paymentInProgress: {},
            insuranceDropdownOpen: {},
            checkingCollectiveInvoice: false,
            checkingInvoicesForEmail: false,
            checkingInvoicesForFiken: false,
            checkingSalesForFiken: false,
            checkingInvoicesForTripletex: false,
            checkingSalesForTripletex: false,
            sendAllInvoicesToPowerOffice: false,
            adminToolbarSection: null,  // null = show menu, 'collective' | 'accounting' | 'poweroffice' | 'email' | 'medicalHistory' | 'reminders'
            settingUpCollectiveInvoice: false,
            collectiveInvoiceStep1Done: false,
            collectiveInvoiceStep2Done: false,
            collectiveInvoiceStep3Done: false,
            collectiveInvoiceSummary: null,  // { invoicesCreated, salesProcessed }
            // Email invoice wizard steps
            settingUpEmailInvoice: false,
            emailInvoiceStep1Done: false,
            emailInvoiceStep2Done: false,
            emailInvoiceStep3Done: false,
            emailInvoiceSummary: null,  // { invoicesSent }
            // Tripletex/Fiken wizard steps
            accountingMode: null,  // null = show mode selection, 'transfer' = show wizard, 'update' = show update buttons
            accountingProvider: null,  // 'fiken' | 'tripletex' — which provider tile was clicked
            accountingTransferStep1Done: false,
            accountingTransferStep2Done: false,
            accountingTransferStep3Done: false,
            accountingTransferSummary: null,  // { invoicesSent, salesSent }
            accountingUpdateStep1Done: false,
            accountingUpdateStep2Done: false,
            accountingUpdateStep3Done: false,
            updateFromTripletexPreviewLoading: false,
            settingUpAccountingTransfer: false,
            currentAccountingTransfer: 0,
            totalAccountingTransfers: 0,
            // Debug data loaded on-demand for admins
            saleDebugData: {},  // { [saleId]: { tripletexApiResponses, fikenApiResponses, ... } }
            loadingDebugData: {},  // { [saleId]: true/false }
            // Medical history wizard
            medicalHistoryMode: 'batch', // 'batch' or 'single'
            medicalHistoryStep: 1,
            medicalHistoryPreview: null,
            medicalHistorySendResult: null,
            medicalHistorySpeciesInPeriod: [],
            medicalHistorySelectedSpecies: [],
            medicalHistoryLoading: false,
            medicalHistorySending: false,
            // Single animal mode
            medicalHistorySingleVisitor: null,
            medicalHistorySingleAnimals: [],
            medicalHistorySingleAnimal: null,
            medicalHistorySingleAllTime: true,
            medicalHistorySingleStartDate: '',
            medicalHistorySingleEndDate: '',
            medicalHistorySingleSent: false,
            medicalHistorySingleError: '',
            // Purringer (bulk invoice reminder) wizard
            reminderStep: 1,
            reminderChannels: { email: true, sms: true },
            reminderSmsText: '',
            reminderSmsTemplateId: null,
            reminderRows: [],
            reminderSelected: {},
            reminderLoading: false,
            reminderSearchText: '',
            reminderListLoading: false,
            reminderSending: false,
            reminderProgress: 0,
            reminderResult: null,
            showOverpaymentOnly: false,
            showDhpNotReportedOnly: false,
            dhpBulkReporting: false,
            currentDhpBulk: 0,
            totalDhpBulk: 0,
            showDhpReportConfirm: false,
            dhpReportCount: 0,
            showCollectiveInvoiceConfirm: false,
            collectiveInvoiceConfirmSales: 0,
            collectiveInvoiceConfirmVisitors: 0,
        };
    }

    loadAll = async () => {
        const { userStore } = this.props.stores;
        // userStore.getInfo({ forceLoad: true });
        userStore.getInfo();
        this.checkForExternalTransfer();
    }

    loadJournals = async (animalIds, sale, journalIds) => {
        const { journalStore, saleStore } = this.props.stores;
        if (!util.isArray(journalIds) || journalIds.length === 0) {
            return false;
        }
        const response = await journalStore.load({
            query: {
                id: journalIds,
                // $or: [
                //     {
                //         animal: animalIds,
                //         sale,
                //     },
                //     {
                //         id: { $in: journalIds },
                //     },
                // ],
            },
            skipUpdate: true,
            addData: ['totalSales'],
        });
        saleStore.updateKeyValue('animalJournals', response);
    }

    loadAnimals = async visitorIds => {
        const { animalStore, saleStore } = this.props.stores;
        const response = await animalStore.load({
            query: {
                owner: visitorIds,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('visitorAnimals', response);

        await animalStore.load({
            query: {
                owner: visitorIds,
            },
        });
    }

    loadSales = async visitorIds => {
        const { saleStore, userStore } = this.props.stores;
        await saleStore.loadVisitorSales(visitorIds, {
            query: { today: 1 },
            myCustomers: sharedSalesEnabled(userStore),
        });
    }

    loadSalePayments = async saleId => {
        if (!util.isNumber(saleId)) {
            return false;
        }
        const { salePaymentStore, saleStore, paymentStore } = this.props.stores;
        const response = await salePaymentStore.load({
            query: {
                sale: saleId,
            },
            skipUpdate: true,
            limit: 5000,
        });
        saleStore.updateKeyValue('currentPayments', response);

        // Load Vipps and PayPal payments
        const externalPayments = await paymentStore.load({
            query: {
                sale: saleId,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('currentExternalPayments', externalPayments);

        // Load all Vipps and PayPal payments for this user
        const { newSale } = saleStore;
        if (newSale?.user) {
            const externalUserPayments = await paymentStore.load({
                query: {
                    user: newSale.user,
                },
                skipUpdate: true,
            });
            saleStore.updateKeyValue('externalUserPayments', externalUserPayments);
        }
    }

    loadAllSalePayments = async () => {
        const { salePaymentStore, saleStore } = this.props.stores;
        const { sales } = saleStore;
        const saleIds = sales.map(e => e.id);
        if (saleIds.length === 0) {
            return false;
        }
        const response = await salePaymentStore.load({
            query: {
                sale: saleIds,
            },
            skipUpdate: true,
            limit: 5000,
        });
        saleStore.updateKeyValue('allPayments', response?.data || []);
    }

    loadAllVisitorSalePayments = async () => {
        const { salePaymentStore, saleStore } = this.props.stores;
        saleStore.updateKeyValue('visitorAllPayments', []);

        const { currentVisitorSales = [] } = saleStore;
        const saleIds = currentVisitorSales.map(e => e.id);
        if (saleIds.length === 0) {
            return false;
        }
        const response = await salePaymentStore.load({
            query: {
                sale: saleIds,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('visitorAllPayments', response?.data || []);
    }

    onClickDeleteSale = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { id, logreason } = e.target.closest('button').dataset;
        const dialogAction = beginDialogAction(this, `onClickDeleteSale:${parseInt(id, 10)}`);
        if (!dialogAction) return false;
        try {
            const { deleteAreYouSure, deleteReason, deleteReasonError, callback = () => {} } = this.props;
            const { saleStore, userStore } = this.props.stores;
            const targetSale = (saleStore.sales || []).find(s => s && s.id === parseInt(id, 10));
            if (targetSale && isForeignSale(targetSale, userStore)) {
                return;
            }
            // q: Is it possible to get a native confirm dialog to have an input field?
            // A: No, not possible. Need to create a custom modal for that.
            let reason = null;
            if (((await appConfirm(deleteAreYouSure)) && dialogAction.isCurrent())) {
                if (logreason) {
                    reason = await appPrompt(deleteReason);
                    if (reason === null || !dialogAction.isCurrent()) return;
                    if (!reason || reason.trim().length === 0) {
                        (await appAlert(deleteReasonError));
                        return;
                    }
                }
                const response = await saleStore.deleteSale(parseInt(id, 10), reason);
                if (response?.status >= 400) {
                    await appAlert(response.message || 'Salget ble ikke slettet.');
                }
                callback();
            }
        } finally {
            dialogAction.finish();
        }
    }

    onClickUndeleteSale = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { callback = () => {} } = this.props;
        const { id } = e.target.closest('button').dataset;
        const { saleStore, userStore } = this.props.stores;
        const targetSale = (saleStore.sales || []).find(s => s && s.id === parseInt(id, 10));
        if (targetSale && isForeignSale(targetSale, userStore)) {
            return;
        }
        const response = await saleStore.save({
            id: parseInt(id, 10),
            offline: 0,
        });
        callback();
    }

    onClickViewVisit = async e => {
        const { id } = e.target.closest('tr').dataset;
        this.viewVisit(parseInt(id, 10));
    }

    onClickFilterVisitor = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const visitorId = parseInt(e.currentTarget.dataset.visitorId, 10);
        if (!visitorId) return;
        const { saleStore } = this.props.stores;
        const { callback = () => {} } = this.props;
        saleStore.updateKeyValue('activeVisitor', visitorId);
        callback();
    }

    // Unsaved product-line edits on the currently open sale (e.g. a typed price
    // whose debounced save hasn't fired yet) are lost when this list replaces
    // newSale. Every product mutation sets productAdded/productRemoved and each
    // save snapshot consumes them, so flags still set = unsaved edits — flush
    // them before switching sale (TICKET-00980).
    flushPendingProductEdits = async () => {
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        if (newSale?.id && (newSale.productAdded || newSale.productRemoved)) {
            await saleStore.saveSale({ calledFrom: 'allVisitsTable.flushPendingProductEdits', fieldsToSave: ['products'] });
        }
    }

    viewVisit = async id => {
        await this.flushPendingProductEdits();
        const { drawerLevel = 1 } = this.props;
		const { appState, saleStore, userStore } = this.props.stores;

        // Foreign sales (cross-customer list, featureFlags.sharedSales) are
        // find-only here: opening one switches to the owning company first so
        // payments/DHP/insurance/accounting all run in the right context.
        const clickedSale = (saleStore.sales || []).find(s => s && s.id === id);
        if (clickedSale && isForeignSale(clickedSale, userStore)) {
            const switched = await switchToSaleCustomerAndOpen({ stores: this.props.stores, sale: clickedSale });
            if (switched) {
                appState.toggleDrawerRight(false, drawerLevel);
            }
            return;
        }

        saleStore.createNewSale({});

        const newSale = await saleStore.load(id, false, { skipUpdate: true });
        if (!newSale || !newSale[0]) {
            console.warn('viewVisit: sale not found for id', id);
            return;
        }
        saleStore.updateKeyValue('newSale', newSale[0]);
        const { visitors, isProductionAnimal } = newSale[0];

        appState.appContainerScrollTop();
        // "Alle besøk" is a right-side drawer that overlays whatever route is
        // underneath. Closing it isn't enough: if it was opened over e.g.
        // /dyrejournal-payments/ (Betalinger) or /visitor/ (Besøkende), the user
        // would just see that page again instead of the clicked visit (TICKET-01125).
        // Navigate to the sale view so the freshly loaded newSale is actually shown.
        // The BASE path (no :sale param) displays the already-loaded newSale without
        // triggering the route's gotoSale() reload, which would clobber it.
        route('/dyrejournal-sale/');
        appState.toggleDrawerRight(false, drawerLevel);

        if (id) {
            await this.loadSalePayments(id);
        }
        if (newSale[0].visitors) {
            const visitorIds = newSale[0].visitors.map(e => e.id);
            // console.log('onClickViewVisit', visitorIds)
            await this.loadAnimals(visitorIds);
            await this.loadSales(visitorIds);
        }

        if (newSale[0].animals) {
            const journalIds = newSale[0].journals.map(e => e.id);
            const animalIds = newSale[0].animals.map(e => e.id);
            await this.loadJournals(animalIds, newSale[0].id, journalIds);
        }

        const isProductionAnimals = util.isDefined(isProductionAnimal) ? isProductionAnimal : visitors.some(v => v?.producerNumber);
        saleStore.updateKeyValue('isProductionAnimals', isProductionAnimals);
        console.log('viewVisit: done');

    }

    createFikenCompanyInvoice = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id, recreate } = e.target.closest('button').dataset;
        const dialogAction = beginDialogAction(this, `createFikenCompanyInvoice:${parseInt(id, 10)}`);
        if (!dialogAction) return false;
        try {
            const isRecreate = recreate === 'true';
            if (isRecreate) {
                if (!((await appConfirm('Opprett fakturaen i Fiken på nytt?\n\nFinnes salget fortsatt i Fiken, får du et duplikat der. Slett det gamle salget i Fiken først.')) && dialogAction.isCurrent())) {
                    return;
                }
            }
            const idInt = parseInt(id, 10);
            const { fikenLoading = {} } = this.state;
            fikenLoading[idInt] = true;
            this.setState({ fikenLoading });

            const { callback = () => {} } = this.props;
            const { fikenStore } = this.props.stores;
            const response = await fikenStore.createCompanyInvoice(id, { force: isRecreate });
            // console.log('createFikenCompanyInvoice', response);
            callback();
            fikenLoading[idInt] = false;
            this.setState({ fikenLoading });
        } finally {
            dialogAction.finish();
        }
    }

    createFikenCompanySale = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);
        const { fikenLoading = {} } = this.state;
        fikenLoading[idInt] = true;
        this.setState({ fikenLoading });
        const { callback = () => {} } = this.props;
        const { fikenStore } = this.props.stores;
        const response = await fikenStore.createCompanySale(id);
        // console.log('createFikenCompanyInvoice', response);
        callback();
        fikenLoading[idInt] = false;
        this.setState({ fikenLoading });
    }

    sendFikenInvoice = async e => {
        e.preventDefault();
        // Stop the row's onClickViewVisit from also firing — same reason as the
        // Tripletex "Send nå" button (TICKET-01260).
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);
        const { fikenSendLoading = {}, fikenSentToCustomer = {}, fikenSendError = {} } = this.state;
        if (fikenSendLoading[idInt]) return;
        fikenSendLoading[idInt] = true;
        delete fikenSendError[idInt];
        this.setState({ fikenSendLoading, fikenSendError });
        const { callback = () => {} } = this.props;
        const { fikenStore } = this.props.stores;
        const response = await fikenStore.sendCompanyInvoice(idInt);
        fikenSendLoading[idInt] = false;
        if (response && response.status === 200) {
            fikenSentToCustomer[idInt] = true;
        } else {
            // Surface the backend error instead of swallowing it — the button
            // otherwise looks dead to the user (same lesson as TICKET-01362).
            const fikenMessage = response?.data?.message;
            fikenSendError[idInt] = [response?.message || 'Sending feilet.', fikenMessage]
                .filter(m => m).join(' ');
        }
        this.setState({ fikenSendLoading, fikenSentToCustomer, fikenSendError });
        callback();
    }

    sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

    // Bulk actions (Send all invoices, Create all samlefakturaer, Tripletex/Fiken
    // batch transfers) all iterate over saleStore.sales — which is the *currently
    // displayed page*, capped by saleStore.limit (default 25). Without this helper
    // a bulk action silently skips anything on later pages, and any sales that
    // were just created (e.g. samlefakturaer) won't be picked up either.
    loadAllSalesForBulkAction = async () => {
        const { saleStore } = this.props.stores;
        const { callback = () => {} } = this.props;
        const previousLimit = saleStore.limit;
        const previousPage = saleStore.currentPage;
        const { totalSales } = saleStore;
        // Bulk actions iterate saleStore.sales — never let them run on a
        // cross-company list (featureFlags.sharedSales toggle).
        const hadMyCustomers = !!saleStore.myCustomers;
        if (hadMyCustomers) {
            saleStore.updateKeyValue('myCustomers', null);
        }
        const expandedLimit = Math.min(Math.max(totalSales || 1000, 100), 5000);
        const needsReload = expandedLimit > previousLimit || previousPage > 1 || hadMyCustomers;
        if (needsReload) {
            saleStore.updateKeyValue('limit', expandedLimit);
            saleStore.updateKeyValue('currentPage', 1);
            await callback();
        }
        return async () => {
            if (needsReload) {
                saleStore.updateKeyValue('limit', previousLimit);
                saleStore.updateKeyValue('currentPage', previousPage);
            }
            await callback();
        };
    }

    // Email invoices
    sendEmailInvoiceByID = async id => {
        const idInt = parseInt(id, 10);
        const { emailLoading = {}, emailSent = {} } = this.state;
        emailLoading[idInt] = true;
        this.setState({ emailLoading });

        const { saleStore } = this.props.stores;
        const response = await saleStore.emailReminder({
            sale: {
                id: idInt,
            },
        });
        await this.sleep(200);

        const success = response?.status === 200;
        emailLoading[idInt] = false;
        if (success) {
            emailSent[idInt] = true;
        }
        this.setState({ emailLoading, emailSent });
        return { success, message: response?.message };
    }
    sendAllInvoicesViaEmail = async e => {
        this.setState({ sendAllInvoicesViaEmail: true, invoicesSent: 0, invoicesFailed: 0 });
        const { saleStore } = this.props.stores;
        const restorePagination = await this.loadAllSalesForBulkAction();

        const { sales } = saleStore;
        const invoiceSalesForEmail = sales.filter(isInvoiceSaleReadyForEmail);

        let invoicesSent = 0;
        let invoicesFailed = 0;
        let failMessage = null;
        for (let i = 0, l = invoiceSalesForEmail.length; i < l; i += 1) {
            const sale = invoiceSalesForEmail[i];
            const { success, message } = await this.sendEmailInvoiceByID(sale.id) || {};
            if (success) {
                invoicesSent += 1;
            } else {
                invoicesFailed += 1;
                failMessage = message || failMessage;
            }
            this.setState({ invoicesSent, invoicesFailed });
        }

        await restorePagination();
        this.setState({
            sendAllInvoicesViaEmail: false,
            allInvoicesSentViaEmail: true,
            emailInvoiceStep3Done: true,
            emailInvoiceSummary: { invoicesSent, invoicesFailed, failMessage },
        });
    }
    checkForInvoicesReadyForEmail = async e => {
        this.setState({ checkingInvoicesForEmail: true });
        const { saleStore } = this.props.stores;
        const { sales } = saleStore;
        const invoiceSalesForEmail = sales.filter(isInvoiceSaleReadyForEmail);

        // Ensure spinner is visible for at least 300ms
        await new Promise(resolve => setTimeout(resolve, 300));
        this.setState({ invoiceSalesForEmail, checkingInvoicesForEmail: false });
        console.log('checkForInvoicesReadyForEmail', invoiceSalesForEmail);
    }

    // Purringer (bulk invoice reminders)
    presetReminderFilters = async () => {
        const { saleStore } = this.props.stores;
        const { callback = () => {}, setQuickDateFilter } = this.props;
        this.setState({
            reminderStep: 1,
            reminderLoading: true,
            reminderRows: [],
            reminderSelected: {},
            reminderResult: null,
            reminderProgress: 0,
            // Show the page's active search inside the wizard so a leftover
            // search (e.g. a customer name) is visible and changeable in step 2.
            reminderSearchText: saleStore.searchText || '',
            reminderListLoading: false,
        });
        this.applyWizardListLimit();
        // The overdueInvoices critical-task query (status 2-5, dueDate passed, not paid)
        // defines exactly which sales qualify for a purring — reuse it as the list filter.
        saleStore.updateKeyValue('criticalTask', 'overdueInvoices');
        saleStore.updateKeyValue('filterIds', null);
        saleStore.updateKeyValue('activeStatus', null);
        saleStore.updateKeyValue('activePaymentMethod', null);
        saleStore.updateKeyValue('cutoffDate', null);
        saleStore.updateKeyValue('cutoffDateEnd', null);
        if (setQuickDateFilter) {
            setQuickDateFilter(null, null);
        }
        await callback();
        await this.loadReminderSmsTemplate();
        this.setState({ reminderLoading: false });
    }

    loadReminderSmsTemplate = async () => {
        try {
            // GET /api/textTemplates/ can filter by section only — pick field client-side.
            const response = await util.fetchApi('/api/textTemplates/', { method: 'GET' }, { section: 'paymentReminder' });
            const templates = (response && response.data) || [];
            const smsTemplate = templates.find(t => t.field === 'sms');
            this.setState({
                reminderSmsText: (smsTemplate && smsTemplate.body) || REMINDER_DEFAULT_SMS,
                reminderSmsTemplateId: (smsTemplate && smsTemplate.id) || null,
            });
        } catch (err) {
            console.error('Error loading SMS reminder template:', err);
            this.setState({ reminderSmsText: REMINDER_DEFAULT_SMS });
        }
    }

    saveReminderSmsTemplate = async () => {
        const { reminderSmsText, reminderSmsTemplateId } = this.state;
        try {
            if (reminderSmsTemplateId) {
                await util.fetchApi(`/api/textTemplates/${reminderSmsTemplateId}`, { method: 'PATCH' }, { body: reminderSmsText });
            } else {
                const response = await util.fetchApi('/api/textTemplates/', { method: 'POST' }, {
                    section: 'paymentReminder',
                    field: 'sms',
                    title: 'SMS purring',
                    body: reminderSmsText,
                });
                // POST /api/textTemplates/ returns 201 with data: { texttemplate: {...} }
                const created = response && response.data && (response.data.texttemplate || response.data);
                if (created && created.id) {
                    this.setState({ reminderSmsTemplateId: created.id });
                }
            }
        } catch (err) {
            console.error('Error saving SMS reminder template:', err);
        }
    }

    toggleReminderChannel = (channel) => {
        this.setState(prevState => ({
            reminderChannels: {
                ...prevState.reminderChannels,
                [channel]: !prevState.reminderChannels[channel],
            },
        }));
    }

    toggleReminderRow = (saleId) => {
        this.setState(prevState => ({
            reminderSelected: {
                ...prevState.reminderSelected,
                [saleId]: !prevState.reminderSelected[saleId],
            },
        }));
    }

    reminderGoToStep2 = async () => {
        const { reminderChannels } = this.state;
        if (!reminderChannels.email && !reminderChannels.sms) {
            return;
        }
        if (reminderChannels.sms) {
            await this.saveReminderSmsTemplate();
        }
        this.buildReminderRows();
        this.setState({ reminderStep: 2 });
    }

    buildReminderRows = () => {
        const { saleStore } = this.props.stores;
        const { sales, allPayments = [] } = saleStore;
        const { reminderRows: prevRows, reminderSelected: prevSelected } = this.state;
        // Row building + default/preserved selection lives in lib/reminderRows.js.
        const { reminderRows, reminderSelected } = buildReminderRowsFromSales({
            sales,
            allPayments,
            sumTotal: (products, format) => saleStore.sumTotal(products, format),
            sumRestPayments: (payments, total, format, hideEmpty) => saleStore.sumRestPayments(payments, total, format, hideEmpty),
            prevRows,
            prevSelected,
        });
        this.setState({ reminderRows, reminderSelected });
    }

    // Step 2 search: filters which overdue sales are listed. Reuses the page's
    // search (saleStore.searchText + reload), so the same tokens work here.
    reminderSearchInput = (e) => {
        const search = e.target.value;
        this.setState({ reminderSearchText: search });
        clearTimeout(this.reminderSearchTimer);
        // Mirror the page search behaviour: search on >= 3 chars, reset on empty.
        if (search.length === 0 || search.length >= 3) {
            this.reminderSearchTimer = setTimeout(() => this.reloadReminderRows(search), 600);
        }
    }

    clearReminderSearch = () => {
        clearTimeout(this.reminderSearchTimer);
        this.setState({ reminderSearchText: '' });
        this.reloadReminderRows('');
    }

    reloadReminderRows = async (search) => {
        const { saleStore } = this.props.stores;
        const { callback = () => {} } = this.props;
        saleStore.updateKeyValue('searchText', search);
        this.setState({ reminderListLoading: true });
        await callback();
        this.buildReminderRows();
        this.setState({ reminderListLoading: false });
    }

    sendAllReminders = async () => {
        const { saleStore } = this.props.stores;
        const { reminderRows, reminderSelected, reminderChannels, reminderSmsText } = this.state;
        const selectedRows = reminderRows.filter(row => reminderSelected[row.sale.id]);

        this.setState({ reminderSending: true, reminderStep: 3, reminderProgress: 0 });

        const result = { emailsSent: 0, smsSent: 0, failed: [], skipped: [] };

        for (let i = 0, l = selectedRows.length; i < l; i += 1) {
            const row = selectedRows[i];
            const saleId = row.sale.id;

            if (reminderChannels.email) {
                if (row.email) {
                    const response = await saleStore.emailReminder({ sale: { id: saleId }, purring: true });
                    if (response && response.status === 200) {
                        result.emailsSent += 1;
                    } else {
                        result.failed.push({ saleId, channel: 'e-post', error: (response && response.message) || 'Ukjent feil' });
                    }
                } else {
                    result.skipped.push({ saleId, reason: 'Mangler e-postadresse' });
                }
            }

            if (reminderChannels.sms) {
                if (row.cellphone) {
                    const response = await saleStore.smsReminder({ sale: { id: saleId }, message: reminderSmsText });
                    if (response && response.status === 200) {
                        result.smsSent += 1;
                    } else {
                        result.failed.push({ saleId, channel: 'SMS', error: (response && response.message) || 'Ukjent feil' });
                    }
                } else {
                    result.skipped.push({ saleId, reason: 'Mangler mobilnummer' });
                }
            }

            await this.sleep(200);
            this.setState({ reminderProgress: i + 1 });
        }

        this.setState({ reminderSending: false, reminderResult: result });
    }
    // / Purringer

    // / Email invoices

    // Medical history
    loadMedicalHistoryPreview = async () => {
        this.setState({ medicalHistoryLoading: true });
        const { saleStore } = this.props.stores;
        const { cutoffDate, cutoffDateEnd } = saleStore;

        if (!cutoffDate || !cutoffDateEnd) {
            this.setState({ medicalHistoryLoading: false });
            return;
        }

        const { medicalHistorySelectedSpecies } = this.state;
        let url = `/api/journals/email/medical-history?preview=true&cutoffDate=${cutoffDate}&cutoffDateEnd=${cutoffDateEnd}`;
        if (medicalHistorySelectedSpecies.length > 0) {
            url += `&species=${medicalHistorySelectedSpecies.join(',')}`;
        }

        try {
            const response = await util.fetchApi(url);
            if (response && response.status === 200) {
                this.setState({
                    medicalHistoryPreview: response,
                    medicalHistorySpeciesInPeriod: response.speciesInPeriod || [],
                    medicalHistoryLoading: false,
                });
            } else {
                this.setState({ medicalHistoryLoading: false });
            }
        } catch (err) {
            console.error('Error loading medical history preview:', err);
            this.setState({ medicalHistoryLoading: false });
        }
    }

    setupMedicalHistoryFilters = async () => {
        this.setState({ medicalHistoryLoading: true });
        const { saleStore } = this.props.stores;
        const { setQuickDateFilter } = this.props;

        // Calculate previous month date range
        const now = new Date();
        const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDayPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);

        const prevYear = firstDayPrevMonth.getFullYear();
        const prevMonth = firstDayPrevMonth.getMonth() + 1;

        const fromDate = `${prevYear}-${String(prevMonth).padStart(2, '0')}-01`;
        const toDate = `${lastDayPrevMonth.getFullYear()}-${String(lastDayPrevMonth.getMonth() + 1).padStart(2, '0')}-${String(lastDayPrevMonth.getDate()).padStart(2, '0')}`;

        saleStore.updateKeyValue('cutoffDate', fromDate);
        saleStore.updateKeyValue('cutoffDateEnd', toDate);

        if (setQuickDateFilter) {
            setQuickDateFilter(prevYear, prevMonth);
        }

        // Load species available in period
        try {
            const url = `/api/journals/email/medical-history?preview=true&cutoffDate=${fromDate}&cutoffDateEnd=${toDate}`;
            const response = await util.fetchApi(url);
            if (response && response.status === 200) {
                this.setState({
                    medicalHistorySpeciesInPeriod: response.speciesInPeriod || [],
                });
            }
        } catch (err) {
            console.error('Error loading species:', err);
        }

        this.setState({ medicalHistoryLoading: false });
    }

    toggleMedicalHistorySpecies = (speciesId) => {
        this.setState(prevState => {
            const selected = prevState.medicalHistorySelectedSpecies;
            const idx = selected.indexOf(speciesId);
            if (idx === -1) {
                return { medicalHistorySelectedSpecies: [...selected, speciesId] };
            }
            return { medicalHistorySelectedSpecies: selected.filter(id => id !== speciesId) };
        });
    }

    sendMedicalHistoryEmails = async () => {
        const dialogAction = beginDialogAction(this, 'sendMedicalHistoryEmails', () => [
            this.props.stores.saleStore.cutoffDate,
            this.props.stores.saleStore.cutoffDateEnd,
            this.state.medicalHistorySelectedSpecies,
        ]);
        if (!dialogAction) return false;
        try {
            const { medicalHistoryPreview } = this.state;
            const count = medicalHistoryPreview ? medicalHistoryPreview.summary.withEmail : 0;
            if (!((await appConfirm(`Er du sikker på at du vil sende ${count} e-poster med medisinsk historikk?`)) && dialogAction.isCurrent())) {
                return;
            }
            this.setState({ medicalHistorySending: true });
            const { saleStore } = this.props.stores;
            const { cutoffDate, cutoffDateEnd } = saleStore;
            const { medicalHistorySelectedSpecies } = this.state;

            let url = `/api/journals/email/medical-history?cutoffDate=${cutoffDate}&cutoffDateEnd=${cutoffDateEnd}`;
            if (medicalHistorySelectedSpecies.length > 0) {
                url += `&species=${medicalHistorySelectedSpecies.join(',')}`;
            }

            try {
                const response = await util.fetchApi(url);
                if (response) {
                    this.setState({
                        medicalHistorySendResult: response,
                        medicalHistorySending: false,
                        medicalHistoryStep: 3,
                    });
                } else {
                    this.setState({ medicalHistorySending: false });
                }
            } catch (err) {
                console.error('Error sending medical history emails:', err);
                this.setState({ medicalHistorySending: false });
            }
        } finally {
            dialogAction.finish();
        }
    }

    // Single animal medical history
    onSelectVisitorForMedicalHistory = async (visitor) => {
        console.log('onSelectVisitorForMedicalHistory called with:', visitor);
        if (!visitor || !visitor.id) return;
        this.setState({ medicalHistorySingleVisitor: visitor, medicalHistorySingleAnimal: null });
        const { animalStore } = this.props.stores;
        const response = await animalStore.load({
            query: { owner: [visitor.id] },
            skipUpdate: true,
        });
        this.setState({ medicalHistorySingleAnimals: response || [] });
    }

    sendSingleAnimalMedicalHistory = async () => {
        const dialogAction = beginDialogAction(this, 'sendSingleAnimalMedicalHistory', () => [
            this.state?.medicalHistorySingleAnimal?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { medicalHistorySingleAnimal, medicalHistorySingleAllTime, medicalHistorySingleStartDate, medicalHistorySingleEndDate } = this.state;
            if (!medicalHistorySingleAnimal) return;

            const ownerEmail = this.state.medicalHistorySingleVisitor?.email || '';
            if (!ownerEmail) {
                (await appAlert('Eieren har ingen e-postadresse registrert.'));
                return;
            }

            if (!((await appConfirm(`Sende medisinsk historikk for ${medicalHistorySingleAnimal.name || 'dyr'} til ${ownerEmail}?`)) && dialogAction.isCurrent())) {
                return;
            }

            this.setState({ medicalHistorySending: true });

            let url = `/api/journals/email/medical-history?animalId=${medicalHistorySingleAnimal.id}`;
            if (!medicalHistorySingleAllTime && medicalHistorySingleStartDate && medicalHistorySingleEndDate) {
                url += `&cutoffDate=${medicalHistorySingleStartDate}&cutoffDateEnd=${medicalHistorySingleEndDate}`;
            }

            try {
                const response = await util.fetchApi(url);
                if (response) {
                    if (response.sent > 0) {
                        this.setState({
                            medicalHistorySendResult: response,
                            medicalHistorySending: false,
                            medicalHistorySingleSent: true,
                        });
                    } else {
                        // No journals or send failed — show error briefly
                        const reason = response.skippedAnimals?.[0]?.reason || 'Ingen journaler funnet';
                        this.setState({
                            medicalHistorySending: false,
                            medicalHistorySingleError: reason,
                        });
                        setTimeout(() => {
                            this.setState({ medicalHistorySingleError: '' });
                        }, 2000);
                    }
                } else {
                    this.setState({ medicalHistorySending: false });
                }
            } catch (err) {
                console.error('Error sending single animal medical history:', err);
                this.setState({ medicalHistorySending: false });
            }
        } finally {
            dialogAction.finish();
        }
    }

    // / Medical history

    // Fiken
    sendFikenInvoiceByID = async id => {
        const idInt = parseInt(id, 10);
        const { fikenLoading = {}, fikenSent = {} } = this.state;
        // Guard against double-click / parallel calls for the same sale.
        if (fikenLoading[idInt]) return;
        fikenLoading[idInt] = true;
        this.setState({ fikenLoading });

        const { fikenStore } = this.props.stores;
        const response = await fikenStore.createCompanyInvoice(id);
        await this.sleep(200);

        fikenLoading[idInt] = false;
        fikenSent[idInt] = true;
        this.setState({ fikenLoading, fikenSent });
    }

    sendFikenSaleByID = async id => {
        const idInt = parseInt(id, 10);
        const { fikenLoading = {}, fikenSent = {} } = this.state;
        // Guard against double-click / parallel calls for the same sale.
        if (fikenLoading[idInt]) return;
        fikenLoading[idInt] = true;
        this.setState({ fikenLoading });

        const { fikenStore } = this.props.stores;
        const response = await fikenStore.createCompanySale(id);
        await this.sleep(200);

        fikenLoading[idInt] = false;
        fikenSent[idInt] = true;
        this.setState({ fikenLoading, fikenSent });
    }

    sendAllInvoicesToFiken = async e => {
        // Guard against double-click on the bulk button.
        if (this.state.sendAllInvoicesToFiken || this.state.sendAllSalesToFiken) return;
        this.setState({ sendAllInvoicesToFiken: true });
        const { saleStore } = this.props.stores;
        const restorePagination = await this.loadAllSalesForBulkAction();
        const { sales } = saleStore;

        const invoiceSalesForFikenTransfer = sales.filter(e => {
            const isInvoice = e.paymentMethod === 'invoice';
            const isTransferred = !!(e.fikenLocation && e.fikenLocation.length > 0);
            const transferCheck = this.canTransferToAccounting(e);
            return isInvoice && !isTransferred && transferCheck.canTransfer;
        });

        for (let i = 0, l = invoiceSalesForFikenTransfer.length; i < l; i += 1) {
            const sale = invoiceSalesForFikenTransfer[i];
            await this.sendFikenInvoiceByID(sale.id);
        }

        await restorePagination();
        this.setState({ sendAllInvoicesToFiken: false, allInvoicesSentToFiken: true });
    }

    sendAllSalesToFiken = async e => {
        // Guard against double-click on the bulk button.
        if (this.state.sendAllInvoicesToFiken || this.state.sendAllSalesToFiken) return;
        this.setState({ sendAllSalesToFiken: true });
        const { saleStore } = this.props.stores;
        const restorePagination = await this.loadAllSalesForBulkAction();
        const { sales } = saleStore;

        const saleSalesForFikenTransfer = sales.filter(e => {
            const isPaid = e.status === 1;
            const isTransferred = !!(e.fikenLocation && e.fikenLocation.length > 0);
            const transferCheck = this.canTransferToAccounting(e);
            return isPaid && !isTransferred && transferCheck.canTransfer;
        });

        for (let i = 0, l = saleSalesForFikenTransfer.length; i < l; i += 1) {
            const sale = saleSalesForFikenTransfer[i];
            await this.sendFikenSaleByID(sale.id);
        }

        await restorePagination();
        this.setState({ sendAllSalesToFiken: false, allSalesSentToFiken: true });
    }

    checkForInvoicesReadyForFiken = async e => {
        this.setState({ checkingInvoicesForFiken: true });
        const { saleStore } = this.props.stores;
        const { sales } = saleStore;
        const invoiceSalesForFikenTransfer = sales.filter(e => {
            // paymentMethod	"invoice"
            const isInvoice = e.paymentMethod === 'invoice';
            const isTransferred = !!(e.fikenLocation && e.fikenLocation.length > 0);
            // Filter out credit notes and invalid sales
            const transferCheck = this.canTransferToAccounting(e);
            return isInvoice && !isTransferred && transferCheck.canTransfer;
        });

        // Ensure spinner is visible for at least 300ms
        await new Promise(resolve => setTimeout(resolve, 300));
        this.setState({ invoiceSalesForFikenTransfer, checkingInvoicesForFiken: false });
        // console.log('checkForInvoicesReadyForFiken', invoiceSalesForFikenTransfer);
    }

    checkForSalesReadyForFiken = async e => {
        this.setState({ checkingSalesForFiken: true });
        const { saleStore } = this.props.stores;
        const { sales } = saleStore;
        const saleSalesForFikenTransfer = sales.filter(e => {
            // paymentMethod	"invoice"
            const isPaid = e.status === 1;
            const isTransferred = !!(e.fikenLocation && e.fikenLocation.length > 0);
            // Filter out credit notes and invalid sales
            const transferCheck = this.canTransferToAccounting(e);
            return isPaid && !isTransferred && transferCheck.canTransfer;
        });

        // Ensure spinner is visible for at least 300ms
        await new Promise(resolve => setTimeout(resolve, 300));
        this.setState({ saleSalesForFikenTransfer, checkingSalesForFiken: false });
        // console.log('checkForInvoicesReadyForFiken', invoiceSalesForFikenTransfer);
    }

    updateFromFikenInvoices = async e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ updateFromFikenInvoices: true });
        const { saleStore } = this.props.stores;

        const { sales, searchText, activeStatus, activeUser, activePaymentMethod, cutoffDate, cutoffDateEnd, limit } = saleStore;
        // const saleIds = sales.map(e => e.id);
        // if (saleIds.length === 0) {
        //     return false;
        // }
        const { fikenStore } = this.props.stores;
        // const response = await tripletexStore.updateInvoices(saleIds);
        const response = await fikenStore.updateInvoicesByDate(cutoffDate, cutoffDateEnd);
        // console.log('updateFromFikenInvoices', response);
        this.setState({
            updateFromFikenInvoices: false,
            fikenUpdateResponse: response
        });

        const { callback = () => {} } = this.props;
        callback();
    }
    // / Fiken


    // invoiceSalesForTripletexTransfer
    checkForInvoicesReadyForTripletex = async e => {
        this.setState({ checkingInvoicesForTripletex: true });
        const { saleStore } = this.props.stores;
        const { sales } = saleStore;
        const invoiceSalesForTripletexTransfer = sales.filter(e => {
            // paymentMethod	"invoice"
            const isInvoice = e.paymentMethod === 'invoice';
            const isTransferred = !!e.tripletexInvoice?.id || (e.tripletexResponse && e.tripletexResponse?.statusCode === 201);
            // Filter out credit notes and invalid sales
            const transferCheck = this.canTransferToAccounting(e);
            return isInvoice && !isTransferred && transferCheck.canTransfer;
        });

        // Ensure spinner is visible for at least 300ms
        await new Promise(resolve => setTimeout(resolve, 300));
        this.setState({ invoiceSalesForTripletexTransfer, checkingInvoicesForTripletex: false });
        // console.log('checkForInvoicesReadyForTripletex', invoiceSalesForTripletexTransfer);
    }

    checkForSalesReadyForTripletex = async e => {
        this.setState({ checkingSalesForTripletex: true });
        const { saleStore } = this.props.stores;
        const { sales } = saleStore;
        const saleSalesForTripletexTransfer = sales.filter(e => {
            // paymentMethod	"invoice"
            const isPaid = e.status === 1;
            const isTransferred = !!e.tripletexInvoice?.id || (e.tripletexResponse && e.tripletexResponse?.statusCode === 201);
            // Filter out credit notes and invalid sales
            const transferCheck = this.canTransferToAccounting(e);
            return isPaid && !isTransferred && transferCheck.canTransfer;
        });

        // Ensure spinner is visible for at least 300ms
        await new Promise(resolve => setTimeout(resolve, 300));
        this.setState({ saleSalesForTripletexTransfer, checkingSalesForTripletex: false });
        // console.log('checkForInvoicesReadyForTripletex', invoiceSalesForTripletexTransfer);
    }

    sendAllInvoicesToTripletex = async e => {
        this.setState({ sendAllInvoicesToTripletex: true });
        const { saleStore } = this.props.stores;
        const restorePagination = await this.loadAllSalesForBulkAction();
        const { sales } = saleStore;

        const invoiceSalesForTripletexTransfer = sales.filter(e => {
            const isInvoice = e.paymentMethod === 'invoice';
            const isTransferred = !!e.tripletexInvoice?.id || (e.tripletexResponse && e.tripletexResponse?.statusCode === 201);
            const transferCheck = this.canTransferToAccounting(e);
            return isInvoice && !isTransferred && transferCheck.canTransfer;
        });

        for (let i = 0, l = invoiceSalesForTripletexTransfer.length; i < l; i += 1) {
            const sale = invoiceSalesForTripletexTransfer[i];
            await this.sendTripletexInvoiceById(sale.id);
        }

        await restorePagination();
        this.setState({ sendAllInvoicesToTripletex: false, allInvoicesSentToTripletex: true });
    }

    sendAllSalesToTripletex = async e => {
        this.setState({ sendAllSalesToTripletex: true });
        const { saleStore } = this.props.stores;
        const restorePagination = await this.loadAllSalesForBulkAction();
        const { sales } = saleStore;

        const saleSalesForTripletexTransfer = sales.filter(e => {
            const isPaid = e.status === 1;
            const isTransferred = !!e.tripletexInvoice?.id || (e.tripletexResponse && e.tripletexResponse?.statusCode === 201);
            const transferCheck = this.canTransferToAccounting(e);
            return isPaid && !isTransferred && transferCheck.canTransfer;
        });

        for (let i = 0, l = saleSalesForTripletexTransfer.length; i < l; i += 1) {
            const sale = saleSalesForTripletexTransfer[i];
            await this.sendTripletexInvoiceById(sale.id);
        }

        await restorePagination();
        this.setState({ sendAllSalesToTripletex: false, allSalesSentToTripletex: true });
    }

    // TICKET-01186: the backend replies HTTP 200 even when Tripletex rejected the
    // invoice — success is only signalled by response.data.value.id (the same check
    // the backend uses before promoting the sale). Extract a human readable reason
    // for the failure so the row can show WHY instead of a silent no-op.
    getTripletexCreateError = response => {
        const data = response?.data || {};
        if (data.value?.id) {
            return null;
        }
        const messages = [];
        // Pre-flight rejections (transfer lock, insurance block, missing customer)
        // come back as a non-200 status with the reason in the top level message.
        if (response?.status && response.status !== 200 && response.message) {
            messages.push(response.message);
        }
        if (data.message) {
            messages.push(data.message);
        }
        if (Array.isArray(data.validationMessages)) {
            data.validationMessages.forEach(v => {
                if (v && v.message) {
                    messages.push(v.message);
                }
            });
        }
        if (messages.length === 0 && typeof data.error === 'string' && data.error) {
            messages.push(data.error);
        }
        return messages.join(' - ') || 'Ukjent feil ved overføring til Tripletex';
    }

    sendTripletexInvoiceById = async id => {
        const idInt = parseInt(id, 10);
        const { tripletexLoading = {}, tripletexSent = {}, tripletexCreateError = {} } = this.state;
        // Guard against double-click / parallel calls for the same sale.
        // React's `disabled` prop has a frame's latency before it takes effect,
        // so we must also block in-handler.
        if (tripletexLoading[idInt]) return;
        tripletexLoading[idInt] = true;
        delete tripletexCreateError[idInt];
        this.setState({ tripletexLoading, tripletexCreateError });
        const { tripletexStore } = this.props.stores;

        const response = await tripletexStore.createInvoice(id);
        // console.log('sendTripletexInvoiceById', response);
        await this.sleep(200);

        tripletexLoading[idInt] = false;
        // TICKET-01186: only flag the row as sent when Tripletex actually created
        // the invoice. Marking it unconditionally showed a green "Faktura opprettet"
        // for failed transfers, so the user believed the invoice existed in
        // Tripletex when it never did.
        const createError = this.getTripletexCreateError(response);
        if (createError) {
            tripletexCreateError[idInt] = createError;
        } else {
            tripletexSent[idInt] = true;
        }
        this.setState({ tripletexLoading, tripletexSent, tripletexCreateError });
    }

    sendTripletexInvoiceToCustomer = async e => {
        e.preventDefault();
        // The whole <tr> opens the visit via onClickViewVisit. Without stopping
        // the bubbling, every "Send nå" click also navigated to the sale view and
        // closed the "Alle besøk" drawer, forcing the user back into the list for
        // each invoice (TICKET-01260).
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);
        const { tripletexSendLoading = {}, tripletexSentToCustomer = {}, tripletexSendError = {} } = this.state;
        if (tripletexSendLoading[idInt]) return;
        tripletexSendLoading[idInt] = true;
        delete tripletexSendError[idInt];
        this.setState({ tripletexSendLoading, tripletexSendError });
        const { tripletexStore } = this.props.stores;
        const { callback = () => {} } = this.props;
        const response = await tripletexStore.sendInvoiceToCustomer(idInt);
        tripletexSendLoading[idInt] = false;
        if (response && response.status === 200) {
            tripletexSentToCustomer[idInt] = true;
        } else {
            // A failed send used to be swallowed here, so the button looked
            // dead to the user (TICKET-01362). Backend puts the Tripletex
            // error body under data (e.g. data.message "Validering feilet.").
            const tripletexMessage = response?.data?.message;
            tripletexSendError[idInt] = [response?.message || 'Sending feilet.', tripletexMessage]
                .filter(m => m).join(' ');
        }
        this.setState({ tripletexSendLoading, tripletexSentToCustomer, tripletexSendError });
        callback();
    }

    createTripletexInvoice = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id, recreate } = e.target.closest('button').dataset;
        const dialogAction = beginDialogAction(this, `createTripletexInvoice:${parseInt(id, 10)}`);
        if (!dialogAction) return false;
        try {
            // Show confirm dialog for re-creation to prevent duplicate invoices
            if (recreate === 'true') {
                if (!((await appConfirm('Are you sure you want to re-create this invoice/sale in Tripletex?\n\nWARNING: This will create a DUPLICATE entry in Tripletex. Only proceed if you are certain the previous entry was deleted or failed.')) && dialogAction.isCurrent())) {
                    return;
                }
            }

            const idInt = parseInt(id, 10);
            const { tripletexLoading = {}, tripletexCreateError = {} } = this.state;
            // Guard against double-click / parallel calls for the same sale.
            if (tripletexLoading[idInt]) return;
            tripletexLoading[idInt] = true;
            delete tripletexCreateError[idInt];
            this.setState({ tripletexLoading, tripletexCreateError });

            const { callback = () => {} } = this.props;
            const { tripletexStore } = this.props.stores;
            const response = await tripletexStore.createInvoice(id);
            // console.log('createTripletexInvoice', response);
            // TICKET-01186: surface failures — previously a failed transfer just put the
            // "Opprett faktura" button back with no explanation.
            const createError = this.getTripletexCreateError(response);
            if (createError) {
                tripletexCreateError[idInt] = createError;
            }
            callback();
            tripletexLoading[idInt] = false;
            this.setState({ tripletexLoading, tripletexCreateError });
        } finally {
            dialogAction.finish();
        }
    }

    simulateTripletexInvoice = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);
        const { tripletexLoading = {}, tripletexError = {} } = this.state;
        tripletexLoading[idInt] = true;
        this.setState({ tripletexLoading });

        const { callback = () => {} } = this.props;
        const { tripletexStore } = this.props.stores;
        const response = await tripletexStore.simulateInvoice(id);
        // console.log('simulateTripletexInvoice', response);
        callback();
        tripletexLoading[idInt] = false;
        // Auto-expand debug section to show simulation results
        tripletexError[idInt] = true;
        this.setState({ tripletexLoading, tripletexError });
    }

    addTripletexPayment = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);
        const { tripletexLoading = {} } = this.state;
        tripletexLoading[idInt] = true;
        this.setState({ tripletexLoading });

        const { callback = () => {} } = this.props;
        const { tripletexStore } = this.props.stores;
        const response = await tripletexStore.addPayment(id);
        // console.log('addTripletexPayment', response);
        callback();
        tripletexLoading[idInt] = false;
        this.setState({ tripletexLoading });
    }

    createTripletexCreditNote = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id, recreate } = e.target.closest('button').dataset;
        const dialogAction = beginDialogAction(this, `createTripletexCreditNote:${parseInt(id, 10)}`);
        if (!dialogAction) return false;
        try {
            // Show confirm dialog for re-creation to prevent duplicate credit notes
            if (recreate === 'true') {
                if (!((await appConfirm('Are you sure you want to re-create this credit note in Tripletex?\n\nWARNING: This will create a DUPLICATE entry in Tripletex. Only proceed if you are certain the previous entry was deleted or failed.')) && dialogAction.isCurrent())) {
                    return;
                }
            }

            const idInt = parseInt(id, 10);
            const { tripletexLoading = {}, tripletexCreateError = {} } = this.state;
            tripletexLoading[idInt] = true;
            delete tripletexCreateError[idInt];
            this.setState({ tripletexLoading, tripletexCreateError });

            const { callback = () => {} } = this.props;
            const { tripletexStore } = this.props.stores;
            const response = await tripletexStore.createCreditNote(id);
            // console.log('createTripletexCreditNote', response);
            // TICKET-01186: surface failures instead of silently re-showing the button.
            const createError = this.getTripletexCreateError(response);
            if (createError) {
                tripletexCreateError[idInt] = createError;
            }
            callback();
            tripletexLoading[idInt] = false;
            this.setState({ tripletexLoading, tripletexCreateError });
        } finally {
            dialogAction.finish();
        }
    }

    // PowerOffice Go methods
    createPowerOfficeInvoice = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id, recreate } = e.target.closest('button').dataset;
        const dialogAction = beginDialogAction(this, `createPowerOfficeInvoice:${parseInt(id, 10)}`);
        if (!dialogAction) return false;
        try {
            // Show confirm dialog for re-creation to prevent duplicate invoices
            if (recreate === 'true') {
                if (!((await appConfirm('Are you sure you want to re-create this invoice/sale in PowerOffice?\n\nWARNING: This will create a DUPLICATE entry in PowerOffice. Only proceed if you are certain the previous entry was deleted or failed.')) && dialogAction.isCurrent())) {
                    return;
                }
            }

            const idInt = parseInt(id, 10);
            const { powerofficeLoading = {} } = this.state;
            // Guard against double-click / parallel calls for the same sale.
            if (powerofficeLoading[idInt]) return;
            powerofficeLoading[idInt] = true;
            this.setState({ powerofficeLoading });

            const { callback = () => {} } = this.props;
            const { powerofficeStore } = this.props.stores;
            const response = await powerofficeStore.createInvoiceFromSale(id);
            callback();
            powerofficeLoading[idInt] = false;
            this.setState({ powerofficeLoading });
        } finally {
            dialogAction.finish();
        }
    }

    simulatePowerOfficeInvoice = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);
        const { powerofficeLoading = {}, powerofficeError = {} } = this.state;
        powerofficeLoading[idInt] = true;
        this.setState({ powerofficeLoading });

        const { callback = () => {} } = this.props;
        const { powerofficeStore } = this.props.stores;
        const response = await powerofficeStore.simulateInvoice(id);
        callback();
        powerofficeLoading[idInt] = false;
        // Auto-expand debug section to show simulation results
        powerofficeError[idInt] = true;
        this.setState({ powerofficeLoading, powerofficeError });
    }

    // Duett Økonomi methods
    createDuettInvoiceFromRow = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);
        const { duettLoading = {}, duettSent = {}, duettError = {}, duettSimResult = {} } = this.state;
        // Guard against double-click / parallel calls for the same sale.
        if (duettLoading[idInt]) return;
        duettLoading[idInt] = true;
        this.setState({ duettLoading });

        const { callback = () => {} } = this.props;
        const { duettStore } = this.props.stores;
        const response = await duettStore.createInvoice(idInt);
        duettLoading[idInt] = false;
        if (response && response.status < 300) {
            duettSent[idInt] = true;
            duettError[idInt] = null;
            duettSimResult[idInt] = null;
        } else {
            // Backend messages already include Duett errorMessages verbatim
            // ("Duett avviste ordren: ..."), incl. the 409 already-transferred case.
            duettError[idInt] = (response && response.message) || 'Ukjent feil ved sending til Duett.';
        }
        this.setState({ duettLoading, duettSent, duettError, duettSimResult });
        callback();
    }

    // Standard Dyrejournal credit flow → Duett (Tripletex parity, 2026-09-04): the
    // credit-note sale row sends an orderType 'credit' order against its parent's
    // Duett invoice. Same double-click guard and error surfacing as the invoice row.
    createDuettCreditNoteFromRow = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);
        const { duettLoading = {}, duettSent = {}, duettError = {} } = this.state;
        if (duettLoading[idInt]) return;
        duettLoading[idInt] = true;
        this.setState({ duettLoading });

        const { callback = () => {} } = this.props;
        const { duettStore } = this.props.stores;
        const response = await duettStore.createCreditNote(idInt);
        duettLoading[idInt] = false;
        if (response && response.status < 300) {
            duettSent[idInt] = true;
            duettError[idInt] = null;
        } else {
            duettError[idInt] = (response && response.message) || 'Ukjent feil ved sending av kreditnota til Duett.';
        }
        this.setState({ duettLoading, duettSent, duettError });
        callback();
    }

    simulateDuettInvoiceFromRow = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);
        const { duettLoading = {}, duettError = {}, duettSimResult = {} } = this.state;
        if (duettLoading[idInt]) return;
        duettLoading[idInt] = true;
        this.setState({ duettLoading });

        const { duettStore } = this.props.stores;
        const response = await duettStore.simulateInvoice(idInt);
        duettLoading[idInt] = false;
        if (response && response.status < 300 && response.data) {
            duettSimResult[idInt] = {
                expectedGross: response.data.expectedGross,
                duettCustomerId: response.data.customerResolution?.duettCustomerId || null,
            };
            duettError[idInt] = null;
        } else {
            duettSimResult[idInt] = null;
            duettError[idInt] = (response && response.message) || 'Ukjent feil ved simulering.';
        }
        this.setState({ duettLoading, duettError, duettSimResult });
    }

    togglePowerOfficeError = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const { powerofficeError = {} } = this.state;
        powerofficeError[id] = !powerofficeError[id];
        this.setState({ powerofficeError });
    }

    togglePowerOfficeExpandedSummary = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('span').dataset;
        const { powerofficeExpandedSummary = {} } = this.state;
        powerofficeExpandedSummary[id] = !powerofficeExpandedSummary[id];
        this.setState({ powerofficeExpandedSummary });
    }

    togglePowerOfficeExpandedErrors = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('span').dataset;
        const { powerofficeExpandedErrors = {} } = this.state;
        powerofficeExpandedErrors[id] = !powerofficeExpandedErrors[id];
        this.setState({ powerofficeExpandedErrors });
    }

    previewFromTripletexInvoices = async () => {
        this.setState({ updateFromTripletexPreviewLoading: true });
        const { saleStore, tripletexStore } = this.props.stores;
        const { cutoffDate, cutoffDateEnd } = saleStore;
        const response = await tripletexStore.updateInvoicesByDate(cutoffDate, cutoffDateEnd, true);
        this.setState({
            updateFromTripletexPreviewLoading: false,
            tripletexUpdateResponse: response,
            accountingUpdateStep2Done: true,
        });
    }

    updateFromTripletexInvoices = async e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ updateFromTripletexInvoices: true });
        const { saleStore } = this.props.stores;

        const { sales, searchText, activeStatus, activeUser, activePaymentMethod, cutoffDate, cutoffDateEnd, limit } = saleStore;
        // const saleIds = sales.map(e => e.id);
        // if (saleIds.length === 0) {
        //     return false;
        // }
        const { tripletexStore } = this.props.stores;
        // const response = await tripletexStore.updateInvoices(saleIds);
        const response = await tripletexStore.updateInvoicesByDate(cutoffDate, cutoffDateEnd);
        // console.log('updateFromTripletexInvoices', response);
        this.setState({
            updateFromTripletexInvoices: false,
            tripletexUpdateResponse: response,
            accountingUpdateStep3Done: true,
        });

        const { callback = () => {} } = this.props;
        callback();
    }

    updateFromPowerOfficeInvoices = async e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ updateFromPowerOfficeInvoices: true });
        const { saleStore } = this.props.stores;

        const { cutoffDate, cutoffDateEnd } = saleStore;
        const { powerofficeStore } = this.props.stores;
        const response = await powerofficeStore.updateInvoicesByDate(cutoffDate, cutoffDateEnd);
        this.setState({
            updateFromPowerOfficeInvoices: false,
            powerofficeUpdateResponse: response
        });

        const { callback = () => {} } = this.props;
        callback();
    }

    checkForSalesReadyForCollectiveInvoice = async e => {
        this.setState({ checkingCollectiveInvoice: true });
        const { saleStore } = this.props.stores;
        const { sales } = saleStore;
        const saleForCollectiveInvoice = sales.filter(e => {
            // paymentMethod	"invoice"
            const isCollectiveInvoice = e.status === 1003;
            return isCollectiveInvoice;
        });

        // Ensure spinner is visible for at least 300ms
        await new Promise(resolve => setTimeout(resolve, 300));
        this.setState({ saleForCollectiveInvoice, checkingCollectiveInvoice: false });
        // console.log('checkForInvoicesReadyForTripletex', invoiceSalesForTripletexTransfer);
    }

    onClickCreateCollectiveInvoice = e => {
        e.stopPropagation();
        e.preventDefault();

        // Guard against double-click
        const { createAllCollectiveInvoices } = this.state;
        if (createAllCollectiveInvoices) {
            return false;
        }

        // Recompute from saleStore.sales — the same source the button's own visibility
        // condition uses in render(). state.saleForCollectiveInvoice can be stale.
        const { saleStore } = this.props.stores;
        const saleForCollectiveInvoice = (saleStore.sales || []).filter(s => s.status === 1003);

        // Open a prominent, centered in-app confirmation modal instead of the native
        // browser confirm() that pops up as an easy-to-miss little bar at the top of the
        // page — and that a browser may suppress entirely once the user has ticked
        // "prevent this page from creating additional dialogs", making the red button
        // look completely dead (TICKET-01156, TICKET-01382). The real run is in
        // confirmCreateCollectiveInvoice().
        const visitorIds = new Set();
        saleForCollectiveInvoice.forEach(sale => {
            const visitorId = sale.visitors?.[0]?.id;
            if (visitorId) {
                visitorIds.add(visitorId);
            }
        });
        this.setState({
            showCollectiveInvoiceConfirm: true,
            collectiveInvoiceConfirmSales: saleForCollectiveInvoice.length,
            collectiveInvoiceConfirmVisitors: visitorIds.size,
        });
        return true;
    }

    cancelCreateCollectiveInvoice = () => {
        this.setState({ showCollectiveInvoiceConfirm: false });
    }

    // TICKET-01715: `method` is 'invoice' (ordinary samlefaktura, the default) or
    // 'convene' (create the samlefaktura and hand it to Convene). The Convene choice is
    // only offered in the modal when the clinic has Convene configured.
    confirmCreateCollectiveInvoice = async (method = COLLECTIVE_INVOICE_METHOD_INVOICE) => {
        this.setState({ showCollectiveInvoiceConfirm: false });

        // Guard against double-click
        const { createAllCollectiveInvoices } = this.state;
        if (createAllCollectiveInvoices) {
            return false;
        }

        this.setState({ createAllCollectiveInvoices: true });

        const { saleStore, userStore } = this.props.stores;
        const { customer } = userStore;
        const viaConvene = isConveneCollectiveInvoice(method) && canCreateCollectiveInvoiceViaConvene(customer);
        const restorePagination = await this.loadAllSalesForBulkAction();

        const { sales, cutoffDateEnd } = saleStore;
        // Invoice date for every samlefaktura = the LAST date in the selected period
        // (cutoffDateEnd), e.g. 30. juni for a June run. It must NEVER be in the future,
        // so if the period end is after today (a whole-year filter ends …-12-31) we fall
        // back to today. TICKET-01186.
        const collectiveInvoiceToday = new Date();
        const collectiveInvoicePeriodEnd = cutoffDateEnd ? new Date(cutoffDateEnd) : null;
        const collectiveInvoiceDate = (collectiveInvoicePeriodEnd
            && !Number.isNaN(collectiveInvoicePeriodEnd.getTime())
            && collectiveInvoicePeriodEnd <= collectiveInvoiceToday)
            ? collectiveInvoicePeriodEnd
            : collectiveInvoiceToday;
        const saleForCollectiveInvoice = sales.filter(e => {
            // paymentMethod	"invoice"
            const isCollectiveInvoice = e.status === 1003;
            return isCollectiveInvoice;
        });

        const visitorIds = [];
        const salesByVisitor = {};
        saleForCollectiveInvoice.forEach(sale => {
            const { visitors = [] } = sale;
            const visitorId = visitors[0].id;
            // check if visitorId is already in the array
            if (!visitorIds.includes(visitorId)) {
                visitorIds.push(visitorId);
            }
            if (!salesByVisitor[visitorId]) {
                salesByVisitor[visitorId] = [];
            }
            salesByVisitor[visitorId].push(sale);
        });

        this.setState({
            totalCollectiveInvoices: saleForCollectiveInvoice.length,
            totalCollectiveInvoicesByVisitor: visitorIds.length,
        });
        // console.log('saleForCollectiveInvoice', saleForCollectiveInvoice);
        // console.log('salesByVisitor', salesByVisitor);

        // TICKET-01186: count customers skipped because their visits sum to 0 kr.
        let skippedZeroSumCustomers = 0;
        let skippedZeroSumSales = 0;
        // TICKET-01715: samlefakturaer created but not accepted by Convene.
        let conveneFailed = 0;

        for (let i = 0, l = visitorIds.length; i < l; i += 1) {
            const visitorId = visitorIds[i];
            const currentVisitor = salesByVisitor[visitorId][0].visitors?.[0] || {};
            this.setState({
                currentCollectiveInvoice: i + 1,
                currentCollectiveInvoiceName: `${currentVisitor.firstname || ''} ${currentVisitor.lastname || ''}`.trim(),
                currentCollectiveInvoiceSaleCount: salesByVisitor[visitorId].length,
            });

            // TICKET-01186: skip customers whose visits sum to 0 kr (journals without
            // billable lines) — a 0-kr samlefaktura only clutters the transfer list
            // (and would even get the fakturagebyr added). The visits keep status 1003
            // (Samlefaktura) so they stay visible and can be handled manually.
            const groupTotal = salesByVisitor[visitorId].reduce((sum, s) => sum + saleStore.sumTotal(s.products, false), 0);
            if (Math.round(groupTotal * 100) === 0) {
                skippedZeroSumCustomers += 1;
                skippedZeroSumSales += salesByVisitor[visitorId].length;
                continue;
            }

            const visitors = salesByVisitor[visitorId][0].visitors;
            saleStore.createNewSale(collectiveInvoiceSaleProps({
                method: viaConvene ? COLLECTIVE_INVOICE_METHOD_CONVENE : COLLECTIVE_INVOICE_METHOD_INVOICE,
                visitors,
                // Last date in the period, capped at today (see computation above). TICKET-01186.
                saleDate: collectiveInvoiceDate,
                // Today + customer.invoiceDueDays
                dueDate: new Date(Date.now() + customer.invoiceDueDays * 24 * 60 * 60 * 1000),
            }));

            const collectiveInvoiceProducts = [];
            const sales = salesByVisitor[visitorId];
            for (let j = 0, k = sales.length; j < k; j += 1) {
                const sale = sales[j];

                sale.products.forEach(product => {
                    collectiveInvoiceProducts.push({
                        saleUser: sale.user, // We want to keep track of the original sale user
                        ...product,
                        saleDate: sale.saleDate,
                        saleId: sale.id,
                        saleNumber: sale.saleNumber,
                    });
                });
            }

            // Add invoice fee if enabled on customer
            if (parseInt(customer.collectiveInvoiceFeeEnabled, 10) === 1 && customer.collectiveInvoiceFee > 0) {
                const invoiceFeeProduct = {
                    name: customer.collectiveInvoiceFeeName || 'Fakturagebyr',
                    type: 'invoiceFee',
                    qty: 1,
                    price: customer.collectiveInvoiceFee || 70,
                    account: customer.collectiveInvoiceFeeAccount || 3030,
                    vat: customer.collectiveInvoiceFeeVat || 0,
                    customer: customer.id,
                    createdDate: new Date(),
                };
                collectiveInvoiceProducts.push({
                    ...invoiceFeeProduct,
                    md5: md5(JSON.stringify(invoiceFeeProduct)).toString(),
                });
            }

            saleStore.updateObjectKeyValue('newSale', 'products', collectiveInvoiceProducts);
            saleStore.updateObjectKeyValue('newSale', 'subInvoices', sales.map(e => e.id));
            await saleStore.saveSale();

            // TICKET-01715: hand the saved samlefaktura to Convene. A failed send leaves
            // the sale at status 0 with the underbilag attached and conveneLastError set,
            // so it can be retried from the sale with «Betal via Convene».
            const createdSaleId = saleStore.newSale?.id;
            if (viaConvene && createdSaleId > 0) {
                const response = await saleStore.sendSaleToConvene(createdSaleId);
                const error = conveneErrorMessage(response);
                if (error) {
                    conveneFailed += 1;
                    console.error(`Samlefaktura ${createdSaleId}: Convene send failed: ${error}`);
                }
            }
            await this.sleep(1000);
        }

        // Restore original pagination and reload so the UI matches what the user had.
        await restorePagination();

        // Mark step 3 as done and show summary
        this.setState({
            createAllCollectiveInvoices: false,
            collectiveInvoiceStep3Done: true,
            collectiveInvoiceSummary: {
                invoicesCreated: visitorIds.length - skippedZeroSumCustomers,
                salesProcessed: saleForCollectiveInvoice.length - skippedZeroSumSales,
                skippedZeroSum: skippedZeroSumCustomers,
                viaConvene,
                conveneFailed,
            },
        });
    }




    onClickGotoAnimaliaAuth = e => {
        e.preventDefault();
        const { userStore, appState } = this.props.stores;
        const { isDevelopment } = appState;
        const { user } = userStore;

        // Open popup window to Animalia Auth
        let apiServer = util.getApiServer();
        apiServer = apiServer.replace('www.', '');

        const animalia_redirect_uri = `${apiServer}/api/integrations/animalia/token-exchange`;
        const animalia_client_id = 'dyrejournal';
        const animalia_state = user.id;
        // const animalia_scope = 'openid profile offline_access'.split(' ').join('+');
        const animalia_scope = 'offline_access'.split(' ').join('+');

        let animalia_auth_url = `https://sso.animalia.no/authorize?redirect_uri=${animalia_redirect_uri}&client_id=${animalia_client_id}&state=${animalia_state}&auto_login=true&response_type=code&scope=${animalia_scope}`;
        if (isDevelopment) {
            animalia_auth_url = `https://staging-sso.animalia.no/authorize?redirect_uri=${animalia_redirect_uri}&client_id=${animalia_client_id}&state=${animalia_state}&auto_login=true&response_type=code&scope=${animalia_scope}`;
        }
        // const animalia_auth_url = `https://staging-sso.animalia.no/authorize?redirect_uri=${animalia_redirect_uri}&client_id=${animalia_client_id}&state=${animalia_state}&auto_login=true&response_type=code&scope=${animalia_scope}`;
        window.open(animalia_auth_url, 'Animalia Auth', 'width=600,height=600');

        // set timeout to update userInfo
        setTimeout(() => {
            userStore.getInfo({ forceLoad: true });
        }, 3000);
    }

    onClickLogoutAnimalia = e => {
        e.preventDefault();
        const { userStore, appState } = this.props.stores;
        const { isDevelopment } = appState;

        // Open Animalia SSO logout endpoint in popup
        // This invalidates the SSO token and refresh token on Animalia's side
        let animalia_logout_url = 'https://sso.animalia.no/logout';
        if (isDevelopment) {
            animalia_logout_url = 'https://staging-sso.animalia.no/logout';
        }
        window.open(animalia_logout_url, 'Animalia Logout', 'width=600,height=400');

        // Clear local animalia data and refresh user info after a short delay
        setTimeout(() => {
            userStore.getInfo({ forceLoad: true });
        }, 2000);
    }

    onClickPostAnimaliaVisit = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const idInt = parseInt(id, 10);
        const { animaliaLoading = {} } = this.state;
        animaliaLoading[idInt] = true;
        this.setState({ animaliaLoading });

        const { callback = () => {} } = this.props;
        const { appState, userStore } = this.props.stores;
        const response = await appState.animaliaUploadVisit(id);

        console.log('onClickPostAnimaliaVisit.response', response);
        // Check the whole response, not just response.error — a backend crash comes back
        // as an HTML 500 with no `error` property, which used to fail silently (TICKET-01280).
        const errorMessage = dhpErrorMessage(response);
        if (errorMessage) {
            (await appAlert(`DHP: ${errorMessage}`));
            await userStore.getInfo({ forceLoad: true });
        }
        callback();
        animaliaLoading[idInt] = false;
        this.setState({ animaliaLoading });
    }

    onClickRefreshUserInfo = async e => {
        console.log('onClickRefreshUserInfo');
        e.preventDefault();
        e.stopPropagation();
        this.setState({ userLoading: true });
        const { userStore } = this.props.stores;
        await userStore.getInfo({ forceLoad: true });
        this.setState({ userLoading: false });
    }

    handleGotoSale = e => {
        e.preventDefault();
        e.stopPropagation();
        console.log('handleGotoSale', e.key);
        const { saleStore } = this.props.stores;
        const { sales, searchResults } = saleStore;
        const finalSales = searchResults && searchResults.length > 0 ? searchResults : sales;
        const idx = parseInt(e.key, 10) - 1;
        const currentSale = finalSales[idx];
        if (currentSale?.id) {
            this.viewVisit(currentSale.id);
        }
    }

    toggleAnimaliaError = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const { displayAnimaliaError } = this.state;
        displayAnimaliaError[id] = !displayAnimaliaError[id];
        this.setState({ displayAnimaliaError });
    }

    toggleTripleTexError = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const { tripletexError, saleDebugData, loadingDebugData } = this.state;

        // Toggle the visibility
        const isOpening = !tripletexError[id];
        tripletexError[id] = isOpening;
        this.setState({ tripletexError });

        // If opening and we don't have debug data yet, fetch it
        if (isOpening && !saleDebugData[id] && !loadingDebugData[id]) {
            loadingDebugData[id] = true;
            this.setState({ loadingDebugData });

            try {
                // Fetch single sale to get full debug data (tripletexApiResponses, fikenApiResponses)
                const response = await util.fetchApi(`/api/sales/${id}`, { method: 'GET' }, {});
                if (response?.data?.[0]) {
                    saleDebugData[id] = {
                        tripletexApiResponses: response.data[0].tripletexApiResponses,
                        fikenApiResponses: response.data[0].fikenApiResponses,
                        duettApiResponses: response.data[0].duettApiResponses,
                        tripletexDebug: response.data[0].tripletexDebug,
                    };
                }
            } catch (err) {
                console.error('Error fetching sale debug data:', err);
            }

            loadingDebugData[id] = false;
            this.setState({ saleDebugData, loadingDebugData });
        }
    }

    toggleFikenError = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const { displayFikenError, saleDebugData, loadingDebugData } = this.state;

        // Toggle the visibility
        const isOpening = !displayFikenError[id];
        displayFikenError[id] = isOpening;
        this.setState({ displayFikenError });

        // If opening and we don't have debug data yet, fetch it
        if (isOpening && !saleDebugData[id] && !loadingDebugData[id]) {
            loadingDebugData[id] = true;
            this.setState({ loadingDebugData });

            try {
                // Fetch single sale to get full debug data (tripletexApiResponses, fikenApiResponses)
                const response = await util.fetchApi(`/api/sales/${id}`, { method: 'GET' }, {});
                if (response?.data?.[0]) {
                    saleDebugData[id] = {
                        tripletexApiResponses: response.data[0].tripletexApiResponses,
                        fikenApiResponses: response.data[0].fikenApiResponses,
                        duettApiResponses: response.data[0].duettApiResponses,
                        tripletexDebug: response.data[0].tripletexDebug,
                    };
                }
            } catch (err) {
                console.error('Error fetching sale debug data:', err);
            }

            loadingDebugData[id] = false;
            this.setState({ saleDebugData, loadingDebugData });
        }
    }

    toggleNetsError = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('small').dataset;
        const { displayNetsError } = this.state;
        displayNetsError[id] = !displayNetsError[id];
        this.setState({ displayNetsError });
    }

    toggleTripletexExpandedSummary = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('span').dataset;
        const { tripletexExpandedSummary } = this.state;
        tripletexExpandedSummary[id] = !tripletexExpandedSummary[id];
        this.setState({ tripletexExpandedSummary });
    }

    toggleTripletexExpandedErrors = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('span').dataset;
        const { tripletexExpandedErrors } = this.state;
        tripletexExpandedErrors[id] = !tripletexExpandedErrors[id];
        this.setState({ tripletexExpandedErrors });
    }

    toggleFikenExpandedSummary = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('span').dataset;
        const { fikenExpandedSummary } = this.state;
        fikenExpandedSummary[id] = !fikenExpandedSummary[id];
        this.setState({ fikenExpandedSummary });
    }

    toggleFikenExpandedErrors = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('span').dataset;
        const { fikenExpandedErrors } = this.state;
        fikenExpandedErrors[id] = !fikenExpandedErrors[id];
        this.setState({ fikenExpandedErrors });
    }

    toggleDuettLog = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const { displayDuettError, saleDebugData, loadingDebugData } = this.state;

        // Toggle the visibility
        const isOpening = !displayDuettError[id];
        displayDuettError[id] = isOpening;
        this.setState({ displayDuettError });

        // If opening and we don't have debug data yet, fetch it (the list query
        // excludes duettApiResponses when limit > 25)
        if (isOpening && !saleDebugData[id] && !loadingDebugData[id]) {
            loadingDebugData[id] = true;
            this.setState({ loadingDebugData });

            try {
                const response = await util.fetchApi(`/api/sales/${id}`, { method: 'GET' }, {});
                if (response?.data?.[0]) {
                    saleDebugData[id] = {
                        tripletexApiResponses: response.data[0].tripletexApiResponses,
                        fikenApiResponses: response.data[0].fikenApiResponses,
                        duettApiResponses: response.data[0].duettApiResponses,
                        tripletexDebug: response.data[0].tripletexDebug,
                    };
                }
            } catch (err) {
                console.error('Error fetching sale debug data:', err);
            }

            loadingDebugData[id] = false;
            this.setState({ saleDebugData, loadingDebugData });
        }
    }

    toggleDuettExpandedSummary = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('span').dataset;
        const { duettExpandedSummary } = this.state;
        duettExpandedSummary[id] = !duettExpandedSummary[id];
        this.setState({ duettExpandedSummary });
    }

    toggleDuettExpandedErrors = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('span').dataset;
        const { duettExpandedErrors } = this.state;
        duettExpandedErrors[id] = !duettExpandedErrors[id];
        this.setState({ duettExpandedErrors });
    }

    onClickShipping = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { drawerLevel = 2 } = this.props;
        const { appState, saleStore, animalStore, calendarEventStore } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;

        const { id } = e.target.closest('button').dataset;
        saleStore.createNewSale({});
        const newSale = await saleStore.load(id, false, { skipUpdate: true });
        saleStore.updateKeyValue('newSale', newSale[0]);

        if (id) {
            await this.loadSalePayments(newSale[0].id);
        }

        appState.openDrawer('saleShipping', {
            height: drawerHeightMediumLarge,
            callback: () => {
                saleStore.createNewSale({});
                // saleStore.load({});
            },
        }, drawerLevel + 1);
    }

    onClickPrintSaleRow = async e => {
        e.preventDefault();
        e.stopPropagation();
        const button = e.target.closest('button');
        if (!button) return;
        const { id } = button.dataset;
        const saleId = parseInt(id, 10);

        const { saleStore, userStore } = this.props.stores;
        const { sales = [], newSale: previousNewSale } = saleStore;
        const { user = {} } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language] || countryMap.default;

        const sale = sales.find(s => s.id === saleId);
        if (!sale) {
            console.warn('onClickPrintSaleRow: sale not found for id', saleId);
            return;
        }

        saleStore.updateKeyValue('newSale', sale);

        try {
            const receipt = (
                <IntlProvider definition={languageDef}>
                    <DyrejournalSaleReceiptPrint stores={this.props.stores} useCustomerFromSale={true} suppressReminder={true} />
                </IntlProvider>
            );
            const tempDiv = document.createElement('div');
            tempDiv.className = 'receipt';
            document.body.appendChild(tempDiv);
            render(receipt, tempDiv);
            const htmlContent = tempDiv.outerHTML;
            document.body.removeChild(tempDiv);
            render(null, tempDiv);
            printViaIframe(htmlContent);
        } finally {
            saleStore.updateKeyValue('newSale', previousNewSale);
        }
        // Record the print: the row reads "Skrevet ut" right away and drops out
        // of the "ikke sendt" filters on the next reload (a mailed invoice is
        // sent). See saleStore.markPrinted.
        await saleStore.markPrinted(saleId);
    }

    // "Ikke sendt til kunde" — or, once "Skriv ut" has stamped printedDate,
    // "Skrevet ut <date>". Shared by the Tripletex and Fiken columns.
    renderNotSentLabel = (e) => {
        if (e.printedDate) {
            return (
                <span class='text-success'>
                    <i class='fa-solid fa-print' /> Skrevet ut {util.isoDate(e.printedDate, false, false, true)}
                </span>
            );
        }
        return <><i class='fa-solid fa-paper-plane' style='opacity: 0.4;' /> Ikke sendt til kunde</>;
    }

    onClickPrintAddressLabel = async e => {
        e.preventDefault();
        e.stopPropagation();

        const { id } = e.target.closest('button').dataset;
        const { saleStore } = this.props.stores;
        saleStore.createNewSale({});
        const newSale = await saleStore.load(id, false, { skipUpdate: true });
        saleStore.updateKeyValue('newSale', newSale[0]);
        const { visitors } = newSale[0];
        const { firstname, lastname, address, postalcode, place } = visitors[0] || {};

        // console.log('onClickPrintSale', e);
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // One FitTextSvg for all three lines, so name/address/postal share a
        // single font size and the block fills the label as one unit.
        const node = <IntlProvider definition={languageDef}>
            <div class='printLabel' style='padding: 2mm;'>
                <FitTextSvg
                    lines={[
                        `${firstname || ''} ${lastname || ''}`.trim(),
                        `${address || ''}`,
                        `${postalcode || '____'} ${place || ''}`.trim(),
                    ]}
                    align='left'
                />
            </div>
        </IntlProvider>;

        // Render to temp div, capture HTML, print via iframe
        const tempDiv = document.createElement('div');
        document.body.appendChild(tempDiv);
        render(node, tempDiv);
        const htmlContent = tempDiv.innerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printLabelViaIframe(htmlContent);
    }

    onClickPrintShippingCode = async e => {
        e.preventDefault();
        e.stopPropagation();

        const { id } = e.target.closest('button').dataset;
        const { saleStore } = this.props.stores;
        saleStore.createNewSale({});
        const newSale = await saleStore.load(id, false, { skipUpdate: true });
        saleStore.updateKeyValue('newSale', newSale[0]);

        // console.log('onClickPrintSale', e);
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];
        const parts = newSale[0].shippingCode ? newSale[0].shippingCode.split(' ') : [];

        // Render Receipt component with data
        // Brother QL-820NWB: Portrait mode - 62mm width, auto height (continuous) - shipping code labels
        const node = <IntlProvider definition={languageDef}>
            <div style='margin: 3mm;'>
                <FitTextOneLine text={`${parts[0] || ''}`} widthMm={58} maxPx={80} />
                <FitTextOneLine text={`${parts[1] || ''}`} widthMm={58} maxPx={80} />
                <FitTextOneLine text={`${parts[2] || ''}`} widthMm={58} maxPx={80} />
            </div>
        </IntlProvider>;

        // Render to temp div, capture HTML, print via iframe
        const tempDiv = document.createElement('div');
        tempDiv.className = 'barcodeLabelPagePortrait';
        document.body.appendChild(tempDiv);
        render(node, tempDiv);
        await new Promise(resolve => setTimeout(resolve, 100)); // Give RAF time to execute
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent);
    }

    onClickPrintCompanyLabel = async e => {
        e.preventDefault();
        e.stopPropagation();

        // console.log('onClickPrintSale', e);
        const { userStore } = this.props.stores;
        const { user, customer } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Logo and address block filling the label: side by side on a landscape
        // roll, logo stacked on top on a portrait one (see .printLabel-split in
        // printHelper.js). The logo keeps its aspect ratio either way.
        const hasLogo = customer?.images?.length > 0;
        const node = <IntlProvider definition={languageDef}>
            <div class='printLabel printLabel-split' style='padding: 2mm;'>
                {hasLogo && <div class='printLabel-media'>
                    <img src={localUtil.displayImg(customer.images[0], this.props, '800x')} />
                </div>}
                <div class='printLabel-body'>
                    <FitTextSvg
                        lines={[
                            `${customer.name || ''}`,
                            `${customer.address || ''}`,
                            `${customer.postalcode || ''} ${customer.place || ''}`.trim(),
                        ]}
                        align='left'
                    />
                </div>
            </div>
        </IntlProvider>;

        // Render to temp div, capture HTML, print via iframe
        const tempDiv = document.createElement('div');
        document.body.appendChild(tempDiv);
        render(node, tempDiv);
        const htmlContent = tempDiv.innerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printLabelViaIframe(htmlContent);
    }

    onClickMakeInsider = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
// console.log('onClickMakeInsider', id);
        const { userStore } = this.props.stores;

        const { saleUserUpdating = {}, saleUserIsUpdated = {} } = this.state;
        saleUserUpdating[id] = true;
        this.setState({ saleUserUpdating });

        const response = await userStore.makeInsider(parseInt(id, 10));

        saleUserUpdating[id] = false;
        saleUserIsUpdated[id] = true;
        this.setState({ saleUserUpdating, saleUserIsUpdated });

        // const { saleStore } = this.props.stores;
        // const response = await saleStore.save({
        //     id: parseInt(id, 10),
        //     insider: 1,
        // });
    }

    checkForExternalTransfer = async e => {
        this.checkForInvoicesReadyForEmail();
        this.checkForInvoicesReadyForFiken();
        this.checkForSalesReadyForFiken();
        this.checkForInvoicesReadyForTripletex();
        this.checkForSalesReadyForTripletex();
        this.checkForSalesReadyForCollectiveInvoice();
    }

    toggleAdminToolbar = e => {
        e.preventDefault();
        e.stopPropagation();
        const { showAdminToolbar } = this.state;
        const opening = !showAdminToolbar;
        this.setState({ showAdminToolbar: opening, adminToolbarSection: null });
        // The admin toolbar's wizards and counters iterate saleStore.sales —
        // they must never see a cross-company list (featureFlags.sharedSales).
        const { saleStore } = this.props.stores;
        if (opening && saleStore.myCustomers) {
            const { callback = () => {} } = this.props;
            saleStore.updateKeyValue('myCustomers', null);
            callback();
        }
        this.checkForExternalTransfer();
    }

    setAdminToolbarSection = (section) => {
        this.setState({ adminToolbarSection: section });
    }

    backToAdminMenu = async () => {
        const { saleStore } = this.props.stores;
        const { callback = () => {}, setQuickDateFilter } = this.props;

        // Reset filters that were set during collective invoice procedure
        saleStore.updateKeyValue('limit', 25);
        saleStore.updateKeyValue('currentPage', 1);
        saleStore.updateKeyValue('activeStatus', null);
        saleStore.updateKeyValue('activePaymentMethod', null);
        saleStore.updateKeyValue('cutoffDate', null);
        saleStore.updateKeyValue('cutoffDateEnd', null);
        saleStore.updateKeyValue('criticalTask', null);
        saleStore.updateKeyValue('filterIds', null);

        // Reset quick date filter
        if (setQuickDateFilter) {
            setQuickDateFilter(null, null);
        }

        // Reset wizard state
        this.setState({
            adminToolbarSection: null,
            collectiveInvoiceStep1Done: false,
            collectiveInvoiceStep2Done: false,
            collectiveInvoiceStep3Done: false,
            collectiveInvoiceSummary: null,
            // Reset email wizard state
            settingUpEmailInvoice: false,
            emailInvoiceStep1Done: false,
            emailInvoiceStep2Done: false,
            emailInvoiceStep3Done: false,
            emailInvoiceSummary: null,
            allInvoicesSentViaEmail: false,
            invoicesSent: 0,
            // Reset reminder wizard state
            reminderStep: 1,
            reminderChannels: { email: true, sms: true },
            reminderRows: [],
            reminderSelected: {},
            reminderLoading: false,
            reminderSending: false,
            reminderProgress: 0,
            reminderResult: null,
            // Reset accounting wizard state
            accountingMode: null,
            accountingProvider: null,
            accountingTransferStep1Done: false,
            accountingTransferStep2Done: false,
            accountingTransferStep3Done: false,
            accountingTransferSummary: null,
            accountingUpdateStep1Done: false,
            accountingUpdateStep2Done: false,
            accountingUpdateStep3Done: false,
            updateFromTripletexPreviewLoading: false,
            tripletexUpdateResponse: null,
            currentAccountingTransfer: 0,
            totalAccountingTransfers: 0,
            // Medical history wizard
            medicalHistoryMode: 'batch',
            medicalHistoryStep: 1,
            medicalHistoryPreview: null,
            medicalHistorySendResult: null,
            medicalHistorySpeciesInPeriod: [],
            medicalHistorySelectedSpecies: [],
            medicalHistoryLoading: false,
            medicalHistorySending: false,
            medicalHistorySingleVisitor: null,
            medicalHistorySingleAnimals: [],
            medicalHistorySingleAnimal: null,
            medicalHistorySingleAllTime: true,
            medicalHistorySingleStartDate: '',
            medicalHistorySingleEndDate: '',
            medicalHistorySingleSent: false,
        });

        // Clear comparison data
        const { tripletexStore } = this.props.stores;
        if (tripletexStore) {
            tripletexStore.clearComparisonData();
        }

        // Reload data with reset filters
        await callback();
    }

    startTripletexComparison = async () => {
        const { tripletexStore, saleStore } = this.props.stores;
        const { cutoffDate, cutoffDateEnd } = saleStore;
        await tripletexStore.getInvoicesCompare({
            dateFrom: cutoffDate || undefined,
            dateTo: cutoffDateEnd || undefined,
        });
    }

    startTripletexOpenPosts = async () => {
        const { tripletexStore, saleStore } = this.props.stores;
        const { cutoffDate, cutoffDateEnd } = saleStore;
        await tripletexStore.getOpenPosts({
            dateFrom: cutoffDate || undefined,
            dateTo: cutoffDateEnd || undefined,
        });
    }

    startDuettPaymentSync = async () => {
        const { duettStore } = this.props.stores;
        const dateTo = new Date().toISOString().slice(0, 10);
        const dateFrom = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
        // Dry-run first so the admin sees what will change before anything is written.
        const preview = await duettStore.updateInvoicesByDate(dateFrom, dateTo, true);
        this.setState({
            duettSyncPreview: preview?.data || null,
            accountingMode: 'duettSync',
            duettSyncRange: { dateFrom, dateTo },
            duettSyncDone: false,
        });
    }

    confirmDuettPaymentSync = async () => {
        const { duettStore } = this.props.stores;
        const { duettSyncRange } = this.state;
        const result = await duettStore.updateInvoicesByDate(duettSyncRange.dateFrom, duettSyncRange.dateTo, false);
        this.setState({ duettSyncResult: result?.data || null, duettSyncDone: true });
        if (this.props.callback) this.props.callback();
    }

    setupCollectiveInvoiceFilters = async () => {
        this.setState({
            settingUpCollectiveInvoice: true,
            collectiveInvoiceStep1Done: false,
            collectiveInvoiceStep2Done: false,
            collectiveInvoiceStep3Done: false,
            collectiveInvoiceSummary: null,
        });
        const { saleStore } = this.props.stores;
        const { setQuickDateFilter, callback = () => {} } = this.props;

        try {
            // Calculate previous month date range as ISO strings
            const now = new Date();
            const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const lastDayPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);

            const prevYear = firstDayPrevMonth.getFullYear();
            const prevMonth = firstDayPrevMonth.getMonth() + 1; // 1-indexed for display

            const fromDate = `${prevYear}-${String(prevMonth).padStart(2, '0')}-01`;
            const toDate = `${lastDayPrevMonth.getFullYear()}-${String(lastDayPrevMonth.getMonth() + 1).padStart(2, '0')}-${String(lastDayPrevMonth.getDate()).padStart(2, '0')}`;

            // Set filters for collective invoices:
            // - Limit = 1000 + back to page 1 (applyWizardListLimit)
            // - Status 1003 = collective invoice status
            // - Date period = previous month
            this.applyWizardListLimit();
            saleStore.updateKeyValue('activeStatus', 1003);
            saleStore.updateKeyValue('activePaymentMethod', null); // Clear payment method filter
            saleStore.updateKeyValue('cutoffDate', fromDate);
            saleStore.updateKeyValue('cutoffDateEnd', toDate);

            // Set quick date selector to show previous month
            if (setQuickDateFilter) {
                setQuickDateFilter(prevYear, prevMonth);
            }

            // Reload sales with the new filters via callback (loadAll in allVisits.js)
            await callback();

            // Check for sales ready for collective invoice
            await this.checkForSalesReadyForCollectiveInvoice();

            this.setState({ settingUpCollectiveInvoice: false, collectiveInvoiceStep1Done: true });
        } catch (err) {
            console.error('Error setting up collective invoice filters:', err);
            this.setState({ settingUpCollectiveInvoice: false });
        }
    }

    markCollectiveInvoiceStep2Done = () => {
        this.setState({ collectiveInvoiceStep2Done: true });
    }

    // Accounting transfer wizard methods (Tripletex/Fiken)
    setupAccountingTransferFilters = async () => {
        const { callback = () => {}, setQuickDateFilter } = this.props;
        this.setState({
            settingUpAccountingTransfer: true,
            accountingTransferStep1Done: false,
            accountingTransferStep2Done: false,
            accountingTransferStep3Done: false,
            accountingTransferSummary: null,
        });

        const { saleStore } = this.props.stores;

        try {
            // Calculate previous month date range
            const now = new Date();
            const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const lastDayPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);

            const prevYear = firstDayPrevMonth.getFullYear();
            const prevMonth = firstDayPrevMonth.getMonth() + 1; // 1-indexed for display

            const fromDate = `${prevYear}-${String(prevMonth).padStart(2, '0')}-01`;
            const toDate = `${lastDayPrevMonth.getFullYear()}-${String(lastDayPrevMonth.getMonth() + 1).padStart(2, '0')}-${String(lastDayPrevMonth.getDate()).padStart(2, '0')}`;

            // Set filters for accounting transfer:
            // - Limit = 1000 + back to page 1 (applyWizardListLimit)
            // - Payment method = invoice
            // - Date period = previous month
            this.applyWizardListLimit();
            saleStore.updateKeyValue('activeStatus', null); // Clear status filter
            saleStore.updateKeyValue('activePaymentMethod', 'invoice'); // Filter by invoice payment method
            saleStore.updateKeyValue('cutoffDate', fromDate);
            saleStore.updateKeyValue('cutoffDateEnd', toDate);

            // Set quick date selector to show previous month
            if (setQuickDateFilter) {
                setQuickDateFilter(prevYear, prevMonth);
            }

            // Reload data
            await callback();

            // Check for items ready to transfer
            await this.checkForExternalTransfer();

            this.setState({
                settingUpAccountingTransfer: false,
                accountingTransferStep1Done: true,
            });
        } catch (err) {
            console.error('Error setting up accounting transfer filters:', err);
            this.setState({ settingUpAccountingTransfer: false });
        }
    }

    markAccountingTransferStep2Done = () => {
        this.setState({ accountingTransferStep2Done: true });
    }

    // Opening the E-post wizard presets betalingsmetode = Faktura right away: the
    // bulk send only ever targets invoice sales, so the list should show what can
    // actually be sent even before the user touches "Sett opp automatisk".
    presetEmailInvoiceFilters = async () => {
        const { saleStore } = this.props.stores;
        const { callback = () => {} } = this.props;
        this.applyWizardListLimit();
        saleStore.updateKeyValue('activePaymentMethod', 'invoice');
        await callback();
        await this.checkForInvoicesReadyForEmail();
    }

    // Email invoice wizard methods (mirrors setupAccountingTransferFilters)
    setupEmailInvoiceFilters = async () => {
        const { callback = () => {}, setQuickDateFilter } = this.props;
        this.setState({
            settingUpEmailInvoice: true,
            emailInvoiceStep1Done: false,
            emailInvoiceStep2Done: false,
            emailInvoiceStep3Done: false,
            emailInvoiceSummary: null,
            allInvoicesSentViaEmail: false,
            invoicesSent: 0,
        });

        const { saleStore } = this.props.stores;

        try {
            // Calculate previous month date range
            const now = new Date();
            const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const lastDayPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);

            const prevYear = firstDayPrevMonth.getFullYear();
            const prevMonth = firstDayPrevMonth.getMonth() + 1; // 1-indexed for display

            const fromDate = `${prevYear}-${String(prevMonth).padStart(2, '0')}-01`;
            const toDate = `${lastDayPrevMonth.getFullYear()}-${String(lastDayPrevMonth.getMonth() + 1).padStart(2, '0')}-${String(lastDayPrevMonth.getDate()).padStart(2, '0')}`;

            // Set filters for sending invoices via email:
            // - Limit = 1000 + back to page 1 (applyWizardListLimit)
            // - Payment method = invoice
            // - Date period = previous month
            this.applyWizardListLimit();
            saleStore.updateKeyValue('activeStatus', null); // Clear status filter
            saleStore.updateKeyValue('activePaymentMethod', 'invoice');
            saleStore.updateKeyValue('cutoffDate', fromDate);
            saleStore.updateKeyValue('cutoffDateEnd', toDate);

            // Set quick date selector to show previous month
            if (setQuickDateFilter) {
                setQuickDateFilter(prevYear, prevMonth);
            }

            // Reload data
            await callback();

            // Refresh the readiness state used outside the wizard
            await this.checkForInvoicesReadyForEmail();

            this.setState({
                settingUpEmailInvoice: false,
                emailInvoiceStep1Done: true,
            });
        } catch (err) {
            console.error('Error setting up email invoice filters:', err);
            this.setState({ settingUpEmailInvoice: false });
        }
    }

    markEmailInvoiceStep2Done = () => {
        this.setState({ emailInvoiceStep2Done: true });
    }

    // ---- Inline wizard filter handlers (Step 1) ----
    // These update saleStore observables and trigger the same reload used by the top filter bar.
    // They also mark the active wizard's step 1 as done so the user can proceed without
    // having to click "Sett opp automatisk" separately.

    // The admin-toolbar wizards operate on the whole filtered result set — the bulk
    // actions iterate saleStore.sales. Any filter change made inside a wizard must
    // therefore expand the visible list to 1000 rows (same as "Sett opp automatisk")
    // AND jump back to page 1: loadAll requests offset = (currentPage - 1) * limit,
    // so keeping a stale page 2+ with the new limit would ask for offset >= 1000
    // and render an empty list.
    applyWizardListLimit = () => {
        const { saleStore } = this.props.stores;
        saleStore.updateKeyValue('limit', 1000);
        saleStore.updateKeyValue('currentPage', 1);
        // Wizards must never operate on a cross-company list — the bulk
        // actions would sweep sibling companies' sales into this company's
        // accounting (featureFlags.sharedSales toggle).
        if (saleStore.myCustomers) {
            saleStore.updateKeyValue('myCustomers', null);
        }
    }

    markWizardStep1Done = () => {
        this.applyWizardListLimit();
        const { adminToolbarSection } = this.state;
        if (adminToolbarSection === 'collective') {
            if (!this.state.collectiveInvoiceStep1Done) {
                this.setState({ collectiveInvoiceStep1Done: true });
            }
        } else if (adminToolbarSection === 'email') {
            if (!this.state.emailInvoiceStep1Done) {
                this.setState({ emailInvoiceStep1Done: true });
            }
        } else if (!this.state.accountingTransferStep1Done) {
            this.setState({ accountingTransferStep1Done: true });
        }
    }

    wizardChangePaymentMethod = async (e) => {
        const { value } = e.target;
        const { saleStore } = this.props.stores;
        const { callback = () => {} } = this.props;
        saleStore.updateKeyValue('activePaymentMethod', value || null);
        this.markWizardStep1Done();
        await callback();
        if (this.state.adminToolbarSection === 'collective') {
            await this.checkForSalesReadyForCollectiveInvoice();
        } else {
            await this.checkForExternalTransfer();
        }
    }

    wizardChangeStatus = async (e) => {
        const { value } = e.target;
        const { saleStore } = this.props.stores;
        const { callback = () => {} } = this.props;
        saleStore.updateKeyValue('activeStatus', value === '' ? null : parseInt(value, 10));
        this.markWizardStep1Done();
        await callback();
        if (this.state.adminToolbarSection === 'collective') {
            await this.checkForSalesReadyForCollectiveInvoice();
        } else {
            await this.checkForExternalTransfer();
        }
    }

    wizardChangeCutoffDate = async (e) => {
        const { value } = e.target;
        const { saleStore } = this.props.stores;
        const { callback = () => {}, setQuickDateFilter } = this.props;
        saleStore.updateKeyValue('cutoffDate', value);
        if (setQuickDateFilter) setQuickDateFilter(0, 0);
        this.markWizardStep1Done();
        await callback();
    }

    wizardChangeCutoffDateEnd = async (e) => {
        const { value } = e.target;
        const { saleStore } = this.props.stores;
        const { callback = () => {}, setQuickDateFilter } = this.props;
        saleStore.updateKeyValue('cutoffDateEnd', value);
        if (setQuickDateFilter) setQuickDateFilter(0, 0);
        this.markWizardStep1Done();
        await callback();
    }

    wizardSetMonthRange = async (year, month) => {
        const { saleStore } = this.props.stores;
        const { callback = () => {}, setQuickDateFilter } = this.props;
        const fromDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const toDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        saleStore.updateKeyValue('cutoffDate', fromDate);
        saleStore.updateKeyValue('cutoffDateEnd', toDate);
        if (setQuickDateFilter) setQuickDateFilter(year, month);
        this.markWizardStep1Done();
        await callback();
    }

    wizardSetThisMonth = () => {
        const now = new Date();
        this.wizardSetMonthRange(now.getFullYear(), now.getMonth() + 1);
    }

    wizardSetPreviousMonth = () => {
        const now = new Date();
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        this.wizardSetMonthRange(prev.getFullYear(), prev.getMonth() + 1);
    }

    wizardSetThisYear = async () => {
        const { saleStore } = this.props.stores;
        const { callback = () => {}, setQuickDateFilter } = this.props;
        const year = (new Date()).getFullYear();
        saleStore.updateKeyValue('cutoffDate', `${year}-01-01`);
        saleStore.updateKeyValue('cutoffDateEnd', `${year}-12-31`);
        if (setQuickDateFilter) setQuickDateFilter(year, 0);
        this.markWizardStep1Done();
        await callback();
    }

    wizardPresetAllInvoices = async () => {
        const { saleStore } = this.props.stores;
        const { callback = () => {} } = this.props;
        saleStore.updateKeyValue('activePaymentMethod', 'invoice');
        saleStore.updateKeyValue('activeStatus', null);
        this.markWizardStep1Done();
        await callback();
        if (this.state.adminToolbarSection === 'collective') {
            await this.checkForSalesReadyForCollectiveInvoice();
        } else {
            await this.checkForExternalTransfer();
        }
    }

    wizardPresetAllPaid = async () => {
        const { saleStore } = this.props.stores;
        const { callback = () => {} } = this.props;
        saleStore.updateKeyValue('activePaymentMethod', null);
        saleStore.updateKeyValue('activeStatus', 1);
        this.markWizardStep1Done();
        await callback();
        if (this.state.adminToolbarSection === 'collective') {
            await this.checkForSalesReadyForCollectiveInvoice();
        } else {
            await this.checkForExternalTransfer();
        }
    }

    renderWizardFilters = ({ showStatus = true, showPaymentMethod = true, showQuickPresets = true } = {}) => {
        const { saleStore, userStore } = this.props.stores;
        const { language = 'no' } = userStore.user || {};
        const { activeStatus, activePaymentMethod, cutoffDate, cutoffDateEnd, paymentMethodsFilter } = saleStore;
        const now = new Date();
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const isThisMonth = cutoffDate === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        const isPrevMonth = cutoffDate === `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-01`
            && cutoffDateEnd && cutoffDateEnd.startsWith(`${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`);
        const isThisYear = cutoffDate === `${now.getFullYear()}-01-01` && cutoffDateEnd === `${now.getFullYear()}-12-31`;
        const isAllInvoices = activePaymentMethod === 'invoice' && (activeStatus === null || activeStatus === undefined);
        const isAllPaid = activeStatus === 1 && !activePaymentMethod;

        return (
            <div class='bg-body rounded p-2 mt-2'>
                <div class='d-flex flex-wrap align-items-center gap-2'>
                    <small class='text-body-secondary fw-bold me-1'>
                        <i class='fa-duotone fa-filter me-1' />
                        <Text id='dyrejournal.wizard-filters-label'>Filtre</Text>:
                    </small>

                    {showPaymentMethod && (
                        <select
                            class='form-select form-select-sm rounded-pill'
                            style='width: auto;'
                            value={activePaymentMethod ?? ''}
                            onChange={this.wizardChangePaymentMethod}
                        >
                            <option value=''><Text id='dyrejournal.select-sale-payment-method'>-- Choose payment method --</Text></option>
                            {Object.keys(paymentMethodsFilter).map((key) => (
                                <option value={key}>{saleStore.getPaymentMethodText(key, language)}</option>
                            ))}
                        </select>
                    )}

                    {showStatus && (
                        <select
                            class='form-select form-select-sm rounded-pill'
                            style='width: auto;'
                            value={activeStatus ?? ''}
                            onChange={this.wizardChangeStatus}
                        >
                            <option value=''><Text id='dyrejournal.select-sale-status'>-- Choose status --</Text></option>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 1001, 1002, 1003, 1005, 1004, 1006, 9999].map((s) => (
                                <option value={s}>{util.ucfirst(saleStore.getSaleStatusById(s, language))}</option>
                            ))}
                        </select>
                    )}

                    <input
                        type='date'
                        class='form-control form-control-sm rounded-pill'
                        style='width: auto;'
                        value={cutoffDate || ''}
                        onChange={this.wizardChangeCutoffDate}
                    />
                    <span class='text-body-secondary'>–</span>
                    <input
                        type='date'
                        class='form-control form-control-sm rounded-pill'
                        style='width: auto;'
                        value={cutoffDateEnd || ''}
                        onChange={this.wizardChangeCutoffDateEnd}
                    />

                    <button
                        type='button'
                        class={`btn btn-sm ${isPrevMonth ? 'btn-primary' : 'btn-outline-primary'} rounded-pill`}
                        onClick={this.wizardSetPreviousMonth}
                    >
                        <Text id='dyrejournal.preset-previous-month'>Forrige måned</Text>
                    </button>
                    <button
                        type='button'
                        class={`btn btn-sm ${isThisMonth ? 'btn-primary' : 'btn-outline-primary'} rounded-pill`}
                        onClick={this.wizardSetThisMonth}
                    >
                        <Text id='dyrejournal.preset-this-month'>Denne måned</Text>
                    </button>
                    <button
                        type='button'
                        class={`btn btn-sm ${isThisYear ? 'btn-primary' : 'btn-outline-primary'} rounded-pill`}
                        onClick={this.wizardSetThisYear}
                    >
                        <Text id='dyrejournal.preset-this-year'>I år</Text>
                    </button>
                </div>

                {showQuickPresets && (
                    <div class='d-flex flex-wrap align-items-center gap-2 mt-2 pt-2 border-top'>
                        <small class='text-body-secondary fw-bold me-1'>
                            <i class='fa-duotone fa-bolt me-1' />
                            <Text id='dyrejournal.wizard-quick-presets-label'>Hurtigvalg</Text>:
                        </small>
                        <button
                            type='button'
                            class={`btn btn-sm ${isAllInvoices ? 'btn-success' : 'btn-outline-success'} rounded-pill`}
                            onClick={this.wizardPresetAllInvoices}
                        >
                            <i class='fa-duotone fa-file-invoice me-1' />
                            <Text id='dyrejournal.wizard-preset-all-invoices'>Alle faktura</Text>
                        </button>
                        <button
                            type='button'
                            class={`btn btn-sm ${isAllPaid ? 'btn-success' : 'btn-outline-success'} rounded-pill`}
                            onClick={this.wizardPresetAllPaid}
                        >
                            <i class='fa-duotone fa-badge-check me-1' />
                            <Text id='dyrejournal.wizard-preset-all-paid'>Alle betalte besøk</Text>
                        </button>
                    </div>
                )}
            </div>
        );
    }

    renderWizardActiveFiltersSummary = () => {
        const { saleStore, userStore } = this.props.stores;
        const { language = 'no' } = userStore.user || {};
        const { activeStatus, activePaymentMethod, cutoffDate, cutoffDateEnd, totalSales = 0, limit = 0, currentPage = 1, searchText } = saleStore;

        const parts = [];
        if (activeStatus !== null && activeStatus !== undefined) {
            parts.push(`med status «${saleStore.getSaleStatusById(activeStatus, language)}»`);
        }
        if (activePaymentMethod) {
            parts.push(`betalt med ${saleStore.getPaymentMethodText(activePaymentMethod, language)}`);
        }
        if (cutoffDate && cutoffDateEnd) {
            parts.push(`i perioden ${cutoffDate} til ${cutoffDateEnd}`);
        } else if (cutoffDate) {
            parts.push(`fra ${cutoffDate}`);
        } else if (cutoffDateEnd) {
            parts.push(`til ${cutoffDateEnd}`);
        }
        if (searchText) {
            parts.push(`som matcher søket «${searchText}»`);
        }

        const truncated = limit > 0 && totalSales > limit;
        const shownCount = Math.min(limit || totalSales, Math.max(totalSales - (currentPage - 1) * limit, 0));
        const lead = truncated
            ? `Viser ${shownCount} av ${totalSales} salg`
            : `Du ser alle ${totalSales || 0} salg`;
        let sentence = `${lead}${parts.length ? ' ' + parts.join(', ') : ''}.`;
        if (truncated) {
            sentence += ' Øk grensen eller bla videre for å se resten.';
        }

        return (
            <div class='small mt-2 px-2 py-1 bg-body rounded text-body'>
                <i class='fa-duotone fa-circle-info me-1' />
                {sentence}
            </div>
        );
    }

    sendAllToTripletex = async () => {
        // Guard against double-click on the bulk button. Two parallel for-loops
        // here would race through the same `salesToTransfer` array and trigger
        // duplicate Tripletex invoices on any sale whose POSTs interleave.
        if (this.state.sendAllInvoicesToTripletex || this.state.sendAllSalesToTripletex) return;
        const { saleStore, userStore } = this.props.stores;
        // Bulk accounting transfers must never run on the currently-displayed
        // page of a cross-company list — loadAllSalesForBulkAction turns the
        // myCustomers toggle off and loads the full, own-company-only set.
        const restorePagination = await this.loadAllSalesForBulkAction();
        const { sales } = saleStore;

        // Helper to check if already transferred to Tripletex
        const isTripletexTransferred = (s) => {
            if (s.tripletexInvoice?.id) return true;
            if (s.tripletexResponse && s.tripletexResponse?.statusCode === 201) return true;
            if (s.tripletexApiResponses && s.tripletexApiResponses.length > 0) {
                const analysis = analyzeTripletexResponses(s.tripletexApiResponses);
                if (analysis.invoiceCreated) return true;
            }
            return false;
        };

        // Filter from current store data (respects user's manual filter changes)
        // Combined filter: invoices OR paid sales (no double-counting)
        // Belt-and-braces: never transfer a foreign (sibling-company) sale.
        const salesToTransfer = sales.filter(e => {
            const isInvoice = e.paymentMethod === 'invoice';
            const isPaid = e.status === 1;
            const transferCheck = this.canTransferToAccounting(e);
            return (isInvoice || isPaid) && !isTripletexTransferred(e) && transferCheck.canTransfer;
        }).filter(e => !isForeignSale(e, userStore));

        const total = salesToTransfer.length;

        this.setState({
            sendAllInvoicesToTripletex: true,
            totalAccountingTransfers: total,
            currentAccountingTransfer: 0,
            currentAccountingTransferLabel: '',
            currentAccountingTransferFailed: 0,
        });

        let sent = 0;
        let tripletexFailedSoFar = 0;

        // Send all sales
        for (const sale of salesToTransfer) {
            this.setState({ currentAccountingTransferLabel: transferLabel(sale) });
            await this.sendTripletexInvoiceById(sale.id);
            sent++;
            if (this.state.tripletexCreateError?.[sale.id]) {
                tripletexFailedSoFar += 1;
            }
            this.setState({ currentAccountingTransfer: sent, currentAccountingTransferFailed: tripletexFailedSoFar });
        }

        await restorePagination();

        // TICKET-01186: report actual successes — sendTripletexInvoiceById records
        // failed transfers in tripletexCreateError, so don't count those as sent.
        const { tripletexCreateError = {} } = this.state;
        const failedTransfers = salesToTransfer.filter(s => tripletexCreateError[s.id]).length;

        this.setState({
            sendAllInvoicesToTripletex: false,
            sendAllSalesToTripletex: false,
            accountingTransferStep3Done: true,
            accountingTransferSummary: { totalSent: sent - failedTransfers, totalFailed: failedTransfers },
            totalAccountingTransfers: 0,
            currentAccountingTransfer: 0,
        });

        // Reload to show updated data
        const { callback = () => {} } = this.props;
        await callback();
    }

    sendAllToFiken = async () => {
        // Guard against double-click on the bulk button.
        if (this.state.sendAllInvoicesToFiken || this.state.sendAllSalesToFiken) return;
        const { saleStore, userStore } = this.props.stores;
        // Bulk accounting transfers must never run on the currently-displayed
        // page of a cross-company list — loadAllSalesForBulkAction turns the
        // myCustomers toggle off and loads the full, own-company-only set.
        const restorePagination = await this.loadAllSalesForBulkAction();
        const { sales } = saleStore;

        // Helper to check if already transferred to Fiken
        const isFikenTransferred = (s) => {
            if (s.fikenLocation && s.fikenLocation.length > 0) return true;
            if (s.fikenApiResponses && s.fikenApiResponses.length > 0) {
                const analysis = analyzeFikenResponses(s.fikenApiResponses);
                if (analysis.invoiceCreated || analysis.saleCreated) return true;
            }
            return false;
        };

        // Filter from current store data (respects user's manual filter changes)
        // Combined filter: invoices OR paid sales (no double-counting)
        // Belt-and-braces: never transfer a foreign (sibling-company) sale.
        const salesToTransfer = sales.filter(e => {
            const isInvoice = e.paymentMethod === 'invoice';
            const isPaid = e.status === 1;
            const transferCheck = this.canTransferToAccounting(e);
            return (isInvoice || isPaid) && !isFikenTransferred(e) && transferCheck.canTransfer;
        }).filter(e => !isForeignSale(e, userStore));

        const total = salesToTransfer.length;

        this.setState({
            sendAllInvoicesToFiken: true,
            totalAccountingTransfers: total,
            currentAccountingTransfer: 0,
            currentAccountingTransferLabel: '',
            currentAccountingTransferFailed: 0,
        });

        let sent = 0;

        // Send all sales - use invoice method for invoices, sale method for paid sales
        for (const sale of salesToTransfer) {
            this.setState({ currentAccountingTransferLabel: transferLabel(sale) });
            if (sale.paymentMethod === 'invoice') {
                await this.sendFikenInvoiceByID(sale.id);
            } else {
                await this.sendFikenSaleByID(sale.id);
            }
            sent++;
            this.setState({ currentAccountingTransfer: sent });
        }

        await restorePagination();

        this.setState({
            sendAllInvoicesToFiken: false,
            sendAllSalesToFiken: false,
            accountingTransferStep3Done: true,
            accountingTransferSummary: { totalSent: sent },
            totalAccountingTransfers: 0,
            currentAccountingTransfer: 0,
        });

        // Reload to show updated data
        const { callback = () => {} } = this.props;
        await callback();
    }

    sendAllToPowerOffice = async () => {
        // Guard against double-click on the bulk button.
        if (this.state.sendAllInvoicesToPowerOffice) return;
        const { saleStore, userStore } = this.props.stores;
        // Bulk accounting transfers must never run on the currently-displayed
        // page of a cross-company list — loadAllSalesForBulkAction turns the
        // myCustomers toggle off and loads the full, own-company-only set.
        const restorePagination = await this.loadAllSalesForBulkAction();
        const { sales } = saleStore;

        const isPowerOfficeTransferred = (s) => {
            if (s.powerOfficeTransferred) return true;
            const r = s.powerOfficeResponse;
            if (r && (
                r.statusCode === 201
                || r.statusCode === 202
                || r.salesOrder?.statusCode === 201
                || r.salesOrder?.statusCode === 202
                || r.cashVoucher?.statusCode === 201
                || r.success
            )) return true;
            return false;
        };

        // Belt-and-braces: never transfer a foreign (sibling-company) sale.
        const salesToTransfer = sales.filter(e => {
            const isInvoice = e.paymentMethod === 'invoice';
            const isPaid = e.status === 1;
            const transferCheck = this.canTransferToAccounting(e);
            return (isInvoice || isPaid) && !isPowerOfficeTransferred(e) && transferCheck.canTransfer;
        }).filter(e => !isForeignSale(e, userStore));

        const total = salesToTransfer.length;

        this.setState({
            sendAllInvoicesToPowerOffice: true,
            totalAccountingTransfers: total,
            currentAccountingTransfer: 0,
            currentAccountingTransferLabel: '',
            currentAccountingTransferFailed: 0,
        });

        const { powerofficeStore } = this.props.stores;
        let sent = 0;
        for (const sale of salesToTransfer) {
            const idInt = parseInt(sale.id, 10);
            const { powerofficeLoading = {}, powerofficeSent = {} } = this.state;
            powerofficeLoading[idInt] = true;
            this.setState({ powerofficeLoading, currentAccountingTransferLabel: transferLabel(sale) });
            await powerofficeStore.createInvoiceFromSale(sale.id);
            await this.sleep(200);
            powerofficeLoading[idInt] = false;
            powerofficeSent[idInt] = true;
            sent += 1;
            this.setState({ powerofficeLoading, powerofficeSent, currentAccountingTransfer: sent });
        }

        await restorePagination();

        this.setState({
            sendAllInvoicesToPowerOffice: false,
            accountingTransferStep3Done: true,
            accountingTransferSummary: { totalSent: sent },
            totalAccountingTransfers: 0,
            currentAccountingTransfer: 0,
        });

        const { callback = () => {} } = this.props;
        await callback();
    }

    sendAllToDuett = async () => {
        // Guard against double-click on the bulk button.
        if (this.state.sendAllInvoicesToDuett) return;
        const { saleStore, userStore, duettStore } = this.props.stores;
        // Bulk accounting transfers must never run on the currently-displayed
        // page of a cross-company list — loadAllSalesForBulkAction turns the
        // myCustomers toggle off and loads the full, own-company-only set.
        const restorePagination = await this.loadAllSalesForBulkAction();
        const { sales } = saleStore;

        // Invoice sales only — cash/card revenue reaches Duett via the
        // dagsoppgjør voucher; qualifiesForDuettBulkSend also excludes sales
        // already invoiced or already included in a posted voucher.
        // Belt-and-braces: never transfer a foreign (sibling-company) sale.
        const salesToTransfer = sales.filter(s => {
            const transferCheck = this.canTransferToAccounting(s);
            return qualifiesForDuettBulkSend(s) && transferCheck.canTransfer;
        }).filter(s => !isForeignSale(s, userStore));

        const total = salesToTransfer.length;

        this.setState({
            sendAllInvoicesToDuett: true,
            totalAccountingTransfers: total,
            currentAccountingTransfer: 0,
            currentAccountingTransferLabel: '',
            currentAccountingTransferFailed: 0,
        });

        let sent = 0;
        let processed = 0;
        const failed = [];
        for (const sale of salesToTransfer) {
            const idInt = parseInt(sale.id, 10);
            const { duettLoading = {}, duettSent = {} } = this.state;
            duettLoading[idInt] = true;
            this.setState({ duettLoading, currentAccountingTransferLabel: transferLabel(sale) });
            // The server-side duettTransferLockedAt lock + duettOrder marker make
            // this loop double-send-safe: a re-run or interrupted run gets 409s,
            // which land in the failed list with the backend's message.
            const response = await duettStore.createInvoice(sale.id);
            await this.sleep(200);
            duettLoading[idInt] = false;
            processed += 1;
            if (response && response.status < 300) {
                sent += 1;
                duettSent[idInt] = true;
            } else {
                failed.push({
                    saleId: sale.id,
                    saleNumber: sale.saleNumber || sale.id,
                    message: (response && response.message) || 'Ukjent feil',
                });
            }
            this.setState({
                duettLoading, duettSent, currentAccountingTransfer: processed, currentAccountingTransferFailed: failed.length,
            });
        }

        await restorePagination();

        this.setState({
            sendAllInvoicesToDuett: false,
            accountingTransferStep3Done: true,
            accountingTransferSummary: { totalSent: sent, failed },
            totalAccountingTransfers: 0,
            currentAccountingTransfer: 0,
        });

        const { callback = () => {} } = this.props;
        await callback();
    }

    togglePayments = e => {
        e.preventDefault();
        e.stopPropagation();
        const { showPayments } = this.state;
        this.setState({ showPayments: !showPayments });
    }

    toggleOverpaymentFilter = e => {
        e.preventDefault();
        e.stopPropagation();
        const { showOverpaymentOnly } = this.state;
        this.setState({ showOverpaymentOnly: !showOverpaymentOnly });
    }

    isDhpReportable = (sale) => {
        if (!sale.animals || sale.animals.length === 0) return false;
        if (sale.animaliaId) return false;
        // Report all production animals and horses to DHP regardless of the production/clinic
        // view toggle (isProductionAnimal) — the vet shouldn't have to switch each patient to
        // production mode to report (TICKET-01184). The species filter below
        // (getDhpRelevantJournals, companion species excluded by speciesId) is what keeps
        // companion-animal journals out — a sheep farmer's dog vaccinations still won't be
        // flagged as missing DHP reporting (TICKET-01020).
        const dhpJournals = getDhpRelevantJournals(sale);
        if (dhpJournals.length === 0) return false;
        if (!dhpJournals.some(j => Array.isArray(j.diagnoses) && j.diagnoses.filter(d => !d.differential).length > 0)) return false;
        if (!dhpJournals.some(j => Array.isArray(j.medicine) && j.medicine.length > 0)) return false;
        return true;
    }

    toggleDhpNotReportedFilter = e => {
        e.preventDefault();
        e.stopPropagation();
        const { showDhpNotReportedOnly } = this.state;
        this.setState({ showDhpNotReportedOnly: !showDhpNotReportedOnly });
    }

    onClickReportAllDhp = e => {
        e.preventDefault();
        e.stopPropagation();
        const { saleStore } = this.props.stores;
        const { sales, searchResults } = saleStore;
        const finalSales = searchResults && searchResults.length > 0 ? searchResults : sales;
        const reportable = finalSales.filter(this.isDhpReportable);
        if (reportable.length === 0) return;

        // Open a prominent, centered in-app confirmation modal instead of the native
        // browser confirm() that pops up as an easy-to-miss little bar at the top of the
        // page (TICKET-01156). The real reporting runs in confirmReportAllDhp().
        this.setState({
            showDhpReportConfirm: true,
            dhpReportCount: reportable.length,
        });
    }

    cancelReportAllDhp = () => {
        this.setState({ showDhpReportConfirm: false });
    }

    confirmReportAllDhp = async () => {
        this.setState({ showDhpReportConfirm: false });
        const { callback = () => {} } = this.props;
        const { appState, saleStore, userStore } = this.props.stores;
        const { sales, searchResults } = saleStore;
        const finalSales = searchResults && searchResults.length > 0 ? searchResults : sales;
        const reportable = finalSales.filter(this.isDhpReportable);
        if (reportable.length === 0) return;

        this.setState({
            dhpBulkReporting: true,
            currentDhpBulk: 0,
            totalDhpBulk: reportable.length,
            animaliaLoading: { ...this.state.animaliaLoading },
        });

        let sent = 0;
        let stopOnAuthError = false;
        for (const sale of reportable) {
            const animaliaLoading = { ...this.state.animaliaLoading };
            animaliaLoading[sale.id] = true;
            this.setState({ animaliaLoading });

            // eslint-disable-next-line no-await-in-loop
            const response = await appState.animaliaUploadVisit(sale.id);
            if (response?.error) {
                console.warn('onClickReportAllDhp error', sale.id, response?.error);
                if (response?.status === 401 || response?.status === 403) {
                    stopOnAuthError = true;
                }
            }

            sent += 1;
            const animaliaLoadingDone = { ...this.state.animaliaLoading };
            animaliaLoadingDone[sale.id] = false;
            this.setState({ animaliaLoading: animaliaLoadingDone, currentDhpBulk: sent });

            if (stopOnAuthError) {
                // eslint-disable-next-line no-await-in-loop
                await userStore.getInfo({ forceLoad: true });
                break;
            }
        }

        this.setState({
            dhpBulkReporting: false,
            currentDhpBulk: 0,
            totalDhpBulk: 0,
        });
        callback();
    }

    onClickShowAnimals = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('small').dataset;
        const { drawerLevel = 2, animalTitle } = this.props;
        const { appState, saleStore } = this.props.stores;
        const { drawerHeightSmall } = appState;
        const { sales, allPayments, searchResults, totalSales, saleUsers = [] } = saleStore;
        const finalSales = searchResults && searchResults.length > 0 ? searchResults : sales;
        const currentSale = finalSales.find(sale => sale.id === parseInt(id, 10));
        if (!currentSale?.id) {
            console.warn('No sale found with id', id);
            return;
        }
        appState.openDrawer('miniAnimal', {
            height: drawerHeightSmall,
            title: animalTitle,
            animals: currentSale.animals,
        }, drawerLevel + 1);
    }

    onClickShowJournals = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('small').dataset;
        const { drawerLevel = 2, journalTitle } = this.props;
        const { appState, saleStore } = this.props.stores;
        const { drawerHeightSmall } = appState;
        const { sales, allPayments, searchResults, totalSales, saleUsers = [] } = saleStore;
        const finalSales = searchResults && searchResults.length > 0 ? searchResults : sales;
        const currentSale = finalSales.find(sale => sale.id === parseInt(id, 10));
        if (!currentSale?.id) {
            console.warn('No sale found with id', id);
            return;
        }
        appState.openDrawer('miniJournal', {
            height: drawerHeightSmall,
            title: journalTitle,
            journals: currentSale.journals,
        }, drawerLevel + 1);
    }

    onClickShowPrescriptions = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('small').dataset;
        const { drawerLevel = 2, prescriptionTitle } = this.props;
        const { appState, saleStore } = this.props.stores;
        const { drawerHeightSmall } = appState;
        const { sales, allPayments, searchResults, totalSales, saleUsers = [] } = saleStore;
        const finalSales = searchResults && searchResults.length > 0 ? searchResults : sales;
        const currentSale = finalSales.find(sale => sale.id === parseInt(id, 10));
        if (!currentSale?.id) {
            console.warn('No sale found with id', id);
            return;
        }

        const currentSalePrescriptions = currentSale.journals.reduce((acc, journal) => {
            if (journal.prescriptions && journal.prescriptions.length > 0) {
                acc.push(...journal.prescriptions);
            }
            return acc;
        }, []);

        const currentJournal = currentSale.journals[0];

        appState.openDrawer('miniPrescription', {
            height: drawerHeightSmall,
            title: prescriptionTitle,
            prescriptions: currentSalePrescriptions,
            journal: currentJournal,
        }, drawerLevel + 1);
    }

    onClickShowDiagnoses = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('small').dataset;
        const { drawerLevel = 2, diagnosesTitle } = this.props;
        const { appState, saleStore } = this.props.stores;
        const { drawerHeightSmall } = appState;
        const { sales, allPayments, searchResults, totalSales, saleUsers = [] } = saleStore;
        const finalSales = searchResults && searchResults.length > 0 ? searchResults : sales;
        const currentSale = finalSales.find(sale => sale.id === parseInt(id, 10));
        if (!currentSale?.id) {
            console.warn('No sale found with id', id);
            return;
        }

        const currentSaleDiagnoses = currentSale.journals.reduce((acc, journal) => {
            if (journal.diagnoses && journal.diagnoses.length > 0) {
                acc.push(...journal.diagnoses);
            }
            return acc;
        }, []);

        appState.openDrawer('miniDiagnoses', {
            height: drawerHeightSmall,
            title: diagnosesTitle,
            diagnoses: currentSaleDiagnoses,
        }, drawerLevel + 1);
    }

    onClickShowMedicines = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('small').dataset;
        const { drawerLevel = 2, medicinesTitle } = this.props;
        const { appState, saleStore } = this.props.stores;
        const { drawerHeightSmall } = appState;
        const { sales, allPayments, searchResults, totalSales, saleUsers = [] } = saleStore;
        const finalSales = searchResults && searchResults.length > 0 ? searchResults : sales;
        const currentSale = finalSales.find(sale => sale.id === parseInt(id, 10));
        if (!currentSale?.id) {
            console.warn('No sale found with id', id);
            return;
        }
        const currentSaleMedicines = currentSale.journals.reduce((acc, journal) => {
            if (journal.medicine && journal.medicine.length > 0) {
                acc.push(...journal.medicine);
            }
            return acc;
        }, []);

        appState.openDrawer('miniMedicine', {
            height: drawerHeightSmall,
            title: medicinesTitle,
            medicines: currentSaleMedicines,
        }, drawerLevel + 1);
    }

    setSortField = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { callback = () => {} } = this.props;
        const { saleStore } = this.props.stores;
        const { sort } = saleStore;
        const { field } = e.target.closest('th').dataset;
        // Check if field is equal to sort (-saleDate, saleDate) fields.
        if (sort === field || sort === `-${field}`) {
            // If so, toggle the sort direction
            saleStore.setSortField(sort.startsWith('-') ? sort.slice(1) : `-${sort}`);
        } else {
            // Otherwise, set the sort field to the new field
            saleStore.setSortField(field);
        }
        callback();
    }

    gotoSale = async saleId => {
        await this.flushPendingProductEdits();
        const saleIdInt = parseInt(saleId, 10);
		const { appState, saleStore } = this.props.stores;
        saleStore.createNewSale({});

        const newSale = await saleStore.load(saleIdInt, false, { skipUpdate: true });
        if (!newSale || !newSale[0]) {
            console.warn('gotoSale: sale not found for id', saleId);
            return;
        }
        if (saleIdInt) {
            await this.loadSalePayments(saleIdInt);
        }
        if (newSale[0].visitors) {
            const visitorIds = newSale[0].visitors.map(e => e.id);
            // console.log('onClickViewVisit', visitorIds)
            await this.loadAnimals(visitorIds);
            await this.loadSales(visitorIds);
        }

        if (newSale[0].animals) {
            const journalIds = newSale[0].journals.map(e => e.id);
            const animalIds = newSale[0].animals.map(e => e.id);
            await this.loadJournals(animalIds, journalIds);
        }
        saleStore.updateKeyValue('newSale', newSale[0]);
    }

    // Open a sale from the voucher "Avvik per salg" drill-down (TICKET-01095). Leaves the
    // admin/accounting toolbar and shows the sale (same as clicking a besøk row) so staff can
    // fix the missing payment, then re-open the voucher afterwards.
    gotoSaleFromAccounting = async saleId => {
        const { tripletexStore } = this.props.stores;
        if (tripletexStore && tripletexStore.clearVoucherData) {
            tripletexStore.clearVoucherData();
        }
        this.setState({ showAdminToolbar: false, adminToolbarSection: null, accountingMode: null });
        await this.viewVisit(parseInt(saleId, 10));
    }

    /**
     * Handles the click event for the payment button.
     * @param {MouseEvent} e - The click event object.
     * @returns {void}
     */
    onClickPaymentSale = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        if (!id) {
            console.warn('No sale id found for payment button');
            return;
        }
        const { saleStore, userStore } = this.props.stores;
        const targetSale = (saleStore.sales || []).find(s => s && s.id === parseInt(id, 10));
        if (targetSale && isForeignSale(targetSale, userStore)) {
            return;
        }
        await this.gotoSale(id);

        const { drawerLevel = 2, callback = () => {} } = this.props;
		const { appState, animalStore, calendarEventStore } = this.props.stores;
		const { drawerHeightMediumMedium } = appState;

        appState.openDrawer('salePayment', {
            height: drawerHeightMediumMedium,
            callback: async () => {
                await this.gotoSale(id);
                callback();
                appState.toggleDrawer(false, drawerLevel + 1);
                //     // Save payment info to sale.
                //     // await this.saveSale({ calledFrom: 'sale.onClickPaymentSale.callback', fieldsToSave: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'] });
                //     const { newSale } = saleStore;
                //     await this.loadAll();
                //     await this.loadSalePayments(id);
                //     const visitorIds = newSale.visitors.map(v => v.id);
                //     if (visitorIds.length > 0) {
                //         await this.loadAllVisitorSales(visitorIds);
                //     }

                // }
            },
        }, drawerLevel + 1);
    }

    markAsPaid = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        if (!id) {
            console.warn('No sale id found for markAsPaid button');
            return;
        }
        const idInt = parseInt(id, 10);

        const { saleStore, userStore } = this.props.stores;
        const targetSale = (saleStore.sales || []).find(s => s && s.id === idInt);
        if (targetSale && isForeignSale(targetSale, userStore)) {
            return;
        }

        const { paymentInProgress = {} } = this.state;
        paymentInProgress[id] = true;
        this.setState({ paymentInProgress });

        const { callback = () => {} } = this.props;
        const { searchResults, sales, allPayments } = saleStore;
        const finalSales = searchResults && searchResults.length > 0 ? searchResults : sales;

        const currentSale = finalSales.find(sale => sale.id === idInt);
        if (!currentSale) {
            console.warn('No sale found with id', idInt);
            this.setState({ paymentInProgress: false });
            return;
        }
        const currentPayments = allPayments.filter(p => p.sale === idInt);


        // Get last payment and mark as paid with this paymentMethod
        // Sort by paidDate descending
        const saleUpdateObject = {
            id: idInt,
        };
        const lastPayment = [...currentPayments].sort((a, b) => new Date(b.paidDate) - new Date(a.paidDate))[0];
        if (lastPayment && lastPayment.paymentMethod) {
            saleUpdateObject.paymentMethod = lastPayment.paymentMethod;
        } else {
            saleUpdateObject.paymentMethod = 'cash';
        }
        saleUpdateObject.status = saleStore.getSaleStatus('paid');
        // Use the last payment's date if available, then sale date, then current date
        saleUpdateObject.paidDate = (lastPayment && lastPayment.paidDate) ? new Date(lastPayment.paidDate)
            : currentSale.saleDate ? new Date(currentSale.saleDate)
            : new Date();

        await saleStore.save(saleUpdateObject);
        paymentInProgress[id] = false;
        this.setState({ paymentInProgress });
        callback(true);
    }

    markAsCollectiveInvoice = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('[data-id]').dataset;
        const dialogAction = beginDialogAction(this, `markAsCollectiveInvoice:${parseInt(id, 10)}`);
        if (!dialogAction) return false;
        try {
            if (!id) {
                return;
            }
            const idInt = parseInt(id, 10);
            const { saleStore, userStore } = this.props.stores;
            const targetSale = (saleStore.sales || []).find(s => s && s.id === idInt);
            if (targetSale && isForeignSale(targetSale, userStore)) {
                return;
            }
            if (!((await appConfirm('Merk som samlefaktura?')) && dialogAction.isCurrent())) {
                return;
            }
            const { callback = () => {} } = this.props;
            await saleStore.save({
                id: idInt,
                status: saleStore.getSaleStatus('collectiveInvoice'),
                paymentMethod: 'collectiveInvoice',
            });
            callback(true);
        } finally {
            dialogAction.finish();
        }
    }

    toggleInsuranceDropdown = (e, saleId) => {
        e.preventDefault();
        e.stopPropagation();
        const { insuranceDropdownOpen } = this.state;
        // Close all other dropdowns and toggle this one
        this.setState({
            insuranceDropdownOpen: {
                [saleId]: !insuranceDropdownOpen[saleId],
            },
        });
    }

    onChangeInsuranceStatus = async (e, saleId, newStatus) => {
        e.preventDefault();
        e.stopPropagation();

        const { saleStore, userStore } = this.props.stores;
        const targetSale = (saleStore.sales || []).find(s => s && s.id === saleId);
        if (targetSale && isForeignSale(targetSale, userStore)) {
            return;
        }

        // Update the sale in the store's sales array immediately for UI feedback
        const { sales } = saleStore;
        const saleIndex = sales.findIndex(s => s.id === saleId);
        if (saleIndex !== -1) {
            saleStore.updateArrayKeyValue('sales', saleIndex, 'insuranceStatus', newStatus);
        }

        // Close the dropdown
        this.setState({ insuranceDropdownOpen: {} });

        // Save to backend
        await saleStore.save({
            id: saleId,
            insuranceStatus: newStatus,
        });
    }

    componentDidMount() {
        this.loadAll();
        // Ensure the speciesId -> name map is available so the visits list can label
        // the animal species even when the embedded animal has an empty species name
        // (many animals only carry speciesId, not the species string) — TICKET-01184.
        const { journalStore } = this.props.stores;
        if (!journalStore.animalSpeciesList || journalStore.animalSpeciesList.length === 0) {
            journalStore.loadAnimalSpeciesList();
        }
        // Set focus to the body element of the pwa
        // We have to find it in the dom
        const { appState } = this.props.stores;
        // appState.setFocusOnAppContainer();
        Mousetrap.bind(['1', '2', '3', '4', '5', '6', '7', '8', '9'], this.handleGotoSale);
    }

    componentWillUnmount() {
        // Unbind shortcuts
        Mousetrap.unbind(['1', '2', '3', '4', '5', '6', '7', '8', '9']);
        clearTimeout(this.reminderSearchTimer);
    }

    componentWillReceiveProps(nextProps) {
        // console.log('AllVisitsTable.componentWillReceiveProps', nextProps);
        const keysToCheck = [
            'limit',
            'searchText',
            'activeStatus',
            'activeUser',
            'activePaymentMethod',
            'cutoffDate',
            'cutoffDateEnd'
        ];

        if (keysToCheck.some(key => nextProps[key] !== this.props[key])) {
            // Reset collective invoice step 2 when filters change
            // User must re-review the list after any filter modification
            this.setState({ collectiveInvoiceStep2Done: false });

            this.checkForExternalTransfer();
        }
    }

    // Helper to check if a sale can be transferred to accounting systems (Fiken/Tripletex)
    // Returns { canTransfer: boolean, reason: string | null, isCreditNote: boolean }
    canTransferToAccounting = (sale) => {
        // Insurance krav-sales (1007 = krav sent, 1008 = krav settled) are internal
        // documents — only the child sales created after the settlement is registered
        // are exported. The krav-document itself is never transferred (that would
        // double-count revenue), but the message differs by state:
        // 1008 = oppgjør registered -> point the user to the child sales that carry it.
        // 1007 = still waiting for the insurer -> nothing to transfer yet.
        if (sale.status === 1008) {
            return { canTransfer: false, reason: 'Krav behandlet — overføres via egenandelsfaktura og forsikringsoppgjør (egne salg)' };
        }
        if (sale.status === 1007) {
            return { canTransfer: false, reason: 'Krav sendt til forsikring — kan ikke overføres før oppgjør er registrert' };
        }
        // Insurance cases still waiting on the insurer must not be transferred or
        // sent (incl. auto-send) until resolved. insuranceStatus 1 = Venter,
        // 2 = Sendt til forsikring. Child settlement sales keep insuranceStatus 0,
        // so paid insurance/egenandel sales remain transferable.
        if (sale.insuranceStatus === 1 || sale.insuranceStatus === 2) {
            const reason = sale.insuranceStatus === 2
                ? 'Sendt til forsikring — kan ikke overføres før forsikringssaken er avklart'
                : 'Venter på forsikring — kan ikke overføres før forsikringssaken er avklart';
            return { canTransfer: false, reason };
        }
        // Credit notes can be transferred via special endpoint
        if (sale.paymentMethod === 'creditNote' || sale.status === 6) {
            return { canTransfer: true, reason: null, isCreditNote: true };
        }
        if (sale.paymentMethod === 'invoiceCredited') {
            return { canTransfer: false, reason: 'Kreditert faktura kan ikke overføres' };
        }
        if (sale.paymentMethod === 'saleCredited') {
            return { canTransfer: false, reason: 'Kreditert salg kan ikke overføres' };
        }
        // Cancelled or deleted sales
        if (sale.offline >= 1) {
            return { canTransfer: false, reason: 'Slettet salg kan ikke overføres' };
        }
        return { canTransfer: true, reason: null, isCreditNote: false };
    }

    // Helper to render validation warnings as compact badges
    renderVisitorWarnings = (visitors) => {
        const hasEmail = visitorHasEmail(visitors);
        const hasCellphone = visitorHasCellphone(visitors);
        const hasValidEmail = visitorHasValidEmail(visitors);
        const hasValidCellphone = visitorHasValidCellphone(visitors);
        const hasEmailOrCellphone = visitorHasEmailOrCellphone(visitors);

        const warnings = [];
        if (hasEmail && !hasValidEmail) {
            warnings.push({ icon: 'fa-envelope', text: 'Mobil for besøkende er ikke gyldig' });
        }
        if (hasCellphone && !hasValidCellphone) {
            warnings.push({ icon: 'fa-phone', text: 'Mobil for besøkende er ikke gyldig' });
        }
        if (!hasEmailOrCellphone) {
            warnings.push({ icon: 'fa-user', text: 'Mangler e-post og mobil' });
        }

        if (warnings.length === 0) return null;

        return (
            <div class='d-flex flex-wrap gap-1 mb-1 justify-content-start'>
                {warnings.map((w, idx) => (
                    <span key={idx} class='badge text-bg-danger' style='font-size: 0.65em;' title={w.text}>
                        <i class={`fa-solid ${w.icon}`} /> <i class='fa-solid fa-warning' />
                    </span>
                ))}
            </div>
        );
    }

    // Render Fiken column content
    renderFikenColumn = (e, { customer, saleIsPaid, fikenLoading, fikenSent, displayFikenError, fikenExpandedSummary, fikenExpandedErrors, isAdmin, isCustomerAdmin }) => {
        const { userStore } = this.props.stores;
        if (isForeignSale(e, userStore)) {
            return null;
        }
        // Check for missing configuration. The raw Fiken token is a secret and is never
        // sent to the browser (Batch 3) — use the derived fikenConfigured boolean instead.
        // fikenSlug is an identifier, not a secret, and stays visible.
        if (!customer.fikenConfigured || !customer.fikenSlug) {
            return (
                <small class='text-muted'>
                    {!customer.fikenConfigured && <div><i class='fa-solid fa-key text-warning' /> Token mangler</div>}
                    {!customer.fikenSlug && <div><i class='fa-solid fa-building text-warning' /> Slug mangler</div>}
                </small>
            );
        }

        // Check if sale can be transferred (not credit note, not cancelled, etc.)
        const transferCheck = this.canTransferToAccounting(e);
        if (!transferCheck.canTransfer) {
            return (
                <small class='text-muted'>
                    <i class='fa-solid fa-ban text-secondary' /> {transferCheck.reason}
                </small>
            );
        }

        const isInvoice = e.paymentMethod === 'invoice';
        const hasLocation = !!e.fikenLocation;
        // What actually exists in Fiken: an invoice (/invoices/) or a paid sale
        // (/sales/, kontantsalg). The badge used to key on the CURRENT payment
        // method, so a sale that was transferred as kontantsalg, reopened and
        // switched to invoice showed «Faktura opprettet» while «Send faktura»
        // could never work (TICKET-01749).
        const fikenHasInvoice = !!e.fikenResponse?.invoiceId || /\/invoices\//.test(e.fikenLocation || '');
        const fikenBookedAsSale = hasLocation && !fikenHasInvoice;
        const hasError = e.fikenResponse && (e.fikenResponse?.statusCode > 201 || (e.fikenResponse && e.fikenResponse[0] && e.fikenResponse[0].message));
        const hasApiResponses = e.fikenApiResponses && e.fikenApiResponses.length > 0;

        return (
            <div class='d-flex flex-column align-items-start' style='font-size: 0.9em;'>
                {/* Visitor warnings */}
                {this.renderVisitorWarnings(e.visitors)}

                {/* Status indicator */}
                {hasLocation ? (
                    <div class='text-success mb-1' style='font-size: 0.85em; white-space: nowrap;'>
                        <i class='fa-solid fa-check-circle' /> {fikenHasInvoice ? 'Faktura opprettet' : 'Salg opprettet'}
                        {isInvoice && fikenBookedAsSale && (
                            <div class='text-warning' style='white-space: normal; max-width: 280px; line-height: 1.3em;'>
                                <i class='fa-solid fa-triangle-exclamation' /> Bokført i Fiken som betalt salg (kontantsalg), ikke faktura. Slett salget i Fiken og trykk «Gjenopprett».
                            </div>
                        )}
                    </div>
                ) : fikenSent[e.id] ? (
                    <div class='text-success mb-1' style='font-size: 0.85em; white-space: nowrap;'>
                        <i class='fa-solid fa-check-circle' /> Opprettet
                    </div>
                ) : null}

                {/* Action buttons */}
                <div class='d-flex flex-column gap-1 align-items-start'>
                    {isInvoice ? (
                        <>
                            {hasLocation ? (
                                <>
                                    {(e.fikenInvoiceSent || this.state.fikenSentToCustomer?.[e.id]) ? (
                                        <span class='text-success' style='font-size: 0.85em; white-space: nowrap;'><i class='fa-solid fa-paper-plane' /> Sendt</span>
                                    ) : (
                                        <div class='text-body-secondary' style='font-size: 0.85em;'>
                                            <div style='white-space: nowrap;'>
                                                {e.fikenSentToCustomer === false && <>
                                                    {this.renderNotSentLabel(e)}
                                                    <button
                                                        type='button'
                                                        class='btn btn-outline-primary font-weight-light btn-sm py-0 ms-2'
                                                        style='font-size: 0.85em; line-height: 1.3em;'
                                                        data-id={e.id}
                                                        onClick={this.onClickPrintSaleRow}
                                                        title='Skriv ut faktura for postutsendelse'
                                                    >
                                                        <i class='fa-solid fa-print' /> Skriv ut
                                                    </button>
                                                </>}
                                                {!e.paidDate && (
                                                    <button
                                                        disabled={this.state.fikenSendLoading?.[e.id]}
                                                        type='button'
                                                        class={`btn btn-sm btn-outline-success py-0${e.fikenSentToCustomer === false ? ' ms-2' : ''}`}
                                                        // pointer-events: auto — see the Tripletex "Send nå"
                                                        // button: without it a click during "Sender..."
                                                        // falls through the disabled button to the <tr>
                                                        // and opens the sale (TICKET-01362 follow-up).
                                                        style='font-size: 0.85em; white-space: nowrap; pointer-events: auto;'
                                                        data-id={e.id}
                                                        onClick={this.sendFikenInvoice}
                                                        title='Send faktura til kunde via Fiken'
                                                    >
                                                        {this.state.fikenSendLoading?.[e.id]
                                                            ? <><i class='fa-solid fa-spinner-third fa-spin' /> Sender...</>
                                                            : <><i class='fa-duotone fa-paper-plane-top' /> Send faktura</>}
                                                    </button>
                                                )}
                                            </div>
                                            {this.state.fikenSendError?.[e.id] && (
                                                <div
                                                    class='text-danger mt-1'
                                                    style='font-size: 0.9em; white-space: normal; max-width: 280px; line-height: 1.3em;'
                                                    title={this.state.fikenSendError[e.id]}
                                                >
                                                    <i class='fa-solid fa-triangle-exclamation' /> {this.state.fikenSendError[e.id]}
                                                </div>
                                            )}
                                            {e.fikenSentToCustomer === false && e.fikenSentToCustomerReason && (
                                                <div
                                                    class='text-warning mt-1'
                                                    style='font-size: 0.9em; white-space: normal; max-width: 280px; line-height: 1.3em;'
                                                    title={e.fikenSentToCustomerReason}
                                                >
                                                    <i class='fa-solid fa-triangle-exclamation' /> {e.fikenSentToCustomerReason}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {/* Same gate as «Sett til pågående»: a clinic that reopened a sale
                                        already booked in Fiken (as kontantsalg) must be able to re-create
                                        the invoice itself after deleting the old sale in Fiken (TICKET-01749). */}
                                    {(isAdmin || isCustomerAdmin) && <button
                                        disabled={fikenLoading[e.id]}
                                        type='button'
                                        class='btn btn-sm btn-outline-danger py-0'
                                        style='font-size: 0.8em; white-space: nowrap;'
                                        data-id={e.id}
                                        data-recreate='true'
                                        onClick={this.createFikenCompanyInvoice}
                                    >
                                        <i class='fa-duotone fa-refresh' /> Gjenopprett
                                        {fikenLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                    </button>}
                                </>
                            ) : (
                                <>
                                    {saleIsPaid ? (
                                        <button
                                            disabled={fikenLoading[e.id]}
                                            type='button'
                                            class='btn btn-sm btn-outline-success py-0'
                                            style='font-size: 0.85em; white-space: nowrap;'
                                            data-id={e.id}
                                            onClick={this.createFikenCompanySale}
                                        >
                                            <i class='fa-duotone fa-cloud-arrow-up' /> Opprett salg
                                            {fikenLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                        </button>
                                    ) : !fikenSent[e.id] && (
                                        <button
                                            disabled={fikenLoading[e.id]}
                                            type='button'
                                            class='btn btn-sm btn-success py-0'
                                            style='font-size: 0.85em; white-space: nowrap;'
                                            data-id={e.id}
                                            onClick={this.createFikenCompanyInvoice}
                                        >
                                            <i class='fa-duotone fa-cloud-arrow-up' /> Opprett faktura
                                            {fikenLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                        </button>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {saleIsPaid && ['cash', 'card', 'cardExternal', 'vipps', 'account', 'paypal', 'insurance'].includes(e.paymentMethod) && !hasLocation && !fikenSent[e.id] && (
                                <button
                                    disabled={fikenLoading[e.id]}
                                    type='button'
                                    class='btn btn-sm btn-outline-success py-0'
                                    style='font-size: 0.85em; white-space: nowrap;'
                                    data-id={e.id}
                                    onClick={this.createFikenCompanySale}
                                >
                                    <i class='fa-duotone fa-cloud-arrow-up' /> Opprett salg
                                    {fikenLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                </button>
                            )}
                        </>
                    )}

                </div>

                {/* API Response Summary */}
                {hasApiResponses && (() => {
                    const analysis = analyzeFikenResponses(e.fikenApiResponses);
                    const isExpanded = fikenExpandedSummary[e.id];
                    const isErrorsExpanded = fikenExpandedErrors[e.id];
                    const displaySummary = isExpanded ? analysis.fullSummary : analysis.summary;
                    const displayErrors = isErrorsExpanded ? analysis.fullErrors : analysis.errors;

                    return (
                        <div class='mt-1 w-100' style='font-size: 0.8em; line-height: 1.3;'>
                            {analysis.summary.length > 0 && (
                                <div class={analysis.hasErrors ? 'text-danger' : 'text-success'}>
                                    {displaySummary.map((line, idx) => (
                                        <div key={idx}>• {line}</div>
                                    ))}
                                    {analysis.hiddenSummaryCount > 0 && (
                                        <span
                                            data-id={e.id}
                                            onClick={this.toggleFikenExpandedSummary}
                                            class='text-muted'
                                            style='cursor: pointer; text-decoration: underline;'
                                        >
                                            {isExpanded ? '(skjul)' : `(+${analysis.hiddenSummaryCount} til)`}
                                        </span>
                                    )}
                                </div>
                            )}
                            {analysis.hasErrors && analysis.errors.length > 0 && (
                                <div class='text-danger mt-1'>
                                    {displayErrors.map((err, idx) => (
                                        <div key={idx}>• {err}</div>
                                    ))}
                                    {analysis.hiddenErrorsCount > 0 && (
                                        <span
                                            data-id={e.id}
                                            onClick={this.toggleFikenExpandedErrors}
                                            class='text-muted'
                                            style='cursor: pointer; text-decoration: underline;'
                                        >
                                            {isErrorsExpanded ? '(skjul)' : `(+${analysis.hiddenErrorsCount} feil til)`}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Debug button - show for admin when there's any Fiken activity */}
                {isAdmin && (hasLocation || hasError || hasApiResponses) && (
                    <button
                        class={`btn btn-sm ${displayFikenError[e.id] ? 'btn-secondary' : 'btn-outline-secondary'} py-0 mt-1`}
                        style='font-size: 0.8em; white-space: nowrap;'
                        onClick={this.toggleFikenError}
                        data-id={e.id}
                    >
                        <i class='fa-duotone fa-bug' /> Debug
                    </button>
                )}
            </div>
        );
    }

    // Render Tripletex column content
    renderTripletexSendStatus = (e) => {
        const ehfStatus = e.tripletexInvoice?.ehfSendStatus;
        const sentToCustomer = e.tripletexDebug?.[0]?.sentToCustomer;
        const { tripletexSendLoading = {}, tripletexSentToCustomer = {}, tripletexSendError = {} } = this.state;
        // Invoice sent via the manual "Send nå" action (new tripletexInvoiceSent field),
        // or optimistically just now. Show as sent and stop here.
        if (e.tripletexInvoiceSent || tripletexSentToCustomer[e.id]) {
            return (
                <div class='text-success mb-1' style='font-size: 0.8em; white-space: nowrap;'>
                    <i class='fa-solid fa-paper-plane' /> Sendt til kunde
                </div>
            );
        }

        if (ehfStatus === 'SENT') {
            return (
                <div class='text-success mb-1' style='font-size: 0.8em; white-space: nowrap;'>
                    <i class='fa-solid fa-paper-plane' /> Sendt til kunde (EHF)
                </div>
            );
        }
        if (ehfStatus === 'SEND') {
            return (
                <div class='text-warning mb-1' style='font-size: 0.8em; white-space: nowrap;'>
                    <i class='fa-solid fa-paper-plane' /> Venter på sending (EHF)
                </div>
            );
        }
        if (ehfStatus === 'SEND_FAILURE_RECIPIENT_NOT_FOUND') {
            return (
                <div class='text-danger mb-1' style='font-size: 0.8em; white-space: nowrap;'>
                    <i class='fa-solid fa-triangle-exclamation' /> Sending feilet (EHF)
                </div>
            );
        }
        // No EHF status (or DO_NOT_SEND) - check tripletexDebug for email sending
        if (sentToCustomer === true) {
            return (
                <div class='text-success mb-1' style='font-size: 0.8em; white-space: nowrap;'>
                    <i class='fa-solid fa-paper-plane' /> Sendt til kunde
                </div>
            );
        }
        if (sentToCustomer === false) {
            const sentToCustomerReason = e.tripletexDebug?.[0]?.sentToCustomerReason;
            return (
                <div class='text-body-secondary mb-1' style='font-size: 0.8em;'>
                    <div style='white-space: nowrap;'>
                        {this.renderNotSentLabel(e)}
                        <button
                            type='button'
                            class='btn btn-outline-primary font-weight-light btn-sm py-0 ms-2 rounded-pill'
                            style='font-size: 0.85em; line-height: 1.3em;'
                            data-id={e.id}
                            onClick={this.onClickPrintSaleRow}
                            title='Skriv ut faktura for postutsendelse'
                        >
                            <i class='fa-solid fa-print' /> Skriv ut
                        </button>
                        {/* No «Send nå» on a part-paid sale: Tripletex would e-mail the
                            invoice with the full amount, not the remaining amount. */}
                        {!e.paidDate && !isPartiallyPaidSale(e) && (
                            <button
                                type='button'
                                class='btn btn-primary font-weight-light btn-sm py-0 ms-2 rounded-pill'
                                // pointer-events: auto overrides Bootstrap's .btn:disabled
                                // { pointer-events: none } — without it, a second click
                                // during the "Sender..." window passes THROUGH the disabled
                                // button to the <tr>, which opens the sale and closes the
                                // list (TICKET-01362 follow-up). With it, the disabled
                                // button absorbs the click (no click event is dispatched
                                // for disabled controls).
                                style='font-size: 0.85em; line-height: 1.3em; pointer-events: auto;'
                                data-id={e.id}
                                disabled={tripletexSendLoading[e.id]}
                                onClick={this.sendTripletexInvoiceToCustomer}
                                title='Send faktura til kunde via Tripletex'
                            >
                                {tripletexSendLoading[e.id]
                                    ? <><i class='fa-solid fa-spinner-third fa-spin' /> Sender...</>
                                    : <><i class='fa-solid fa-paper-plane' /> Send nå</>}
                            </button>
                        )}
                    </div>
                    {tripletexSendError[e.id] && (
                        <div
                            class='text-danger mt-1'
                            style='font-size: 0.9em; white-space: normal; max-width: 280px; line-height: 1.3em;'
                            title={tripletexSendError[e.id]}
                        >
                            <i class='fa-solid fa-triangle-exclamation' /> {tripletexSendError[e.id]}
                        </div>
                    )}
                    {sentToCustomerReason && (
                        <div
                            class='text-warning mt-1'
                            style='font-size: 0.9em; white-space: normal; max-width: 280px; line-height: 1.3em;'
                            title={sentToCustomerReason}
                        >
                            <i class='fa-solid fa-triangle-exclamation' /> {tripletexSendReasonLabel(sentToCustomerReason)}
                        </div>
                    )}
                </div>
            );
        }
        return null;
    }

    // A split sale (card at the desk, rest on invoice). Tripletex prints the invoice
    // total, not the remaining amount, so the owner must get the Dyrejournal print
    // (paid amount + rest + KID) instead of the Tripletex e-mail.
    renderTripletexPartialPaidHelp = (e) => {
        if (!isPartiallyPaidSale(e) || !e.tripletexInvoice?.id) {
            return null;
        }
        const { amount, amountOutstanding } = e.tripletexInvoice;
        const hasAmounts = amount > 0 && amountOutstanding > 0 && amountOutstanding < amount;
        return (
            <div
                class='text-warning mb-1'
                style='font-size: 0.8em; white-space: normal; max-width: 280px; line-height: 1.3em;'
            >
                <i class='fa-solid fa-circle-info' /> Delvis betalt. Tripletex-fakturaen viser totalbeløpet
                {hasAmounts ? ` (kr ${util.format(amount, 2)})` : ''}, ikke restbeløpet
                {hasAmounts ? ` (kr ${util.format(amountOutstanding, 2)})` : ''}.
                {' '}Skriv ut fakturaen fra Dyrejournal – den viser betalt beløp, rest å betale og KID.
            </div>
        );
    }

    renderTripletexColumn = (e, { customer, saleIsPaid, tripletexLoading, tripletexSent, tripletexError, tripletexCreateError = {}, tripletexExpandedSummary, tripletexExpandedErrors, tripletexInvoice, hasPayments, isAdmin }) => {
        const { userStore } = this.props.stores;
        if (isForeignSale(e, userStore)) {
            return null;
        }
        // Check for missing configuration. The raw employee token is a secret and is
        // never sent to the browser (Batch 3) — use the derived tripletexConfigured
        // boolean instead.
        if (!customer.tripletexConfigured) {
            return (
                <small class='text-muted'>
                    <i class='fa-solid fa-key text-warning' /> Employee Token mangler
                </small>
            );
        }

        // Check if sale can be transferred (not credit note, not cancelled, etc.)
        const transferCheck = this.canTransferToAccounting(e);
        if (!transferCheck.canTransfer) {
            return (
                <small class='text-muted'>
                    <i class='fa-solid fa-ban text-secondary' /> {transferCheck.reason}
                </small>
            );
        }

        // Already booked in Tripletex through a posted samlebilag (bilagsrapportering).
        // post-invoices.js blocks a second transfer (tripletex-voucher-guard.js,
        // TICKET-01427) — show where the sale is booked instead of a send button,
        // mirroring the Duett "I bilag" marker (TICKET-01502).
        if (!e.tripletexInvoice?.id && e.tripletexVoucher?.voucherId) {
            const voucherNo = formatTripletexVoucherNo(e.tripletexVoucher.voucherNumber, e.tripletexVoucher.voucherId, '');
            const { periodStart, periodEnd } = e.tripletexVoucher;
            return (
                <div class='d-flex flex-column align-items-start' style='font-size: 0.9em;'>
                    {this.renderVisitorWarnings(e.visitors)}
                    <small class='text-muted' title={periodStart && periodEnd ? `Bilagsrapportering ${periodStart} - ${periodEnd}` : 'Bilagsrapportering'}>
                        <i class='fa-solid fa-file-invoice' /> I bilag{voucherNo ? ` #${voucherNo}` : ''}
                    </small>
                </div>
            );
        }

        const isInvoice = ['invoice', 'partial'].includes(e.paymentMethod);
        const isCashSale = saleIsPaid && ['cash', 'card', 'cardExternal', 'vipps', 'account', 'paypal', 'insurance', 'customerCredit'].includes(e.paymentMethod);
        const isCreditNote = transferCheck.isCreditNote;
        const hasResponse = !!e.tripletexInvoice?.id || (e.tripletexResponse && e.tripletexResponse?.statusCode === 201);
        const hasError = e.tripletexResponse && e.tripletexResponse.status > 202;
        const hasApiResponses = e.tripletexApiResponses && e.tripletexApiResponses.length > 0;

        return (
            <div class='d-flex flex-column align-items-start' style='font-size: 0.9em;'>
                {/* Visitor warnings */}
                {this.renderVisitorWarnings(e.visitors)}

                {/* TICKET-01186: failed transfers used to be silent (or even showed a
                    green "Faktura opprettet") — show the reason so the user knows the
                    invoice does NOT exist in Tripletex and can retry. */}
                {tripletexCreateError[e.id] && (
                    <div class='alert alert-danger p-1 mb-1' style='font-size: 0.85em; white-space: normal; max-width: 240px;'>
                        <i class='fa-solid fa-triangle-exclamation' /> Overføring til Tripletex feilet: {tripletexCreateError[e.id]}
                    </div>
                )}

                {/* Credit note handling */}
                {isCreditNote ? (
                    <>
                        {hasResponse ? (
                            <>
                                <div class='text-success mb-1' style='font-size: 0.85em; white-space: nowrap;'>
                                    <i class='fa-solid fa-check-circle' /> Kreditnota opprettet
                                </div>
                                <div class='d-flex flex-column gap-1 align-items-start'>
                                    {isAdmin && <button
                                        disabled={tripletexLoading[e.id]}
                                        type='button'
                                        class='btn btn-sm btn-outline-danger py-0'
                                        style='font-size: 0.8em; white-space: nowrap;'
                                        data-id={e.id}
                                        data-recreate='true'
                                        onClick={this.createTripletexCreditNote}
                                    >
                                        <i class='fa-duotone fa-refresh' /> Gjenopprett
                                        {tripletexLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                    </button>}
                                </div>
                            </>
                        ) : tripletexSent[e.id] ? (
                            <div class='text-success' style='font-size: 0.85em; white-space: nowrap;'>
                                <i class='fa-solid fa-check-circle' /> Kreditnota opprettet
                            </div>
                        ) : (
                            <>
                                {!e.parentSaleId ? (
                                    <small class='text-danger'>
                                        <i class='fa-solid fa-warning' /> Mangler kobling til original faktura
                                    </small>
                                ) : (
                                    <button
                                        disabled={tripletexLoading[e.id]}
                                        type='button'
                                        class='btn btn-sm btn-warning py-0'
                                        style='font-size: 0.85em; white-space: nowrap;'
                                        data-id={e.id}
                                        onClick={this.createTripletexCreditNote}
                                    >
                                        <i class='fa-duotone fa-cloud-arrow-up' /> Opprett kreditnota
                                        {tripletexLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                    </button>
                                )}
                            </>
                        )}
                    </>
                ) : isInvoice ? (
                    <>
                        {hasResponse ? (
                            <>
                                <div class='text-success mb-1' style='font-size: 0.85em; white-space: nowrap;'>
                                    <i class='fa-solid fa-check-circle' /> Faktura opprettet
                                </div>
                                {this.renderTripletexSendStatus(e)}
                                {this.renderTripletexPartialPaidHelp(e)}
                                <div class='d-flex flex-column gap-1 align-items-start'>
                                    {tripletexInvoice.amountOutstanding >= 1 && hasPayments ? (
                                        <button
                                            disabled={tripletexLoading[e.id]}
                                            type='button'
                                            class='btn btn-sm btn-primary py-0'
                                            style='font-size: 0.85em; white-space: nowrap;'
                                            data-id={e.id}
                                            onClick={this.addTripletexPayment}
                                        >
                                            <i class='fa-duotone fa-cloud-arrow-up' /> Legg til betaling
                                            {tripletexLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                        </button>
                                    ) : (
                                        isAdmin && <button
                                            disabled={tripletexLoading[e.id]}
                                            type='button'
                                            class='btn btn-sm btn-outline-danger py-0'
                                            style='font-size: 0.8em; white-space: nowrap;'
                                            data-id={e.id}
                                            data-recreate='true'
                                            onClick={this.createTripletexInvoice}
                                        >
                                            <i class='fa-duotone fa-refresh' /> Gjenopprett
                                            {tripletexLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                        </button>
                                    )}
                                </div>
                            </>
                        ) : tripletexSent[e.id] ? (
                            <div class='text-success' style='font-size: 0.85em; white-space: nowrap;'>
                                <i class='fa-solid fa-check-circle' /> Faktura opprettet
                            </div>
                        ) : (
                            <div class='d-flex flex-column gap-1'>
                                <button
                                    disabled={tripletexLoading[e.id]}
                                    type='button'
                                    class='btn btn-sm btn-info py-0'
                                    style='font-size: 0.85em; white-space: nowrap;'
                                    data-id={e.id}
                                    onClick={this.createTripletexInvoice}
                                >
                                    <i class='fa-duotone fa-cloud-arrow-up' /> Opprett faktura
                                    {tripletexLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                </button>
                                <button
                                    disabled={tripletexLoading[e.id]}
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary py-0'
                                    style='font-size: 0.8em; white-space: nowrap;'
                                    data-id={e.id}
                                    onClick={this.simulateTripletexInvoice}
                                >
                                    <i class='fa-duotone fa-eye' /> Simuler
                                    {tripletexLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                </button>
                            </div>
                        )}
                    </>
                ) : isCashSale ? (
                    <>
                        {hasResponse ? (
                            <>
                                <div class='text-success mb-1' style='font-size: 0.85em; white-space: nowrap;'>
                                    <i class='fa-solid fa-check-circle' /> Salg opprettet
                                </div>
                                {this.renderTripletexSendStatus(e)}
                                <div class='d-flex flex-column gap-1 align-items-start'>
                                    {tripletexInvoice.amountOutstanding && hasPayments > 1 ? (
                                        <button
                                            disabled={tripletexLoading[e.id]}
                                            type='button'
                                            class='btn btn-sm btn-primary py-0'
                                            style='font-size: 0.85em; white-space: nowrap;'
                                            data-id={e.id}
                                            onClick={this.addTripletexPayment}
                                        >
                                            <i class='fa-duotone fa-cloud-arrow-up' /> Legg til betaling
                                            {tripletexLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                        </button>
                                    ) : (
                                        isAdmin && <button
                                            disabled={tripletexLoading[e.id]}
                                            type='button'
                                            class='btn btn-sm btn-outline-danger py-0'
                                            style='font-size: 0.8em; white-space: nowrap;'
                                            data-id={e.id}
                                            data-recreate='true'
                                            onClick={this.createTripletexInvoice}
                                        >
                                            <i class='fa-duotone fa-refresh' /> Gjenopprett
                                            {tripletexLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                        </button>
                                    )}
                                </div>
                            </>
                        ) : tripletexSent[e.id] ? (
                            <div class='text-success' style='font-size: 0.85em; white-space: nowrap;'>
                                <i class='fa-solid fa-check-circle' /> Salg opprettet
                            </div>
                        ) : (
                            <div class='d-flex flex-column gap-1'>
                                <button
                                    disabled={tripletexLoading[e.id]}
                                    type='button'
                                    class='btn btn-sm btn-outline-info py-0'
                                    style='font-size: 0.85em; white-space: nowrap;'
                                    data-id={e.id}
                                    onClick={this.createTripletexInvoice}
                                >
                                    <i class='fa-duotone fa-cloud-arrow-up' /> Opprett salg
                                    {tripletexLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                </button>
                                <button
                                    disabled={tripletexLoading[e.id]}
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary py-0'
                                    style='font-size: 0.8em; white-space: nowrap;'
                                    data-id={e.id}
                                    onClick={this.simulateTripletexInvoice}
                                >
                                    <i class='fa-duotone fa-eye' /> Simuler
                                    {tripletexLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                </button>
                            </div>
                        )}
                    </>
                ) : null}

                {/* Debug button for simulation preview (when no API responses yet) */}
                {!hasApiResponses && e.tripletexInvoicePreview && (
                    <div class='mt-1'>
                        <button
                            class={`btn btn-sm ${tripletexError[e.id] ? 'btn-secondary' : 'btn-outline-secondary'} py-0`}
                            style='font-size: 0.8em; white-space: nowrap;'
                            onClick={this.toggleTripleTexError}
                            data-id={e.id}
                        >
                            <i class='fa-duotone fa-eye' /> Vis simulering
                            <span class={`badge ms-1 ${e.tripletexInvoicePreview.status === 'simulated' ? 'bg-info' : 'bg-warning'}`} style='font-size: 0.75em;'>
                                {e.tripletexInvoicePreview.status}
                            </span>
                        </button>
                    </div>
                )}

                {/* API Response Summary */}
                {hasApiResponses && (() => {
                    const analysis = analyzeTripletexResponses(e.tripletexApiResponses, e.tripletexInvoice);
                    const isExpanded = tripletexExpandedSummary[e.id];
                    const isErrorsExpanded = tripletexExpandedErrors[e.id];
                    const displaySummary = isExpanded ? analysis.fullSummary : analysis.summary;
                    const displayErrors = isErrorsExpanded ? analysis.fullErrors : analysis.errors;

                    return (
                        <div class='mt-1 w-100' style='font-size: 0.8em; line-height: 1.3;'>
                            {analysis.summary.length > 0 && (
                                <div class={analysis.hasErrors ? 'text-danger' : 'text-success'}>
                                    {displaySummary.map((line, idx) => (
                                        <div key={idx}>• {line}</div>
                                    ))}
                                    {analysis.hiddenSummaryCount > 0 && (
                                        <span
                                            data-id={e.id}
                                            onClick={this.toggleTripletexExpandedSummary}
                                            class='text-muted'
                                            style='cursor: pointer; text-decoration: underline;'
                                        >
                                            {isExpanded ? '(skjul)' : `(+${analysis.hiddenSummaryCount} til)`}
                                        </span>
                                    )}
                                </div>
                            )}
                            {analysis.hasErrors && analysis.errors.length > 0 && (
                                <div class='text-danger mt-1'>
                                    {displayErrors.map((err, idx) => (
                                        <div key={idx}>• {err}</div>
                                    ))}
                                    {analysis.hiddenErrorsCount > 0 && (
                                        <span
                                            data-id={e.id}
                                            onClick={this.toggleTripletexExpandedErrors}
                                            class='text-muted'
                                            style='cursor: pointer; text-decoration: underline;'
                                        >
                                            {isErrorsExpanded ? '(skjul)' : `(+${analysis.hiddenErrorsCount} feil til)`}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Debug button - show for admin when there's any Tripletex activity */}
                {isAdmin && (hasResponse || hasError || e.tripletexDebug?.length > 0 || e.tripletexInvoicePreview || hasApiResponses) && (
                    <div class='d-flex align-items-center gap-2 mt-1' style='font-size: 0.8em;'>
                        <button
                            class={`btn btn-sm ${tripletexError[e.id] ? 'btn-secondary' : 'btn-outline-secondary'} py-0`}
                            style='font-size: 0.8em; white-space: nowrap;'
                            onClick={this.toggleTripleTexError}
                            data-id={e.id}
                        >
                            <i class='fa-duotone fa-bug' /> Debug
                            {(() => {
                                const debugCount = e.tripletexDebug?.length || 0;
                                return debugCount > 0 ? (
                                    <span class='badge bg-secondary ms-1' style='font-size: 0.75em;'>{debugCount}</span>
                                ) : null;
                            })()}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // Render PowerOffice column content
    renderPowerOfficeColumn = (e, { customer, saleIsPaid, powerofficeLoading, powerofficeSent, powerofficeError, powerofficeExpandedSummary, powerofficeExpandedErrors, powerofficeInvoice, hasPayments, isAdmin }) => {
        const { userStore } = this.props.stores;
        if (isForeignSale(e, userStore)) {
            return null;
        }
        // Check for missing configuration. powerOfficeCredentials is a secret and is
        // never sent to the browser (Batch 3) — use the derived powerofficeConfigured
        // boolean instead (also covers demo mode, since the presence check on the
        // backend counts an { environment: 'demo' }-only object as configured).
        const hasCredentials = !!customer.powerofficeConfigured;

        if (!hasCredentials) {
            return (
                <small class='text-muted'>
                    <i class='fa-solid fa-key text-warning' /> Credentials mangler
                </small>
            );
        }

        // Check if sale can be transferred (not credit note, not cancelled, etc.)
        const transferCheck = this.canTransferToAccounting(e);
        if (!transferCheck.canTransfer) {
            return (
                <small class='text-muted'>
                    <i class='fa-solid fa-ban text-secondary' /> {transferCheck.reason}
                </small>
            );
        }

        const isInvoice = ['invoice', 'partial'].includes(e.paymentMethod);
        const isCashSale = saleIsPaid && ['cash', 'card', 'cardExternal', 'cardApp', 'vipps', 'account', 'bank', 'giftCard', 'paypal', 'insurance', 'customerCredit'].includes(e.paymentMethod);
        // Check for successful response - can be direct statusCode or nested in salesOrder
        const hasResponse = e.powerOfficeTransferred || (e.powerOfficeResponse && (
            e.powerOfficeResponse?.statusCode === 201 ||
            e.powerOfficeResponse?.statusCode === 202 ||
            e.powerOfficeResponse?.salesOrder?.statusCode === 201 ||
            e.powerOfficeResponse?.salesOrder?.statusCode === 202 ||
            e.powerOfficeResponse?.cashVoucher?.statusCode === 201 ||
            e.powerOfficeResponse?.success
        ));
        const hasApiResponses = e.powerOfficeApiResponses && e.powerOfficeApiResponses.length > 0;
        // Get sales order details for display
        const salesOrderNo = e.powerOfficeResponse?.salesOrder?.SalesOrderNo || e.powerOfficeResponse?.SalesOrderNo;
        const salesOrderStatus = e.powerOfficeResponse?.salesOrder?.SalesOrderStatus || e.powerOfficeResponse?.SalesOrderStatus;
        const invoiceSent = e.powerOfficeResponse?.sendInvoice?.statusCode === 201;

        return (
            <div class='d-flex flex-column align-items-start' style='font-size: 0.9em;'>
                {/* Visitor warnings */}
                {this.renderVisitorWarnings(e.visitors)}

                {/* Invoice handling */}
                {isInvoice ? (
                    <>
                        {hasResponse ? (
                            <>
                                <div class='text-success mb-1' style='font-size: 0.85em; white-space: nowrap;'>
                                    <i class='fa-solid fa-check-circle' /> Overført
                                </div>
                                {salesOrderNo && (
                                    <div class='text-muted mb-1' style='font-size: 0.75em;'>
                                        Ordre #{salesOrderNo}
                                        {salesOrderStatus && <span class={`badge ms-1 ${salesOrderStatus === 'Draft' ? 'bg-warning' : 'bg-success'}`} style='font-size: 0.7em;'>
                                            {salesOrderStatus === 'Draft' ? 'Utkast' : salesOrderStatus}
                                        </span>}
                                        {invoiceSent && <span class='badge bg-success ms-1' style='font-size: 0.7em;'>Sendt</span>}
                                    </div>
                                )}
                                <div class='d-flex flex-column gap-1 align-items-start'>
                                    {isAdmin && <button
                                        disabled={powerofficeLoading[e.id]}
                                        type='button'
                                        class='btn btn-sm btn-outline-danger py-0'
                                        style='font-size: 0.8em; white-space: nowrap;'
                                        data-id={e.id}
                                        data-recreate='true'
                                        onClick={this.createPowerOfficeInvoice}
                                    >
                                        <i class='fa-duotone fa-refresh' /> Gjenopprett
                                        {powerofficeLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                    </button>}
                                </div>
                            </>
                        ) : powerofficeSent[e.id] ? (
                            <div class='text-success' style='font-size: 0.85em; white-space: nowrap;'>
                                <i class='fa-solid fa-check-circle' /> Overført
                            </div>
                        ) : (
                            <div class='d-flex flex-column gap-1'>
                                <button
                                    disabled={powerofficeLoading[e.id]}
                                    type='button'
                                    class='btn btn-sm btn-info py-0'
                                    style='font-size: 0.85em; white-space: nowrap;'
                                    data-id={e.id}
                                    onClick={this.createPowerOfficeInvoice}
                                >
                                    <i class='fa-duotone fa-cloud-arrow-up' /> Opprett faktura
                                    {powerofficeLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                </button>
                                <button
                                    disabled={powerofficeLoading[e.id]}
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary py-0'
                                    style='font-size: 0.8em; white-space: nowrap;'
                                    data-id={e.id}
                                    onClick={this.simulatePowerOfficeInvoice}
                                >
                                    <i class='fa-duotone fa-eye' /> Simuler
                                    {powerofficeLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                </button>
                            </div>
                        )}
                    </>
                ) : isCashSale ? (
                    <>
                        {hasResponse ? (
                            <>
                                <div class='text-success mb-1' style='font-size: 0.85em; white-space: nowrap;'>
                                    <i class='fa-solid fa-check-circle' /> Overført
                                </div>
                                {salesOrderNo && (
                                    <div class='text-muted mb-1' style='font-size: 0.75em;'>
                                        Ordre #{salesOrderNo}
                                        {salesOrderStatus && <span class={`badge ms-1 ${salesOrderStatus === 'Draft' ? 'bg-warning' : 'bg-success'}`} style='font-size: 0.7em;'>
                                            {salesOrderStatus === 'Draft' ? 'Utkast' : salesOrderStatus}
                                        </span>}
                                        {invoiceSent && <span class='badge bg-success ms-1' style='font-size: 0.7em;'>Sendt</span>}
                                    </div>
                                )}
                                <div class='d-flex flex-column gap-1 align-items-start'>
                                    {isAdmin && <button
                                        disabled={powerofficeLoading[e.id]}
                                        type='button'
                                        class='btn btn-sm btn-outline-danger py-0'
                                        style='font-size: 0.8em; white-space: nowrap;'
                                        data-id={e.id}
                                        data-recreate='true'
                                        onClick={this.createPowerOfficeInvoice}
                                    >
                                        <i class='fa-duotone fa-refresh' /> Gjenopprett
                                        {powerofficeLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                    </button>}
                                </div>
                            </>
                        ) : powerofficeSent[e.id] ? (
                            <div class='text-success' style='font-size: 0.85em; white-space: nowrap;'>
                                <i class='fa-solid fa-check-circle' /> Overført
                            </div>
                        ) : (
                            <div class='d-flex flex-column gap-1'>
                                <button
                                    disabled={powerofficeLoading[e.id]}
                                    type='button'
                                    class='btn btn-sm btn-outline-info py-0'
                                    style='font-size: 0.85em; white-space: nowrap;'
                                    data-id={e.id}
                                    onClick={this.createPowerOfficeInvoice}
                                >
                                    <i class='fa-duotone fa-cloud-arrow-up' /> Opprett salg
                                    {powerofficeLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                </button>
                                <button
                                    disabled={powerofficeLoading[e.id]}
                                    type='button'
                                    class='btn btn-sm btn-outline-secondary py-0'
                                    style='font-size: 0.8em; white-space: nowrap;'
                                    data-id={e.id}
                                    onClick={this.simulatePowerOfficeInvoice}
                                >
                                    <i class='fa-duotone fa-eye' /> Simuler
                                    {powerofficeLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                                </button>
                            </div>
                        )}
                    </>
                ) : null}

                {/* Debug button for simulation preview (when no API responses yet) */}
                {!hasApiResponses && e.powerOfficeInvoicePreview && (
                    <div class='mt-1'>
                        <button
                            class={`btn btn-sm ${powerofficeError[e.id] ? 'btn-secondary' : 'btn-outline-secondary'} py-0`}
                            style='font-size: 0.8em; white-space: nowrap;'
                            onClick={this.togglePowerOfficeError}
                            data-id={e.id}
                        >
                            <i class='fa-duotone fa-eye' /> Vis simulering
                            <span class={`badge ms-1 ${e.powerOfficeInvoicePreview.status === 'simulated' ? 'bg-info' : 'bg-warning'}`} style='font-size: 0.75em;'>
                                {e.powerOfficeInvoicePreview.status}
                            </span>
                        </button>
                    </div>
                )}

                {/* API Response Summary */}
                {hasApiResponses && (() => {
                    const analysis = this.analyzePowerOfficeResponses(e.powerOfficeApiResponses);
                    const isExpanded = powerofficeExpandedSummary[e.id];
                    const isErrorsExpanded = powerofficeExpandedErrors[e.id];
                    const displaySummary = isExpanded ? analysis.fullSummary : analysis.summary;
                    const displayErrors = isErrorsExpanded ? analysis.fullErrors : analysis.errors;

                    return (
                        <div class='mt-1 w-100' style='font-size: 0.8em; line-height: 1.3;'>
                            {analysis.summary.length > 0 && (
                                <div class={analysis.hasErrors ? 'text-danger' : 'text-success'}>
                                    {displaySummary.map((line, idx) => (
                                        <div key={idx}>• {line}</div>
                                    ))}
                                    {analysis.hiddenSummaryCount > 0 && (
                                        <span
                                            data-id={e.id}
                                            onClick={this.togglePowerOfficeExpandedSummary}
                                            class='text-muted'
                                            style='cursor: pointer; text-decoration: underline;'
                                        >
                                            {isExpanded ? '(skjul)' : `(+${analysis.hiddenSummaryCount} til)`}
                                        </span>
                                    )}
                                </div>
                            )}
                            {analysis.hasErrors && analysis.errors.length > 0 && (
                                <div class='text-danger mt-1'>
                                    {displayErrors.map((err, idx) => (
                                        <div key={idx}>• {err}</div>
                                    ))}
                                    {analysis.hiddenErrorsCount > 0 && (
                                        <span
                                            data-id={e.id}
                                            onClick={this.togglePowerOfficeExpandedErrors}
                                            class='text-muted'
                                            style='cursor: pointer; text-decoration: underline;'
                                        >
                                            {isErrorsExpanded ? '(skjul)' : `(+${analysis.hiddenErrorsCount} feil til)`}
                                        </span>
                                    )}
                                </div>
                            )}
                            <div class='d-flex align-items-center gap-2 mt-1'>
                                <button
                                    class={`btn btn-sm ${powerofficeError[e.id] ? 'btn-secondary' : 'btn-outline-secondary'} py-0`}
                                    style='font-size: 0.8em; white-space: nowrap;'
                                    onClick={this.togglePowerOfficeError}
                                    data-id={e.id}
                                >
                                    <i class='fa-duotone fa-bug' /> Debug
                                    {(() => {
                                        const debugCount = e.powerOfficeDebug?.length || 0;
                                        const apiCount = e.powerOfficeApiResponses?.length || 0;
                                        const totalCount = debugCount || apiCount;
                                        return totalCount > 0 ? (
                                            <span class='badge bg-secondary ms-1' style='font-size: 0.75em;'>{totalCount}</span>
                                        ) : null;
                                    })()}
                                </button>
                            </div>
                        </div>
                    );
                })()}
            </div>
        );
    }

    // Duett sent-status line (Tripletex parity, 2026-09-04). Duett dispatches the
    // invoice itself (e-post / EHF / print); lib/duettSaleStatus.duettSentState reads
    // the dispatch the backend parsed from the Duett order log (duettOrder.dispatch,
    // duettInvoiceSent — filled by the transfer read-back and the payment sync, i.e.
    // «Oppdater betalinger fra Duett» / the sync script). No «Send nå»: Duett v3 has no resend
    // endpoint, so a failed dispatch is redone inside Duett or printed from here.
    renderDuettSendStatus = (e) => {
        const sent = duettSentState(e);
        if (!sent) return null;
        const methodLabel = { email: 'e-post', ehf: 'EHF', print: 'print fra Duett' }[sent.method];
        if (sent.state === 'sent') {
            // Duett downgraded the requested option silently (EHF → e-post/print, or
            // e-post → print); lib/duettSaleStatus.isDuettSendFallback. EHF needs org
            // number + kontaktperson on the Duett customer card, ELMA registration and
            // an AutoTrans agreement on the client.
            const requestedLabel = { autoTrans: 'EHF', email: 'e-post' }[sent.requested];
            const fallbackTitle = sent.fallback
                ? `${requestedLabel} var valgt, men Duett sendte ${methodLabel}. EHF krever org.nr. og kontaktperson på kundekortet i Duett, at mottaker er registrert i ELMA, og en aktiv AutoTrans-avtale på klienten.`
                : null;
            return (
                <div class={`${sent.fallback ? 'text-warning' : 'text-success'} mb-1`} style='font-size: 0.8em; white-space: nowrap;' title={fallbackTitle || sent.description || undefined}>
                    <i class='fa-solid fa-paper-plane' /> Sendt til kunde{methodLabel ? ` (${methodLabel})` : ''}
                    {sent.fallback ? <span class='ms-1'>– {requestedLabel} ikke mulig</span> : null}
                    {sent.at ? <span class='text-body-secondary ms-1'>{util.isoDate(sent.at, false, false, true)}</span> : null}
                </div>
            );
        }
        if (sent.state === 'printed') {
            return (
                <div class='text-body-secondary mb-1' style='font-size: 0.8em; white-space: nowrap;'>
                    {this.renderNotSentLabel(e)}
                </div>
            );
        }
        const printButton = (
            <button
                type='button'
                class='btn btn-outline-primary font-weight-light btn-sm py-0 ms-2 rounded-pill'
                style='font-size: 0.85em; line-height: 1.3em;'
                data-id={e.id}
                onClick={this.onClickPrintSaleRow}
                title='Skriv ut faktura for postutsendelse'
            >
                <i class='fa-solid fa-print' /> Skriv ut
            </button>
        );
        if (sent.state === 'failed') {
            return (
                <div class='text-danger mb-1' style='font-size: 0.8em;'>
                    <div style='white-space: nowrap;'>
                        <i class='fa-solid fa-triangle-exclamation' /> Utsending feilet i Duett
                        {printButton}
                    </div>
                    {sent.description ? (
                        <div class='mt-1' style='white-space: normal; max-width: 280px; line-height: 1.3em;' title={sent.description}>
                            {sent.description}
                        </div>
                    ) : null}
                </div>
            );
        }
        return (
            <div class='text-body-secondary mb-1' style='font-size: 0.8em;'>
                <div
                    style='white-space: nowrap;'
                    title='Duett bekrefter utsendingen i ordreloggen noen sekunder etter overføring. «Oppdater betalinger fra Duett» henter den inn.'
                >
                    <i class='fa-solid fa-paper-plane' style='opacity: 0.4;' /> Utsending ikke bekreftet ennå
                    {printButton}
                </div>
            </div>
        );
    }

    renderDuettColumn = (e, { customer, duettLoading, duettSent, duettError, duettSimResult, duettExpandedSummary, duettExpandedErrors, displayDuettError, isAdmin }) => {
        const { userStore } = this.props.stores;
        if (isForeignSale(e, userStore)) {
            return null;
        }
        if (!customer.duettClientId) {
            return (
                <small class='text-muted'>
                    <i class='fa-solid fa-key text-warning' /> ClientId mangler
                </small>
            );
        }
        const rowState = duettRowState(e);
        const transferCheck = this.canTransferToAccounting(e);
        // The ban only concerns SENDING. A sale that is already in Duett (e.g. the
        // original invoice after it was credited → paymentMethod 'invoiceCredited')
        // keeps showing its Duett state instead of the ban reason.
        if (!transferCheck.canTransfer && rowState.kind !== 'transferred') {
            return (
                <small class='text-muted'>
                    <i class='fa-solid fa-ban text-secondary' /> {transferCheck.reason}
                </small>
            );
        }

        const error = duettError[e.id];
        const sim = duettSimResult[e.id];
        const hasApiResponses = e.duettApiResponses && e.duettApiResponses.length > 0;

        return (
            <div class='d-flex flex-column align-items-start' style='font-size: 0.9em;'>
                {this.renderVisitorWarnings(e.visitors)}

                {rowState.kind === 'creditNoteSent' && (
                    <div class='text-success mb-1' style='font-size: 0.85em; white-space: nowrap;'>
                        <i class='fa-solid fa-check-circle' /> Kreditnota sendt til Duett
                        <span class='text-muted ms-1' style='font-size: 0.85em;'>
                            Ordre #{rowState.orderNumber || rowState.orderId}
                        </span>
                    </div>
                )}

                {rowState.kind === 'creditNoteNoParent' && (
                    <small class='text-danger'>
                        <i class='fa-solid fa-warning' /> Mangler kobling til original faktura
                    </small>
                )}

                {rowState.kind === 'creditNoteUnsent' && (duettSent[e.id] ? (
                    <div class='text-success' style='font-size: 0.85em; white-space: nowrap;'>
                        <i class='fa-solid fa-check-circle' /> Kreditnota sendt til Duett
                    </div>
                ) : (
                    <button
                        disabled={duettLoading[e.id]}
                        type='button'
                        class='btn btn-sm btn-warning py-0'
                        style='font-size: 0.85em; white-space: nowrap;'
                        data-id={e.id}
                        onClick={this.createDuettCreditNoteFromRow}
                        title='Sender en kreditnota mot originalfakturaen i Duett'
                    >
                        <i class='fa-duotone fa-cloud-arrow-up' /> Send kreditnota til Duett
                        {duettLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                    </button>
                ))}

                {rowState.kind === 'transferred' && <>
                    <div class='text-success mb-1' style='font-size: 0.85em; white-space: nowrap;'>
                        <i class='fa-solid fa-check-circle' /> Overført
                    </div>
                    <div class='text-muted mb-1' style='font-size: 0.75em;'>
                        Ordre #{rowState.orderId}
                        {!!rowState.invoiceNumber && <span class='ms-1'>Faktura #{rowState.invoiceNumber}</span>}
                        {rowState.status ? (
                            <span class='badge bg-secondary ms-1' style='font-size: 0.7em;' title='Ordrestatus i Duett — sier ikke om fakturaen er betalt'>{duettOrderStatusLabel(rowState.status)}</span>
                        ) : null}
                        {rowState.credited && <span class='badge bg-danger ms-1' style='font-size: 0.7em;'>Kreditert</span>}
                    </div>
                    {this.renderDuettSendStatus(e)}
                </>}

                {rowState.kind === 'inVoucher' && (
                    <small class='text-muted'>
                        <i class='fa-solid fa-file-invoice' /> I bilag{rowState.voucherNumber ? ` #${rowState.voucherNumber}` : ''}
                    </small>
                )}

                {rowState.kind === 'viaVoucher' && (
                    <small class='text-muted' title={rowState.paid ? 'Betalt salg — overføres til Duett via dagsoppgjør-bilaget, ikke som faktura' : undefined}>
                        {rowState.paid ? 'betalt — via dagsoppgjør' : 'via dagsoppgjør'}
                    </small>
                )}

                {rowState.kind === 'unsent' && (duettSent[e.id] ? (
                    <div class='text-success' style='font-size: 0.85em; white-space: nowrap;'>
                        <i class='fa-solid fa-check-circle' /> Overført
                    </div>
                ) : (
                    <div class='d-flex flex-column gap-1'>
                        <button
                            disabled={duettLoading[e.id]}
                            type='button'
                            class='btn btn-sm btn-info py-0'
                            style='font-size: 0.85em; white-space: nowrap;'
                            data-id={e.id}
                            onClick={this.createDuettInvoiceFromRow}
                        >
                            <i class='fa-duotone fa-cloud-arrow-up' /> Send til Duett
                            {duettLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                        </button>
                        <button
                            disabled={duettLoading[e.id]}
                            type='button'
                            class='btn btn-sm btn-outline-secondary py-0'
                            style='font-size: 0.8em; white-space: nowrap;'
                            data-id={e.id}
                            onClick={this.simulateDuettInvoiceFromRow}
                        >
                            <i class='fa-duotone fa-eye' /> Simuler
                            {duettLoading[e.id] && <i class='fa-duotone fa-spinner fa-spin ms-1' />}
                        </button>
                    </div>
                ))}

                {!!error && (
                    <small class='text-danger mt-1' style='font-size: 0.75em;'>
                        <i class='fa-solid fa-triangle-exclamation' /> {error}
                    </small>
                )}
                {!!sim && (
                    <small class='text-muted mt-1' style='font-size: 0.75em;'>
                        <i class='fa-duotone fa-eye' /> Simulert OK: {sim.expectedGross} kr
                        {sim.duettCustomerId ? ` (Duett-kunde ${sim.duettCustomerId})` : ' (ny kunde opprettes)'}
                    </small>
                )}

                {/* API Response Summary */}
                {hasApiResponses && (() => {
                    const analysis = analyzeDuettResponses(e.duettApiResponses);
                    const isExpanded = duettExpandedSummary[e.id];
                    const isErrorsExpanded = duettExpandedErrors[e.id];
                    const displaySummary = isExpanded ? analysis.fullSummary : analysis.summary;
                    const displayErrors = isErrorsExpanded ? analysis.fullErrors : analysis.errors;

                    return (
                        <div class='mt-1 w-100' style='font-size: 0.8em; line-height: 1.3;'>
                            {analysis.summary.length > 0 && (
                                <div class={analysis.hasErrors ? 'text-danger' : 'text-success'}>
                                    {displaySummary.map((line, idx) => (
                                        <div key={idx}>• {line}</div>
                                    ))}
                                    {analysis.hiddenSummaryCount > 0 && (
                                        <span
                                            data-id={e.id}
                                            onClick={this.toggleDuettExpandedSummary}
                                            class='text-muted'
                                            style='cursor: pointer; text-decoration: underline;'
                                        >
                                            {isExpanded ? '(skjul)' : `(+${analysis.hiddenSummaryCount} til)`}
                                        </span>
                                    )}
                                </div>
                            )}
                            {analysis.hasErrors && analysis.errors.length > 0 && (
                                <div class='text-danger mt-1'>
                                    {displayErrors.map((err, idx) => (
                                        <div key={idx}>• {err}</div>
                                    ))}
                                    {analysis.hiddenErrorsCount > 0 && (
                                        <span
                                            data-id={e.id}
                                            onClick={this.toggleDuettExpandedErrors}
                                            class='text-muted'
                                            style='cursor: pointer; text-decoration: underline;'
                                        >
                                            {isErrorsExpanded ? '(skjul)' : `(+${analysis.hiddenErrorsCount} feil til)`}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Log button - show for admin when there's any Duett activity on the sale */}
                {isAdmin && (rowState.kind === 'transferred' || hasApiResponses || duettSent[e.id]) && (
                    <div class='d-flex align-items-center gap-2 mt-1' style='font-size: 0.8em;'>
                        <button
                            class={`btn btn-sm ${displayDuettError[e.id] ? 'btn-secondary' : 'btn-outline-secondary'} py-0`}
                            style='font-size: 0.8em; white-space: nowrap;'
                            onClick={this.toggleDuettLog}
                            data-id={e.id}
                        >
                            <i class='fa-duotone fa-list-timeline' /> Vis logg
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // Helper to analyze PowerOffice API responses
    analyzePowerOfficeResponses = (responses) => {
        if (!responses || responses.length === 0) {
            return {
                summary: [],
                fullSummary: [],
                hiddenSummaryCount: 0,
                errors: [],
                fullErrors: [],
                hiddenErrorsCount: 0,
                hasErrors: false,
            };
        }

        const summaryLines = [];
        const errorLines = [];
        let hasErrors = false;

        responses.forEach((response) => {
            const { type } = response;

            // Customer search/create
            if (type === 'customerSearch') {
                const count = response.customersFound || 0;
                if (count > 0) {
                    const customer = response.response?.[0];
                    summaryLines.push(`Kunde funnet: ${customer?.Name || customer?.name || 'Ukjent'}`);
                } else {
                    summaryLines.push('Kunde ikke funnet (opprettes)');
                }
            } else if (type === 'customerCreate') {
                const customer = response.response;
                if (customer?.Id || customer?.id) {
                    summaryLines.push(`Kunde opprettet: ${customer?.Name || customer?.name || 'Ukjent'}`);
                }
            } else if (type === 'customerCreateError') {
                hasErrors = true;
                errorLines.push(`Kunde opprettelse feilet: ${response.error}`);
            }

            // Product search/create
            if (type === 'productSearch') {
                summaryLines.push(`Produkter: ${response.existingProductsCount || 0} funnet`);
            } else if (type === 'productCreate') {
                const product = response.response;
                if (product?.statusCode === 201) {
                    summaryLines.push(`Produkt opprettet: ${response.productCode}`);
                }
            }

            // VAT codes fetch
            if (type === 'vatCodesFetch') {
                if (response.fetchError) {
                    errorLines.push(`MVA-koder: ${response.fetchError}`);
                    hasErrors = true;
                } else if (response.vatCodesFound === 0) {
                    errorLines.push('MVA-koder: Ingen koder returnert fra PowerOffice');
                    hasErrors = true;
                }
            }

            // Sales order
            if (type === 'salesOrder' || type === 'salesOrderWithPayment') {
                const salesOrder = response.salesOrderResponse;
                if (salesOrder?.statusCode === 201 || salesOrder?.statusCode === 202) {
                    const orderNo = salesOrder?.SalesOrderNo;
                    const status = salesOrder?.SalesOrderStatus;
                    if (orderNo) {
                        summaryLines.push(`Ordre #${orderNo} opprettet (${status === 'Draft' ? 'Utkast' : status || 'Sendt'})`);
                    } else {
                        summaryLines.push('Ordre sendt til PowerOffice');
                    }

                    // Check if invoice was sent
                    const sendInvoice = response.sendInvoiceResponse;
                    if (sendInvoice?.statusCode === 201 || sendInvoice?.statusCode === 202) {
                        summaryLines.push('Faktura sendt');
                    } else if (sendInvoice?.status === 400) {
                        // Not really an error - demo account limitation
                        summaryLines.push('Faktura må sendes manuelt fra PowerOffice');
                    }

                    // Check payment registration
                    const payment = response.paymentResponse;
                    if (payment?.statusCode === 201 || payment?.statusCode === 200) {
                        const extra = payment?.journalEntryVoucherUsed ? ' (via bankjournal)' : '';
                        summaryLines.push(`Betaling registrert: ${response.actualPaidAmount} kr${extra}`);
                    } else if (payment?.statusCode >= 400) {
                        hasErrors = true;
                        if (payment?.hint) {
                            errorLines.push(payment.hint);
                        } else {
                            const detail = payment?.detail || payment?.title || payment?.error || 'Ukjent feil';
                            errorLines.push(`Betaling feilet: ${detail}`);
                        }
                        if (response.vatCodesFetchError && response.vatCodesFetchError.indexOf('403') >= 0) {
                            errorLines.push('Legg til CommonServices-tillatelsen i PowerOffice Go for å fikse dette.');
                        }
                    } else if (payment?.error) {
                        hasErrors = true;
                        errorLines.push(`Betaling: ${payment.error}`);
                    } else if (response.isPaidSale && !payment) {
                        summaryLines.push(`Betalt ${response.actualPaidAmount} kr (registrer betaling manuelt i PowerOffice)`);
                    }
                } else if (salesOrder?.status === 400 || salesOrder?.statusCode === 400) {
                    hasErrors = true;
                    errorLines.push(`Ordre feilet: ${salesOrder?.detail || salesOrder?.title || 'Ukjent feil'}`);
                }
            }

            // Invoice create error
            if (type === 'invoiceCreateError') {
                hasErrors = true;
                errorLines.push(`Faktura opprettelse feilet: ${response.error}`);
            }

            // Generic error handling
            if (response.error && !type) {
                hasErrors = true;
                errorLines.push(response.error);
            }
        });

        return {
            summary: summaryLines.slice(0, 3),
            fullSummary: summaryLines,
            hiddenSummaryCount: Math.max(0, summaryLines.length - 3),
            errors: errorLines.slice(0, 3),
            fullErrors: errorLines,
            hiddenErrorsCount: Math.max(0, errorLines.length - 3),
            hasErrors,
        };
    }

    render() {
        const {
            search,
            currentPage = 0,
            emailLoading = {},
            emailSent = {},
            animaliaLoading,
            fikenLoading,
            fikenSent = {},
            tripletexLoading,
            tripletexSent,
            powerofficeLoading,
            powerofficeSent,
            userLoading,
            displayAnimaliaError = {},
            tripletexError = {},
            tripletexCreateError = {},
            tripletexExpandedSummary = {},
            tripletexExpandedErrors = {},
            powerofficeError = {},
            powerofficeExpandedSummary = {},
            powerofficeExpandedErrors = {},
            fikenExpandedSummary = {},
            fikenExpandedErrors = {},
            displayFikenError = {},
            saleUserUpdating = {},
            saleUserIsUpdated = {},

            displayNetsError = {},

            showAdminToolbar,
            showPayments = false,
            showOverpaymentOnly = false,
            showDhpNotReportedOnly = false,
            dhpBulkReporting = false,
            currentDhpBulk = 0,
            totalDhpBulk = 0,
            showDhpReportConfirm = false,
            dhpReportCount = 0,
            showCollectiveInvoiceConfirm = false,
            collectiveInvoiceConfirmSales = 0,
            collectiveInvoiceConfirmVisitors = 0,

            invoiceSalesForFikenTransfer = [],
            saleSalesForFikenTransfer = [],

            invoiceSalesForTripletexTransfer = [],
            saleSalesForTripletexTransfer = [],

            // saleForCollectiveInvoice is now computed in render from saleStore.sales

            sendAllInvoicesViaEmail,
            invoicesSent = 0,
            invoicesFailed = 0,

            sendAllInvoicesToFiken,
            allInvoicesSentToFiken,
            sendAllSalesToFiken,
            allSalesSentToFiken,

            sendAllInvoicesToTripletex,
            allInvoicesSentToTripletex,
            sendAllSalesToTripletex,
            allSalesSentToTripletex,
            updateFromTripletexInvoices,
            tripletexUpdateResponse,

            updateFromPowerOfficeInvoices,
            powerofficeUpdateResponse,
            sendAllInvoicesToPowerOffice,
            currentAccountingTransferLabel = '',
            currentAccountingTransferFailed = 0,

            updateFromFikenInvoices,
            fikenUpdateResponse,

            createAllCollectiveInvoices,

            totalCollectiveInvoices = 0,
            totalCollectiveInvoicesByVisitor = 0,
            currentCollectiveInvoice = 0,
            currentCollectiveInvoiceName = '',
            currentCollectiveInvoiceSaleCount = 0,

            paymentInProgress = {},
            insuranceDropdownOpen = {},

            checkingCollectiveInvoice,
            checkingInvoicesForFiken,
            checkingSalesForFiken,
            checkingInvoicesForTripletex,
            checkingSalesForTripletex,
            adminToolbarSection,
            settingUpCollectiveInvoice,
            collectiveInvoiceStep1Done,
            collectiveInvoiceStep2Done,
            collectiveInvoiceStep3Done,
            collectiveInvoiceSummary,
            // Email invoice wizard
            settingUpEmailInvoice,
            emailInvoiceStep1Done,
            emailInvoiceStep2Done,
            emailInvoiceStep3Done,
            emailInvoiceSummary,
            // Reminders (Purringer) wizard
            reminderStep, reminderChannels, reminderSmsText, reminderRows, reminderSelected,
            reminderLoading, reminderSearchText, reminderListLoading, reminderSending, reminderProgress, reminderResult,
            // Accounting transfer wizard
            accountingMode,
            accountingProvider,
            accountingTransferStep1Done,
            accountingTransferStep2Done,
            accountingTransferStep3Done,
            accountingTransferSummary,
            accountingUpdateStep1Done,
            accountingUpdateStep2Done,
            accountingUpdateStep3Done,
            updateFromTripletexPreviewLoading,
            settingUpAccountingTransfer,
            currentAccountingTransfer,
            totalAccountingTransfers,
            // Duett column + bulk send
            duettLoading = {},
            duettSent = {},
            duettError = {},
            duettSimResult = {},
            displayDuettError = {},
            duettExpandedSummary = {},
            duettExpandedErrors = {},
            sendAllInvoicesToDuett,
            // Debug data loaded on-demand
            saleDebugData = {},
            loadingDebugData = {},
            // Medical history wizard
            medicalHistoryMode,
            medicalHistoryStep,
            medicalHistoryPreview,
            medicalHistorySendResult,
            medicalHistorySpeciesInPeriod,
            medicalHistorySelectedSpecies,
            medicalHistoryLoading,
            medicalHistorySending,
            medicalHistorySingleVisitor,
            medicalHistorySingleAnimals,
            medicalHistorySingleAnimal,
            medicalHistorySingleAllTime,
            medicalHistorySingleStartDate,
            medicalHistorySingleEndDate,
            medicalHistorySingleSent,
        } = this.state;
        const {
            saleTitle,
            searchTitle,
            formatting = true,
            showDelete = true,
            activeStatus,
        } = this.props;
        const { userStore, saleStore, appState, fikenStore, journalStore } = this.props.stores;
        const { isDevelopment } = appState;
        // speciesId -> name map (e.g. 9 -> "Hest", 2 -> "Katt") used as a fallback when
        // an embedded animal has no species name string (TICKET-01184).
        const speciesNameById = {};
        (journalStore.animalSpeciesList || []).forEach(s => {
            if (s && s.id > 0 && s.name) speciesNameById[s.id] = s.name;
        });
        const { user, isAdmin, customer, isCustomerAdmin } = userStore;
        const { language = 'no' } = user;

        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const {
            sales,
            allPayments = [],
            searchResults,
            totalSales,
            saleUsers = [],
            sort,
            cutoffDate,
            cutoffDateEnd,
        } = saleStore;
        let finalSales = searchResults && searchResults.length > 0 ? searchResults : sales;
        // const finalSales = sales;

        if (showOverpaymentOnly) {
            finalSales = finalSales.filter(sale => {
                const salePayments = allPayments.filter(p => p.sale === sale.id);
                if (!salePayments || salePayments.length === 0) return false;
                const productTotal = saleStore.sumTotal(sale.products, false);
                const restAmount = saleStore.sumRestPayments(salePayments, productTotal, false, false);
                return restAmount < -0.5;
            });
        }

        if (showDhpNotReportedOnly) {
            finalSales = finalSales.filter(this.isDhpReportable);
        }

        const showCompanyColumn = new Set(finalSales.map(s => s?.customer)).size > 1;

        const dhpReportableCount = finalSales.filter(this.isDhpReportable).length;

        // Compute saleForCollectiveInvoice directly from current sales to ensure it's always in sync
        // This overrides the state value which can become stale due to timing issues
        const saleForCollectiveInvoice = sales.filter(s => s.status === 1003);

        // Compute invoice sales ready for email directly from current sales so the
        // e-post wizard count is always in sync with the active filters (same
        // pattern as saleForCollectiveInvoice above).
        const invoiceSalesReadyForEmail = sales.filter(isInvoiceSaleReadyForEmail);
        const invoiceSalesEmailAlreadySent = sales.filter(s => s.paymentMethod === 'invoice' && !isInvoiceSaleReadyForEmail(s));

        // Compute accounting transfer counts from current sales (respects user's manual filter changes)
        // Helper to check if already transferred to Tripletex
        const isTripletexTransferred = (s) => {
            // Check saved invoice id first (survives partial/empty Tripletex responses)
            if (s.tripletexInvoice?.id) return true;
            // Check tripletexResponse
            if (s.tripletexResponse && s.tripletexResponse?.statusCode === 201) return true;
            // Check tripletexApiResponses for invoice creation
            if (s.tripletexApiResponses && s.tripletexApiResponses.length > 0) {
                const analysis = analyzeTripletexResponses(s.tripletexApiResponses);
                if (analysis.invoiceCreated) return true;
            }
            return false;
        };
        // Helper to check if already transferred to Fiken
        const isFikenTransferred = (s) => {
            // Check fikenLocation
            if (s.fikenLocation && s.fikenLocation.length > 0) return true;
            // Check fikenApiResponses for invoice/sale creation
            if (s.fikenApiResponses && s.fikenApiResponses.length > 0) {
                const analysis = analyzeFikenResponses(s.fikenApiResponses);
                if (analysis.invoiceCreated || analysis.saleCreated) return true;
            }
            return false;
        };

        // Extract feature flags early - needed for accounting transfer count calculation
        const { featureFlags = {} } = customer;
        const { animalia = false, fiken = false, tripletex, powerOffice, duett, saleLineColors, dyreid, dyreIdReport } = featureFlags;
        // Coerce to strict booleans: a disabled toggle stores numeric 0, and
        // `{0 && <th />}` renders a literal "0" in the header and every row.
        const showFiken = !!(fiken && isCustomerAdmin);
        const showTripletex = !!(tripletex && isCustomerAdmin);
        const showPowerOffice = !!(powerOffice && isCustomerAdmin);
        const showDuett = !!(duett && isCustomerAdmin);
        const showAnimalia = !!animalia;
        const showDyreId = !!(dyreIdReport && customer.dyreIdClinicId);

        // Combined filter for Tripletex: invoices OR paid sales (no double-counting)
        const accountingSalesForTripletex = sales.filter(s => {
            const isInvoice = s.paymentMethod === 'invoice';
            const isPaid = s.status === 1;
            const transferCheck = this.canTransferToAccounting(s);
            // Include if invoice OR paid, but not already transferred
            return (isInvoice || isPaid) && !isTripletexTransferred(s) && transferCheck.canTransfer;
        });
        // Combined filter for Fiken: invoices OR paid sales (no double-counting)
        const accountingSalesForFiken = sales.filter(s => {
            const isInvoice = s.paymentMethod === 'invoice';
            const isPaid = s.status === 1;
            const transferCheck = this.canTransferToAccounting(s);
            // Include if invoice OR paid, but not already transferred
            return (isInvoice || isPaid) && !isFikenTransferred(s) && transferCheck.canTransfer;
        });
        // Combined filter for PowerOffice: invoices OR paid sales (no double-counting)
        const isPowerOfficeTransferred = (s) => {
            if (s.powerOfficeTransferred) return true;
            const r = s.powerOfficeResponse;
            if (r && (
                r.statusCode === 201
                || r.statusCode === 202
                || r.salesOrder?.statusCode === 201
                || r.salesOrder?.statusCode === 202
                || r.cashVoucher?.statusCode === 201
                || r.success
            )) return true;
            return false;
        };
        const accountingSalesForPowerOffice = sales.filter(s => {
            const isInvoice = s.paymentMethod === 'invoice';
            const isPaid = s.status === 1;
            const transferCheck = this.canTransferToAccounting(s);
            return (isInvoice || isPaid) && !isPowerOfficeTransferred(s) && transferCheck.canTransfer;
        });
        const isPowerOfficeSection = adminToolbarSection === 'poweroffice';
        const isDuettSection = adminToolbarSection === 'duett';
        const isTripletexProvider = accountingProvider === 'tripletex';
        // Duett bulk send is invoice-only (no status===1 cash branch like the other
        // providers) — cash sales go to Duett via the dagsoppgjør voucher.
        const accountingSalesForDuett = sales.filter(s => {
            const transferCheck = this.canTransferToAccounting(s);
            return qualifiesForDuettBulkSend(s) && transferCheck.canTransfer;
        });
        const accountingTransferCount = isDuettSection
            ? accountingSalesForDuett.length
            : isPowerOfficeSection
                ? accountingSalesForPowerOffice.length
                : isTripletexProvider
                    ? accountingSalesForTripletex.length
                    : accountingSalesForFiken.length;

        const isDebitorList = activeStatus === 99;

        // Footer totals — sum across every visible row, including non-financial statuses.
        // EXCEPT settled insurance-krav sales (status 1008): an insurance settlement keeps
        // the original krav as an internal record on the visit date AND creates two child
        // sales (insurance-paid + egenandel) that re-state the same money on the settlement
        // date. Counting the krav too double-counts the visit on both dates. The child sales
        // carry the real (accounting-exported) amounts, so we count those instead. This
        // matches the sale-report convention where status {$gt:0,$lt:1000} excludes 1008.
        // TICKET-01065.
        const countsTowardTurnover = s => s.status !== 1008;
        const footerTotalSum = (finalSales || []).reduce((sum, s) => (
            countsTowardTurnover(s) ? sum + saleStore.sumTotal(s.products, false) : sum
        ), 0);
        // Rest: credited invoices / credit notes and internal consumption (forbruk) owe
        // nothing, so they never count as outstanding — the row logic (isCreditNote) already
        // shows rest 0 for them; the footer must agree. TICKET-01612.
        const owesNothing = s => ['creditNote', 'invoiceCredited', 'saleCredited', 'forbruk'].includes(s.paymentMethod);
        const footerRestSum = (finalSales || []).reduce((sum, s) => {
            if (!countsTowardTurnover(s) || owesNothing(s)) {
                return sum;
            }
            const salePaymentsForSum = allPayments.filter(p => p.sale === s.id);
            const productTotalForSum = saleStore.sumTotal(s.products, false);
            return sum + saleStore.sumRestPayments(salePaymentsForSum, productTotalForSum, false, false);
        }, 0);

        // Footer colspan — must align with header column count
        const footerLeftColspan = 8 + (showCompanyColumn ? 1 : 0);
        let footerRightColspan = 1; // KID
        if (!isDebitorList && showPayments) footerRightColspan += 1;
        if (showFiken) footerRightColspan += 1;
        if (showTripletex) footerRightColspan += 1;
        if (showPowerOffice) footerRightColspan += 1;
        if (showDuett) footerRightColspan += 1;
        if (showAnimalia) footerRightColspan += 1;
        if (showDyreId) footerRightColspan += 1;
        if (showDelete) footerRightColspan += 1;

        let sumRestPayment = 0;

        const threeAndHalfHours = 3.5 * 60 * 60 * 1000;
        const animaliaRefreshTokenExpire = user?.animalia?.expires_at + threeAndHalfHours;
        const nowUtime = new Date().getTime();
        let animaliaRefreshTokenExpired = true;
        if (user?.animalia?.expires_at) {
            animaliaRefreshTokenExpired = nowUtime > animaliaRefreshTokenExpire || user?.animalia?.error;
        }
        const now = new Date();

        return (<>
            <div class={`d-flex flex-column mx-3 mt-2 px-3 py-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>

                <div class='d-flex flex-row justify-content-between bg-light rounded-lg py-1'>
                    <div class='d-flex flex-row'>
                        {showAnimalia && animaliaRefreshTokenExpired  ? <>
                            <button
                                type='button'
                                class='btn btn-sm btn-primary rounded-pill ms-2'
                                onClick={this.onClickGotoAnimaliaAuth}
                            >
                                <nobr><i class='fa-duotone fa-right-to-bracket' /> <Text id='dyrejournal.animalia-auth-login'>Login to DHP and grant access</Text></nobr>
                            </button>
                            <button
                                type='button'
                                class='btn btn-sm btn-link ms-2 p-0'
                                onClick={this.onClickRefreshUserInfo}
                            >
                                <i class='fa-duotone fa-sync' />
                                {userLoading ? <i class='fa-duotone fa-spinner fa-spin ms-2' /> : <></>}
                            </button>
                        </> : <>
                            <button
                                type='button'
                                class='btn btn-link ms-2'
                                onClick={this.onClickGotoAnimaliaAuth}
                            >
                                <nobr><i class='fa-duotone fa-right-to-bracket' /> <Text id='dyrejournal.animalia-auth-relogin'>Re-login to DHP and grant access</Text></nobr>
                            </button>
                            <button
                                type='button'
                                class='btn btn-link text-danger ms-2'
                                onClick={this.onClickLogoutAnimalia}
                            >
                                <nobr><i class='fa-duotone fa-right-from-bracket' /> <Text id='dyrejournal.animalia-logout'>Logout from DHP</Text></nobr>
                            </button>
                            <button
                                type='button'
                                class='btn btn-link ms-2 p-0'
                                onClick={this.onClickRefreshUserInfo}
                            >
                                <i class='fa-duotone fa-sync' /> <Text id='dyrejournal.refresh-user-info'>Refresh user info</Text>
                                {userLoading ? <i class='fa-duotone fa-spinner fa-spin ms-2' /> : <></>}
                            </button>
                        </>}
                    </div>

                    <div class='mx-2 d-flex flex-row justify-content-center align-items-center'>
                        <button
                            class={`btn btn-sm btn-${showPayments ? 'secondary' : 'outline-secondary'} rounded-pill`}
                            onClick={this.togglePayments}
                        >
                            <i class={`fa-duotone fa-credit-card`} /> {showPayments ? <Text id='dyrejournal.hide-payments'>Hide payments</Text> : <Text id='dyrejournal.show-payments'>Show payments</Text>}
                        </button>
                        <button
                            class={`btn btn-sm btn-${showOverpaymentOnly ? 'warning' : 'outline-warning'} rounded-pill ms-2`}
                            onClick={this.toggleOverpaymentFilter}
                            title='Show only sales where payment exceeds product total'
                        >
                            <i class={`fa-solid fa-triangle-exclamation`} /> <Text id='dyrejournal.overpayment-filter'>Overpayment</Text>
                        </button>
                        {showAnimalia && <>
                            <button
                                class={`btn btn-sm btn-${showDhpNotReportedOnly ? 'danger' : 'outline-danger'} rounded-pill ms-2`}
                                onClick={this.toggleDhpNotReportedFilter}
                                title='Show only visits not yet reported to DHP'
                            >
                                <i class='fa-duotone fa-cloud-arrow-up' /> <Text id='dyrejournal.dhp-not-reported-filter'>DHP not reported</Text>
                                {dhpReportableCount > 0 ? <span class='badge bg-light text-dark ms-1'>{dhpReportableCount}</span> : <></>}
                            </button>
                            {!animaliaRefreshTokenExpired && dhpReportableCount > 0 && (
                                <button
                                    class='btn btn-sm btn-success rounded-pill ms-2'
                                    onClick={this.onClickReportAllDhp}
                                    disabled={dhpBulkReporting}
                                    title='Report all visits not yet reported to DHP'
                                >
                                    {dhpBulkReporting
                                        ? <><i class='fa-duotone fa-spinner fa-spin' /> <Text id='dyrejournal.dhp-report-all-progress' fields={{ current: currentDhpBulk, total: totalDhpBulk }}>Reporting {currentDhpBulk}/{totalDhpBulk}...</Text></>
                                        : <><i class='fa-duotone fa-cloud-arrow-up' /> <Text id='dyrejournal.dhp-report-all' fields={{ count: dhpReportableCount }}>Report all to DHP ({dhpReportableCount})</Text></>}
                                </button>
                            )}
                        </>}
                        <button
                            class={`btn btn-sm btn-${showAdminToolbar ? 'primary' : 'outline-primary'} rounded-pill ms-2`}
                            onClick={this.toggleAdminToolbar}
                        >
                            <i class={`fa-duotone fa-cog`} /> <Text id='dyrejournal.admin-toolbar'>Admin toolbar</Text>
                        </button>
                    </div>

                </div>
            </div>

            {(isCustomerAdmin && showAdminToolbar) && <>
                {/* Main Navigation Menu - shown when no section selected */}
                {!adminToolbarSection && (
                    <div class='d-flex flex-row flex-wrap justify-content-center mt-2 mb-2 mx-3 bg-warning rounded-lg p-3 gap-3'>
                        <button
                            class='btn btn-lg btn-outline-dark rounded-lg p-4 d-flex flex-column align-items-center'
                            onClick={() => this.setAdminToolbarSection('collective')}
                            style='min-width: 200px;'
                        >
                            <i class='fa-duotone fa-file-invoice fa-2x mb-2' />
                            <strong>Samlefaktura</strong>
                            <small class='text-body-secondary'>Opprett samlefakturaer</small>
                        </button>

                        {showFiken && (
                            <button
                                class='btn btn-lg btn-outline-dark rounded-lg p-4 d-flex flex-column align-items-center'
                                onClick={() => this.setState({ adminToolbarSection: 'accounting', accountingProvider: 'fiken' })}
                                style='min-width: 200px;'
                            >
                                <i class='fa-duotone fa-building-columns fa-2x mb-2' />
                                <strong>Fiken</strong>
                                <small class='text-body-secondary'>Send og oppdater</small>
                            </button>
                        )}

                        {showTripletex && (
                            <button
                                class='btn btn-lg btn-outline-dark rounded-lg p-4 d-flex flex-column align-items-center'
                                onClick={() => this.setState({ adminToolbarSection: 'accounting', accountingProvider: 'tripletex' })}
                                style='min-width: 200px;'
                            >
                                <i class='fa-duotone fa-building-columns fa-2x mb-2' />
                                <strong>Tripletex</strong>
                                <small class='text-body-secondary'>Send og oppdater</small>
                            </button>
                        )}

                        {showPowerOffice && (
                            <button
                                class='btn btn-lg btn-outline-dark rounded-lg p-4 d-flex flex-column align-items-center'
                                onClick={() => this.setAdminToolbarSection('poweroffice')}
                                style='min-width: 200px;'
                            >
                                <i class='fa-duotone fa-building-columns fa-2x mb-2' />
                                <strong>PowerOffice</strong>
                                <small class='text-body-secondary'>Send og oppdater</small>
                            </button>
                        )}

                        {showDuett && (
                            <button
                                class='btn btn-lg btn-outline-dark rounded-lg p-4 d-flex flex-column align-items-center'
                                onClick={() => this.setAdminToolbarSection('duett')}
                                style='min-width: 200px;'
                            >
                                <i class='fa-duotone fa-building-columns fa-2x mb-2' />
                                <strong>Duett</strong>
                                <small class='text-body-secondary'>Send og oppdater</small>
                            </button>
                        )}

                        <button
                            class='btn btn-lg btn-outline-dark rounded-lg p-4 d-flex flex-column align-items-center'
                            onClick={() => {
                                this.setAdminToolbarSection('email');
                                this.presetEmailInvoiceFilters();
                            }}
                            style='min-width: 200px;'
                        >
                            <i class='fa-duotone fa-envelope fa-2x mb-2' />
                            <strong>E-post</strong>
                            <small class='text-body-secondary'>Send fakturaer via e-post</small>
                        </button>

                        <button
                            class='btn btn-lg btn-outline-dark rounded-lg p-4 d-flex flex-column align-items-center'
                            onClick={() => {
                                this.setAdminToolbarSection('reminders');
                                this.presetReminderFilters();
                            }}
                            style='min-width: 200px;'
                        >
                            <i class='fa-duotone fa-bell-exclamation fa-2x mb-2' />
                            <strong>Purringer</strong>
                            <small class='text-body-secondary'>Send purringer for forfalte fakturaer</small>
                        </button>

                        <button
                            class='btn btn-lg btn-outline-dark rounded-lg p-4 d-flex flex-column align-items-center'
                            onClick={() => {
                                this.setAdminToolbarSection('medicalHistory');
                                this.setupMedicalHistoryFilters();
                            }}
                            style='min-width: 200px;'
                        >
                            <i class='fa-duotone fa-file-medical fa-2x mb-2' />
                            <strong>Medisinsk historikk</strong>
                            <small class='text-body-secondary'>Send medisinsk historikk via e-post</small>
                        </button>
                    </div>
                )}

                {/* Section: Collective Invoice */}
                {adminToolbarSection === 'collective' && (
                    <div class='mt-2 mb-2 mx-3 bg-warning rounded-lg p-2'>
                        <div class='d-flex justify-content-between align-items-center mb-2 px-2'>
                            <button class='btn btn-sm btn-outline-dark rounded-pill' onClick={this.backToAdminMenu}>
                                <i class='fa-solid fa-arrow-left' /> Tilbake
                            </button>
                            <strong>Samlefaktura</strong>
                            <div style='width: 80px;'></div>
                        </div>
                        <div class='d-flex flex-column p-3 border border-dark rounded'>
                            {/* Step 1: Set filters */}
                            <div class={`p-2 mb-2 rounded ${collectiveInvoiceStep1Done ? 'bg-success text-white' : 'bg-light'}`}>
                                <div class='d-flex flex-row align-items-center'>
                                    <div class='me-3'>
                                        {collectiveInvoiceStep1Done
                                            ? <i class='fa-solid fa-check-circle fa-lg' />
                                            : <span class='badge bg-dark rounded-circle' style='width: 24px; height: 24px; line-height: 24px;'>1</span>
                                        }
                                    </div>
                                    <div class='flex-grow-1'>
                                        <strong>Sett opp filtre</strong>
                                        <div><small>Velg betalingsmåte, status og periode for samlefaktura</small></div>
                                    </div>
                                    <button
                                        class={`btn btn-sm ${collectiveInvoiceStep1Done ? 'btn-outline-light' : 'btn-success'} rounded-pill`}
                                        disabled={settingUpCollectiveInvoice}
                                        onClick={this.setupCollectiveInvoiceFilters}
                                    >
                                        {settingUpCollectiveInvoice ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-magic' />}
                                        {' '}{collectiveInvoiceStep1Done ? 'Tilbakestill' : 'Sett opp automatisk'}
                                    </button>
                                </div>
                                {this.renderWizardFilters({ showStatus: true, showPaymentMethod: true, showQuickPresets: false })}
                            </div>

                            {/* Step 2: Review list */}
                            <div class={`p-2 mb-2 rounded ${collectiveInvoiceStep2Done ? 'bg-success text-white' : collectiveInvoiceStep1Done ? 'bg-light' : 'bg-secondary bg-opacity-25'}`}>
                                <div class='d-flex flex-row align-items-center'>
                                    <div class='me-3'>
                                        {collectiveInvoiceStep2Done
                                            ? <i class='fa-solid fa-check-circle fa-lg' />
                                            : <span class={`badge ${collectiveInvoiceStep1Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>2</span>
                                        }
                                    </div>
                                    <div class='flex-grow-1'>
                                        <strong>Se gjennom listen</strong>
                                        <div><small>Sjekk at alle salg i listen nedenfor er korrekte</small></div>
                                        {collectiveInvoiceStep1Done && saleForCollectiveInvoice.length > 0 && (
                                            <div><small class='text-danger fw-bold'>{saleForCollectiveInvoice.length} salg klare for samlefaktura</small></div>
                                        )}
                                    </div>
                                    {collectiveInvoiceStep1Done && !collectiveInvoiceStep2Done && (
                                        <button
                                            class='btn btn-sm btn-success rounded-pill'
                                            onClick={this.markCollectiveInvoiceStep2Done}
                                        >
                                            <i class='fa-solid fa-check' /> Ferdig gjennomgått
                                        </button>
                                    )}
                                </div>
                                {collectiveInvoiceStep1Done && this.renderWizardActiveFiltersSummary()}
                            </div>

                            {/* Step 3: Create collective invoices */}
                            <div class={`d-flex flex-row align-items-center p-2 mb-2 rounded ${collectiveInvoiceStep3Done ? 'bg-success text-white' : collectiveInvoiceStep2Done ? 'bg-light' : 'bg-secondary bg-opacity-25'}`}>
                                <div class='me-3'>
                                    {collectiveInvoiceStep3Done
                                        ? <i class='fa-solid fa-check-circle fa-lg' />
                                        : <span class={`badge ${collectiveInvoiceStep2Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>3</span>
                                    }
                                </div>
                                <div class='flex-grow-1'>
                                    <strong>Opprett samlefakturaer</strong>
                                    <div><small>Lag samlefakturaer for alle kunder</small></div>
                                    {collectiveInvoiceStep3Done && collectiveInvoiceSummary && (
                                        <div>
                                            <small class='fw-bold'>{collectiveInvoiceSummary.invoicesCreated} samlefaktura(er) opprettet fra {collectiveInvoiceSummary.salesProcessed} salg{collectiveInvoiceSummary.viaConvene ? ' og sendt til Convene' : ''}</small>
                                            {collectiveInvoiceSummary.skippedZeroSum > 0 && (
                                                <div><small>{collectiveInvoiceSummary.skippedZeroSum} kunde(r) med 0 kr i sum ble hoppet over</small></div>
                                            )}
                                            {collectiveInvoiceSummary.conveneFailed > 0 && (
                                                <div><small>{collectiveInvoiceSummary.conveneFailed} samlefaktura(er) ble ikke godtatt av Convene</small></div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {collectiveInvoiceStep2Done && !collectiveInvoiceStep3Done && saleForCollectiveInvoice.length > 0 && (
                                    <button
                                        disabled={createAllCollectiveInvoices}
                                        class='btn btn-sm btn-danger rounded-pill'
                                        onClick={this.onClickCreateCollectiveInvoice}
                                    >
                                        {createAllCollectiveInvoices ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-paper-plane' />}
                                        {' '}Opprett alle samlefakturaer
                                    </button>
                                )}
                            </div>

                            {createAllCollectiveInvoices && (
                                <CollectiveInvoiceProgress
                                    current={currentCollectiveInvoice}
                                    total={totalCollectiveInvoicesByVisitor}
                                    totalSales={totalCollectiveInvoices}
                                    customerName={currentCollectiveInvoiceName}
                                    saleCount={currentCollectiveInvoiceSaleCount}
                                />
                            )}

                            {collectiveInvoiceStep3Done && (
                                <div class='mt-2 p-3 bg-success text-white rounded-lg'>
                                    <i class='fa-solid fa-check-circle' /> Samlefakturaer er opprettet!
                                    {collectiveInvoiceSummary && (
                                        <div class='mt-2'>
                                            <strong>{collectiveInvoiceSummary.invoicesCreated}</strong> samlefaktura(er) ble opprettet fra <strong>{collectiveInvoiceSummary.salesProcessed}</strong> salg.
                                            {collectiveInvoiceSummary.skippedZeroSum > 0 && (
                                                <div class='mt-1'>
                                                    <small>{collectiveInvoiceSummary.skippedZeroSum} kunde(r) ble hoppet over fordi besøkene summerer seg til 0 kr. Det ble ikke opprettet faktura for disse.</small>
                                                </div>
                                            )}
                                            {collectiveInvoiceSummary.conveneFailed > 0 && (
                                                <div class='mt-1'>
                                                    <small>{collectiveInvoiceSummary.conveneFailed} samlefaktura(er) ble opprettet, men ikke godtatt av Convene. Åpne dem fra listen og bruk «Betal via Convene» for å prøve på nytt.</small>
                                                </div>
                                            )}
                                            <div class='mt-2'>
                                                {collectiveInvoiceSummary.viaConvene
                                                    ? <small>De nye samlefakturaene har status "Sendt til Convene". Convene fakturerer kundene og melder tilbake når de er betalt.</small>
                                                    : <small>De nye samlefakturaene har status "Faktura" og kan nå sendes til kunder via e-post eller overføres til regnskapssystem.</small>
                                                }
                                            </div>
                                        </div>
                                    )}
                                    <div class='mt-3 text-center'>
                                        <button
                                            class='btn btn-lg btn-light text-success fw-bold rounded-pill px-5'
                                            onClick={this.backToAdminMenu}
                                        >
                                            <i class='fa-solid fa-check' /> Ferdig
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Section: Email */}
                {adminToolbarSection === 'email' && (
                    <div class='mt-2 mb-2 mx-3 bg-warning rounded-lg p-2'>
                        <div class='d-flex justify-content-between align-items-center mb-2 px-2'>
                            <button class='btn btn-sm btn-outline-dark rounded-pill' onClick={this.backToAdminMenu}>
                                <i class='fa-solid fa-arrow-left' /> Tilbake
                            </button>
                            <strong>E-post</strong>
                            <div style='width: 80px;'></div>
                        </div>
                        <div class='d-flex flex-column p-3 border border-dark rounded'>
                            {/* Step 1: Set filters */}
                            <div class={`p-2 mb-2 rounded ${emailInvoiceStep1Done ? 'bg-success text-white' : 'bg-light'}`}>
                                <div class='d-flex flex-row align-items-center'>
                                    <div class='me-3'>
                                        {emailInvoiceStep1Done
                                            ? <i class='fa-solid fa-check-circle fa-lg' />
                                            : <span class='badge bg-dark rounded-circle' style='width: 24px; height: 24px; line-height: 24px;'>1</span>
                                        }
                                    </div>
                                    <div class='flex-grow-1'>
                                        <strong>Sett opp filtre</strong>
                                        <div><small>Velg betalingsmåte, status og periode for e-postutsending</small></div>
                                    </div>
                                    <button
                                        class={`btn btn-sm ${emailInvoiceStep1Done ? 'btn-outline-light' : 'btn-success'} rounded-pill`}
                                        disabled={settingUpEmailInvoice}
                                        onClick={this.setupEmailInvoiceFilters}
                                    >
                                        {settingUpEmailInvoice ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-magic' />}
                                        {' '}{emailInvoiceStep1Done ? 'Tilbakestill' : 'Sett opp automatisk'}
                                    </button>
                                </div>
                                {this.renderWizardFilters({ showStatus: true, showPaymentMethod: true, showQuickPresets: false })}
                            </div>

                            {/* Step 2: Review list */}
                            <div class={`p-2 mb-2 rounded ${emailInvoiceStep2Done ? 'bg-success text-white' : emailInvoiceStep1Done ? 'bg-light' : 'bg-secondary bg-opacity-25'}`}>
                                <div class='d-flex flex-row align-items-center'>
                                    <div class='me-3'>
                                        {emailInvoiceStep2Done
                                            ? <i class='fa-solid fa-check-circle fa-lg' />
                                            : <span class={`badge ${emailInvoiceStep1Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>2</span>
                                        }
                                    </div>
                                    <div class='flex-grow-1'>
                                        <strong>Se gjennom listen</strong>
                                        <div><small>Sjekk at alle salg i listen nedenfor er korrekte</small></div>
                                        {emailInvoiceStep1Done && invoiceSalesReadyForEmail.length > 0 && (
                                            <div><small class='text-danger fw-bold'>{invoiceSalesReadyForEmail.length} faktura(er) klare til å sendes via e-post</small></div>
                                        )}
                                        {emailInvoiceStep1Done && invoiceSalesReadyForEmail.length === 0 && (
                                            <div><small class='fw-bold'>Ingen fakturaer klare til å sendes via e-post med disse filtrene</small></div>
                                        )}
                                        {emailInvoiceStep1Done && invoiceSalesEmailAlreadySent.length > 0 && (
                                            <div><small><i class='fa-solid fa-paper-plane text-success' /> {invoiceSalesEmailAlreadySent.length} faktura(er) er allerede sendt via e-post og hoppes over</small></div>
                                        )}
                                    </div>
                                    {emailInvoiceStep1Done && !emailInvoiceStep2Done && (
                                        <button
                                            class='btn btn-sm btn-success rounded-pill'
                                            onClick={this.markEmailInvoiceStep2Done}
                                        >
                                            <i class='fa-solid fa-check' /> Ferdig gjennomgått
                                        </button>
                                    )}
                                </div>
                                {emailInvoiceStep1Done && this.renderWizardActiveFiltersSummary()}
                            </div>

                            {/* Step 3: Send invoices via email */}
                            <div class={`d-flex flex-row align-items-center p-2 mb-2 rounded ${emailInvoiceStep3Done ? 'bg-success text-white' : emailInvoiceStep2Done ? 'bg-light' : 'bg-secondary bg-opacity-25'}`}>
                                <div class='me-3'>
                                    {emailInvoiceStep3Done
                                        ? <i class='fa-solid fa-check-circle fa-lg' />
                                        : <span class={`badge ${emailInvoiceStep2Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>3</span>
                                    }
                                </div>
                                <div class='flex-grow-1'>
                                    <strong>Send fakturaer via e-post</strong>
                                    <div><small>Send alle fakturaer i listen til kundenes e-postadresser</small></div>
                                    {emailInvoiceStep3Done && emailInvoiceSummary && (
                                        <div><small class='fw-bold'>{emailInvoiceSummary.invoicesSent} faktura(er) sendt via e-post</small></div>
                                    )}
                                </div>
                                {emailInvoiceStep2Done && !emailInvoiceStep3Done && invoiceSalesReadyForEmail.length > 0 && (
                                    <button
                                        disabled={sendAllInvoicesViaEmail}
                                        class='btn btn-sm btn-danger rounded-pill'
                                        onClick={this.sendAllInvoicesViaEmail}
                                    >
                                        {sendAllInvoicesViaEmail ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-paper-plane' />}
                                        {' '}<Text id='dyrejournal.send-all-invoices-via-email'>Send all invoices via email</Text>
                                    </button>
                                )}
                            </div>

                            {sendAllInvoicesViaEmail && <div class='mt-2 p-3 bg-danger text-white rounded-lg'>
                                <i class='fa-duotone fa-spinner fa-spin' /> <Text id='dyrejournal.sending-all-invoices-via-email'>Sending all invoices via email. DO NOT CLOSE THIS WINDOW!</Text><br />
                                {invoicesSent > 0 && <span>{invoicesSent} {invoicesSent === 1 ? <Text id='dyrejournal.invoice-sent'>invoice sent</Text> : <Text id='dyrejournal.invoices-sent'>invoices sent</Text>}</span>}
                                {invoicesFailed > 0 && <span class='ms-2'>{invoicesFailed} <Text id='dyrejournal.invoices-failed'>failed</Text></span>}
                            </div>}

                            {emailInvoiceStep3Done && (
                                <div class={`mt-2 p-3 ${emailInvoiceSummary?.invoicesFailed > 0 ? 'bg-warning text-dark' : 'bg-success text-white'} rounded-lg`}>
                                    {emailInvoiceSummary?.invoicesFailed > 0 ? <>
                                        <i class='fa-solid fa-triangle-exclamation' /> Ikke alle fakturaene ble sendt via e-post.
                                    </> : <>
                                        <i class='fa-solid fa-check-circle' /> Fakturaene er sendt via e-post!
                                    </>}
                                    {emailInvoiceSummary && (
                                        <div class='mt-2'>
                                            <strong>{emailInvoiceSummary.invoicesSent}</strong> faktura(er) ble sendt via e-post.
                                            {emailInvoiceSummary.invoicesFailed > 0 && (
                                                <div class='mt-1'>
                                                    <strong>{emailInvoiceSummary.invoicesFailed}</strong> faktura(er) ble IKKE sendt.
                                                    {emailInvoiceSummary.failMessage && (
                                                        <div class='small mt-1'>{emailInvoiceSummary.failMessage}</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div class='mt-3 text-center'>
                                        <button
                                            class='btn btn-lg btn-light text-success fw-bold rounded-pill px-5'
                                            onClick={this.backToAdminMenu}
                                        >
                                            <i class='fa-solid fa-check' /> Ferdig
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Section: Purringer (bulk invoice reminders) */}
                {adminToolbarSection === 'reminders' && (
                    <div class='mt-2 mb-2 mx-3 bg-warning rounded-lg p-2'>
                        <div class='d-flex justify-content-between align-items-center mb-2 px-2'>
                            <button class='btn btn-sm btn-outline-dark rounded-pill' onClick={this.backToAdminMenu}>
                                <i class='fa-solid fa-arrow-left' /> Tilbake
                            </button>
                            <strong>Purringer</strong>
                            <div style='width: 80px;'></div>
                        </div>
                        <div class='d-flex flex-column p-3 border border-dark rounded'>

                            {/* Step 1: Channels & message */}
                            <div class={`p-2 mb-2 rounded ${reminderStep > 1 ? 'bg-success text-white' : 'bg-light'}`}>
                                <div class='d-flex flex-row align-items-center'>
                                    <div class='me-3'>
                                        {reminderStep > 1
                                            ? <i class='fa-solid fa-check-circle fa-lg' />
                                            : <span class='badge bg-dark rounded-circle' style='width: 24px; height: 24px; line-height: 24px;'>1</span>
                                        }
                                    </div>
                                    <div class='flex-grow-1'>
                                        <strong>Velg kanaler og melding</strong>
                                        <div><small>Purringer sendes kun for forfalte, ubetalte fakturaer/salg</small></div>
                                    </div>
                                    {reminderStep === 1 && (
                                        <button
                                            class='btn btn-sm btn-success rounded-pill'
                                            disabled={reminderLoading || (!reminderChannels.email && !reminderChannels.sms)}
                                            onClick={this.reminderGoToStep2}
                                        >
                                            {reminderLoading ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-solid fa-arrow-right' />}
                                            {' '}Neste
                                        </button>
                                    )}
                                </div>
                                {reminderStep === 1 && <div class='mt-2 ms-5'>
                                    <div class='form-check'>
                                        <input
                                            class='form-check-input'
                                            type='checkbox'
                                            id='reminderChannelEmail'
                                            checked={reminderChannels.email}
                                            onInput={() => this.toggleReminderChannel('email')}
                                        />
                                        <label class='form-check-label' for='reminderChannelEmail'>
                                            E-post (faktura-PDF med påminnelse)
                                        </label>
                                    </div>
                                    <div class='form-check'>
                                        <input
                                            class='form-check-input'
                                            type='checkbox'
                                            id='reminderChannelSms'
                                            checked={reminderChannels.sms}
                                            onInput={() => this.toggleReminderChannel('sms')}
                                        />
                                        <label class='form-check-label' for='reminderChannelSms'>
                                            SMS (med betalingslenke)
                                        </label>
                                    </div>
                                    {reminderChannels.sms && <div class='mt-2'>
                                        <label class='form-label mb-1' for='reminderSmsText'><small><strong>SMS-tekst</strong></small></label>
                                        <textarea
                                            class='form-control'
                                            id='reminderSmsText'
                                            rows='3'
                                            value={reminderSmsText}
                                            onInput={e => this.setState({ reminderSmsText: e.target.value })}
                                        />
                                        <small class='text-body-secondary'>
                                            Flettefelt: {'{{firstname}}'}, {'{{saleNumber}}'}, {'{{amount}}'}, {'{{payLink}}'}, {'{{clinicName}}'}.
                                            Betalingslenken viser purringen med betalingsinformasjon — med Vipps-oppsett kan kunden også betale direkte med Vipps.
                                        </small>
                                    </div>}
                                    {!reminderChannels.email && !reminderChannels.sms && (
                                        <small class='text-danger'>Velg minst én kanal.</small>
                                    )}
                                </div>}
                            </div>

                            {/* Step 2: Review recipients */}
                            <div class={`p-2 mb-2 rounded ${reminderStep > 2 ? 'bg-success text-white' : reminderStep === 2 ? 'bg-light' : 'bg-secondary bg-opacity-25'}`}>
                                <div class='d-flex flex-row align-items-center'>
                                    <div class='me-3'>
                                        {reminderStep > 2
                                            ? <i class='fa-solid fa-check-circle fa-lg' />
                                            : <span class={`badge ${reminderStep === 2 ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>2</span>
                                        }
                                    </div>
                                    <div class='flex-grow-1'>
                                        <strong>Se gjennom mottakerne</strong>
                                        <div><small>Salg purret siste 7 dager er avhuket som standard, men kan velges på nytt</small></div>
                                    </div>
                                    {reminderStep === 2 && (
                                        <button
                                            class='btn btn-sm btn-danger rounded-pill'
                                            disabled={reminderListLoading || reminderRows.filter(r => reminderSelected[r.sale.id]).length === 0}
                                            onClick={this.sendAllReminders}
                                        >
                                            <i class='fa-duotone fa-paper-plane' />
                                            {' '}Send {reminderRows.filter(r => reminderSelected[r.sale.id]).length} purringer
                                        </button>
                                    )}
                                </div>
                                {reminderStep === 2 && <div class='mt-2'>
                                    <div class='d-flex flex-row align-items-center mb-1'>
                                        <div class='input-group input-group-sm' style='max-width: 380px;'>
                                            <span class='input-group-text'><i class='fa-regular fa-magnifying-glass' /></span>
                                            <input
                                                class='form-control'
                                                type='text'
                                                placeholder='Filtrer på navn, telefon, salgsnr...'
                                                value={reminderSearchText}
                                                onInput={this.reminderSearchInput}
                                            />
                                            {reminderSearchText && (
                                                <button class='btn btn-outline-secondary' type='button' onClick={this.clearReminderSearch}>
                                                    <i class='fa-solid fa-xmark' />
                                                </button>
                                            )}
                                        </div>
                                        {reminderListLoading && <i class='fa-duotone fa-spinner fa-spin ms-2' />}
                                    </div>
                                    <small>
                                        {reminderRows.length} forfalte salg
                                        {' · '}{reminderRows.filter(r => reminderSelected[r.sale.id]).length} valgt
                                        {' · '}{reminderRows.filter(r => r.email).length} med e-post
                                        {' · '}{reminderRows.filter(r => r.cellphone).length} med mobil
                                        {' · '}{reminderRows.filter(r => !r.reachable).length} uten kontaktinfo
                                    </small>
                                    {reminderRows.length === 0 && !reminderListLoading && (
                                        <div class='alert alert-info mt-2 mb-1 py-2'>
                                            <small>
                                                Ingen forfalte salg matcher
                                                {reminderSearchText ? <span> søket «{reminderSearchText}» — endre eller tøm søket over.</span> : ' filtrene.'}
                                            </small>
                                        </div>
                                    )}
                                    <div class='table-responsive mt-1' style='max-height: 320px; overflow-y: auto;'>
                                        <table class='table table-sm table-striped bg-body'>
                                            <thead>
                                                <tr>
                                                    <th />
                                                    <th>Salg</th>
                                                    <th>Kunde</th>
                                                    <th>E-post</th>
                                                    <th>Mobil</th>
                                                    <th>Forfall</th>
                                                    <th class='text-end'>Dager</th>
                                                    <th class='text-end'>Restbeløp</th>
                                                    <th>Sist purret</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reminderRows.map(row => (
                                                    <tr key={`reminder-row-${row.sale.id}`} class={!row.reachable ? 'opacity-50' : ''}>
                                                        <td>
                                                            <input
                                                                type='checkbox'
                                                                checked={!!reminderSelected[row.sale.id]}
                                                                disabled={!row.reachable}
                                                                onInput={() => this.toggleReminderRow(row.sale.id)}
                                                            />
                                                        </td>
                                                        <td>#{row.sale.saleNumber || row.sale.id}</td>
                                                        <td>{row.visitor ? `${row.visitor.firstname || ''} ${row.visitor.lastname || ''}`.trim() : <span class='text-danger'>Mangler kunde</span>}</td>
                                                        <td>{row.email || '—'}</td>
                                                        <td>{row.cellphone || '—'}</td>
                                                        <td>{row.sale.dueDate ? util.isoDate(row.sale.dueDate, false, false, true) : '—'}</td>
                                                        <td class='text-end'>{row.daysOverdue > 0 ? row.daysOverdue : '—'}</td>
                                                        <td class='text-end'>{util.format(row.restAmount, 2, ',', ' ')}</td>
                                                        <td>{row.lastReminder ? util.isoDate(row.lastReminder, false, false, true) : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>}
                            </div>

                            {/* Step 3: Send & result */}
                            <div class={`d-flex flex-row align-items-center p-2 mb-2 rounded ${reminderResult ? 'bg-success text-white' : reminderStep === 3 ? 'bg-light' : 'bg-secondary bg-opacity-25'}`}>
                                <div class='me-3'>
                                    {reminderResult
                                        ? <i class='fa-solid fa-check-circle fa-lg' />
                                        : <span class={`badge ${reminderStep === 3 ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>3</span>
                                    }
                                </div>
                                <div class='flex-grow-1'>
                                    <strong>Send purringer</strong>
                                    <div><small>Purringene sendes én og én til kundene</small></div>
                                </div>
                            </div>

                            {reminderSending && <div class='mt-2 p-3 bg-danger text-white rounded-lg'>
                                <i class='fa-duotone fa-spinner fa-spin' /> Sender purringer. IKKE LUKK DETTE VINDUET!<br />
                                {reminderProgress > 0 && <span>{reminderProgress} av {reminderRows.filter(r => reminderSelected[r.sale.id]).length} behandlet</span>}
                            </div>}

                            {reminderResult && (
                                <div class='mt-2 p-3 bg-success text-white rounded-lg'>
                                    <i class='fa-solid fa-check-circle' /> Purringene er sendt!
                                    <div class='mt-2'>
                                        <strong>{reminderResult.emailsSent}</strong> e-post(er) og <strong>{reminderResult.smsSent}</strong> SMS sendt.
                                    </div>
                                    {reminderResult.failed.length > 0 && (
                                        <div class='mt-2 p-2 bg-danger rounded'>
                                            <strong>{reminderResult.failed.length} feilet:</strong>
                                            <ul class='mb-0'>
                                                {reminderResult.failed.map(f => (
                                                    <li key={`reminder-failed-${f.saleId}-${f.channel}`}>Salg #{f.saleId} ({f.channel}): {f.error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {reminderResult.skipped.length > 0 && (
                                        <div class='mt-2'>
                                            <small>{reminderResult.skipped.length} hoppet over (manglende kontaktinfo)</small>
                                        </div>
                                    )}
                                    <div class='mt-3 text-center'>
                                        <button
                                            class='btn btn-lg btn-light text-success fw-bold rounded-pill px-5'
                                            onClick={this.backToAdminMenu}
                                        >
                                            <i class='fa-solid fa-check' /> Ferdig
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Section: Accounting (Fiken/Tripletex) */}
                {adminToolbarSection === 'accounting' && (
                    <div class='mt-2 mb-2 mx-3 bg-warning rounded-lg p-2'>
                        {/* Header with back button */}
                        <div class='d-flex justify-content-between align-items-center mb-2 px-2'>
                            <button class='btn btn-sm btn-outline-dark rounded-pill' onClick={this.backToAdminMenu}>
                                <i class='fa-solid fa-arrow-left' /> Tilbake
                            </button>
                            <strong>{isTripletexProvider ? 'Tripletex' : 'Fiken'}</strong>
                            <div style='width: 80px;'></div>
                        </div>

                        {/* Mode Selection */}
                        {!accountingMode && (
                            <div class='d-flex flex-row gap-2 justify-content-center p-3'>
                                <button
                                    class='btn btn-lg btn-dark rounded-pill px-4'
                                    onClick={() => this.setState({ accountingMode: 'transfer' })}
                                >
                                    <i class='fa-solid fa-paper-plane me-2' />
                                    Overfør til {isTripletexProvider ? 'Tripletex' : 'Fiken'}
                                </button>
                                <button
                                    class='btn btn-lg btn-success rounded-pill px-4'
                                    onClick={() => this.setState({ accountingMode: 'update' })}
                                >
                                    <i class='fa-solid fa-sync me-2' />
                                    Oppdater fra {isTripletexProvider ? 'Tripletex' : 'Fiken'}
                                </button>
                                {isTripletexProvider && (
                                    <button
                                        class='btn btn-lg btn-info rounded-pill px-4'
                                        onClick={() => { this.setState({ accountingMode: 'compare' }); this.startTripletexComparison(); }}
                                    >
                                        <i class='fa-solid fa-code-compare me-2' />
                                        Sammenlign med Tripletex
                                    </button>
                                )}
                                {isTripletexProvider && (
                                    <button
                                        class='btn btn-lg btn-secondary rounded-pill px-4'
                                        onClick={() => { this.setState({ accountingMode: 'openPosts' }); this.startTripletexOpenPosts(); }}
                                    >
                                        <i class='fa-duotone fa-money-bill-transfer me-2' />
                                        Åpne poster
                                    </button>
                                )}
                                {isTripletexProvider && customer?.featureFlags?.tripletexVoucher == 1 && (
                                    <button
                                        class='btn btn-lg btn-warning rounded-pill px-4'
                                        onClick={() => this.setState({ accountingMode: 'voucher' })}
                                    >
                                        <i class='fa-solid fa-file-invoice me-2' />
                                        Bilagsrapportering
                                    </button>
                                )}
                            </div>
                        )}

                        {/* 3-Step Wizard for Accounting Transfer */}
                        {accountingMode === 'transfer' && !accountingTransferStep3Done && (
                            <div class='d-flex flex-column p-3 border border-dark rounded'>
                                {/* Step 1: Set Filters */}
                                <div class={`p-2 mb-2 rounded ${accountingTransferStep1Done ? 'bg-success text-white' : 'bg-light'}`}>
                                    <div class='d-flex flex-row align-items-center'>
                                        <div class='me-3'>
                                            {accountingTransferStep1Done
                                                ? <i class='fa-solid fa-check-circle fa-lg' />
                                                : <span class='badge bg-dark rounded-circle' style='width: 24px; height: 24px; line-height: 24px;'>1</span>
                                            }
                                        </div>
                                        <div class='flex-grow-1'>
                                            <strong>Sett opp filtre</strong>
                                            <div><small>Velg betalingsmåte (faktura, kontant, kort, …) og periode</small></div>
                                        </div>
                                        <button
                                            class={`btn btn-sm ${accountingTransferStep1Done ? 'btn-outline-light' : 'btn-success'} rounded-pill`}
                                            onClick={this.setupAccountingTransferFilters}
                                            disabled={settingUpAccountingTransfer}
                                        >
                                            {settingUpAccountingTransfer ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-duotone fa-magic me-1' />}
                                            {accountingTransferStep1Done ? 'Tilbakestill' : 'Sett opp automatisk'}
                                        </button>
                                    </div>
                                    {this.renderWizardFilters({ showStatus: true, showPaymentMethod: true })}
                                </div>

                                {/* Step 2: Check List */}
                                <div class={`p-2 mb-2 rounded ${accountingTransferStep2Done ? 'bg-success text-white' : (accountingTransferStep1Done ? 'bg-light' : 'bg-secondary bg-opacity-25')}`}>
                                    <div class='d-flex flex-row align-items-center'>
                                        <div class='me-3'>
                                            {accountingTransferStep2Done
                                                ? <i class='fa-solid fa-check-circle fa-lg' />
                                                : <span class={`badge ${accountingTransferStep1Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>2</span>
                                            }
                                        </div>
                                        <div class='flex-grow-1'>
                                            <strong>Sjekk listen nedenfor</strong>
                                            <div><small>Sjekk at alle salg i listen nedenfor er korrekte</small></div>
                                            {accountingTransferStep1Done && !accountingTransferStep2Done && accountingTransferCount > 0 && (
                                                <div><small class='text-danger fw-bold'>{accountingTransferCount} klare for overføring</small></div>
                                            )}
                                        </div>
                                        {accountingTransferStep1Done && !accountingTransferStep2Done && (
                                            <button
                                                class='btn btn-sm btn-success rounded-pill'
                                                onClick={this.markAccountingTransferStep2Done}
                                            >
                                                <i class='fa-duotone fa-check me-1' />
                                                Listen er OK
                                            </button>
                                        )}
                                    </div>
                                    {accountingTransferStep1Done && this.renderWizardActiveFiltersSummary()}
                                </div>

                                {/* Step 3: Send to System */}
                                <div class={`d-flex flex-row align-items-center p-2 rounded ${accountingTransferStep3Done ? 'bg-success text-white' : (accountingTransferStep2Done ? 'bg-light' : 'bg-secondary bg-opacity-25')}`}>
                                    <div class='me-3'>
                                        {accountingTransferStep3Done
                                            ? <i class='fa-solid fa-check-circle fa-lg' />
                                            : <span class={`badge ${accountingTransferStep2Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>3</span>
                                        }
                                    </div>
                                    <div class='flex-grow-1'>
                                        <strong>Send til {isTripletexProvider ? 'Tripletex' : 'Fiken'}</strong>
                                        <div><small>Overfør fakturaer og salg</small></div>
                                        {/* Show count when step 2 is done */}
                                        {accountingTransferStep2Done && !accountingTransferStep3Done && accountingTransferCount > 0 && (
                                            <div><small class='text-success fw-bold'>{accountingTransferCount} klare for overføring</small></div>
                                        )}
                                        {/* Show message when nothing to send */}
                                        {accountingTransferStep2Done && !accountingTransferStep3Done && accountingTransferCount === 0 && (
                                            <div><small class='text-warning fw-bold'>Ingen salg å overføre</small></div>
                                        )}
                                    </div>
                                    {accountingTransferStep2Done && !accountingTransferStep3Done && accountingTransferCount > 0 && (
                                        <button
                                            class='btn btn-sm btn-success rounded-pill'
                                            disabled={sendAllInvoicesToTripletex || sendAllSalesToTripletex || sendAllInvoicesToFiken || sendAllSalesToFiken}
                                            onClick={isTripletexProvider ? this.sendAllToTripletex : this.sendAllToFiken}
                                        >
                                            {(sendAllInvoicesToTripletex || sendAllSalesToTripletex || sendAllInvoicesToFiken || sendAllSalesToFiken)
                                                ? <i class='fa-duotone fa-spinner fa-spin me-1' />
                                                : <i class='fa-duotone fa-paper-plane me-1' />}
                                            Send alle
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {accountingMode === 'transfer' && (sendAllInvoicesToTripletex || sendAllSalesToTripletex || sendAllInvoicesToFiken || sendAllSalesToFiken) && totalAccountingTransfers > 0 && (
                            <AccountingTransferProgress
                                current={currentAccountingTransfer}
                                total={totalAccountingTransfers}
                                systemName={isTripletexProvider ? 'Tripletex' : 'Fiken'}
                                label={currentAccountingTransferLabel}
                                failed={currentAccountingTransferFailed}
                            />
                        )}

                        {/* Success message with Done button */}
                        {accountingMode === 'transfer' && accountingTransferStep3Done && (
                            <div class={`mt-2 p-3 ${accountingTransferSummary?.totalFailed > 0 ? 'bg-warning text-dark' : 'bg-success text-white'} rounded-lg`}>
                                <i class='fa-solid fa-check-circle' /> Overføring fullført!
                                {accountingTransferSummary && (
                                    <div class='mt-2'>
                                        <strong>{accountingTransferSummary.totalSent}</strong> salg ble overført til {isTripletexProvider ? 'Tripletex' : 'Fiken'}.
                                        {accountingTransferSummary.totalFailed > 0 && (
                                            <div class='mt-1'>
                                                <i class='fa-solid fa-triangle-exclamation' /> <strong>{accountingTransferSummary.totalFailed}</strong> salg feilet og ble IKKE overført. Se feilmelding i Tripletex-kolonnen på hvert salg.
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div class='mt-3 text-center'>
                                    <button
                                        class='btn btn-lg btn-light text-success fw-bold rounded-pill px-5'
                                        onClick={this.backToAdminMenu}
                                    >
                                        <i class='fa-solid fa-check' /> Ferdig
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Update from Tripletex */}
                        {accountingMode === 'update' && <>
                        {isTripletexProvider && <div class='d-flex flex-column px-2 w-100 mb-2'>
                            {/* Step 1: Velg periode */}
                            <div class={`p-2 mb-2 rounded ${accountingUpdateStep1Done ? 'bg-success text-white' : 'bg-light'}`}>
                                <div class='d-flex flex-row align-items-center'>
                                    <div class='me-3'>
                                        {accountingUpdateStep1Done
                                            ? <i class='fa-solid fa-check-circle fa-lg' />
                                            : <span class='badge bg-dark rounded-circle' style='width: 24px; height: 24px; line-height: 24px;'>1</span>
                                        }
                                    </div>
                                    <div class='flex-grow-1'>
                                        <strong>Velg periode</strong>
                                        <div><small>Velg datoperiode for oppdateringen</small></div>
                                    </div>
                                    <button
                                        class={`btn btn-sm ${accountingUpdateStep1Done ? 'btn-outline-light' : 'btn-success'} rounded-pill`}
                                        onClick={() => this.setState({ accountingUpdateStep1Done: true })}
                                    >
                                        <i class='fa-duotone fa-check me-1' />
                                        {accountingUpdateStep1Done ? 'Endre' : 'Neste'}
                                    </button>
                                </div>
                                {this.renderWizardFilters({ showStatus: false, showPaymentMethod: false })}
                            </div>

                            {/* Step 2: Forhåndsvis (dry-run) */}
                            <div class={`p-2 mb-2 rounded ${accountingUpdateStep2Done ? 'bg-success text-white' : (accountingUpdateStep1Done ? 'bg-light' : 'bg-secondary bg-opacity-25')}`}>
                                <div class='d-flex flex-row align-items-center'>
                                    <div class='me-3'>
                                        {accountingUpdateStep2Done
                                            ? <i class='fa-solid fa-check-circle fa-lg' />
                                            : <span class={`badge ${accountingUpdateStep1Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>2</span>
                                        }
                                    </div>
                                    <div class='flex-grow-1'>
                                        <strong>Forhåndsvis</strong>
                                        <div><small>Se hva som vil skje – ingenting lagres</small></div>
                                    </div>
                                    {accountingUpdateStep1Done && (
                                        <button
                                            class='btn btn-sm btn-info rounded-pill'
                                            disabled={updateFromTripletexPreviewLoading}
                                            onClick={this.previewFromTripletexInvoices}
                                        >
                                            {updateFromTripletexPreviewLoading ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-duotone fa-eye me-1' />}
                                            Forhåndsvis
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Step 3: Kjør oppdatering */}
                            <div class={`d-flex flex-row align-items-center p-2 mb-2 rounded ${accountingUpdateStep3Done ? 'bg-success text-white' : (accountingUpdateStep2Done ? 'bg-light' : 'bg-secondary bg-opacity-25')}`}>
                                <div class='me-3'>
                                    {accountingUpdateStep3Done
                                        ? <i class='fa-solid fa-check-circle fa-lg' />
                                        : <span class={`badge ${accountingUpdateStep2Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>3</span>
                                    }
                                </div>
                                <div class='flex-grow-1'>
                                    <strong>Kjør oppdatering</strong>
                                    <div><small>Hent betalingsstatus fra Tripletex</small></div>
                                </div>
                                {accountingUpdateStep2Done && (
                                    <button
                                        class='btn btn-sm btn-success rounded-pill'
                                        disabled={updateFromTripletexInvoices}
                                        onClick={this.updateFromTripletexInvoices}
                                    >
                                        {updateFromTripletexInvoices ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-duotone fa-paper-plane me-1' />}
                                        Kjør
                                    </button>
                                )}
                            </div>
                            <small class='text-muted text-center'>
                                <span class='bg-danger text-white p-1 rounded'><Text id='dyrejournal.updating-invoices-from-tripletex-info'>Note: Only invoices within the selected date range will be updated. Please do not overuse this function.</Text></span>
                            </small>
                            {tripletexUpdateResponse?.data?.summary && <>
                                <div class={`d-flex flex-column mx-0 px-2 py-2 mt-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                    <h6 class='mb-2'><i class='fa-duotone fa-chart-bar me-2' />Tripletex Sync Summary</h6>
                                    {tripletexUpdateResponse.data.dryRun
                                        ? <div class='alert alert-info py-2 mb-2'><i class='fa-duotone fa-eye me-2' /><strong>Forhåndsvisning</strong> – ingenting er lagret ennå. Gå til steg 3 for å kjøre oppdateringen.</div>
                                        : <div class='alert alert-success py-2 mb-2'><i class='fa-duotone fa-check me-2' />Oppdatering kjørt.</div>
                                    }
                                    {/* Show date range of updated invoices */}
                                    {(() => {
                                        const data = tripletexUpdateResponse.data;
                                        const details = data.details || {};
                                        const allItems = [
                                            ...(details.fullyPaid || []),
                                            ...(details.partiallyPaid || []),
                                            ...(details.unpaid || []),
                                            ...(details.creditNotes || []),
                                        ];
                                        const dates = allItems
                                            .map(item => item.invoiceDate)
                                            .filter(d => d)
                                            .sort();
                                        const formatDate = (d) => {
                                            if (!d) return '';
                                            const parts = d.split('-');
                                            return `${parts[2]}.${parts[1]}.${parts[0]}`;
                                        };
                                        const hasQueryRange = data.invoiceDateFrom || data.invoiceDateTo;
                                        const hasInvoiceDates = dates.length > 0;
                                        if (hasQueryRange || hasInvoiceDates) {
                                            const firstDate = dates[0];
                                            const lastDate = dates[dates.length - 1];
                                            return (
                                                <div class='alert alert-info py-2 mb-3' style='font-size: 0.9em;'>
                                                    {hasQueryRange && (
                                                        <div class='mb-1'>
                                                            <i class='fa-duotone fa-calendar-range me-2' />
                                                            <strong>Søkeperiode:</strong>{' '}
                                                            {formatDate(data.invoiceDateFrom)} - {formatDate(data.invoiceDateTo)}
                                                        </div>
                                                    )}
                                                    {hasInvoiceDates && (
                                                        <div>
                                                            <i class='fa-duotone fa-file-invoice me-2' />
                                                            <strong>Fakturaer funnet:</strong>{' '}
                                                            {firstDate === lastDate
                                                                ? formatDate(firstDate)
                                                                : `${formatDate(firstDate)} - ${formatDate(lastDate)}`
                                                            }
                                                            <span class='text-muted ms-2'>({dates.length} stk)</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                    {/* Warning if more invoices exist than returned */}
                                    {tripletexUpdateResponse.data.summary.hasMoreInvoices && (
                                        <div class='alert alert-warning py-2 mb-3' style='font-size: 0.9em;'>
                                            <i class='fa-duotone fa-triangle-exclamation me-2' />
                                            <strong>Advarsel:</strong> Det finnes flere fakturaer i Tripletex ({tripletexUpdateResponse.data.summary.totalInvoicesInTripletex} totalt)
                                            enn det som ble hentet ({tripletexUpdateResponse.data.summary.invoicesFromTripletex}).
                                            Vennligst velg en kortere periode for å hente alle fakturaer.
                                        </div>
                                    )}
                                    <div class='d-flex flex-wrap gap-2 mb-3'>
                                        <span class='badge bg-secondary'>
                                            <i class='fa-duotone fa-file-invoice me-1' />
                                            From Tripletex: {tripletexUpdateResponse.data.summary.invoicesFromTripletex}
                                        </span>
                                        <span class='badge bg-info'>
                                            <i class='fa-duotone fa-link me-1' />
                                            Matched: {tripletexUpdateResponse.data.summary.invoicesMatched}
                                        </span>
                                        {tripletexUpdateResponse.data.summary.invoicesNotFound > 0 &&
                                            <span class='badge bg-warning text-dark'>
                                                <i class='fa-duotone fa-question me-1' />
                                                Not Found: {tripletexUpdateResponse.data.summary.invoicesNotFound}
                                            </span>
                                        }
                                        <span class='badge bg-success'>
                                            <i class='fa-duotone fa-check-circle me-1' />
                                            Fully Paid: {tripletexUpdateResponse.data.summary.fullyPaidCount}
                                        </span>
                                        {tripletexUpdateResponse.data.summary.partiallyPaidCount > 0 &&
                                            <span class='badge bg-warning text-dark'>
                                                <i class='fa-duotone fa-circle-half-stroke me-1' />
                                                Partially Paid: {tripletexUpdateResponse.data.summary.partiallyPaidCount}
                                            </span>
                                        }
                                        {tripletexUpdateResponse.data.summary.unpaidCount > 0 &&
                                            <span class='badge bg-danger'>
                                                <i class='fa-duotone fa-clock me-1' />
                                                Unpaid: {tripletexUpdateResponse.data.summary.unpaidCount}
                                            </span>
                                        }
                                        {tripletexUpdateResponse.data.summary.paymentsCreatedCount > 0 &&
                                            <span class='badge bg-primary'>
                                                <i class='fa-duotone fa-plus me-1' />
                                                Payments Created: {tripletexUpdateResponse.data.summary.paymentsCreatedCount}
                                            </span>
                                        }
                                        {tripletexUpdateResponse.data.summary.kidUpdatedCount > 0 &&
                                            <span class='badge bg-info'>
                                                <i class='fa-duotone fa-barcode me-1' />
                                                KID Updated: {tripletexUpdateResponse.data.summary.kidUpdatedCount}
                                            </span>
                                        }
                                        {tripletexUpdateResponse.data.summary.errorsCount > 0 &&
                                            <span class='badge bg-warning text-dark'>
                                                <i class='fa-duotone fa-file-circle-question me-1' />
                                                Ikke i Dyrejournal: {tripletexUpdateResponse.data.summary.errorsCount}
                                            </span>
                                        }
                                        {tripletexUpdateResponse.data.summary.creditNotesNotInDyrejournalCount > 0 &&
                                            <span class='badge bg-info text-dark'>
                                                <i class='fa-duotone fa-file-minus me-1' />
                                                Kreditnotaer ikke i Dyrejournal: {tripletexUpdateResponse.data.summary.creditNotesNotInDyrejournalCount}
                                            </span>
                                        }
                                    </div>

                                    {/* Fully Paid Details */}
                                    {tripletexUpdateResponse.data.details?.fullyPaid?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-success fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-check-circle me-1' />
                                                Fully Paid ({tripletexUpdateResponse.data.details.fullyPaid.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Invoice #</th>
                                                        <th>Invoice Date</th>
                                                        <th>Due Date</th>
                                                        <th>KID</th>
                                                        <th class='text-end'>Amount</th>
                                                        <th class='text-end'>Paid</th>
                                                        <th class='text-end'>New Payment</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tripletexUpdateResponse.data.details.fullyPaid.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.invoiceNumber}</td>
                                                            <td>{item.invoiceDate}</td>
                                                            <td>{item.dueDate}</td>
                                                            <td><code class='text-muted'>{item.kid}</code></td>
                                                            <td class='text-end'>{util.format(item.tripletexAmount, 0, ',', ' ')}</td>
                                                            <td class='text-end'>{util.format(item.tripletexPaidAmount, 0, ',', ' ')}</td>
                                                            <td class='text-end'>
                                                                {item.newPaymentRecorded > 0
                                                                    ? <span class='text-success'>+{util.format(item.newPaymentRecorded, 0, ',', ' ')}</span>
                                                                    : <span class='text-muted'>-</span>
                                                                }
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* Partially Paid Details */}
                                    {tripletexUpdateResponse.data.details?.partiallyPaid?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-warning fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-circle-half-stroke me-1' />
                                                Partially Paid ({tripletexUpdateResponse.data.details.partiallyPaid.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Invoice #</th>
                                                        <th>Invoice Date</th>
                                                        <th>Due Date</th>
                                                        <th>KID</th>
                                                        <th class='text-end'>Amount</th>
                                                        <th class='text-end'>Outstanding</th>
                                                        <th class='text-end'>Paid</th>
                                                        <th class='text-end'>New Payment</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tripletexUpdateResponse.data.details.partiallyPaid.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.invoiceNumber}</td>
                                                            <td>{item.invoiceDate}</td>
                                                            <td>{item.dueDate}</td>
                                                            <td><code class='text-muted'>{item.kid}</code></td>
                                                            <td class='text-end'>{util.format(item.tripletexAmount, 0, ',', ' ')}</td>
                                                            <td class='text-end text-danger'>{util.format(item.amountOutstanding, 0, ',', ' ')}</td>
                                                            <td class='text-end'>{util.format(item.tripletexPaidAmount, 0, ',', ' ')}</td>
                                                            <td class='text-end'>
                                                                {item.newPaymentRecorded > 0
                                                                    ? <span class='text-success'>+{util.format(item.newPaymentRecorded, 0, ',', ' ')}</span>
                                                                    : <span class='text-muted'>-</span>
                                                                }
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* Unpaid Details */}
                                    {tripletexUpdateResponse.data.details?.unpaid?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-danger fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-clock me-1' />
                                                Unpaid ({tripletexUpdateResponse.data.details.unpaid.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Invoice #</th>
                                                        <th>Invoice Date</th>
                                                        <th>Due Date</th>
                                                        <th>KID</th>
                                                        <th class='text-end'>Amount</th>
                                                        <th class='text-end'>Outstanding</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tripletexUpdateResponse.data.details.unpaid.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.invoiceNumber}</td>
                                                            <td>{item.invoiceDate}</td>
                                                            <td>{item.dueDate}</td>
                                                            <td><code class='text-muted'>{item.kid}</code></td>
                                                            <td class='text-end'>{util.format(item.tripletexAmount, 0, ',', ' ')}</td>
                                                            <td class='text-end text-danger'>{util.format(item.amountOutstanding, 0, ',', ' ')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* Payments Created Details */}
                                    {tripletexUpdateResponse.data.details?.paymentsCreated?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-primary fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-plus me-1' />
                                                Payments Created ({tripletexUpdateResponse.data.details.paymentsCreated.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Invoice #</th>
                                                        <th class='text-end'>Amount</th>
                                                        <th>Type</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tripletexUpdateResponse.data.details.paymentsCreated.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.invoiceNumber}</td>
                                                            <td class='text-end text-success'>+{util.format(item.amount, 0, ',', ' ')}</td>
                                                            <td>{item.isPartial ? <span class='badge bg-warning text-dark'>Partial</span> : <span class='badge bg-success'>Full</span>}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* KID Updated Details */}
                                    {tripletexUpdateResponse.data.details?.kidUpdated?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-info fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-barcode me-1' />
                                                KID Updated ({tripletexUpdateResponse.data.details.kidUpdated.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Invoice #</th>
                                                        <th>KID</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tripletexUpdateResponse.data.details.kidUpdated.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.invoiceNumber}</td>
                                                            <td><code>{item.kid}</code></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* Invoices not found in Dyrejournal */}
                                    {tripletexUpdateResponse.data.details?.errors?.length > 0 && <>
                                        <details class='mb-2' open>
                                            <summary class='text-warning fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-file-circle-question me-1' />
                                                Ikke funnet i Dyrejournal ({tripletexUpdateResponse.data.details.errors.length})
                                            </summary>
                                            <p class='text-muted mt-2 mb-2' style='font-size: 0.85em;'>
                                                Disse fakturaene finnes i Tripletex, men ble ikke opprettet via Dyrejournal.
                                            </p>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Faktura #</th>
                                                        <th>Tripletex ID</th>
                                                        <th>Kunde</th>
                                                        <th>Dato</th>
                                                        <th>Forfall</th>
                                                        <th class='text-end'>Beløp</th>
                                                        <th class='text-end'>Utestående</th>
                                                        <th>Merknad</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tripletexUpdateResponse.data.details.errors.map((item, idx) => (
                                                        <tr key={idx} class='table-warning'>
                                                            <td>{item.invoiceNumber}</td>
                                                            <td><small class='text-muted'>{item.tripletexId}</small></td>
                                                            <td>{item.customerName || <span class='text-muted'>-</span>}</td>
                                                            <td>{item.invoiceDate || <span class='text-muted'>-</span>}</td>
                                                            <td>{item.dueDate || <span class='text-muted'>-</span>}</td>
                                                            <td class='text-end'>{item.amount ? util.format(item.amount, 0, ',', ' ') : <span class='text-muted'>-</span>}</td>
                                                            <td class='text-end'>{item.amountOutstanding ? util.format(item.amountOutstanding, 0, ',', ' ') : <span class='text-muted'>-</span>}</td>
                                                            <td><small>{item.error}</small></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* Credit Notes Details */}
                                    {tripletexUpdateResponse.data.details?.creditNotes?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-secondary fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-file-minus me-1' />
                                                Credit Notes ({tripletexUpdateResponse.data.details.creditNotes.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Invoice #</th>
                                                        <th>Invoice Date</th>
                                                        <th>KID</th>
                                                        <th class='text-end'>Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tripletexUpdateResponse.data.details.creditNotes.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.invoiceNumber}</td>
                                                            <td>{item.invoiceDate}</td>
                                                            <td><code class='text-muted'>{item.kid}</code></td>
                                                            <td class='text-end'>{util.format(item.amount, 0, ',', ' ')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* Credit Notes NOT in Dyrejournal */}
                                    {tripletexUpdateResponse.data.details?.creditNotesNotInDyrejournal?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-info fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-file-minus me-1' />
                                                Kreditnotaer ikke i Dyrejournal ({tripletexUpdateResponse.data.details.creditNotesNotInDyrejournal.length})
                                            </summary>
                                            <p class='text-muted mt-2 mb-2' style='font-size: 0.85em;'>
                                                Disse kreditnotaene finnes i Tripletex, men ble ikke opprettet via Dyrejournal.
                                            </p>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Invoice #</th>
                                                        <th>Invoice Date</th>
                                                        <th>Customer</th>
                                                        <th class='text-end'>Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tripletexUpdateResponse.data.details.creditNotesNotInDyrejournal.map((item, idx) => (
                                                        <tr key={idx} class='table-info'>
                                                            <td>{item.invoiceNumber}</td>
                                                            <td>{item.invoiceDate}</td>
                                                            <td>{item.customerName}</td>
                                                            <td class='text-end'>{util.format(item.amount, 0, ',', ' ')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}
                                </div>
                            </>}
                        </div>}

                        {/* Update from Fiken */}
                        {!isTripletexProvider && <div class='d-flex flex-column px-2 w-100'>
                            <button
                                disabled={updateFromFikenInvoices}
                                class='btn btn-success rounded-pill'
                                onClick={this.updateFromFikenInvoices}
                            >
                                <nobr>
                                    {updateFromFikenInvoices ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-paper-plane' />} <Text id='dyrejournal.update-all-invoices-to-fiken'>Update all invoices from Fiken</Text>
                                </nobr><br />
                                {cutoffDate ? <small class='ms-2'>{util.formatDate(cutoffDate, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}</small> : ''}
                                {cutoffDateEnd ? <small class='ms-1'> - {util.formatDate(cutoffDateEnd, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}</small> : ''}
                            </button>
                            <small class='text-muted text-center'>
                                <Text id='dyrejournal.updating-invoices-from-fiken'>Updating invoices from Fiken may take some time. Please be patient.</Text><br />
                                <Text id='dyrejournal.updating-invoices-from-fiken-date'>Choose the date range for the update above.</Text><br />
                                <span class='bg-danger text-white p-1 rounded'><Text id='dyrejournal.updating-invoices-from-fiken-info'>Note: Only invoices within the selected date range will be updated. Please do not overuse this function.</Text></span>
                            </small>
                            {fikenUpdateResponse?.data?.summary && <>
                                <div class={`d-flex flex-column mx-0 px-2 py-2 mt-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                    <h6 class='mb-2'><i class='fa-duotone fa-chart-bar me-2' />Fiken Sync Summary</h6>
                                    {/* Show date range of updated invoices */}
                                    {(() => {
                                        const data = fikenUpdateResponse.data;
                                        const details = data.details || {};
                                        const allItems = [
                                            ...(details.fullyPaid || []),
                                            ...(details.partiallyPaid || []),
                                            ...(details.unpaid || []),
                                            ...(details.creditNotes || []),
                                        ];
                                        const dates = allItems
                                            .map(item => item.date)
                                            .filter(d => d)
                                            .sort();
                                        const formatDate = (d) => {
                                            if (!d) return '';
                                            const parts = d.split('-');
                                            return `${parts[2]}.${parts[1]}.${parts[0]}`;
                                        };
                                        const hasQueryRange = data.invoiceDateFrom || data.invoiceDateTo;
                                        const hasInvoiceDates = dates.length > 0;
                                        if (hasQueryRange || hasInvoiceDates) {
                                            const firstDate = dates[0];
                                            const lastDate = dates[dates.length - 1];
                                            return (
                                                <div class='alert alert-info py-2 mb-3' style='font-size: 0.9em;'>
                                                    {hasQueryRange && (
                                                        <div class='mb-1'>
                                                            <i class='fa-duotone fa-calendar-range me-2' />
                                                            <strong>Søkeperiode:</strong>{' '}
                                                            {formatDate(data.invoiceDateFrom)} - {formatDate(data.invoiceDateTo)}
                                                        </div>
                                                    )}
                                                    {hasInvoiceDates && (
                                                        <div>
                                                            <i class='fa-duotone fa-file-invoice me-2' />
                                                            <strong>Fakturaer funnet:</strong>{' '}
                                                            {firstDate === lastDate
                                                                ? formatDate(firstDate)
                                                                : `${formatDate(firstDate)} - ${formatDate(lastDate)}`
                                                            }
                                                            <span class='text-muted ms-2'>({dates.length} stk)</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                    <div class='d-flex flex-wrap gap-2 mb-3'>
                                        <span class='badge bg-secondary'>
                                            <i class='fa-duotone fa-file-invoice me-1' />
                                            Fra Fiken: {fikenUpdateResponse.data.summary.invoicesFromFiken}
                                            {fikenUpdateResponse.data.summary.totalPages > 1 && (
                                                <span class='ms-1'>({fikenUpdateResponse.data.summary.totalPages} sider)</span>
                                            )}
                                        </span>
                                        <span class='badge bg-info'>
                                            <i class='fa-duotone fa-link me-1' />
                                            Matched: {fikenUpdateResponse.data.summary.invoicesMatched}
                                        </span>
                                        {fikenUpdateResponse.data.summary.invoicesNotFound > 0 &&
                                            <span class='badge bg-warning text-dark'>
                                                <i class='fa-duotone fa-question me-1' />
                                                Not Found: {fikenUpdateResponse.data.summary.invoicesNotFound}
                                            </span>
                                        }
                                        <span class='badge bg-success'>
                                            <i class='fa-duotone fa-check-circle me-1' />
                                            Fully Paid: {fikenUpdateResponse.data.summary.fullyPaidCount}
                                        </span>
                                        {fikenUpdateResponse.data.summary.partiallyPaidCount > 0 &&
                                            <span class='badge bg-warning text-dark'>
                                                <i class='fa-duotone fa-circle-half-stroke me-1' />
                                                Partially Paid: {fikenUpdateResponse.data.summary.partiallyPaidCount}
                                            </span>
                                        }
                                        {fikenUpdateResponse.data.summary.unpaidCount > 0 &&
                                            <span class='badge bg-danger'>
                                                <i class='fa-duotone fa-clock me-1' />
                                                Unpaid: {fikenUpdateResponse.data.summary.unpaidCount}
                                            </span>
                                        }
                                        {fikenUpdateResponse.data.summary.paymentsCreatedCount > 0 &&
                                            <span class='badge bg-primary'>
                                                <i class='fa-duotone fa-plus me-1' />
                                                Payments Created: {fikenUpdateResponse.data.summary.paymentsCreatedCount}
                                            </span>
                                        }
                                        {fikenUpdateResponse.data.summary.kidUpdatedCount > 0 &&
                                            <span class='badge bg-info'>
                                                <i class='fa-duotone fa-barcode me-1' />
                                                KID Updated: {fikenUpdateResponse.data.summary.kidUpdatedCount}
                                            </span>
                                        }
                                        {fikenUpdateResponse.data.summary.errorsCount > 0 &&
                                            <span class='badge bg-danger'>
                                                <i class='fa-duotone fa-exclamation-triangle me-1' />
                                                Errors: {fikenUpdateResponse.data.summary.errorsCount}
                                            </span>
                                        }
                                    </div>

                                    {/* Fully Paid Details */}
                                    {fikenUpdateResponse.data.details?.fullyPaid?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-success fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-check-circle me-1' />
                                                Fully Paid ({fikenUpdateResponse.data.details.fullyPaid.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Fiken #</th>
                                                        <th>Date</th>
                                                        <th>Due Date</th>
                                                        <th>KID</th>
                                                        <th class='text-end'>Amount</th>
                                                        <th class='text-end'>Paid</th>
                                                        <th class='text-end'>New Payment</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fikenUpdateResponse.data.details.fullyPaid.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.fikenSaleNumber}</td>
                                                            <td>{item.date}</td>
                                                            <td>{item.dueDate}</td>
                                                            <td><code class='text-muted'>{item.kid}</code></td>
                                                            <td class='text-end'>{util.format(item.fikenTotalAmount, 0, ',', ' ')}</td>
                                                            <td class='text-end'>{util.format(item.fikenPaidAmount, 0, ',', ' ')}</td>
                                                            <td class='text-end'>
                                                                {item.newPaymentRecorded > 0
                                                                    ? <span class='text-success'>+{util.format(item.newPaymentRecorded, 0, ',', ' ')}</span>
                                                                    : <span class='text-muted'>-</span>
                                                                }
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* Partially Paid Details */}
                                    {fikenUpdateResponse.data.details?.partiallyPaid?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-warning fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-circle-half-stroke me-1' />
                                                Partially Paid ({fikenUpdateResponse.data.details.partiallyPaid.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Fiken #</th>
                                                        <th>Date</th>
                                                        <th>Due Date</th>
                                                        <th>KID</th>
                                                        <th class='text-end'>Amount</th>
                                                        <th class='text-end'>Outstanding</th>
                                                        <th class='text-end'>Paid</th>
                                                        <th class='text-end'>New Payment</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fikenUpdateResponse.data.details.partiallyPaid.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.fikenSaleNumber}</td>
                                                            <td>{item.date}</td>
                                                            <td>{item.dueDate}</td>
                                                            <td><code class='text-muted'>{item.kid}</code></td>
                                                            <td class='text-end'>{util.format(item.fikenTotalAmount, 0, ',', ' ')}</td>
                                                            <td class='text-end text-danger'>{util.format(item.outstandingBalance, 0, ',', ' ')}</td>
                                                            <td class='text-end'>{util.format(item.fikenPaidAmount, 0, ',', ' ')}</td>
                                                            <td class='text-end'>
                                                                {item.newPaymentRecorded > 0
                                                                    ? <span class='text-success'>+{util.format(item.newPaymentRecorded, 0, ',', ' ')}</span>
                                                                    : <span class='text-muted'>-</span>
                                                                }
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* Unpaid Details */}
                                    {fikenUpdateResponse.data.details?.unpaid?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-danger fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-clock me-1' />
                                                Unpaid ({fikenUpdateResponse.data.details.unpaid.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Fiken #</th>
                                                        <th>Date</th>
                                                        <th>Due Date</th>
                                                        <th>KID</th>
                                                        <th class='text-end'>Amount</th>
                                                        <th class='text-end'>Outstanding</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fikenUpdateResponse.data.details.unpaid.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.fikenSaleNumber}</td>
                                                            <td>{item.date}</td>
                                                            <td>{item.dueDate}</td>
                                                            <td><code class='text-muted'>{item.kid}</code></td>
                                                            <td class='text-end'>{util.format(item.fikenTotalAmount, 0, ',', ' ')}</td>
                                                            <td class='text-end text-danger'>{util.format(item.outstandingBalance, 0, ',', ' ')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* Payments Created Details */}
                                    {fikenUpdateResponse.data.details?.paymentsCreated?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-primary fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-plus me-1' />
                                                Payments Created ({fikenUpdateResponse.data.details.paymentsCreated.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Fiken #</th>
                                                        <th class='text-end'>Amount</th>
                                                        <th>Type</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fikenUpdateResponse.data.details.paymentsCreated.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.fikenSaleNumber}</td>
                                                            <td class='text-end text-success'>+{util.format(item.amount, 0, ',', ' ')}</td>
                                                            <td>{item.isPartial ? <span class='badge bg-warning text-dark'>Partial</span> : <span class='badge bg-success'>Full</span>}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* KID Updated Details */}
                                    {fikenUpdateResponse.data.details?.kidUpdated?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-info fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-barcode me-1' />
                                                KID Updated ({fikenUpdateResponse.data.details.kidUpdated.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Fiken #</th>
                                                        <th>KID</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fikenUpdateResponse.data.details.kidUpdated.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.fikenSaleNumber}</td>
                                                            <td><code>{item.kid}</code></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* Errors Details */}
                                    {fikenUpdateResponse.data.details?.errors?.length > 0 && <>
                                        <details class='mb-2' open>
                                            <summary class='text-danger fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-exclamation-triangle me-1' />
                                                Errors ({fikenUpdateResponse.data.details.errors.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Faktura #</th>
                                                        <th>Fiken ID</th>
                                                        <th>Kunde</th>
                                                        <th>Dato</th>
                                                        <th>Forfall</th>
                                                        <th class='text-end'>Beløp</th>
                                                        <th class='text-end'>Utestående</th>
                                                        <th>Feil</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fikenUpdateResponse.data.details.errors.map((item, idx) => (
                                                        <tr key={idx} class='table-danger'>
                                                            <td>{item.fikenSaleNumber || <span class='text-muted'>-</span>}</td>
                                                            <td><small class='text-muted'>{item.fikenSaleId}</small></td>
                                                            <td>{item.customerName || <span class='text-muted'>-</span>}</td>
                                                            <td>{item.date || <span class='text-muted'>-</span>}</td>
                                                            <td>{item.dueDate || <span class='text-muted'>-</span>}</td>
                                                            <td class='text-end'>{item.amount ? util.format(item.amount, 0, ',', ' ') : <span class='text-muted'>-</span>}</td>
                                                            <td class='text-end'>{item.amountOutstanding ? util.format(item.amountOutstanding, 0, ',', ' ') : <span class='text-muted'>-</span>}</td>
                                                            <td><small>{item.error}</small></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}

                                    {/* Credit Notes Details */}
                                    {fikenUpdateResponse.data.details?.creditNotes?.length > 0 && <>
                                        <details class='mb-2'>
                                            <summary class='text-secondary fw-bold' style='cursor: pointer;'>
                                                <i class='fa-duotone fa-file-minus me-1' />
                                                Credit Notes ({fikenUpdateResponse.data.details.creditNotes.length})
                                            </summary>
                                            <table class='table table-sm table-striped mt-2' style='font-size: 0.85em;'>
                                                <thead>
                                                    <tr>
                                                        <th>Sale #</th>
                                                        <th>Fiken #</th>
                                                        <th>Date</th>
                                                        <th>KID</th>
                                                        <th class='text-end'>Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fikenUpdateResponse.data.details.creditNotes.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.saleNumber}</td>
                                                            <td>{item.fikenSaleNumber}</td>
                                                            <td>{item.date}</td>
                                                            <td><code class='text-muted'>{item.kid}</code></td>
                                                            <td class='text-end'>{util.format(item.amount, 0, ',', ' ')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    </>}
                                </div>
                            </>}
                        </div>}
                        </>}

                        {/* Compare with Tripletex */}
                        {accountingMode === 'compare' && (
                            <div class='px-2 w-100'>
                                <TripletexComparison
                                    stores={this.props.stores}
                                    onClose={() => {
                                        this.setState({ accountingMode: null });
                                        const { tripletexStore } = this.props.stores;
                                        tripletexStore.clearComparisonData();
                                    }}
                                    onRefresh={this.startTripletexComparison}
                                />
                            </div>
                        )}
                        {/* Open Tripletex posts (payments without an invoice) */}
                        {accountingMode === 'openPosts' && (
                            <div class='px-2 w-100'>
                                <TripletexOpenPosts
                                    stores={this.props.stores}
                                    gotoSale={this.gotoSaleFromAccounting}
                                    getTripletexCreateError={this.getTripletexCreateError}
                                    onClose={() => {
                                        this.setState({ accountingMode: null });
                                        const { tripletexStore } = this.props.stores;
                                        tripletexStore.clearOpenPostsData();
                                    }}
                                    onRefresh={this.startTripletexOpenPosts}
                                />
                            </div>
                        )}
                        {accountingMode === 'voucher' && (
                            <div class='px-2 w-100'>
                                <TripletexVoucherReporting
                                    stores={this.props.stores}
                                    callback={this.props.callback}
                                    setQuickDateFilter={this.props.setQuickDateFilter}
                                    gotoSale={this.gotoSaleFromAccounting}
                                    onClose={() => {
                                        this.setState({ accountingMode: null });
                                        const { tripletexStore } = this.props.stores;
                                        tripletexStore.clearVoucherData();
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Section: PowerOffice */}
                {adminToolbarSection === 'poweroffice' && (
                    <div class='mt-2 mb-2 mx-3 bg-warning rounded-lg p-2'>
                        {/* Header with back button */}
                        <div class='d-flex justify-content-between align-items-center mb-2 px-2'>
                            <button class='btn btn-sm btn-outline-dark rounded-pill' onClick={this.backToAdminMenu}>
                                <i class='fa-solid fa-arrow-left' /> Tilbake
                            </button>
                            <strong>PowerOffice Go</strong>
                            <div style='width: 80px;'></div>
                        </div>

                        {/* Mode Selection */}
                        {!accountingMode && (
                            <div class='d-flex flex-row gap-2 justify-content-center flex-wrap p-3'>
                                <button
                                    class='btn btn-lg btn-dark rounded-pill px-4'
                                    onClick={() => this.setState({ accountingMode: 'transfer' })}
                                >
                                    <i class='fa-solid fa-paper-plane me-2' />
                                    Overfør til PowerOffice
                                </button>
                                <button
                                    class='btn btn-lg btn-success rounded-pill px-4'
                                    onClick={() => this.setState({ accountingMode: 'update' })}
                                >
                                    <i class='fa-solid fa-sync me-2' />
                                    Oppdater fra PowerOffice
                                </button>
                                <button
                                    class='btn btn-lg btn-warning rounded-pill px-4'
                                    onClick={() => this.setState({ accountingMode: 'poVoucher' })}
                                >
                                    <i class='fa-solid fa-file-invoice me-2' />
                                    Bilagsrapportering
                                </button>
                            </div>
                        )}

                        {/* 3-Step Wizard for PowerOffice Transfer */}
                        {accountingMode === 'transfer' && !accountingTransferStep3Done && (
                            <div class='d-flex flex-column p-3 border border-dark rounded'>
                                {/* Step 1: Set Filters */}
                                <div class={`p-2 mb-2 rounded ${accountingTransferStep1Done ? 'bg-success text-white' : 'bg-light'}`}>
                                    <div class='d-flex flex-row align-items-center'>
                                        <div class='me-3'>
                                            {accountingTransferStep1Done
                                                ? <i class='fa-solid fa-check-circle fa-lg' />
                                                : <span class='badge bg-dark rounded-circle' style='width: 24px; height: 24px; line-height: 24px;'>1</span>
                                            }
                                        </div>
                                        <div class='flex-grow-1'>
                                            <strong>Sett opp filtre</strong>
                                            <div><small>Velg betalingsmåte (faktura, kontant, kort, …) og periode</small></div>
                                        </div>
                                        <button
                                            class={`btn btn-sm ${accountingTransferStep1Done ? 'btn-outline-light' : 'btn-success'} rounded-pill`}
                                            onClick={this.setupAccountingTransferFilters}
                                            disabled={settingUpAccountingTransfer}
                                        >
                                            {settingUpAccountingTransfer ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-duotone fa-magic me-1' />}
                                            {accountingTransferStep1Done ? 'Tilbakestill' : 'Sett opp automatisk'}
                                        </button>
                                    </div>
                                    {this.renderWizardFilters({ showStatus: true, showPaymentMethod: true })}
                                </div>

                                {/* Step 2: Check List */}
                                <div class={`p-2 mb-2 rounded ${accountingTransferStep2Done ? 'bg-success text-white' : (accountingTransferStep1Done ? 'bg-light' : 'bg-secondary bg-opacity-25')}`}>
                                    <div class='d-flex flex-row align-items-center'>
                                        <div class='me-3'>
                                            {accountingTransferStep2Done
                                                ? <i class='fa-solid fa-check-circle fa-lg' />
                                                : <span class={`badge ${accountingTransferStep1Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>2</span>
                                            }
                                        </div>
                                        <div class='flex-grow-1'>
                                            <strong>Sjekk listen nedenfor</strong>
                                            <div><small>Sjekk at alle salg i listen nedenfor er korrekte</small></div>
                                            {accountingTransferStep1Done && !accountingTransferStep2Done && accountingTransferCount > 0 && (
                                                <div><small class='text-danger fw-bold'>{accountingTransferCount} klare for overføring</small></div>
                                            )}
                                        </div>
                                        {accountingTransferStep1Done && !accountingTransferStep2Done && (
                                            <button
                                                class='btn btn-sm btn-success rounded-pill'
                                                onClick={this.markAccountingTransferStep2Done}
                                            >
                                                <i class='fa-duotone fa-check me-1' />
                                                Listen er OK
                                            </button>
                                        )}
                                    </div>
                                    {accountingTransferStep1Done && this.renderWizardActiveFiltersSummary()}
                                </div>

                                {/* Step 3: Send to PowerOffice */}
                                <div class={`d-flex flex-row align-items-center p-2 rounded ${accountingTransferStep3Done ? 'bg-success text-white' : (accountingTransferStep2Done ? 'bg-light' : 'bg-secondary bg-opacity-25')}`}>
                                    <div class='me-3'>
                                        {accountingTransferStep3Done
                                            ? <i class='fa-solid fa-check-circle fa-lg' />
                                            : <span class={`badge ${accountingTransferStep2Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>3</span>
                                        }
                                    </div>
                                    <div class='flex-grow-1'>
                                        <strong>Send til PowerOffice</strong>
                                        <div><small>Overfør fakturaer og salg</small></div>
                                        {accountingTransferStep2Done && !accountingTransferStep3Done && accountingTransferCount > 0 && (
                                            <div><small class='text-success fw-bold'>{accountingTransferCount} klare for overføring</small></div>
                                        )}
                                        {accountingTransferStep2Done && !accountingTransferStep3Done && accountingTransferCount === 0 && (
                                            <div><small class='text-warning fw-bold'>Ingen salg å overføre</small></div>
                                        )}
                                    </div>
                                    {accountingTransferStep2Done && !accountingTransferStep3Done && accountingTransferCount > 0 && (
                                        <button
                                            class='btn btn-sm btn-success rounded-pill'
                                            onClick={this.sendAllToPowerOffice}
                                            disabled={sendAllInvoicesToPowerOffice}
                                        >
                                            <i class='fa-duotone fa-paper-plane me-1' />
                                            Send alle
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {accountingMode === 'transfer' && sendAllInvoicesToPowerOffice && totalAccountingTransfers > 0 && (
                            <AccountingTransferProgress
                                current={currentAccountingTransfer}
                                total={totalAccountingTransfers}
                                systemName='PowerOffice'
                                label={currentAccountingTransferLabel}
                                failed={currentAccountingTransferFailed}
                            />
                        )}

                        {/* Success message with Done button */}
                        {accountingMode === 'transfer' && accountingTransferStep3Done && (
                            <div class='mt-2 p-3 bg-success text-white rounded-lg'>
                                <i class='fa-solid fa-check-circle' /> Overføring fullført!
                                {accountingTransferSummary && (
                                    <div class='mt-2'>
                                        <strong>{accountingTransferSummary.totalSent}</strong> salg ble overført til PowerOffice.
                                    </div>
                                )}
                                <div class='mt-3 text-center'>
                                    <button
                                        class='btn btn-lg btn-light text-success fw-bold rounded-pill px-5'
                                        onClick={this.backToAdminMenu}
                                    >
                                        <i class='fa-solid fa-check' /> Ferdig
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Update from PowerOffice */}
                        {accountingMode === 'update' && (
                            <div class='d-flex flex-column px-2 w-100 mb-2'>
                                <button
                                    disabled={updateFromPowerOfficeInvoices}
                                    class='btn btn-success rounded-pill'
                                    onClick={this.updateFromPowerOfficeInvoices}
                                >
                                    <nobr>
                                        {updateFromPowerOfficeInvoices ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-paper-plane' />} Update all invoices from PowerOffice
                                    </nobr><br />
                                    {cutoffDate ? <small class='ms-2'>{util.formatDate(cutoffDate, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}</small> : ''}
                                    {cutoffDateEnd ? <small class='ms-1'> - {util.formatDate(cutoffDateEnd, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}</small> : ''}
                                </button>
                                <small class='text-muted text-center'>
                                    Updating invoices from PowerOffice may take some time. Please be patient.<br />
                                    Choose the date range for the update above.<br />
                                    <span class='bg-danger text-white p-1 rounded'>Note: Only invoices within the selected date range will be updated. Please do not overuse this function.</span>
                                </small>
                                {powerofficeUpdateResponse?.data?.summary && <>
                                    <div class={`d-flex flex-column mx-0 px-2 py-2 mt-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                        <h6 class='mb-2'><i class='fa-duotone fa-chart-bar me-2' />PowerOffice Sync Summary</h6>
                                        <div class='d-flex flex-wrap gap-3'>
                                            <div class='text-center'>
                                                <div class='fw-bold fs-5'>{powerofficeUpdateResponse.data.summary.invoicesFromPowerOffice}</div>
                                                <small class='text-muted'>From PowerOffice</small>
                                            </div>
                                            <div class='text-center'>
                                                <div class='fw-bold fs-5 text-success'>{powerofficeUpdateResponse.data.summary.invoicesMatched}</div>
                                                <small class='text-muted'>Matched</small>
                                            </div>
                                            <div class='text-center'>
                                                <div class='fw-bold fs-5 text-warning'>{powerofficeUpdateResponse.data.summary.invoicesNotFound}</div>
                                                <small class='text-muted'>Not Found</small>
                                            </div>
                                        </div>
                                        <div class='d-flex flex-wrap gap-3 mt-2'>
                                            <div class='text-center'>
                                                <div class='fw-bold fs-5 text-success'>{powerofficeUpdateResponse.data.summary.fullyPaidCount}</div>
                                                <small class='text-muted'>Fully Paid</small>
                                            </div>
                                            <div class='text-center'>
                                                <div class='fw-bold fs-5 text-info'>{powerofficeUpdateResponse.data.summary.partiallyPaidCount}</div>
                                                <small class='text-muted'>Partially Paid</small>
                                            </div>
                                            <div class='text-center'>
                                                <div class='fw-bold fs-5 text-danger'>{powerofficeUpdateResponse.data.summary.unpaidCount}</div>
                                                <small class='text-muted'>Unpaid</small>
                                            </div>
                                            <div class='text-center'>
                                                <div class='fw-bold fs-5 text-primary'>{powerofficeUpdateResponse.data.summary.paymentsCreatedCount}</div>
                                                <small class='text-muted'>Payments Created</small>
                                            </div>
                                        </div>
                                        {powerofficeUpdateResponse.data.summary.errorsCount > 0 && (
                                            <div class='mt-2 text-danger'>
                                                <small><i class='fa-duotone fa-warning' /> {powerofficeUpdateResponse.data.summary.errorsCount} errors</small>
                                            </div>
                                        )}
                                    </div>
                                </>}
                            </div>
                        )}

                        {/* Voucher Reporting (Bilagsrapportering) */}
                        {accountingMode === 'poVoucher' && (
                            <div class='px-2 w-100'>
                                <PowerofficeVoucherReporting
                                    stores={this.props.stores}
                                    callback={this.props.callback}
                                    setQuickDateFilter={this.props.setQuickDateFilter}
                                    onClose={() => {
                                        this.setState({ accountingMode: null });
                                        const { powerofficeStore } = this.props.stores;
                                        powerofficeStore.clearVoucherData();
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Section: Duett */}
                {adminToolbarSection === 'duett' && (
                    <div class='mt-2 mb-2 mx-3 bg-warning rounded-lg p-2'>
                        {/* Header with back button */}
                        <div class='d-flex justify-content-between align-items-center mb-2 px-2'>
                            <button class='btn btn-sm btn-outline-dark rounded-pill' onClick={this.backToAdminMenu}>
                                <i class='fa-solid fa-arrow-left' /> Tilbake
                            </button>
                            <strong>Duett</strong>
                            <div style='width: 80px;'></div>
                        </div>

                        {/* Mode Selection */}
                        {!accountingMode && (
                            <div class='d-flex flex-row gap-2 justify-content-center flex-wrap p-3'>
                                <button
                                    class='btn btn-lg btn-warning rounded-pill px-4'
                                    onClick={() => this.setState({ accountingMode: 'duettVoucher' })}
                                >
                                    <i class='fa-solid fa-file-invoice me-2' />
                                    Bilagsrapportering
                                </button>
                                <button
                                    class='btn btn-lg btn-success rounded-pill px-4'
                                    onClick={() => this.startDuettPaymentSync()}
                                >
                                    <i class='fa-solid fa-sync me-2' />
                                    Oppdater betalinger fra Duett
                                </button>
                                <button
                                    class='btn btn-lg btn-info rounded-pill px-4'
                                    onClick={() => {
                                        this.setState({ accountingMode: 'duettCompare' });
                                        const { duettStore } = this.props.stores;
                                        duettStore.getInvoicesCompare({});
                                    }}
                                >
                                    <i class='fa-solid fa-scale-balanced me-2' />
                                    Sammenlign
                                </button>
                                <button
                                    class='btn btn-lg btn-dark rounded-pill px-4'
                                    onClick={() => this.setState({ accountingMode: 'transfer' })}
                                >
                                    <i class='fa-solid fa-paper-plane me-2' />
                                    Overfør til Duett
                                </button>
                            </div>
                        )}
                        {accountingMode === 'duettVoucher' && (
                            <div class='px-2 w-100'>
                                <DuettVoucherReporting
                                    stores={this.props.stores}
                                    callback={this.props.callback}
                                    setQuickDateFilter={this.props.setQuickDateFilter}
                                    gotoSale={this.gotoSaleFromAccounting}
                                    onClose={() => {
                                        this.setState({ accountingMode: null });
                                        const { duettStore } = this.props.stores;
                                        duettStore.clearVoucherData();
                                    }}
                                />
                            </div>
                        )}
                        {accountingMode === 'duettSync' && (() => {
                            const { duettSyncPreview, duettSyncResult, duettSyncDone } = this.state;
                            const preview = duettSyncPreview?.summary;
                            return (
                                <div class='px-2 w-100'>
                                    <div class='d-flex flex-column p-3 border border-dark rounded'>
                                        <div class='alert alert-info py-2 mb-3'>
                                            <i class='fa-solid fa-circle-info me-1' />
                                            Henter betalingsstatus fra kundereskontroen i Duett, sammen med ordrestatus og bekreftet utsending.
                                            Fakturaer som er betalt (eller delbetalt) i Duett får betalingen registrert her i Dyrejournal.
                                        </div>
                                        {preview && !duettSyncDone && <>
                                            <ul class='list-group mb-3'>
                                                <li class='list-group-item d-flex justify-content-between align-items-center'>
                                                    Betalt i Duett, blir markert betalt
                                                    <span class='badge bg-success rounded-pill'>{preview.paidCount}</span>
                                                </li>
                                                <li class='list-group-item d-flex justify-content-between align-items-center'>
                                                    Delbetalt i Duett, delbetaling registreres
                                                    <span class='badge bg-info rounded-pill'>{preview.partiallyPaidCount}</span>
                                                </li>
                                                <li class='list-group-item d-flex justify-content-between align-items-center'>
                                                    Fortsatt ubetalt i Duett
                                                    <span class='badge bg-primary rounded-pill'>{preview.stillUnpaidCount}</span>
                                                </li>
                                                <li class='list-group-item d-flex justify-content-between align-items-center'>
                                                    Får ny ordrestatus
                                                    <span class='badge bg-secondary rounded-pill'>{preview.statusUpdatedCount}</span>
                                                </li>
                                                <li class='list-group-item d-flex justify-content-between align-items-center'>
                                                    Får bekreftet utsending
                                                    <span class='badge bg-secondary rounded-pill'>{preview.dispatchUpdatedCount}</span>
                                                </li>
                                                {preview.ledgerUnmatchedCount > 0 && (
                                                    <li class='list-group-item d-flex justify-content-between align-items-center'>
                                                        Ikke funnet i kundereskontroen (sjekkes ikke)
                                                        <span class='badge bg-warning text-dark rounded-pill'>{preview.ledgerUnmatchedCount}</span>
                                                    </li>
                                                )}
                                                {preview.ledgerSearchFailedCount > 0 && (
                                                    <li class='list-group-item d-flex justify-content-between align-items-center'>
                                                        Reskontro-oppslag feilet, ingen betalinger hentes
                                                        <span class='badge bg-warning text-dark rounded-pill'>{preview.ledgerSearchFailedCount}</span>
                                                    </li>
                                                )}
                                                <li class='list-group-item d-flex justify-content-between align-items-center'>
                                                    Ikke funnet i Duett
                                                    <span class='badge bg-danger rounded-pill'>{preview.notFoundCount}</span>
                                                </li>
                                                <li class='list-group-item d-flex justify-content-between align-items-center'>
                                                    Betalt i Dyrejournal (ikke sjekket)
                                                    <span class='badge bg-dark rounded-pill'>{preview.skippedAlreadyPaidCount}</span>
                                                </li>
                                                {preview.searchFailedCount > 0 && (
                                                    <li class='list-group-item d-flex justify-content-between align-items-center'>
                                                        Feilet Duett-oppslag
                                                        <span class='badge bg-warning text-dark rounded-pill'>{preview.searchFailedCount}</span>
                                                    </li>
                                                )}
                                            </ul>
                                            <div class='d-flex justify-content-between'>
                                                <button class='btn btn-outline-dark rounded-pill' onClick={this.backToAdminMenu}>
                                                    <i class='fa-solid fa-arrow-left' /> Tilbake
                                                </button>
                                                {(preview.paidCount + preview.partiallyPaidCount + preview.stillUnpaidCount) > 0 && (
                                                    <button
                                                        class='btn btn-success rounded-pill'
                                                        onClick={this.confirmDuettPaymentSync}
                                                    >
                                                        <i class='fa-solid fa-check me-1' />
                                                        Oppdater ({preview.paidCount + preview.partiallyPaidCount} betalinger, {preview.stillUnpaidCount} ubetalte)
                                                    </button>
                                                )}
                                            </div>
                                        </>}
                                        {duettSyncDone && duettSyncResult && (
                                            <div class='p-3 bg-success text-white rounded-lg'>
                                                <i class='fa-solid fa-check-circle' /> Oppdatering fullført!
                                                <div class='mt-2'>
                                                    <strong>{duettSyncResult.summary?.paymentsCreatedCount || 0}</strong> betalinger registrert{' '}
                                                    (<strong>{duettSyncResult.summary?.paidCount || 0}</strong> salg markert betalt,{' '}
                                                    <strong>{duettSyncResult.summary?.partiallyPaidCount || 0}</strong> delbetalt),{' '}
                                                    <strong>{duettSyncResult.summary?.stillUnpaidCount || 0}</strong> fortsatt ubetalt i Duett.{' '}
                                                    <strong>{duettSyncResult.summary?.statusUpdatedCount || 0}</strong> salg fikk ny ordrestatus,{' '}
                                                    <strong>{duettSyncResult.summary?.dispatchUpdatedCount || 0}</strong> fikk bekreftet utsending.
                                                    {duettSyncResult.summary?.paymentsFailedCount > 0 && (
                                                        <div class='mt-1'>
                                                            <i class='fa-solid fa-triangle-exclamation' />{' '}
                                                            <strong>{duettSyncResult.summary.paymentsFailedCount}</strong> betalinger kunne ikke lagres.
                                                        </div>
                                                    )}
                                                    {duettSyncResult.summary?.ledgerSearchFailedCount > 0 && (
                                                        <div class='mt-1'>
                                                            <i class='fa-solid fa-triangle-exclamation' />{' '}
                                                            Reskontro-oppslaget mot Duett feilet, ingen betalinger ble hentet.
                                                        </div>
                                                    )}
                                                    {duettSyncResult.summary?.searchFailedCount > 0 && (
                                                        <div class='mt-1'>
                                                            <i class='fa-solid fa-triangle-exclamation' />{' '}
                                                            <strong>{duettSyncResult.summary.searchFailedCount}</strong> Duett-oppslag feilet.
                                                        </div>
                                                    )}
                                                </div>
                                                <div class='mt-3 text-center'>
                                                    <button
                                                        class='btn btn-lg btn-light text-success fw-bold rounded-pill px-5'
                                                        onClick={this.backToAdminMenu}
                                                    >
                                                        <i class='fa-solid fa-check' /> Ferdig
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                        {accountingMode === 'duettCompare' && (
                            <div class='px-2 w-100'>
                                <DuettComparison
                                    stores={this.props.stores}
                                    onClose={() => {
                                        this.setState({ accountingMode: null });
                                        this.props.stores.duettStore.clearComparisonData();
                                    }}
                                    onRefresh={() => this.props.stores.duettStore.getInvoicesCompare({})}
                                />
                            </div>
                        )}

                        {/* 3-Step Wizard for Duett bulk invoice send */}
                        {accountingMode === 'transfer' && !accountingTransferStep3Done && (
                            <div class='d-flex flex-column p-3 border border-dark rounded'>
                                {/* Step 1: Set Filters */}
                                <div class={`p-2 mb-2 rounded ${accountingTransferStep1Done ? 'bg-success text-white' : 'bg-light'}`}>
                                    <div class='d-flex flex-row align-items-center'>
                                        <div class='me-3'>
                                            {accountingTransferStep1Done
                                                ? <i class='fa-solid fa-check-circle fa-lg' />
                                                : <span class='badge bg-dark rounded-circle' style='width: 24px; height: 24px; line-height: 24px;'>1</span>
                                            }
                                        </div>
                                        <div class='flex-grow-1'>
                                            <strong>Sett opp filtre</strong>
                                            <div><small>Fakturasalg og periode (kontant/kort går via dagsoppgjør-bilag)</small></div>
                                        </div>
                                        <button
                                            class={`btn btn-sm ${accountingTransferStep1Done ? 'btn-outline-light' : 'btn-success'} rounded-pill`}
                                            onClick={this.setupAccountingTransferFilters}
                                            disabled={settingUpAccountingTransfer}
                                        >
                                            {settingUpAccountingTransfer ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-duotone fa-magic me-1' />}
                                            {accountingTransferStep1Done ? 'Tilbakestill' : 'Sett opp automatisk'}
                                        </button>
                                    </div>
                                    {this.renderWizardFilters({ showStatus: true, showPaymentMethod: true })}
                                </div>

                                {/* Step 2: Check List */}
                                <div class={`p-2 mb-2 rounded ${accountingTransferStep2Done ? 'bg-success text-white' : (accountingTransferStep1Done ? 'bg-light' : 'bg-secondary bg-opacity-25')}`}>
                                    <div class='d-flex flex-row align-items-center'>
                                        <div class='me-3'>
                                            {accountingTransferStep2Done
                                                ? <i class='fa-solid fa-check-circle fa-lg' />
                                                : <span class={`badge ${accountingTransferStep1Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>2</span>
                                            }
                                        </div>
                                        <div class='flex-grow-1'>
                                            <strong>Sjekk listen</strong>
                                            <div><small>Kontroller at fakturasalgene under stemmer</small></div>
                                            {accountingTransferStep1Done && !accountingTransferStep2Done && accountingTransferCount > 0 && (
                                                <div><small class='text-success fw-bold'>{accountingTransferCount} klare for overføring</small></div>
                                            )}
                                        </div>
                                        {accountingTransferStep1Done && !accountingTransferStep2Done && (
                                            <button
                                                class='btn btn-sm btn-success rounded-pill'
                                                onClick={this.markAccountingTransferStep2Done}
                                            >
                                                <i class='fa-solid fa-check me-1' /> Listen stemmer
                                            </button>
                                        )}
                                    </div>
                                    {accountingTransferStep1Done && this.renderWizardActiveFiltersSummary()}
                                </div>

                                {/* Step 3: Send to Duett */}
                                <div class={`d-flex flex-row align-items-center p-2 rounded ${accountingTransferStep3Done ? 'bg-success text-white' : (accountingTransferStep2Done ? 'bg-light' : 'bg-secondary bg-opacity-25')}`}>
                                    <div class='me-3'>
                                        {accountingTransferStep3Done
                                            ? <i class='fa-solid fa-check-circle fa-lg' />
                                            : <span class={`badge ${accountingTransferStep2Done ? 'bg-dark' : 'bg-secondary'} rounded-circle`} style='width: 24px; height: 24px; line-height: 24px;'>3</span>
                                        }
                                    </div>
                                    <div class='flex-grow-1'>
                                        <strong>Send til Duett</strong>
                                        <div><small>Overfør fakturaene som direktefaktura i Duett</small></div>
                                        {accountingTransferStep2Done && !accountingTransferStep3Done && accountingTransferCount > 0 && (
                                            <div><small class='text-success fw-bold'>{accountingTransferCount} klare for overføring</small></div>
                                        )}
                                        {accountingTransferStep2Done && !accountingTransferStep3Done && accountingTransferCount === 0 && (
                                            <div><small class='text-warning fw-bold'>Ingen salg å overføre</small></div>
                                        )}
                                    </div>
                                    {accountingTransferStep2Done && !accountingTransferStep3Done && accountingTransferCount > 0 && (
                                        <button
                                            class='btn btn-sm btn-success rounded-pill'
                                            onClick={this.sendAllToDuett}
                                            disabled={sendAllInvoicesToDuett}
                                        >
                                            <i class='fa-duotone fa-paper-plane me-1' />
                                            Send alle
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {accountingMode === 'transfer' && sendAllInvoicesToDuett && totalAccountingTransfers > 0 && (
                            <AccountingTransferProgress
                                current={currentAccountingTransfer}
                                total={totalAccountingTransfers}
                                systemName='Duett'
                                label={currentAccountingTransferLabel}
                                failed={currentAccountingTransferFailed}
                            />
                        )}

                        {/* Success message with failure list and Done button */}
                        {accountingMode === 'transfer' && accountingTransferStep3Done && (
                            <div class='mt-2 p-3 bg-success text-white rounded-lg'>
                                <i class='fa-solid fa-check-circle' /> Overføring fullført!
                                {accountingTransferSummary && (
                                    <div class='mt-2'>
                                        <strong>{accountingTransferSummary.totalSent}</strong> fakturaer ble overført til Duett.
                                        {accountingTransferSummary.failed?.length > 0 && (
                                            <div class='mt-2 p-2 bg-danger bg-opacity-25 rounded text-start'>
                                                <strong><i class='fa-solid fa-triangle-exclamation' /> {accountingTransferSummary.failed.length} feilet:</strong>
                                                <ul class='mb-0 mt-1'>
                                                    {accountingTransferSummary.failed.map(f => (
                                                        <li key={f.saleId}><strong>#{f.saleNumber}</strong>: {f.message}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div class='mt-3 text-center'>
                                    <button
                                        class='btn btn-lg btn-light text-success fw-bold rounded-pill px-5'
                                        onClick={this.backToAdminMenu}
                                    >
                                        <i class='fa-solid fa-check' /> Ferdig
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Section: Medical History */}
                {adminToolbarSection === 'medicalHistory' && (
                    <div class='mt-2 mb-2 mx-3 bg-warning rounded-lg p-2' style='overflow: visible;'>
                        <div class='d-flex justify-content-between align-items-center mb-2 px-2'>
                            <button class='btn btn-sm btn-outline-dark rounded-pill' onClick={this.backToAdminMenu}>
                                <i class='fa-solid fa-arrow-left' /> Tilbake
                            </button>
                            <strong>Medisinsk historikk</strong>
                            <div style='width: 80px;'></div>
                        </div>

                        {/* Mode toggle */}
                        {medicalHistoryStep === 1 && (
                            <div class='d-flex justify-content-center mb-2'>
                                <div class='btn-group' role='group'>
                                    <button
                                        class={`btn btn-sm ${medicalHistoryMode === 'batch' ? 'btn-dark' : 'btn-outline-dark'}`}
                                        onClick={() => this.setState({ medicalHistoryMode: 'batch' })}
                                    >
                                        <i class='fa-duotone fa-users' /> Batch
                                    </button>
                                    <button
                                        class={`btn btn-sm ${medicalHistoryMode === 'single' ? 'btn-dark' : 'btn-outline-dark'}`}
                                        onClick={() => this.setState({ medicalHistoryMode: 'single' })}
                                    >
                                        <i class='fa-duotone fa-paw' /> Enkeltdyr
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Single animal mode */}
                        {medicalHistoryStep === 1 && medicalHistoryMode === 'single' && (
                            <div class='d-flex flex-column p-3 border border-dark rounded' style='overflow: visible;'>
                                <p class='mb-2'><small>Søk etter eier, velg dyr, og send medisinsk historikk via e-post.</small></p>

                                <div class='mb-2'>
                                    <strong>Søk etter eier:</strong>
                                    <div class='input-group mt-1'>
                                        <input
                                            type='text'
                                            class='form-control'
                                            placeholder='Navn, telefon eller e-post...'
                                            value={this.state.medicalHistoryVisitorSearch || ''}
                                            onInput={(e) => {
                                                const val = e.target.value;
                                                this.setState({ medicalHistoryVisitorSearch: val });
                                                if (this._visitorSearchTimer) clearTimeout(this._visitorSearchTimer);
                                                if (val.length >= 3) {
                                                    this._visitorSearchTimer = setTimeout(async () => {
                                                        const { visitorStore } = this.props.stores;
                                                        const results = await visitorStore.load({ query: { search: val }, skipUpdate: true });
                                                        this.setState({ medicalHistoryVisitorSearchResults: results || [] });
                                                    }, 300);
                                                } else {
                                                    this.setState({ medicalHistoryVisitorSearchResults: [] });
                                                }
                                            }}
                                        />
                                    </div>
                                    {this.state.medicalHistoryVisitorSearchResults && this.state.medicalHistoryVisitorSearchResults.length > 0 && !medicalHistorySingleVisitor && (
                                        <div class='list-group mt-1'>
                                            {this.state.medicalHistoryVisitorSearchResults.map(v => (
                                                <button
                                                    key={v.id}
                                                    type='button'
                                                    class='list-group-item list-group-item-action'
                                                    onClick={() => {
                                                        this.setState({ medicalHistoryVisitorSearchResults: [] });
                                                        this.onSelectVisitorForMedicalHistory(v);
                                                    }}
                                                >
                                                    <strong>{v.firstname} {v.lastname}</strong>
                                                    {v.cellphone ? <span class='ms-2 text-body-secondary'>{v.cellphone}</span> : null}
                                                    {v.email ? <span class='ms-2 text-body-secondary'>{v.email}</span> : null}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {!!medicalHistorySingleVisitor && (
                                    <div class='mb-2 p-2 bg-body-secondary rounded'>
                                        <strong>Eier:</strong> {medicalHistorySingleVisitor.firstname} {medicalHistorySingleVisitor.lastname}
                                        {medicalHistorySingleVisitor.email
                                            ? <span class='ms-2 text-body-secondary'>{medicalHistorySingleVisitor.email}</span>
                                            : <span class='ms-2 badge bg-danger'>Ingen e-post</span>
                                        }
                                    </div>
                                )}

                                {medicalHistorySingleAnimals.length > 0 && (
                                    <div class='mb-2'>
                                        <strong>Velg dyr:</strong>
                                        <div class='d-flex flex-wrap gap-2 mt-1'>
                                            {medicalHistorySingleAnimals.map(a => (
                                                <button
                                                    key={a.id}
                                                    class={`btn btn-sm ${medicalHistorySingleAnimal && medicalHistorySingleAnimal.id === a.id ? 'btn-dark' : 'btn-outline-dark'}`}
                                                    onClick={() => this.setState({ medicalHistorySingleAnimal: a })}
                                                >
                                                    <i class='fa-duotone fa-paw' /> {a.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {!!medicalHistorySingleAnimal && (
                                    <div class='mb-2'>
                                        <strong>Periode:</strong>
                                        <div class='d-flex align-items-center gap-2 mt-1'>
                                            <label class='d-flex align-items-center gap-1' style='cursor: pointer;'>
                                                <input
                                                    type='radio'
                                                    name='medHistPeriod'
                                                    checked={medicalHistorySingleAllTime}
                                                    onChange={() => this.setState({ medicalHistorySingleAllTime: true })}
                                                />
                                                All historikk
                                            </label>
                                            <label class='d-flex align-items-center gap-1' style='cursor: pointer;'>
                                                <input
                                                    type='radio'
                                                    name='medHistPeriod'
                                                    checked={!medicalHistorySingleAllTime}
                                                    onChange={() => this.setState({ medicalHistorySingleAllTime: false })}
                                                />
                                                Velg periode
                                            </label>
                                        </div>
                                        {!medicalHistorySingleAllTime && (
                                            <div class='d-flex gap-2 mt-1'>
                                                <input
                                                    type='date'
                                                    class='form-control form-control-sm'
                                                    value={medicalHistorySingleStartDate}
                                                    onChange={(e) => this.setState({ medicalHistorySingleStartDate: e.target.value })}
                                                />
                                                <input
                                                    type='date'
                                                    class='form-control form-control-sm'
                                                    value={medicalHistorySingleEndDate}
                                                    onChange={(e) => this.setState({ medicalHistorySingleEndDate: e.target.value })}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!!this.state.medicalHistorySingleError && (
                                    <div class='mt-2 p-2 bg-danger text-white rounded'>
                                        <i class='fa-solid fa-exclamation-triangle' /> {this.state.medicalHistorySingleError}
                                    </div>
                                )}

                                {medicalHistorySending && (
                                    <div class='mt-2 p-3 bg-danger text-white rounded-lg'>
                                        <i class='fa-duotone fa-spinner fa-spin' /> Sender medisinsk historikk...<br />
                                        <strong>IKKE LUKK DETTE VINDUET!</strong>
                                    </div>
                                )}

                                {medicalHistorySingleSent && (
                                    <div class='mt-2 p-3 bg-success text-white rounded-lg'>
                                        <i class='fa-solid fa-check-circle' /> Medisinsk historikk sendt til {medicalHistorySingleVisitor?.email}
                                        <button
                                            class='btn btn-sm btn-outline-light ms-3'
                                            onClick={() => this.setState({
                                                medicalHistorySingleSent: false,
                                                medicalHistorySingleAnimal: null,
                                                medicalHistorySendResult: null,
                                            })}
                                        >
                                            Send for et annet dyr
                                        </button>
                                    </div>
                                )}

                                {!medicalHistorySingleSent && (
                                    <button
                                        class='btn btn-success rounded-pill mt-2'
                                        disabled={!medicalHistorySingleAnimal || !medicalHistorySingleVisitor?.email || medicalHistorySending}
                                        onClick={this.sendSingleAnimalMedicalHistory}
                                    >
                                        {medicalHistorySending ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-paper-plane' />}
                                        {' '}Send medisinsk historikk
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Batch mode - Step 1: Set filters */}
                        {medicalHistoryStep === 1 && medicalHistoryMode === 'batch' && (
                            <div class='d-flex flex-column p-3 border border-dark rounded'>
                                <p class='mb-2'><small>Velg periode og dyrearter for å sende medisinsk historikk til dyreeiere via e-post.</small></p>

                                <div class='mb-2'>
                                    <strong>Periode:</strong>{' '}
                                    <span>{cutoffDate || '\u2013'} til {cutoffDateEnd || '\u2013'}</span>
                                    <button class='btn btn-sm btn-outline-dark ms-2' onClick={this.setupMedicalHistoryFilters} disabled={medicalHistoryLoading}>
                                        {medicalHistoryLoading ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-calendar' />}
                                        {' '}Forrige måned
                                    </button>
                                </div>

                                {medicalHistorySpeciesInPeriod.length > 0 && (
                                    <div class='mb-3'>
                                        <strong>Dyrearter i perioden:</strong>
                                        <div class='d-flex flex-wrap gap-2 mt-1'>
                                            {medicalHistorySpeciesInPeriod.map(sp => (
                                                <label key={sp.speciesId} class='d-flex align-items-center gap-1' style='cursor: pointer;'>
                                                    <input
                                                        type='checkbox'
                                                        checked={medicalHistorySelectedSpecies.includes(sp.speciesId)}
                                                        onChange={() => this.toggleMedicalHistorySpecies(sp.speciesId)}
                                                    />
                                                    {sp.name}
                                                </label>
                                            ))}
                                        </div>
                                        <small class='text-body-secondary'>
                                            {medicalHistorySelectedSpecies.length === 0 ? 'Alle arter er valgt' : `${medicalHistorySelectedSpecies.length} art(er) valgt`}
                                        </small>
                                    </div>
                                )}

                                {medicalHistorySpeciesInPeriod.length === 0 && !medicalHistoryLoading && !!cutoffDate && (
                                    <div class='mb-3'>
                                        <small class='text-body-secondary'>Ingen journaler funnet i perioden.</small>
                                    </div>
                                )}

                                <button
                                    class='btn btn-success rounded-pill'
                                    disabled={medicalHistoryLoading || !cutoffDate || !cutoffDateEnd}
                                    onClick={async () => {
                                        await this.loadMedicalHistoryPreview();
                                        const { medicalHistoryPreview } = this.state;
                                        if (medicalHistoryPreview) {
                                            this.setState({ medicalHistoryStep: 2 });
                                        }
                                    }}
                                >
                                    {medicalHistoryLoading ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-arrow-right' />}
                                    {' '}Neste
                                </button>
                            </div>
                        )}

                        {/* Step 2: Review list */}
                        {medicalHistoryStep === 2 && !!medicalHistoryPreview && (
                            <div class='d-flex flex-column p-3 border border-dark rounded'>
                                <div class='mb-2 p-2 bg-body-secondary rounded'>
                                    <strong>{medicalHistoryPreview.summary.totalAnimals}</strong> dyr funnet.{' '}
                                    <span class='text-success fw-bold'>{medicalHistoryPreview.summary.withEmail}</span> med e-post,{' '}
                                    <span class='text-danger fw-bold'>{medicalHistoryPreview.summary.withoutEmail}</span> uten e-post.
                                </div>

                                <div class='table-responsive' style='max-height: 400px; overflow-y: auto;'>
                                    <table class='table table-sm table-striped'>
                                        <thead>
                                            <tr>
                                                <th>Dyr</th>
                                                <th>Art</th>
                                                <th>Eier</th>
                                                <th>E-post</th>
                                                <th>Journaler</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {medicalHistoryPreview.data.map(item => (
                                                <tr key={item.animal.id} class={!item.owner || !item.owner.email ? 'text-body-secondary' : ''}>
                                                    <td>{item.animal.name}</td>
                                                    <td>{item.animal.species}</td>
                                                    <td>{item.owner ? `${item.owner.firstname} ${item.owner.lastname}` : '\u2013'}</td>
                                                    <td>
                                                        {item.owner && item.owner.email
                                                            ? item.owner.email
                                                            : <span class='badge bg-danger'>Ingen e-post</span>
                                                        }
                                                    </td>
                                                    <td>{item.journalCount}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {medicalHistorySending && (
                                    <div class='mt-2 p-3 bg-danger text-white rounded-lg'>
                                        <i class='fa-duotone fa-spinner fa-spin' /> Sender {medicalHistoryPreview.summary.withEmail} e-poster... Dette kan ta litt tid.<br />
                                        <strong>IKKE LUKK DETTE VINDUET!</strong>
                                    </div>
                                )}

                                <div class='d-flex justify-content-between mt-2'>
                                    <button
                                        class='btn btn-outline-dark rounded-pill'
                                        disabled={medicalHistorySending}
                                        onClick={() => this.setState({ medicalHistoryStep: 1 })}
                                    >
                                        <i class='fa-solid fa-arrow-left' /> Tilbake
                                    </button>
                                    <button
                                        class='btn btn-danger rounded-pill'
                                        disabled={medicalHistorySending || medicalHistoryPreview.summary.withEmail === 0}
                                        onClick={this.sendMedicalHistoryEmails}
                                    >
                                        {medicalHistorySending ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-paper-plane' />}
                                        {' '}Send {medicalHistoryPreview.summary.withEmail} e-poster
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Result */}
                        {medicalHistoryStep === 3 && !!medicalHistorySendResult && (
                            <div class='d-flex flex-column p-3 border border-dark rounded'>
                                <div class='p-3 bg-success text-white rounded-lg'>
                                    <i class='fa-solid fa-check-circle' /> Ferdig!
                                    <div class='mt-2'>
                                        <strong>{medicalHistorySendResult.sent}</strong> e-poster sendt.
                                        {medicalHistorySendResult.skipped > 0 && (
                                            <span> <strong>{medicalHistorySendResult.skipped}</strong> dyr hoppet over.</span>
                                        )}
                                    </div>
                                </div>

                                {medicalHistorySendResult.skippedAnimals && medicalHistorySendResult.skippedAnimals.length > 0 && (
                                    <div class='mt-2'>
                                        <small class='fw-bold'>Hoppet over:</small>
                                        <ul class='mb-0'>
                                            {medicalHistorySendResult.skippedAnimals.map(a => (
                                                <li key={a.id}><small>{a.name} &mdash; {a.reason}</small></li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div class='mt-3 text-center'>
                                    <button
                                        class='btn btn-lg btn-light text-success fw-bold rounded-pill px-5'
                                        onClick={this.backToAdminMenu}
                                    >
                                        <i class='fa-solid fa-check' /> Ferdig
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}


            </>}

            <div class={`d-flex flex-column mx-3 mt-2 px-3 py-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class={`table-responsive ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} mt-3`}>
                    <table class='table table-sm table-striped mb-0 font-style-narrow' style='font-size: 0.9em;'>
                        <thead>
                            <tr>
                                <th>&nbsp;</th>
                                <th
                                    class={`d-none d-sm-table-cell text-center ${['saleNumber', '-saleNumber'].includes(sort) ? 'text-primary' : ''}`}
                                    style={`cursor: pointer;`}
                                    onClick={this.setSortField}
                                    data-field='saleNumber'
                                >
                                    <Text id='dyrejournal.id'>Id</Text>
                                    {sort === '-saleNumber' ? <i class='fa-solid fa-caret-down ms-2' /> : ''}
                                    {sort === 'saleNumber' ? <i class='fa-solid fa-caret-up ms-2' /> : ''}
                                </th>
                                {showCompanyColumn && <th class='d-none d-sm-table-cell text-center'><Text id='dyrejournal.customer'>Customer</Text></th>}
                                {/* <th><Text id='dyrejournal.createdDate'>CreatedDate</Text></th> */}
                                <th
                                    class={`${['saleDate', '-saleDate'].includes(sort) ? 'text-primary' : ''}`}
                                    style={`cursor: pointer;`}
                                    onClick={this.setSortField}
                                    data-field='saleDate'
                                >
                                    <Text id='dyrejournal.saleDate'>SaleDate</Text>
                                    {sort === '-saleDate' ? <i class='fa-solid fa-caret-down ms-2' /> : ''}
                                    {sort === 'saleDate' ? <i class='fa-solid fa-caret-up ms-2' /> : ''}
                                </th>
                                <th><Text id='dyrejournal.sale.seller'>Seller</Text></th>
                                <th
                                    class={`${['title', '-title'].includes(sort) ? 'text-primary' : ''}`}
                                    style={`cursor: pointer;`}
                                    onClick={this.setSortField}
                                    data-field='title'
                                >
                                    <Text id='dyrejournal.title'>Title</Text>
                                    {sort === '-title' ? <i class='fa-solid fa-caret-down ms-2' /> : ''}
                                    {sort === 'title' ? <i class='fa-solid fa-caret-up ms-2' /> : ''}
                                </th>
                                {/* <th class='text-center'><Text id='dyrejournal.animals'>Animals</Text></th>
                                <th class='text-center'><Text id='dyrejournal.journals'>Journals</Text></th> */}
                                <th class='text-center' style='width: 1%;'><Text id='dyrejournal.insurance'>Insurance</Text></th>
                                <th class='text-start'><Text id='dyrejournal.status'>Status</Text></th>
                                <th class='text-start'><Text id='dyrejournal.payment-method'>Payment method</Text></th>
                                <th class='text-center'><Text id='dyrejournal.total'>Total</Text></th>
                                <th class='text-center'><Text id='dyrejournal.rest'>Rest</Text></th>
                                <th class='text-center'><Text id='dyrejournal.kid'>KID</Text></th>
                                {(!isDebitorList && showPayments) && <th class='text-center'><Text id='dyrejournal.payments'>Payments</Text></th>}
                                {showFiken && <th class='text-center'>Fiken</th>}
                                {showTripletex && <th class='text-center'>Tripletex</th>}
                                {showPowerOffice && <th class='text-center'>PowerOffice</th>}
                                {showDuett && <th class='text-center'>Duett</th>}
                                {showAnimalia && <th class='text-center'>DHP</th>}
                                {showDyreId && <th class='text-center'>DyreId</th>}
                                {showDelete && <th class='text-center'>&nbsp;</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {(finalSales && finalSales.length > 0) && finalSales.map((e, idx) => {
                                const saleStatus = saleStore.getSaleStatusById(e.status);
                                const saleStatusText = saleStore.getSaleStatusById(e.status, language);
                                const saleStatusIcon = saleStore.getSaleStatusIcon(saleStatus);
                                const saleStatusClass = saleStore.getSaleStatusClass(saleStatus);
                                const salePayments = allPayments.filter(p => p.sale === e.id);
                                const sumTotal = saleStore.sumTotal(e.products, false);
                                const tripletexInvoice = e.tripletexInvoice || {};
                                const powerofficeInvoice = e.powerOfficeInvoice || {};

                                let paymentRestAmount = 0;
                                const hasPayments = salePayments && salePayments.length > 0;
                                if (hasPayments) {
                                    paymentRestAmount = saleStore.sumRestPayments(salePayments, saleStore.sumTotal(e.products, false), false, false);
                                }

                                if (isDebitorList) {
                                    sumRestPayment += e.restPayment;
                                }

                                let saleIsPaid = (!!e.paidDate && !!e.paymentMethod);
                                if (paymentRestAmount > 0) {
                                    saleIsPaid = false;
                                }

                                if (e.paymentMethod === 'creditNote'
                                    || e.paymentMethod === 'invoiceCredited'
                                    || e.paymentMethod === 'saleCredited'
                                    // || e.status > 1000
                                ) {
                                    saleIsPaid = true;
                                }
                                // Mark sale line as overdue if not paid within 2 days
                                const isOverDue = !saleIsPaid && now > new Date(e.saleDate).getTime() + (2 * 24 * 60 * 60 * 1000);
                                const isAllowedToDelete = !e.saleNumber;
                                const hasJournals = e.journals && e.journals.length > 0;
                                // Get all diagnoses from journals
                                const allDiagnoses = e.journals ? e.journals.reduce((acc, journal) => {
                                    if (journal.diagnoses && journal.diagnoses.length > 0) {
                                        return acc.concat(journal.diagnoses);
                                    }
                                    return acc;
                                }, []) : [];
                                const allMedicines = e.journals ? e.journals.reduce((acc, journal) => {
                                    if (journal.medicine && journal.medicine.length > 0) {
                                        return acc.concat(journal.medicine);
                                    }
                                    return acc;
                                }, []) : [];
                                const isCreditNote = e.paymentMethod === 'creditNote' || e.paymentMethod === 'saleCredited' || e.paymentMethod === 'invoiceCredited';

                                const hasProductsWithDiscount = e.products && e.products.length > 0 && e.products.some(p => p.discount > 0 || p.discountAmount > 0);
                                const hasDiagnoses = hasJournalWithDiagnoses(e);
                                const hasMedicines = hasJournalWithMedicines(e);
                                const saleHasDhpAnimals = hasDhpRelevantAnimals(e);
                                // Distinct species (art) for this visit, so the overview shows e.g.
                                // "Hest" vs "Katt" at a glance without opening each journal (TICKET-01184).
                                // Many embedded animals carry only speciesId, not the species name,
                                // so fall back to the speciesId -> name map.
                                const saleSpeciesLabel = Array.isArray(e.animals)
                                    ? [...new Set(e.animals
                                        .map(a => a?.species || speciesNameById[parseInt(a?.speciesId, 10)] || '')
                                        .filter(Boolean))].join(', ')
                                    : '';
                                const hasPrescriptions = hasJournalWithPrescriptions(e);
                                const idexxOrderCount = e.journals ? e.journals.reduce((sum, j) => (
                                    sum + (Array.isArray(j.idexxOrders) ? j.idexxOrders.length : 0)
                                ), 0) : 0;
                                const employee = saleStore.getEmployee(e.user) || {};

                                const hasInsideProducts = e.products && e.products.length > 0 && e.products.some(p => p.isGlobalSubscription);
                                // TODO: This saleUser should be renamed to saleCustomer to avoid confusion with original saleUser on each sale line.
                                const saleCustomer = saleUsers.find(u => u.id === e.user) || {};

                                const department = customer.departments?.find(d => d.id === e.customerDepartment) || {};
                                const isReadyForPickup = e.readyForPickup ? true : false;
                                const saleIsForeign = isForeignSale(e, userStore);
                                const textColor = e.color ? localUtil.isDark(e.color) ? 'text-light' : 'text-dark' : '';

                                return (<>
                                    <tr
                                        onClick={this.onClickViewVisit}
                                        data-id={e.id}
                                        class={`
                                            ${e.offline ? 'deleted text-muted' : ''}
                                            ${saleLineColors && !e.color ? `
                                                ${saleIsPaid ? 'table-success' : ''}
                                                ${isOverDue && !saleIsPaid ? 'table-danger' : ''}
                                                ${isReadyForPickup && !saleIsPaid ? 'table-primary' : ''}
                                            ` : ''}
                                        `}
                                        style={`
                                            cursor: pointer;
                                        `}
                                        // ${isReadyForPickup && !saleIsPaid ? '--bs-table-bg: rgba(var(--bs-success-rgb), 0.15); --bs-table-accent-bg: transparent;' : ''}
                                        title={e.offline ? 'This sale is deleted/removed' : ''}
                                    >
                                        <td
                                            class={`${textColor} font-weight-lighter text-muted`}
                                            style={`
                                                ${e.color ? `background-color: ${e.color} !important;` : ''}
                                            `}
                                        >
                                            {idx + 1}
                                        </td>
                                        <td
                                            class={`${textColor} d-none d-sm-table-cell text-center`}
                                            style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}
                                        >
                                            <nobr>
                                                &nbsp;
                                                {e.saleNumber ? <>
                                                    {e.saleNumber}
                                                    {e.id > 0 && e.id !== e.saleNumber && <span class='font-weight-lighter text-muted'> ({e.id})</span>}
                                                </> : <>n/a <span class='font-weight-lighter text-muted'>({e.id})</span></>}
                                            </nobr>
                                        </td>
                                        {showCompanyColumn && <>
                                            <td
                                                class={`d-none d-sm-table-cell text-center ${textColor}`}
                                                style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}
                                            >
                                                {saleIsForeign ? <>
                                                    <span class='badge text-bg-warning'><nobr>{saleCompanyName(e, userStore) || e.customer}</nobr></span>
                                                </> : <>
                                                    {saleCompanyName(e, userStore) || e.customer}
                                                </>}
                                            </td>
                                        </>}
                                        {/* <td>
                                            <nobr>{formatting ? util.formatDate(e.createdDate || e.saleDate, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' }) : util.isoDate(e.createdDate, false)}</nobr>
                                        </td> */}
                                        <td
                                            style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}
                                            class={`${textColor}`}
                                        >
                                            <nobr class='font-weight-light font-style-narrow'>{formatting ? util.formatDate(e.saleDate, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' }) : util.isoDate(e.saleDate, false)}</nobr><br />
                                            {e.saleDate !== e.createdDate && <>
                                                <small class='text-muted font-weight-light font-style-narrow'>
                                                    <Text id='dyrejournal.createdDate'>CreatedDate</Text>: <nobr>{formatting ? util.formatDate(e.createdDate || e.saleDate, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' }) : util.isoDate(e.createdDate, false)}</nobr>
                                                </small>
                                            </>}
                                        </td>
                                        <td
                                            style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}
                                            class={`${textColor}`}
                                        >
                                            <nobr>
                                                {e.user && employee?.firstname ? getInitials(`${employee.firstname} ${employee.lastname}`) : <span class='text-muted'><Text id='dyrejournal.no-data'>n/a</Text></span>}
                                            </nobr>
                                            {department && department.name ? <>
                                                <span class='text-muted ms-1'>&raquo; {department.name}</span>
                                            </> : <></>}
                                        </td>
                                        <td
                                            style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}
                                            class={`${textColor}`}
                                        >
                                            {e.isProductionAnimal ? <i class='fa-duotone fa-cow text-muted me-2' /> : ''}

                                            {e.insuranceClaimSource ? (
                                                <small class='text-body-secondary me-1' title='Tilhører krav-dokument'>
                                                    <i class='fa-solid fa-arrow-turn-down-right me-1' />krav #{e.insuranceClaimSource}
                                                </small>
                                            ) : null}
                                            {getSaleDisplayTitle(e)}
                                            {(e.status === 1007 || e.status === 1008) && e.insuranceClaim && (e.insuranceClaim.insurancePaidSale || e.insuranceClaim.ownerInvoiceSale) ? (
                                                <small class='text-body-secondary ms-2'>
                                                    {e.insuranceClaim.insurancePaidSale ? <span class='me-2' title='Innbetaling fra forsikring'><i class='fa-solid fa-arrow-right me-1' />#{e.insuranceClaim.insurancePaidSale}</span> : null}
                                                    {e.insuranceClaim.ownerInvoiceSale ? <span title='Egenandel-faktura'><i class='fa-solid fa-arrow-right me-1' />#{e.insuranceClaim.ownerInvoiceSale}</span> : null}
                                                </small>
                                            ) : null}
                                            {e.visitors?.[0]?.id ? <button
                                                type='button'
                                                class='btn btn-sm btn-link p-0 ms-2'
                                                style='line-height: 1;'
                                                title='Filtrer på denne eieren'
                                                data-visitor-id={e.visitors[0].id}
                                                onClick={this.onClickFilterVisitor}
                                            >
                                                <i class='fa-solid fa-filter text-muted' style='font-size: 0.8em;' />
                                            </button> : null}
                                            {(e.isWeborder && saleIsPaid && !['creditNote', 'saleCredited'].includes(e.paymentMethod)) && <>
                                                {e.noPackages ? <>
                                                    <i class='fa-duotone fa-check ms-2 text-success' />
                                                </> : <>
                                                    {/* <i class='fa-duotone fa-globe ms-2 text-success' /> */}
                                                    {!saleIsForeign && <button
                                                        class={`btn btn-${e.packedDate && e.shippingDate ? 'outline-primary' : 'primary'} font-weight-light btn-sm py-0 ms-2 rounded-pill`}
                                                        style={`font-size: 0.85em; line-height: 1.3em;`}
                                                        data-id={e.id}
                                                        onClick={this.onClickShipping}
                                                    >
                                                        <i class='fa-regular fa-truck-arrow-right' /> <Text id='dyrejournal.sale.shipping'>Shipping</Text>
                                                    </button>}
                                                    <button
                                                        class={`btn btn-outline-primary font-weight-light btn-sm py-0 ms-2 rounded-pill`}
                                                        style={`font-size: 0.85em; line-height: 1.3em;`}
                                                        data-id={e.id}
                                                        onClick={this.onClickPrintAddressLabel}
                                                    >
                                                        <i class='fa-regular fa-address-card' />
                                                    </button>
                                                    {e.shippingCode && <>
                                                        <button
                                                            class={`btn btn-outline-primary font-weight-light btn-sm py-0 ms-2 rounded-pill`}
                                                            style={`font-size: 0.85em; line-height: 1.3em;`}
                                                            data-id={e.id}
                                                            onClick={this.onClickPrintShippingCode}
                                                        >
                                                            <i class='fa-regular fa-mailbox-flag-up' />
                                                        </button>
                                                    </>}
                                                    <button
                                                        class={`btn btn-outline-primary font-weight-light btn-sm py-0 ms-2 rounded-pill`}
                                                        style={`font-size: 0.85em; line-height: 1.3em;`}
                                                        data-id={e.id}
                                                        onClick={this.onClickPrintCompanyLabel}
                                                    >
                                                        <i class='fa-regular fa-buildings' />
                                                    </button>
                                                    <br />
                                                    {e.packedDate && <>
                                                        <i class='fa-duotone fa-box-check ms-2 text-success' />
                                                    </>}
                                                    {e.shippingDate && <>
                                                        <i class='fa-duotone fa-truck ms-2 me-1 text-success' />
                                                    </>}
                                                    {e.shippingMethod && <>
                                                        {e.shippingMethod}
                                                    </>}
                                                    {e.trackingNumber && <>
                                                        <i class='fa-duotone fa-barcode ms-2 text-success' /> {e.trackingNumber}
                                                    </>}
                                                </>}

                                                {hasInsideProducts && <>
                                                    {saleCustomer?.hasValidSubscription || saleUserIsUpdated[saleCustomer.id] ? <>
                                                        <span class='badge text-bg-danger ms-2'>Insider</span>
                                                    </> : <>
                                                        <button
                                                            type='button'
                                                            class='btn btn-sm btn-warning rounded-pill ms-2'
                                                            onClick={this.onClickMakeInsider}
                                                            data-id={saleCustomer.id}
                                                        >
                                                            No Insider {saleUserUpdating[saleCustomer.id] ? <i class='fa-duotone fa-spinner fa-spin ms-2' /> : <></>}
                                                        </button>
                                                    </>}
                                                </>}
                                            </>}
                                            <br />

                                            {e.animals?.length > 0 ? <>
                                                <small
                                                    class={`bg-glow-primary text-white font-weight-light px-2 py-0 rounded-pill`}
                                                    onClick={this.onClickShowAnimals}
                                                    data-id={e.id}
                                                    style='cursor: pointer;'
                                                >
                                                    <nobr>
                                                        <i class='fa-solid fa-dog me-1' />
                                                        {e.animals.length}
                                                    </nobr>
                                                </small>
                                                {saleSpeciesLabel ? <>
                                                    <small class={`ms-1 bg-glow-secondary text-white font-weight-light px-2 py-0 rounded-pill`}>
                                                        <nobr>{saleSpeciesLabel}</nobr>
                                                    </small>
                                                </> : <></>}
                                            </> : <></>}
                                            {e.journals?.length > 0 ? <>
                                                <small
                                                    class={`ms-2 bg-glow-info text-white font-weight-light px-2 py-0 rounded-pill`}
                                                    onClick={this.onClickShowJournals}
                                                    data-id={e.id}
                                                    style='cursor: pointer;'
                                                >
                                                    <nobr>
                                                        <i class='fa-solid fa-book me-1' />
                                                        {e.journals.length}
                                                    </nobr>
                                                </small>
                                            </> : <></>}
                                            {idexxOrderCount > 0 ? <>
                                                <small
                                                    class={`ms-2 bg-glow-secondary text-white font-weight-light px-2 py-0 rounded-pill`}
                                                    title='IDEXX'
                                                >
                                                    <nobr>
                                                        <i class='fa-solid fa-flask-vial me-1' />
                                                        {idexxOrderCount}
                                                    </nobr>
                                                </small>
                                            </> : <></>}
                                            {hasPrescriptions > 0 ? <>
                                                <small
                                                    class={`ms-2 bg-glow-danger text-white font-weight-light px-2 py-0 rounded-pill`}
                                                    onClick={this.onClickShowPrescriptions}
                                                    data-id={e.id}
                                                    style='cursor: pointer;'
                                                >
                                                    <nobr>
                                                        <i class='fa-solid fa-prescription-bottle me-1' />
                                                        {hasPrescriptions}
                                                    </nobr>
                                                </small>
                                            </> : <></>}

                                            {allDiagnoses && allDiagnoses.length > 0 ? <>
                                                <small
                                                    class={`ms-2 bg-glow-warning text-white font-weight-light px-2 py-0 rounded-pill`}
                                                    onClick={this.onClickShowDiagnoses}
                                                    data-id={e.id}
                                                    style='cursor: pointer;'
                                                >
                                                    <nobr>
                                                        <i class='fa-solid fa-stethoscope me-1' />
                                                        {allDiagnoses.length}
                                                    </nobr>
                                                </small>
                                            </> : <></>}

                                            {allMedicines && allMedicines.length > 0 ? <>
                                                <small
                                                    class={`ms-2 bg-glow-success text-white font-weight-light px-2 py-0 rounded-pill`}
                                                    onClick={this.onClickShowMedicines}
                                                    data-id={e.id}
                                                    style='cursor: pointer;'
                                                >
                                                    <nobr>
                                                        <i class='fa-solid fa-pills me-1' />
                                                        {allMedicines.length}
                                                    </nobr>
                                                </small>
                                            </> : <></>}

                                            {isAdmin && e.netsResponse && e.netsResponse.length > 0 ? <>
                                                <small
                                                    class={`ms-2 ${displayNetsError[e.id] ? 'bg-glow-secondary text-white' : 'bg-light'}  font-weight-light px-2 py-0 rounded-pill`}
                                                    onClick={this.toggleNetsError}
                                                    data-id={e.id}
                                                    style='cursor: pointer;'
                                                >
                                                    <nobr>
                                                        <i class='fa-duotone fa-exclamation-triangle' /> <Text id='dyrejournal.nets-show-debug'>Show Nets Debug</Text>
                                                    </nobr>
                                                </small>
                                            </> : <></>}
                                            {isAdmin && e.netsLastWebResponse ? <>
                                                <small
                                                    class={`ms-2 ${displayNetsError[e.id] ? 'bg-glow-secondary text-white' : 'bg-light'}  font-weight-light px-2 py-0 rounded-pill`}
                                                    onClick={this.toggleNetsError}
                                                    data-id={e.id}
                                                    style='cursor: pointer;'
                                                >
                                                    <nobr>
                                                        <i class='fa-duotone fa-exclamation-triangle' /> <Text id='dyrejournal.nets-show-debug'>Show Nets Debug</Text>
                                                    </nobr>
                                                </small>
                                            </> : <></>}

                                            {isAdmin && hasProductsWithDiscount ? <>
                                                <small class={`ms-2 bg-glow-danger text-white font-weight-light px-2 py-0 rounded-pill`}>
                                                    <nobr>
                                                        <i class='fa-solid fa-badge-dollar me-1' />
                                                        <span class='font-weight-bold'>
                                                            -{util.format(e.products.reduce((sum, p) => sum + (p.discountAmount || 0) * (p.qty || 1), 0), 2, ',', ' ')}
                                                        </span>
                                                    </nobr>
                                                </small>
                                            </> : <></>}

                                            {e.comments && e.comments.length > 0 && <>
                                                <small style='line-height: 1.0em;'>
                                                    <div class='d-flex flex-column'>
                                                        {e.comments && e.comments.length > 0 && e.comments.map((comment, idx) => {
                                                            return (<>
                                                                <div>
                                                                    <nobr class='font-weight-lighter'>{util.formatDate(comment.date, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}</nobr>: {comment.comment}
                                                                </div>
                                                            </>);
                                                        })}
                                                    </div>
                                                </small>
                                            </>}

                                        </td>
                                        {/* <td class='text-center'>{e.animals?.length > 0 ? e.animals.length : ''}</td> */}
                                        {/* <td class='text-center'>
                                            {e.journals?.length > 0 ? e.journals.length : ''}
                                            {hasPrescriptions > 0 ? <>
                                                <span class='font-weight-lighter ms-2'>({hasPrescriptions})</span>
                                            </> : ''}
                                        </td> */}
                                        <td
                                            class='text-center p-1'
                                            style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}
                                            onClick={ev => ev.stopPropagation()}
                                        >
                                            {/*
                                                Show the forsikrings-status badge/dropdown for unpaid sales, OR
                                                for a PAID sale that is still stuck in insurance-pending
                                                (insuranceStatus 1 = Venter, 2 = Sendt til forsikring). A paid +
                                                insurance-pending sale is otherwise a dead-end: the detail view's
                                                Betaling/Faktura buttons are disabled (saleIsPaid || insurancePending)
                                                and "Annet" is disabled (saleIsPaid), so this dropdown is the only
                                                place left to clear the forsikringsmarkering and unlock the sale.
                                            */}
                                            {((!saleIsPaid && e.status !== 0)
                                                || (saleIsPaid && (e.insuranceStatus === 1 || e.insuranceStatus === 2))) && !saleIsForeign && <>
                                                {(() => {
                                                    const insStatus = e.insuranceStatus || 0;
                                                    const insStatusKey = saleStore.getInsuranceStatusById(insStatus);
                                                    const insStatusText = saleStore.getInsuranceStatusById(insStatus, language);
                                                    const insStatusIcon = saleStore.getInsuranceStatusIcon(insStatusKey);
                                                    const insStatusClass = saleStore.getInsuranceStatusClass(insStatusKey);
                                                    const isDropdownOpen = insuranceDropdownOpen[e.id];

                                                    return (<>
                                                        <span class='position-relative d-inline-block'>
                                                            <small
                                                                class={`bg-glow-${insStatusClass} text-white rounded-pill px-2 font-weight-light`}
                                                                style='cursor: pointer; white-space: nowrap;'
                                                                onClick={(ev) => this.toggleInsuranceDropdown(ev, e.id)}
                                                            >
                                                                {insStatus === 0 ? <>--</> : <><i class={insStatusIcon} /> {insStatusText}</>}
                                                            </small>
                                                            {isDropdownOpen && <>
                                                                <div
                                                                    class='position-absolute bg-white shadow rounded-lg p-1 z-3'
                                                                    style='top: 100%; left: 50%; transform: translateX(-50%); min-width: 150px;'
                                                                >
                                                                {[0, 1, 2, 3, 4].map((statusId) => {
                                                                    const statusKey = saleStore.getInsuranceStatusById(statusId);
                                                                    const statusText = saleStore.getInsuranceStatusById(statusId, language);
                                                                    const statusIcon = saleStore.getInsuranceStatusIcon(statusKey);
                                                                    const statusClass = saleStore.getInsuranceStatusClass(statusKey);
                                                                    const isActive = statusId === insStatus;
                                                                    return (
                                                                        <div
                                                                            class={`px-2 py-1 rounded ${isActive ? 'bg-light' : ''}`}
                                                                            style='cursor: pointer; white-space: nowrap;'
                                                                            onClick={(ev) => this.onChangeInsuranceStatus(ev, e.id, statusId)}
                                                                            onMouseOver={(ev) => ev.target.classList.add('bg-light')}
                                                                            onMouseOut={(ev) => { if (!isActive) ev.target.classList.remove('bg-light'); }}
                                                                        >
                                                                            <small class={`text-${statusClass}`}>
                                                                                <i class={statusIcon} /> {statusText}
                                                                            </small>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </>}
                                                        </span>
                                                    </>);
                                                })()}
                                            </>}
                                        </td>
                                        <td
                                            class='text-start'
                                            style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}
                                        >
                                            <nobr>
                                                {/* saleIsPaid: {saleIsPaid ? 'true' : 'false'}:: */}
                                                {/* e.status: {e.status}:: */}
                                                {/* e.paymentMethod: {e.paymentMethod}::<br /> */}

                                                {e.mergedInto > 0 ? (() => {
                                                    const target = finalSales.find(s => s && s.id === e.mergedInto);
                                                    const targetLabel = target && target.saleNumber > 0
                                                        ? `#${target.saleNumber}`
                                                        : `id ${e.mergedInto}`;
                                                    return (
                                                        <small
                                                            class='bg-glow-secondary text-white rounded-pill px-2 font-weight-light'
                                                            style='cursor: pointer;'
                                                            onClick={() => this.gotoSale(e.mergedInto)}
                                                            title={`Merged into ${targetLabel}`}
                                                        >
                                                            <i class='fa-solid fa-merge me-1' /> Merged into {targetLabel}
                                                        </small>
                                                    );
                                                })() : <>{saleIsPaid ? <>
                                                    {['creditNote', 'saleCredited'].includes(e.paymentMethod) ? <>
                                                        <small class={`bg-glow-info text-white rounded-pill px-2 font-weight-light`}><i class={saleStore.getSaleStatusIcon(e.paymentMethod)} /> {saleStore.getSaleStatusById(saleStore.getSaleStatus(e.paymentMethod), language)}</small>
                                                    </> : <>
                                                        <small class={`bg-glow-${saleStore.getSaleStatusClass('paid')} text-white rounded-pill px-2 font-weight-light `}><i class={saleStore.getSaleStatusIcon('paid')} /> {saleStore.getSaleStatusById(1, language)}</small>
                                                    </>}
                                                    {/* paymentRestAmount: {paymentRestAmount}:: */}
                                                </> : <>
                                                    {saleStatus === 'inProgress' && !saleIsForeign ? <>
                                                        <small
                                                            class={`bg-glow-${saleStatusClass} text-white rounded-pill px-2 font-weight-light`}
                                                            style='cursor: pointer; opacity: 0.9;'
                                                            onClick={this.markAsCollectiveInvoice}
                                                            data-id={e.id}
                                                            title='Merk som samlefaktura'
                                                        ><i class={saleStatusIcon} /> {saleStatusText}</small>
                                                    </> : <>
                                                        <small class={`bg-glow-${saleStatusClass} text-white rounded-pill px-2 font-weight-light`}><i class={saleStatusIcon} /> {saleStatusText}</small>
                                                    </>}
                                                    {(e.status === 1007 || e.status === 1008) && e.insuranceClaim && e.insuranceClaim.companyName && (
                                                        <small class='text-body-secondary ms-1'>({e.insuranceClaim.companyName})</small>
                                                    )}
                                                </>}</>}

                                                {(!saleIsPaid && e.status < 1000 && sumTotal > 0 && !saleIsForeign) && <>
                                                    <button
                                                        class={`btn btn-sm px-2 py-0 m-0 btn-outline-primary  ms-2 rounded-pill font-style-narrow`}
                                                        style='font-size: 0.85em; line-height: 1.3em;'
                                                        onClick={this.onClickPaymentSale}
                                                        data-id={e.id}
                                                    >
                                                        <i class='fa-solid fa-cart-shopping' /> <Text id='dyrejournal.sale.payment'>Payment</Text>
                                                    </button>
                                                </>}

                                                {/* saleIsPaid: {saleIsPaid ? 'true' : 'false'}
                                                e.paidDate: {e.paidDate ? util.formatDate(e.paidDate, { month: '2-digit', year: '2-digit', hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' }) : 'n/a'}
                                                e.paymentMethod: {e.paymentMethod}
                                                paymentRestAmount: {paymentRestAmount} */}
                                                {/* paymentRestAmount: {paymentRestAmount} */}
                                                {/* e.status: {e.status} */}
                                                {/* e.paidDate: {e.paidDate ? util.formatDate(e.paidDate, { month: '2-digit', year: '2-digit', hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' }) : 'n/a'} */}
                                                {/* e.saleDate: {e.saleDate ? util.formatDate(e.saleDate, { month: '2-digit', year: '2-digit', hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' }) : 'n/a'} */}
                                                {/* e.paymentMethod: {e.paymentMethod} */}
                                            </nobr>
                                            <br />
                                            <nobr>
                                                {isReadyForPickup && !saleIsPaid && !isOverDue? <>
                                                    <small
                                                        class={`bg-glow-primary text-white font-weight-light px-2 py-0 me-2 rounded-pill`}
                                                        onClick={this.onClickShowAnimals}
                                                        data-id={e.id}
                                                        style='cursor: pointer;'
                                                    >
                                                        <i class='fa-solid fa-dog-leashed fa-bounce me-1' />
                                                        <Text id='dyrejournal.ready-for-pickup'>Ready for pickup</Text>
                                                    </small>
                                                </> : <></>}
                                            </nobr>
                                            {isReadyForPickup && !saleIsPaid && !isOverDue && e.readyForPickupMessage ? <>
                                                <br />
                                                <small class='fst-italic text-primary' style='white-space: pre-line;'>
                                                    <i class='fa-solid fa-comment-dots me-1' />{e.readyForPickupMessage}
                                                </small>
                                            </> : <></>}
                                        </td>
                                        <td
                                            class='text-start'
                                            style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}
                                        >
                                            <i class={saleStore.getPaymentMethodIcon(e.paymentMethod)} /> {saleStore.getPaymentMethodText(e.paymentMethod, language)}
                                            {e.paymentMethod === 'invoice' && (!!invoiceSaleEmailSentDate(e) || emailSent[e.id]) && (
                                                <div class='text-success' style='font-size: 0.85em; white-space: nowrap;'>
                                                    <i class='fa-solid fa-paper-plane' /> E-post sendt
                                                    {!!invoiceSaleEmailSentDate(e) && (
                                                        <small class='font-weight-lighter ms-1'>{util.formatDate(invoiceSaleEmailSentDate(e), { locale: 'nb-NO' })}</small>
                                                    )}
                                                </div>
                                            )}
                                            {/* {['cash', 'card', 'invoice', 'account', 'vipps', 'partial'].includes(e.paymentMethod) ? <>
                                                {salePayments && salePayments.length > 0 ? <>
                                                    <div class='d-flex flex-column w-100'>
                                                        {salePayments.map((payment, idx) => {
                                                            return (<>
                                                                <small class='text-muted font-weight-lighter' style='line-height: 1.0em;'>
                                                                    {formatting ? util.format(payment.amount, 2) : payment.amount} {payment.paymentMethod ? <><span class='text-muted'>({saleStore.getPaymentMethodText(payment.paymentMethod, language)})</span></> : <></>}
                                                                    <br />
                                                                </small>
                                                            </>);
                                                        })}
                                                    </div>
                                                </> : <>
                                                    {e.status > 0 && paymentRestAmount > 0 && <>
                                                        <br /><i class='fa-solid text-danger fa-triangle-exclamation me-2' /> <Text id='dyrejournal.no-payments'>No payments</Text>
                                                    </>}
                                                </>}
                                            </> : <>
                                            </>} */}
                                        </td>
                                        <td
                                            class='text-end'
                                            style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}
                                        >
                                            <nobr>{saleStore.sumTotal(e.products, formatting)}</nobr>
                                        </td>
                                        <td
                                            class={`text-end ${saleIsPaid && paymentRestAmount >= 0 ? '' : 'text-danger font-weight-bold'}`}
                                            style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}
                                        >
                                            {/* e.restPayment: {e.restPayment}<br /> */}
                                            {/* e.totalCost: {e.totalCost}<br /> */}
                                            {/* paymentRestAmount: {paymentRestAmount}<br /> */}
                                            {/* saleIsPaid: {saleIsPaid ? 'true' : 'false'}<br /> */}
                                            {/* e.status: {e.status}<br /> */}
                                            {/* e.paymentMethod: {e.paymentMethod}<br /> */}
                                            {/* isCreditNote: {isCreditNote ? 'true' : 'false'}<br /> */}
                                            {isCreditNote ? <></> : <>

                                                {/* Already paid (status 1) Delvis sales keep the method 'partial'; the button
                                                    would only re-tag them with the last payment's method, so hide it. */}
                                                {e.paymentMethod === 'partial' && paymentRestAmount < 1 && !saleIsForeign && e.status !== 1 ? <>
                                                    <button type='button' class='btn btn-primary btn-small py-0 rounded-pill' disabled={paymentInProgress[e.id]} data-id={e.id} onClick={this.markAsPaid}>
                                                        {paymentInProgress[e.id] && <><i class='fa-duotone fa-spinner-scale me-2' /></>} <i class={`fa-solid fa-check`} /> <Text id='dyrejournal.sale-payment-mark-as-paid'>Mark as paid</Text>
                                                    </button>
                                                </> : <></>}

                                                {paymentRestAmount > 0 ? <>
                                                    {e.totalCost ? <>
                                                        <nobr>{formatting ? util.format(e.restPayment, 2) : e.restPayment}</nobr>
                                                    </> : <>
                                                        {salePayments && salePayments.length > 0 && <>
                                                            <nobr>{saleStore.sumRestPayments(salePayments, saleStore.sumTotal(e.products, false), formatting, false)}</nobr>
                                                        </>}
                                                    </>}
                                                </> : <></>}

                                                {hasPayments && paymentRestAmount < 0 && <>
                                                    <nobr class='text-warning'>
                                                        <i class='fa-solid fa-triangle-exclamation me-1' />
                                                        {formatting ? util.format(paymentRestAmount, 2) : paymentRestAmount}
                                                    </nobr>
                                                </>}
                                            </>}
                                        </td>
                                        <td
                                            class='text-center'
                                            style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}
                                        >
                                            {e.kid ? <>
                                                <nobr class='font-weight-bold'>{e.kid}</nobr>
                                            </> : <>
                                                <span class='text-muted'><Text id='dyrejournal.no-data'>n/a</Text></span>
                                            </>}
                                        </td>
                                        {(!isDebitorList && showPayments) && <td class='text-start' style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}>
                                            <small>
                                                <div class='d-flex flex-column'>
                                                    {salePayments && salePayments.length > 0 && salePayments.map(payment => {
                                                        return (<>
                                                            <div class='d-flex flex-row justify-content-between'>
                                                                <nobr class='text-muted '>
                                                                    {util.formatDate(payment.paidDate, { month: '2-digit', year: '2-digit', hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}
                                                                </nobr>
                                                                <nobr>
                                                                    <span class='font-weight-lighter'>{saleStore.getPaymentMethodText(payment.paymentMethod, language)}</span>
                                                                </nobr>
                                                                <nobr>
                                                                    {formatting ? util.format(payment.amount, 2) : payment.amount}
                                                                </nobr>
                                                            </div>
                                                        </>);
                                                    })}
                                                </div>
                                            </small>
                                        </td>}

                                        {showFiken && <td class='text-start' style={`min-width: 210px; ${e.color ? `background-color: ${e.color} !important;` : ''}`}>
                                            {this.renderFikenColumn(e, { customer, saleIsPaid, fikenLoading, fikenSent, displayFikenError, fikenExpandedSummary, fikenExpandedErrors, isAdmin, isCustomerAdmin })}
                                        </td>}

                                        {showTripletex && <td class='text-start' style={`min-width: 210px; ${e.color ? `background-color: ${e.color} !important;` : ''}`}>
                                            {this.renderTripletexColumn(e, { customer, saleIsPaid, tripletexLoading, tripletexSent, tripletexError, tripletexCreateError, tripletexExpandedSummary, tripletexExpandedErrors, tripletexInvoice, hasPayments, isAdmin })}
                                        </td>}

                                        {showPowerOffice && <td class='text-start' style={`min-width: 210px; ${e.color ? `background-color: ${e.color} !important;` : ''}`}>
                                            {this.renderPowerOfficeColumn(e, { customer, saleIsPaid, powerofficeLoading, powerofficeSent, powerofficeError, powerofficeExpandedSummary, powerofficeExpandedErrors, powerofficeInvoice, hasPayments, isAdmin })}
                                        </td>}

                                        {showDuett && <td class='text-start' style={`min-width: 210px; ${e.color ? `background-color: ${e.color} !important;` : ''}`}>
                                            {this.renderDuettColumn(e, { customer, duettLoading, duettSent, duettError, duettSimResult, duettExpandedSummary, duettExpandedErrors, displayDuettError, isAdmin })}
                                        </td>}

                                        {showAnimalia && <td class='text-center' style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}>
                                            <small>
                                                {e.animals?.length > 0 && (saleHasDhpAnimals || e.animaliaId) ? <>
                                                    {animaliaRefreshTokenExpired ? <>
                                                        {hasDiagnoses && hasMedicines ? <>
                                                            {/* {e.animaliaResponse ? <>
                                                                {JSON.stringify(e.animaliaResponse, null, 4)}
                                                            </> : <></>} */}

                                                            {e.animaliaId ? <>
                                                                <i class='fa-duotone fa-check text-success' /> <Text id='dyrejournal.animalia-reported'>Reported to DHP</Text>
                                                            </> : <>
                                                                <Text id='dyrejournal.animalia-auth-login-first'>Login to DHP before you can send data.</Text>
                                                            </>}
                                                        </> : <>
                                                            {/* DHP-relevant visit but not yet reportable — show why even when
                                                                not logged in, so the row isn't blank (TICKET-01184). */}
                                                            {!hasDiagnoses && <>
                                                                <i class='fa-duotone fa-times text-danger me-1' /> <Text id='dyrejournal.no-diagnoses'>No diagnoses</Text><br />
                                                            </>}
                                                            {!hasMedicines && <>
                                                                <i class='fa-duotone fa-times text-danger me-1' /> <Text id='dyrejournal.no-medicine'>No medicine</Text><br />
                                                            </>}
                                                        </>}
                                                    </> : <>
                                                        {hasDiagnoses && hasMedicines ? <>
                                                            {/* {e.animaliaResponse ? <>
                                                                {JSON.stringify(e.animaliaResponse, null, 4)}
                                                            </> : <></>} */}

                                                            {e.animaliaId ? <>
                                                                <i class='fa-duotone fa-check text-success' /> <Text id='dyrejournal.animalia-reported'>Reported to DHP</Text>
                                                                {!saleIsForeign && <button
                                                                    disabled={animaliaLoading[e.id]}
                                                                    type='button'
                                                                    class={`btn btn-sm btn-link rounded-pill p-0 m-0 font-style-narrow`}
                                                                    style='font-size: 1.0em;'
                                                                    data-id={e.id}
                                                                    onClick={this.onClickPostAnimaliaVisit}
                                                                >
                                                                    <i class='fa-duotone fa-cloud-arrow-up' /> <Text id='dyrejournal.dhp-re-transfer'>Resend to DHP</Text>
                                                                    {animaliaLoading[e.id] ? <i class='fa-duotone fa-spinner fa-spin ms-2' /> : <></>}
                                                                </button>}
                                                            </> : <>
                                                                {!saleIsForeign && <button
                                                                    disabled={animaliaLoading[e.id]}
                                                                    type='button'
                                                                    class={`btn btn-sm btn-${isDevelopment ? 'warning' : 'primary'} rounded-pill py-0 font-style-narrow`}
                                                                    data-id={e.id}
                                                                    onClick={this.onClickPostAnimaliaVisit}
                                                                >
                                                                    <i class='fa-duotone fa-cloud-arrow-up' /> <Text id='dyrejournal.dhp-transfer'>DHP</Text>
                                                                    {animaliaLoading[e.id] ? <i class='fa-duotone fa-spinner fa-spin ms-2' /> : <></>}
                                                                </button>}
                                                            </>}
                                                        </> : <>
                                                            {!hasDiagnoses && <>
                                                                <i class='fa-duotone fa-times text-danger me-1' /> <Text id='dyrejournal.no-diagnoses'>No diagnoses</Text><br />
                                                            </>}
                                                            {!hasMedicines && <>
                                                                <i class='fa-duotone fa-times text-danger me-1' /> <Text id='dyrejournal.no-medicine'>No medicine</Text><br />
                                                            </>}
                                                        </>}
                                                    </>}
                                                </> : <>
                                                    {e.animals?.length > 0 ? <>
                                                        {/* Companion-only visit (dog/cat/...) — journals use small-animal
                                                            codes DHP rejects, so nothing to report (TICKET-01184). */}
                                                        <i class='fa-duotone fa-ban text-muted me-2' />
                                                        <Text id='dyrejournal.dhp-not-relevant'>Not relevant for DHP</Text>
                                                    </> : <>
                                                        <i class='fa-duotone fa-times text-muted me-2' />
                                                        <Text id='dyrejournal.no-animals'>No animals</Text>
                                                    </>}

                                                </>}
                                                {e.animaliaError && !e.animaliaId && saleHasDhpAnimals ? <>
                                                    <button class={`btn btn${displayAnimaliaError[e.id] ? '' : '-outline'}-danger btn-sm rounded-pill py-0 font-style-narrow`} onClick={this.toggleAnimaliaError} data-id={e.id}>
                                                        <nobr>
                                                            <i class='fa-duotone fa-exclamation-triangle text-danger' /> <Text id='dyrejournal.animalia-show-error'>Show Error</Text>
                                                        </nobr>
                                                    </button>
                                                </> : <></>}
                                            </small>
                                        </td>}
                                        {showDyreId && <td class='text-center' style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}>
                                            <small>
                                                {hasDiagnoses ? <>
                                                    <DyreIdReportBatchButton
                                                        stores={this.props.stores}
                                                        saleId={e.id}
                                                        journals={e.journals || []}
                                                        customer={customer}
                                                        user={user}
                                                    />
                                                </> : <>
                                                    <i class='fa-duotone fa-times text-muted me-1' />
                                                    <Text id='dyrejournal.no-diagnoses'>No diagnoses</Text>
                                                </>}
                                            </small>
                                        </td>}
                                        {showDelete && <td class='text-end' style={`${e.color ? `background-color: ${e.color} !important;` : ''}`}>
                                            {isAllowedToDelete  ? <>
                                                {hasJournals ? <>
                                                    <i class='fa-duotone fa-hand text-danger me-2' />
                                                    <small class='font-style-narrow text-muted'>
                                                        <Text id='dyrejournal.sale-has-journals'>Sale has journals</Text>
                                                    </small>
                                                    {isCustomerAdmin ? <>
                                                        {!e.offline && !saleIsForeign && <>
                                                            <button
                                                                type='button'
                                                                class='btn btn-sm btn-link text-danger rounded-pill p-0 ms-2 font-style-narrow'
                                                                onClick={this.onClickDeleteSale}
                                                                data-id={e.id}
                                                            >
                                                                <i class='fa-solid fa-trash' />
                                                            </button>
                                                        </>}
                                                    </> : <></>}
                                                </> : <>
                                                    {!e.offline && !saleIsForeign && <>
                                                        <button
                                                            type='button'
                                                            class='btn btn-sm btn-link text-danger rounded-pill p-0 ms-2 font-style-narrow'
                                                            onClick={this.onClickDeleteSale}
                                                            data-id={e.id}
                                                        >
                                                            <i class='fa-solid fa-trash' />
                                                        </button>
                                                    </>}
                                                </>}
                                            </> : <>
                                                {isCustomerAdmin ? <>
                                                    {!e.offline && !saleIsForeign && <>
                                                        <button
                                                            type='button'
                                                            class='btn btn-sm btn-link text-danger rounded-pill p-0 ms-2 font-style-narrow'
                                                            onClick={this.onClickDeleteSale}
                                                            data-id={e.id}
                                                            data-logreason={true}
                                                        >
                                                            <i class='fa-solid fa-trash' />
                                                        </button>
                                                    </>}
                                                </> : <></>}
                                            </>}
                                            {e.offline ? <>
                                                <Text id='dyrejournal.sale.deleteReason'>Delete reason</Text>: <span class='font-weight-lighter text-muted'>{e.deleteReason || 'n/a'}</span>
                                                {!saleIsForeign && <>
                                                    <button
                                                        class='btn btn-sm btn-link text-danger'
                                                        onClick={this.onClickUndeleteSale}
                                                        data-id={e.id}
                                                    >
                                                        <i class='fa-duotone fa-trash-slash' />
                                                    </button>
                                                </>}
                                            </> : ''}
                                        </td>}
                                    </tr>
                                    {!e.animaliaId && displayAnimaliaError[e.id] ? <>
                                        <tr>
                                            <td colspan='17'>
                                                <h5>Error: {e.animaliaError?.error}</h5>
                                                <ul>
                                                    {Array.isArray(e.animaliaError?.parameters) ? <>
                                                        {e.animaliaError?.parameters.map((line, idx) => {
                                                            return <>
                                                                {Object.keys(line).map(key => {
                                                                    const val = line[key];
                                                                    return (<>
                                                                        <li><span class='font-weight-lighter'>{key}:</span> {val}</li>
                                                                    </>);
                                                                })}
                                                            </>;
                                                        })}
                                                    </> : <>
                                                        {e.animaliaError?.parameters && Object.keys(e.animaliaError?.parameters).map(key => {
                                                            const val = e.animaliaError?.parameters[key];
                                                            return (<>
                                                                <li><span class='font-weight-lighter'>{key}:</span> {val}</li>
                                                            </>);
                                                        })}
                                                    </>}
                                                </ul>
                                                {isAdmin && <>
                                                    <xmp>Error: {JSON.stringify(e.animaliaError?.parameters, null, 4)}</xmp>
                                                    <xmp>Request: {JSON.stringify(e.animaliaRequest, null, 4)}</xmp>
                                                </>}
                                            </td>
                                        </tr>
                                    </> : <></>}
                                    {tripletexError[e.id] ? <>
                                        <tr>
                                            <td colspan='17'>
                                                {/* Tripletex Invoice Preview - Shows what data would be sent */}
                                                {e.tripletexInvoicePreview && <>
                                                    <details class='mb-3' open>
                                                        <summary class={`p-2 border rounded cursor-pointer ${e.tripletexInvoicePreview.status === 'success' ? 'bg-success bg-opacity-10' : e.tripletexInvoicePreview.status === 'failed' ? 'bg-danger bg-opacity-10' : 'bg-warning bg-opacity-10'}`} style='cursor: pointer;'>
                                                            <strong>
                                                                <i class='fa-duotone fa-file-invoice me-2' />
                                                                Invoice Preview
                                                            </strong>
                                                            <span class={`badge ms-2 ${e.tripletexInvoicePreview.status === 'success' ? 'bg-success' : e.tripletexInvoicePreview.status === 'failed' ? 'bg-danger' : 'bg-warning'}`}>
                                                                {e.tripletexInvoicePreview.status}
                                                            </span>
                                                            {e.tripletexInvoicePreview.tripletexInvoiceNumber && <span class='badge bg-info ms-2'>Invoice #{e.tripletexInvoicePreview.tripletexInvoiceNumber}</span>}
                                                        </summary>
                                                        <div class='p-3 border border-top-0 rounded-bottom bg-light' style='font-size: 0.85em;'>
                                                            {/* API Calls Status */}
                                                            <div class='mb-3'>
                                                                <strong>API Calls Status:</strong>
                                                                <div class='d-flex flex-wrap gap-2 mt-1'>
                                                                    {Object.entries(e.tripletexInvoicePreview.apiCallsStatus || {}).map(([key, status]) => (
                                                                        <span key={key} class={`badge ${status === 'success' ? 'bg-success' : status === 'failed' ? 'bg-danger' : 'bg-secondary'}`}>
                                                                            {key}: {status}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {/* Errors */}
                                                            {e.tripletexInvoicePreview.errors && e.tripletexInvoicePreview.errors.length > 0 && <>
                                                                <div class='mb-3 text-danger'>
                                                                    <strong>Errors:</strong>
                                                                    <ul class='mb-0'>
                                                                        {e.tripletexInvoicePreview.errors.map((err, idx) => (
                                                                            <li key={idx}>{typeof err === 'string' ? err : JSON.stringify(err)}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            </>}

                                                            {/* Account/VAT Warnings */}
                                                            {e.tripletexInvoicePreview.productWarnings && e.tripletexInvoicePreview.productWarnings.length > 0 && <>
                                                                <div class='mb-3'>
                                                                    <div class='alert alert-warning py-2 mb-0'>
                                                                        <strong><i class='fa-duotone fa-triangle-exclamation me-2' />Account Configuration Warnings ({e.tripletexInvoicePreview.productWarnings.length}):</strong>
                                                                        <div class='mt-2'>
                                                                            {e.tripletexInvoicePreview.productWarnings.map((warning, idx) => (
                                                                                <div key={idx} class='mb-2 p-2 bg-white rounded border'>
                                                                                    <div class='d-flex align-items-start'>
                                                                                        <span class={`badge me-2 ${warning.type === 'VAT_MISMATCH' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                                                                            {warning.type === 'VAT_MISMATCH' ? 'MVA Mismatch' : 'Konto mangler'}
                                                                                        </span>
                                                                                        <div class='flex-grow-1'>
                                                                                            <div class='fw-bold'>
                                                                                                Konto {warning.accountNumber}
                                                                                                {warning.segment && <span class='text-muted'> ({warning.segment})</span>}
                                                                                                {warning.accountName && <span class='text-muted'> - {warning.accountName}</span>}
                                                                                            </div>
                                                                                            <small class='text-muted'>{warning.message}</small>
                                                                                            {warning.type === 'VAT_MISMATCH' && (
                                                                                                <div class='mt-1'>
                                                                                                    <small>
                                                                                                        <span class='badge bg-danger me-1'>Konto MVA: {warning.accountVatPercent}%</span>
                                                                                                        <span class='badge bg-success'>Produkt MVA: {warning.productVatPercent}%</span>
                                                                                                    </small>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                        <small class='text-muted d-block mt-2'>
                                                                            <i class='fa-duotone fa-info-circle me-1' />
                                                                            Produkter med disse advarslene blir opprettet uten kontotilknytning i Tripletex.
                                                                            Konfigurer riktige kontoer i Tripletex eller i segmentkontoinnstillingene.
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </>}

                                                            {/* Visitor Info */}
                                                            <div class='mb-3'>
                                                                <strong>Customer/Visitor:</strong>
                                                                <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                    <tbody>
                                                                        <tr><td class='fw-bold' style='width: 150px;'>ID</td><td>{e.tripletexInvoicePreview.visitor?.id}</td></tr>
                                                                        <tr><td class='fw-bold'>Name</td><td>{e.tripletexInvoicePreview.visitor?.name}</td></tr>
                                                                        <tr><td class='fw-bold'>Email</td><td>{e.tripletexInvoicePreview.visitor?.email}</td></tr>
                                                                        <tr><td class='fw-bold'>Address</td><td>{e.tripletexInvoicePreview.visitor?.address}</td></tr>
                                                                        <tr><td class='fw-bold'>Postal Code</td><td>{e.tripletexInvoicePreview.visitor?.postalcode}</td></tr>
                                                                        <tr><td class='fw-bold'>Place</td><td>{e.tripletexInvoicePreview.visitor?.place}</td></tr>
                                                                        {e.tripletexInvoicePreview.visitor?.orgNumber && <tr><td class='fw-bold'>Org Number</td><td>{e.tripletexInvoicePreview.visitor?.orgNumber}</td></tr>}
                                                                    </tbody>
                                                                </table>
                                                            </div>

                                                            {/* Tripletex Customer */}
                                                            {e.tripletexInvoicePreview.tripletexCustomer && <>
                                                                <div class='mb-3'>
                                                                    <strong>Tripletex Customer:</strong>
                                                                    <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                        <tbody>
                                                                            <tr><td class='fw-bold' style='width: 150px;'>Tripletex ID</td><td>{e.tripletexInvoicePreview.tripletexCustomer?.id}</td></tr>
                                                                            <tr><td class='fw-bold'>Name</td><td>{e.tripletexInvoicePreview.tripletexCustomer?.name}</td></tr>
                                                                            <tr><td class='fw-bold'>Customer Number</td><td>{e.tripletexInvoicePreview.tripletexCustomer?.customerNumber}</td></tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </>}

                                                            {/* Products */}
                                                            <div class='mb-3'>
                                                                <strong>Products ({e.tripletexInvoicePreview.products?.length || 0}):</strong>
                                                                {e.tripletexInvoicePreview.segmentAccountsEnabled && <span class='badge bg-info ms-2'>Segment Accounts Enabled</span>}
                                                                <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                    <thead class='table-light'>
                                                                        <tr>
                                                                            <th>Product #</th>
                                                                            <th>Name</th>
                                                                            <th class='text-center'>Account</th>
                                                                            <th class='text-end'>Qty</th>
                                                                            <th class='text-end'>Price</th>
                                                                            <th class='text-end'>VAT %</th>
                                                                            <th class='text-end'>Ex. VAT</th>
                                                                            <th class='text-end'>Inc. VAT</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(e.tripletexInvoicePreview.products || []).map((prod, idx) => {
                                                                            // Check if this product's account has a warning
                                                                            const accountWarning = (e.tripletexInvoicePreview.productWarnings || []).find(
                                                                                w => w.accountNumber === prod.account || w.productNumber === prod.productNumber
                                                                            );
                                                                            const hasWarning = !!accountWarning;
                                                                            return (
                                                                                <tr key={idx} class={hasWarning ? 'table-warning' : (prod.segment ? 'table-info' : '')}>
                                                                                    <td><code>{prod.productNumber || prod.id}</code></td>
                                                                                    <td>
                                                                                        {prod.name}
                                                                                        {prod.segment && <span class='badge bg-secondary ms-1' style='font-size: 0.7em;'>{prod.segment}</span>}
                                                                                    </td>
                                                                                    <td class='text-center'>
                                                                                        {hasWarning ? (
                                                                                            <span title={accountWarning.message}>
                                                                                                <code class='text-danger fw-bold'>{prod.account || '-'}</code>
                                                                                                <i class='fa-duotone fa-triangle-exclamation text-warning ms-1' />
                                                                                            </span>
                                                                                        ) : (
                                                                                            <code class={prod.account ? 'text-success fw-bold' : 'text-muted'}>{prod.account || '-'}</code>
                                                                                        )}
                                                                                    </td>
                                                                                    <td class='text-end'>{prod.qty}</td>
                                                                                    <td class='text-end'>{util.format(prod.price, 2)}</td>
                                                                                    <td class='text-end'>{prod.vat}%</td>
                                                                                    <td class='text-end'>{util.format(prod.totalExcludingVat, 2)}</td>
                                                                                    <td class='text-end'>{util.format(prod.totalIncludingVat, 2)}</td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                    <tfoot class='table-light fw-bold'>
                                                                        <tr>
                                                                            <td colspan='6'>Total</td>
                                                                            <td class='text-end'>{util.format(e.tripletexInvoicePreview.totals?.productsTotalExcludingVat ?? (e.tripletexInvoicePreview.totals?.productsTotal / 1.25), 2)}</td>
                                                                            <td class='text-end'>{util.format(e.tripletexInvoicePreview.totals?.productsTotal, 2)}</td>
                                                                        </tr>
                                                                    </tfoot>
                                                                </table>
                                                            </div>

                                                            {/* Payments */}
                                                            {e.tripletexInvoicePreview.payments && e.tripletexInvoicePreview.payments.length > 0 && <>
                                                                <div class='mb-3'>
                                                                    <strong>Payments ({e.tripletexInvoicePreview.payments.length}):</strong>
                                                                    <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                        <thead class='table-light'>
                                                                            <tr>
                                                                                <th>ID</th>
                                                                                <th class='text-end'>Amount</th>
                                                                                <th>Method</th>
                                                                                <th>Paid Date</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {e.tripletexInvoicePreview.payments.map((pay, idx) => (
                                                                                <tr key={idx}>
                                                                                    <td>{pay.id}</td>
                                                                                    <td class='text-end'>{util.format(pay.amount, 2)}</td>
                                                                                    <td><code>{pay.paymentMethod || '(empty)'}</code></td>
                                                                                    <td>{pay.paidDate ? util.formatDate(pay.paidDate, { locale: 'nb-NO' }) : '-'}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                        <tfoot class='table-light fw-bold'>
                                                                            <tr>
                                                                                <td>Total Paid</td>
                                                                                <td class='text-end'>{util.format(e.tripletexInvoicePreview.totals?.paidAmount, 2)}</td>
                                                                                <td colspan='2'></td>
                                                                            </tr>
                                                                        </tfoot>
                                                                    </table>
                                                                </div>
                                                            </>}

                                                            {/* Final Invoice Object - Collapsible */}
                                                            {e.tripletexInvoicePreview.finalInvoiceObject && <>
                                                                <details class='mb-3'>
                                                                    <summary class='p-2 bg-secondary bg-opacity-10 border rounded cursor-pointer' style='cursor: pointer;'>
                                                                        <strong>Final Invoice Object (sent to Tripletex)</strong>
                                                                    </summary>
                                                                    <div class='p-2 border border-top-0 rounded-bottom'>
                                                                        <xmp style='white-space: pre-wrap; word-break: break-all; font-size: 0.8em; margin: 0;'>{JSON.stringify(e.tripletexInvoicePreview.finalInvoiceObject, null, 2)}</xmp>
                                                                    </div>
                                                                </details>
                                                            </>}

                                                            {/* Metadata */}
                                                            <div class='text-muted' style='font-size: 0.8em;'>
                                                                <small>
                                                                    Sale #{e.tripletexInvoicePreview.saleNumber} |
                                                                    Sale Date: {e.tripletexInvoicePreview.saleDate} |
                                                                    Preview Created: {e.tripletexInvoicePreview.createdAt ? util.formatDate(e.tripletexInvoicePreview.createdAt, { locale: 'nb-NO', hour: '2-digit', minute: '2-digit' }) : '-'}
                                                                    {e.tripletexInvoicePreview.tripletexInvoiceId && <> | Tripletex Invoice ID: {e.tripletexInvoicePreview.tripletexInvoiceId}</>}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </details>
                                                </>}

                                                <h5>Error: {e.tripletexResponse?.message}</h5>
                                                {/* <xmp>{JSON.stringify(e.tripletexResponse, null, 4)}</xmp> */}
                                                <ul>
                                                    {Array.isArray(e.tripletexResponse?.validationMessages) ? <>
                                                        {e.tripletexResponse?.validationMessages.map((line, idx) => {
                                                            return <>
                                                                {Object.keys(line).map(key => {
                                                                    const val = line[key];
                                                                    if (!val) return <></>;
                                                                    return (<>
                                                                        <li><span class='font-weight-lighter'>{key}:</span> {val}</li>
                                                                    </>);
                                                                })}
                                                            </>;
                                                        })}
                                                    </> : <>
                                                        {e.tripletexResponse?.validationMessages && Object.keys(e.tripletexResponse?.validationMessages).map(key => {
                                                            const val = e.tripletexResponse?.validationMessages[key];
                                                            return (<>
                                                                <li><span class='font-weight-lighter'>{key}:</span> {val}</li>
                                                            </>);
                                                        })}
                                                    </>}
                                                </ul>

                                                {/* Tripletex Debug Info - User Friendly Display (Array) */}
                                                {/* Use saleDebugData if available (loaded on-demand), otherwise fall back to e.tripletexDebug */}
                                                {(() => {
                                                    const tripletexDebugData = saleDebugData[e.id]?.tripletexDebug || e.tripletexDebug;

                                                    // Loading indicator
                                                    if (loadingDebugData[e.id]) {
                                                        return (
                                                            <div class='d-flex align-items-center justify-content-center py-4'>
                                                                <i class='fa-duotone fa-spinner fa-spin me-2' />
                                                                Laster debug-data...
                                                            </div>
                                                        );
                                                    }

                                                    if (!tripletexDebugData || !Array.isArray(tripletexDebugData) || tripletexDebugData.length === 0) {
                                                        return null;
                                                    }

                                                    return tripletexDebugData.map((debugEntry, debugIdx) => (
                                                        <details key={debugIdx} class='mt-3' open={debugIdx === 0}>
                                                            <summary class={`p-2 border rounded cursor-pointer ${debugEntry.success ? 'bg-light' : 'bg-danger bg-opacity-10'}`} style='cursor: pointer;'>
                                                                <strong>
                                                                    {debugIdx === 0 ? 'Latest ' : ''}Attempt #{tripletexDebugData.length - debugIdx}
                                                                </strong>
                                                                {' - '}
                                                                {debugEntry.createdDate ? util.formatDate(debugEntry.createdDate, { locale: 'nb-NO', hour: '2-digit', minute: '2-digit' }) : 'Unknown date'}
                                                                {debugEntry.success
                                                                    ? <span class='badge bg-success ms-2'>Success (Invoice #{debugEntry.invoiceNumber})</span>
                                                                    : <span class='badge bg-danger ms-2'>Failed</span>
                                                                }
                                                                <span class='badge bg-secondary ms-2'>{debugEntry.paymentStrategy?.strategy || 'unknown'}</span>
                                                            </summary>
                                                            <div class='p-3 border border-top-0 rounded-bottom bg-light' style='font-size: 0.85em;'>
                                                                {/* Payment Strategy */}
                                                                {debugEntry.paymentStrategy && <>
                                                                    <div class='mb-3'>
                                                                        <strong>Payment Strategy:</strong>
                                                                        <span class={`badge ms-2 ${debugEntry.paymentStrategy.strategy === 'individual' ? 'bg-info' : debugEntry.paymentStrategy.strategy === 'shortcut' ? 'bg-success' : 'bg-secondary'}`}>
                                                                            {debugEntry.paymentStrategy.strategy}
                                                                        </span>
                                                                        <ul class='mt-1 mb-0' style='font-size: 0.9em;'>
                                                                            <li>Sale Payment Method: <code>{debugEntry.paymentStrategy.salePaymentMethod || '(none)'}</code></li>
                                                                            <li>Sale is Paid: {debugEntry.paymentStrategy.saleIsPaid ? <span class='text-success'>Yes</span> : <span class='text-warning'>No</span>}</li>
                                                                            <li>Payments Count: {debugEntry.paymentStrategy.paymentsCount}</li>
                                                                            <li>Multiple Payment Methods: {debugEntry.paymentStrategy.multiplePaymentMethods ? 'Yes' : 'No'}</li>
                                                                            <li>Needs Individual Payments: {debugEntry.paymentStrategy.needsIndividualPayments ? 'Yes' : 'No'}</li>
                                                                            <li>Actual Paid: {util.format(debugEntry.paymentStrategy.actualPaidAmount, 0)} kr</li>
                                                                            <li>Product Total: {util.format(debugEntry.paymentStrategy.totalProductCost, 0)} kr</li>
                                                                        </ul>
                                                                    </div>
                                                                </>}

                                                                {/* Sale Payments */}
                                                                {debugEntry.salePayments && debugEntry.salePayments.length > 0 && <>
                                                                    <div class='mb-3'>
                                                                        <strong>Sale Payments ({debugEntry.salePayments.length}):</strong>
                                                                        <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                            <thead class='table-light'>
                                                                                <tr>
                                                                                    <th>ID</th>
                                                                                    <th>Amount</th>
                                                                                    <th>Payment Method</th>
                                                                                    <th>Paid Date</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {debugEntry.salePayments.map((p, idx) => (
                                                                                    <tr key={idx}>
                                                                                        <td>{p.id}</td>
                                                                                        <td>{util.format(p.amount, 0)} kr</td>
                                                                                        <td>
                                                                                            <code class={p.paymentMethod === '(empty)' ? 'text-danger' : ''}>
                                                                                                {p.paymentMethod}
                                                                                            </code>
                                                                                        </td>
                                                                                        <td>{p.paidDate ? util.formatDate(p.paidDate, { locale: 'nb-NO' }) : '-'}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </>}

                                                                {/* Tripletex Payment Types & Mappings - Hidden behind toggle */}
                                                                {(debugEntry.tripletexPaymentTypes?.length > 0 || debugEntry.paymentMappings?.length > 0) && (
                                                                    <details class='mb-3'>
                                                                        <summary class='p-2 border rounded bg-light' style='cursor: pointer; font-size: 0.85em;'>
                                                                            <i class='fa-duotone fa-credit-card me-1' /> Vis betalingstyper og mapping
                                                                            {debugEntry.tripletexPaymentTypes?.length > 0 && ` (${debugEntry.tripletexPaymentTypes.length} typer)`}
                                                                            {debugEntry.paymentMappings?.length > 0 && `, ${debugEntry.paymentMappings.length} mappings`}
                                                                        </summary>
                                                                        <div class='p-3 border border-top-0 rounded-bottom bg-light' style='font-size: 0.85em;'>
                                                                            {/* Tripletex Payment Types */}
                                                                            {debugEntry.tripletexPaymentTypes && debugEntry.tripletexPaymentTypes.length > 0 && (
                                                                                <div class='mb-3'>
                                                                                    <strong style='font-size: 0.9em;'>Tilgjengelige Tripletex betalingstyper ({debugEntry.tripletexPaymentTypes.length}):</strong>
                                                                                    <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                                        <thead class='table-light'>
                                                                                            <tr>
                                                                                                <th>ID</th>
                                                                                                <th>Beskrivelse</th>
                                                                                                <th>Sekvens</th>
                                                                                                <th>Inaktiv</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {debugEntry.tripletexPaymentTypes.map((t, idx) => (
                                                                                                <tr key={idx} class={t.isInactive ? 'text-muted' : ''}>
                                                                                                    <td><code>{t.id}</code></td>
                                                                                                    <td>{t.description}</td>
                                                                                                    <td>{t.sequence}</td>
                                                                                                    <td>{t.isInactive ? 'Ja' : 'Nei'}</td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            )}

                                                                            {/* Payment Mappings */}
                                                                            {debugEntry.paymentMappings && debugEntry.paymentMappings.length > 0 && (
                                                                                <div class='mb-0'>
                                                                                    <strong style='font-size: 0.9em;'>Betalings-mappinger ({debugEntry.paymentMappings.length}):</strong>
                                                                                    <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                                        <thead class='table-light'>
                                                                                            <tr>
                                                                                                <th>Betalings-ID</th>
                                                                                                <th>Beløp</th>
                                                                                                <th>Original metode</th>
                                                                                                <th>Brukte fallback</th>
                                                                                                <th>Metode brukt</th>
                                                                                                <th>Tripletex type</th>
                                                                                                <th>Status</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {debugEntry.paymentMappings.map((m, idx) => (
                                                                                                <tr key={idx} class={m.success ? '' : 'table-danger'}>
                                                                                                    <td><code>{m.paymentId}</code></td>
                                                                                                    <td>{util.format(m.amount, 0)} kr</td>
                                                                                                    <td>
                                                                                                        <code class={m.originalPaymentMethod === '(empty)' ? 'text-danger' : ''}>
                                                                                                            {m.originalPaymentMethod}
                                                                                                        </code>
                                                                                                    </td>
                                                                                                    <td>{m.usedFallback ? <span class='text-warning'>Ja</span> : 'Nei'}</td>
                                                                                                    <td><code>{m.paymentMethodUsed}</code></td>
                                                                                                    <td>{m.tripletexPaymentType} (ID: {m.tripletexPaymentTypeId || 'N/A'})</td>
                                                                                                    <td>
                                                                                                        {m.success
                                                                                                            ? <span class='badge bg-success'>OK</span>
                                                                                                            : <span class='badge bg-danger' title={m.error}>Feilet</span>
                                                                                                        }
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </details>
                                                                )}

                                                                {/* Errors */}
                                                                {debugEntry.errors && debugEntry.errors.length > 0 && <>
                                                                    <div class='mb-3'>
                                                                        <strong class='text-danger'>Errors ({debugEntry.errors.length}):</strong>
                                                                        <ul class='mt-1 mb-0 text-danger'>
                                                                            {debugEntry.errors.map((err, idx) => (
                                                                                <li key={idx}>{err}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </>}
                                                            </div>
                                                        </details>
                                                    ));
                                                })()}
                                                {/* Fallback: Parse tripletexApiResponses when tripletexDebug is not available */}
                                                {(() => {
                                                    const tripletexDebugData = saleDebugData[e.id]?.tripletexDebug || e.tripletexDebug;
                                                    const tripletexApiResponsesData = saleDebugData[e.id]?.tripletexApiResponses || e.tripletexApiResponses;

                                                    // Skip if we have tripletexDebug data (already rendered above)
                                                    if (tripletexDebugData && Array.isArray(tripletexDebugData) && tripletexDebugData.length > 0) {
                                                        return null;
                                                    }
                                                    // Skip if loading
                                                    if (loadingDebugData[e.id]) {
                                                        return null;
                                                    }
                                                    // Skip if no API responses data
                                                    if (!tripletexApiResponsesData || !Array.isArray(tripletexApiResponsesData) || tripletexApiResponsesData.length === 0) {
                                                        return null;
                                                    }

                                                    // Extract all payment types from all responses (they may be in separate API calls)
                                                    let allPaymentTypes = [];
                                                    tripletexApiResponsesData.forEach(resp => {
                                                                // tripletexPaymentTypes.values (not .value.values)
                                                                if (resp.tripletexPaymentTypes?.values && resp.tripletexPaymentTypes.values.length > 0) {
                                                                    allPaymentTypes = resp.tripletexPaymentTypes.values;
                                                                }
                                                            });

                                                            // Payment method mapping reference (internal -> Tripletex)
                                                            // Try to find matching Tripletex payment types
                                                            const findPaymentType = (keywords) => {
                                                                for (const kw of keywords) {
                                                                    const found = allPaymentTypes.find(t => t.description?.toLowerCase().includes(kw.toLowerCase()));
                                                                    if (found) return found;
                                                                }
                                                                return null;
                                                            };

                                                            const kontantType = findPaymentType(['kontant', 'cash']);
                                                            const kortType = findPaymentType(['kort', 'card']);
                                                            const bankType = findPaymentType(['bank']);

                                                            const paymentMethodMappings = [
                                                                { internal: 'cash', tripletexDesc: kontantType?.description || 'Kontant', tripletexId: kontantType?.id || '-' },
                                                                { internal: 'card', tripletexDesc: kortType?.description || bankType?.description || 'Kort/Bank', tripletexId: kortType?.id || bankType?.id || '-' },
                                                                { internal: 'cardExternal', tripletexDesc: kortType?.description || bankType?.description || 'Kort/Bank', tripletexId: kortType?.id || bankType?.id || '-' },
                                                                { internal: 'cardApp', tripletexDesc: kortType?.description || bankType?.description || 'Kort/Bank', tripletexId: kortType?.id || bankType?.id || '-' },
                                                                { internal: 'vipps', tripletexDesc: bankType?.description || 'Betalt til bank', tripletexId: bankType?.id || '-' },
                                                                { internal: 'bank', tripletexDesc: bankType?.description || 'Betalt til bank', tripletexId: bankType?.id || '-' },
                                                                { internal: 'account', tripletexDesc: bankType?.description || 'Betalt til bank', tripletexId: bankType?.id || '-' },
                                                                { internal: 'invoice', tripletexDesc: 'Faktura (ubetalt)', tripletexId: '-' },
                                                                { internal: 'giftCard', tripletexDesc: kontantType?.description || 'Kontant', tripletexId: kontantType?.id || '-' },
                                                            ];

                                                            // Group responses by createdDate (approximate grouping)
                                                            const attempts = [];
                                                            let currentAttempt = null;

                                                            // Sort by createdDate descending
                                                            const sortedResponses = [...tripletexApiResponsesData].sort((a, b) =>
                                                                new Date(b.createdDate || 0) - new Date(a.createdDate || 0)
                                                            );

                                                            sortedResponses.forEach(resp => {
                                                                // Group responses within 60 seconds as one attempt
                                                                const respTime = new Date(resp.createdDate || 0).getTime();
                                                                if (!currentAttempt || (currentAttempt.time - respTime > 60000)) {
                                                                    if (currentAttempt) attempts.push(currentAttempt);
                                                                    currentAttempt = {
                                                                        createdDate: resp.createdDate,
                                                                        time: respTime,
                                                                        responses: [],
                                                                        visitor: null,
                                                                        customerCreated: false,
                                                                        customerExists: false,
                                                                        invoiceCreated: false,
                                                                        invoiceNumber: null,
                                                                        paymentTypes: [],
                                                                        errors: [],
                                                                    };
                                                                }
                                                                currentAttempt.responses.push(resp);

                                                                // Extract visitor info
                                                                if (resp.currentVisitor) {
                                                                    currentAttempt.visitor = resp.currentVisitor;
                                                                }

                                                                // Check customer status
                                                                if (resp.tripletexCustomers && Array.isArray(resp.tripletexCustomers) && resp.tripletexCustomers.length > 0) {
                                                                    currentAttempt.customerExists = true;
                                                                    currentAttempt.tripletexCustomer = resp.tripletexCustomers[0];
                                                                }
                                                                if (resp.tripletexCustomerCreated?.value?.id) {
                                                                    currentAttempt.customerCreated = true;
                                                                    currentAttempt.tripletexCustomer = resp.tripletexCustomerCreated.value;
                                                                }

                                                                // Check payment types
                                                                if (resp.tripletexPaymentTypes?.values && resp.tripletexPaymentTypes.values.length > 0) {
                                                                    currentAttempt.paymentTypes = resp.tripletexPaymentTypes.values;
                                                                }

                                                                // Check invoice creation
                                                                if (resp.apiResponse?.value?.invoiceNumber) {
                                                                    currentAttempt.invoiceCreated = true;
                                                                    currentAttempt.invoiceNumber = resp.apiResponse.value.invoiceNumber;
                                                                    currentAttempt.invoice = resp.apiResponse.value;
                                                                }
                                                                if (resp.tripletexInvoiceResponse?.invoiceNumber) {
                                                                    currentAttempt.invoiceCreated = true;
                                                                    currentAttempt.invoiceNumber = resp.tripletexInvoiceResponse.invoiceNumber;
                                                                    currentAttempt.invoice = resp.tripletexInvoiceResponse;
                                                                }

                                                                // Check for payments (queryPayment + addPayment)
                                                                if (resp.queryPayment || resp.addPayment) {
                                                                    if (!currentAttempt.payments) currentAttempt.payments = [];
                                                                    const paymentInfo = {
                                                                        localPaymentId: resp.localPaymentId,
                                                                        localPaymentMethod: resp.localPaymentMethod,
                                                                        paymentDate: resp.queryPayment?.paymentDate,
                                                                        paymentTypeId: resp.queryPayment?.paymentTypeId,
                                                                        paidAmount: resp.queryPayment?.paidAmount,
                                                                        success: !resp.addPayment?.error && resp.addPayment?.status !== 0,
                                                                        error: resp.addPayment?.error,
                                                                    };
                                                                    // Find payment type description from allPaymentTypes
                                                                    const paymentType = allPaymentTypes.find(t => t.id === paymentInfo.paymentTypeId);
                                                                    paymentInfo.paymentTypeDescription = paymentType?.description || `ID: ${paymentInfo.paymentTypeId}`;
                                                                    currentAttempt.payments.push(paymentInfo);
                                                                }

                                                                // Check for errors
                                                                if (resp.apiResponse?.status === 0 || resp.apiResponse?.validationMessages) {
                                                                    const msgs = resp.apiResponse?.validationMessages || [];
                                                                    msgs.forEach(m => {
                                                                        const errMsg = m.message || m.field || JSON.stringify(m);
                                                                        if (!currentAttempt.errors.includes(errMsg)) {
                                                                            currentAttempt.errors.push(errMsg);
                                                                        }
                                                                    });
                                                                }
                                                            });
                                                            if (currentAttempt) attempts.push(currentAttempt);

                                                            return (<>
                                                                {/* Global Payment Types & Mapping Info - Hidden behind toggle */}
                                                                {allPaymentTypes.length > 0 && (
                                                                    <details class='mb-3'>
                                                                        <summary class='p-2 border rounded bg-light' style='cursor: pointer; font-size: 0.85em;'>
                                                                            <i class='fa-duotone fa-credit-card me-1' /> Vis betalingstyper og mapping ({allPaymentTypes.length} typer)
                                                                        </summary>
                                                                        <div class='p-3 border border-top-0 rounded-bottom bg-light' style='font-size: 0.85em;'>
                                                                            {/* Available Tripletex Payment Types */}
                                                                            <div class='mb-3'>
                                                                                <strong style='font-size: 0.9em;'>Tilgjengelige betalingstyper i Tripletex ({allPaymentTypes.length}):</strong>
                                                                                <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                                    <thead class='table-light'>
                                                                                        <tr>
                                                                                            <th>ID</th>
                                                                                            <th>Beskrivelse</th>
                                                                                            <th>Inaktiv</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {allPaymentTypes.map((t, tIdx) => (
                                                                                            <tr key={tIdx} class={t.isInactive ? 'text-muted' : ''}>
                                                                                                <td><code>{t.id}</code></td>
                                                                                                <td>{t.description}</td>
                                                                                                <td>{t.isInactive ? 'Ja' : 'Nei'}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>

                                                                            {/* Payment Method Mapping */}
                                                                            <div class='mt-2'>
                                                                                <strong style='font-size: 0.9em;'>Betalingsmetode-mapping (intern → Tripletex):</strong>
                                                                                <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                                    <thead class='table-light'>
                                                                                        <tr>
                                                                                            <th>Intern metode</th>
                                                                                            <th>Tripletex type</th>
                                                                                            <th>Tripletex ID</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {paymentMethodMappings.map((m, mIdx) => (
                                                                                            <tr key={mIdx}>
                                                                                                <td><code>{m.internal}</code></td>
                                                                                                <td>{m.tripletexDesc}</td>
                                                                                                <td><code>{m.tripletexId}</code></td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        </div>
                                                                    </details>
                                                                )}

                                                                {/* Attempts */}
                                                                {attempts.map((attempt, idx) => {
                                                                const isSuccess = attempt.invoiceCreated || attempt.invoiceNumber;
                                                                return (
                                                                    <details key={idx} class='mt-3' open={idx === 0}>
                                                                        <summary class={`p-2 border rounded cursor-pointer ${isSuccess ? 'bg-light' : 'bg-danger bg-opacity-10'}`} style='cursor: pointer;'>
                                                                            <strong>
                                                                                {idx === 0 ? 'Latest ' : ''}Attempt #{attempts.length - idx}
                                                                            </strong>
                                                                            {' - '}
                                                                            {attempt.createdDate ? util.formatDate(attempt.createdDate, { locale: 'nb-NO', hour: '2-digit', minute: '2-digit' }) : 'Unknown date'}
                                                                            {isSuccess
                                                                                ? <span class='badge bg-success ms-2'>Success {attempt.invoiceNumber ? `(Invoice #${attempt.invoiceNumber})` : ''}</span>
                                                                                : <span class='badge bg-danger ms-2'>Failed</span>
                                                                            }
                                                                        </summary>
                                                                        <div class='p-3 border border-top-0 rounded-bottom bg-light' style='font-size: 0.85em;'>
                                                                            {/* Visitor Info */}
                                                                            {attempt.visitor && <>
                                                                                <div class='mb-3'>
                                                                                    <strong>Kunde:</strong>
                                                                                    <ul class='mt-1 mb-0' style='font-size: 0.9em;'>
                                                                                        <li>Navn: <code>{attempt.visitor.firstname} {attempt.visitor.lastname}</code></li>
                                                                                        {attempt.visitor.orgNumber && <li>Org.nr: <code>{attempt.visitor.orgNumber}</code></li>}
                                                                                        {attempt.visitor.email && <li>E-post: <code>{attempt.visitor.email}</code></li>}
                                                                                        {attempt.visitor.cellphone && <li>Telefon: <code>{attempt.visitor.cellphone}</code></li>}
                                                                                        {attempt.visitor.address && <li>Adresse: <code>{attempt.visitor.address}</code></li>}
                                                                                        {(attempt.visitor.postalcode || attempt.visitor.place) &&
                                                                                            <li>Sted: <code>{attempt.visitor.postalcode} {attempt.visitor.place}</code></li>
                                                                                        }
                                                                                    </ul>
                                                                                </div>
                                                                            </>}

                                                                            {/* Tripletex Customer Status */}
                                                                            <div class='mb-3'>
                                                                                <strong>Tripletex kundestatus:</strong>
                                                                                <ul class='mt-1 mb-0' style='font-size: 0.9em;'>
                                                                                    <li>Kunde funnet i Tripletex: {attempt.customerExists ? <span class='text-success'>Ja</span> : <span class='text-warning'>Nei</span>}</li>
                                                                                    <li>Kunde opprettet: {attempt.customerCreated ? <span class='text-success'>Ja</span> : <span class='text-muted'>Nei</span>}</li>
                                                                                    {attempt.tripletexCustomer && <>
                                                                                        <li>Tripletex kunde-ID: <code>{attempt.tripletexCustomer.id}</code></li>
                                                                                        <li>Tripletex kundenavn: <code>{attempt.tripletexCustomer.name}</code></li>
                                                                                        {attempt.tripletexCustomer.organizationNumber && <li>Org.nr: <code>{attempt.tripletexCustomer.organizationNumber}</code></li>}
                                                                                    </>}
                                                                                </ul>
                                                                            </div>

                                                                            {/* Invoice Status */}
                                                                            <div class='mb-3'>
                                                                                <strong>Fakturadetaljer:</strong>
                                                                                {attempt.invoiceCreated && attempt.invoice ? (
                                                                                    <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                                        <tbody>
                                                                                            <tr>
                                                                                                <td class='fw-bold' style='width: 40%;'>Status</td>
                                                                                                <td>
                                                                                                    <span class='badge bg-success'>Opprettet</span>
                                                                                                    {attempt.invoice.isCharged && <span class='badge bg-info ms-1'>Belastet</span>}
                                                                                                    {attempt.invoice.amountOutstanding === 0 && <span class='badge bg-success ms-1'>Betalt</span>}
                                                                                                    {attempt.invoice.amountOutstanding > 0 && <span class='badge bg-warning ms-1'>Ubetalt</span>}
                                                                                                    {attempt.invoice.isCredited && <span class='badge bg-danger ms-1'>Kreditert</span>}
                                                                                                    {attempt.invoice.isCreditNote && <span class='badge bg-secondary ms-1'>Kreditnota</span>}
                                                                                                </td>
                                                                                            </tr>
                                                                                            <tr>
                                                                                                <td class='fw-bold'>Fakturanummer</td>
                                                                                                <td><code>{attempt.invoice.invoiceNumber}</code></td>
                                                                                            </tr>
                                                                                            {attempt.invoice.kid && (
                                                                                                <tr>
                                                                                                    <td class='fw-bold'>KID</td>
                                                                                                    <td><code>{attempt.invoice.kid}</code></td>
                                                                                                </tr>
                                                                                            )}
                                                                                            <tr>
                                                                                                <td class='fw-bold'>Fakturadato</td>
                                                                                                <td>{attempt.invoice.invoiceDate}</td>
                                                                                            </tr>
                                                                                            <tr>
                                                                                                <td class='fw-bold'>Forfallsdato</td>
                                                                                                <td>{attempt.invoice.invoiceDueDate}</td>
                                                                                            </tr>
                                                                                            {attempt.invoice.deliveryDate && (
                                                                                                <tr>
                                                                                                    <td class='fw-bold'>Leveringsdato</td>
                                                                                                    <td>{attempt.invoice.deliveryDate}</td>
                                                                                                </tr>
                                                                                            )}
                                                                                            <tr class='table-light'>
                                                                                                <td class='fw-bold'>Beløp inkl. mva</td>
                                                                                                <td><strong>kr {(attempt.invoice.amount || 0).toFixed(2)}</strong></td>
                                                                                            </tr>
                                                                                            <tr>
                                                                                                <td class='fw-bold'>Beløp ekskl. mva</td>
                                                                                                <td>kr {(attempt.invoice.amountExcludingVat || 0).toFixed(2)}</td>
                                                                                            </tr>
                                                                                            <tr class={attempt.invoice.amountOutstanding > 0 ? 'table-warning' : 'table-success'}>
                                                                                                <td class='fw-bold'>Utestående</td>
                                                                                                <td>
                                                                                                    <strong>kr {(attempt.invoice.amountOutstanding || 0).toFixed(2)}</strong>
                                                                                                    {attempt.invoice.amountOutstanding === 0 && <span class='ms-2 text-success'>(Fullt betalt)</span>}
                                                                                                </td>
                                                                                            </tr>
                                                                                            {attempt.invoice.orderLines && attempt.invoice.orderLines.length > 0 && (
                                                                                                <tr>
                                                                                                    <td class='fw-bold'>Ordrelinjer</td>
                                                                                                    <td>{attempt.invoice.orderLines.length} linje(r)</td>
                                                                                                </tr>
                                                                                            )}
                                                                                            <tr>
                                                                                                <td class='fw-bold'>Tripletex ID</td>
                                                                                                <td><code>{attempt.invoice.id}</code></td>
                                                                                            </tr>
                                                                                        </tbody>
                                                                                    </table>
                                                                                ) : (
                                                                                    <ul class='mt-1 mb-0' style='font-size: 0.9em;'>
                                                                                        <li>Faktura opprettet: <span class='text-danger'>Nei</span></li>
                                                                                    </ul>
                                                                                )}
                                                                            </div>

                                                                            {/* Payments Section */}
                                                                            {attempt.payments && attempt.payments.length > 0 ? (
                                                                                <div class='mb-3'>
                                                                                    <strong>Betalinger registrert ({attempt.payments.length}):</strong>
                                                                                    <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                                        <thead class='table-light'>
                                                                                            <tr>
                                                                                                <th>Intern metode</th>
                                                                                                <th>Tripletex type</th>
                                                                                                <th>Beløp</th>
                                                                                                <th>Dato</th>
                                                                                                <th>Status</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {attempt.payments.map((payment, pIdx) => (
                                                                                                <tr key={pIdx} class={payment.success ? '' : 'table-danger'}>
                                                                                                    <td><code>{payment.localPaymentMethod || '-'}</code></td>
                                                                                                    <td>
                                                                                                        {payment.paymentTypeDescription}
                                                                                                        <span class='text-muted ms-1' style='font-size: 0.85em;'>(ID: {payment.paymentTypeId})</span>
                                                                                                    </td>
                                                                                                    <td><strong>kr {(payment.paidAmount || 0).toFixed(2)}</strong></td>
                                                                                                    <td>{payment.paymentDate || '-'}</td>
                                                                                                    <td>
                                                                                                        {payment.success
                                                                                                            ? <span class='badge bg-success'>OK</span>
                                                                                                            : <span class='badge bg-danger' title={payment.error}>Feil</span>
                                                                                                        }
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            ) : attempt.invoice && attempt.invoice.amountOutstanding === 0 && attempt.invoice.amount > 0 ? (
                                                                                <div class='mb-3'>
                                                                                    <strong>Betalingsinfo:</strong>
                                                                                    <div class='mt-1 p-2 border rounded bg-info bg-opacity-10' style='font-size: 0.85em;'>
                                                                                        <i class='fa-duotone fa-info-circle text-info' /> Fakturaen er betalt via <strong>shortcut-metoden</strong> (betaling inkludert i fakturaopprettelsen).
                                                                                        {e.paymentMethod && (
                                                                                            <div class='mt-1'>
                                                                                                Betalingsmetode fra salg: <code>{e.paymentMethod}</code>
                                                                                                {(() => {
                                                                                                    // Try to find matching Tripletex payment type
                                                                                                    const pm = e.paymentMethod?.toLowerCase();
                                                                                                    let matchedType = null;
                                                                                                    if (pm === 'cash') matchedType = allPaymentTypes.find(t => t.description?.toLowerCase().includes('kontant'));
                                                                                                    else if (pm === 'card' || pm === 'cardapp' || pm === 'cardexternal') matchedType = allPaymentTypes.find(t => t.description?.toLowerCase().includes('kort')) || allPaymentTypes.find(t => t.description?.toLowerCase().includes('bank'));
                                                                                                    else if (pm === 'vipps' || pm === 'bank' || pm === 'account') matchedType = allPaymentTypes.find(t => t.description?.toLowerCase().includes('bank'));
                                                                                                    if (matchedType) {
                                                                                                        return <span class='ms-2'>→ Tripletex: <strong>{matchedType.description}</strong> (ID: {matchedType.id})</span>;
                                                                                                    }
                                                                                                    return null;
                                                                                                })()}
                                                                                            </div>
                                                                                        )}
                                                                                        <div class='mt-1'>
                                                                                            Beløp betalt: <strong>kr {(attempt.invoice.amount || 0).toFixed(2)}</strong>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ) : null}

                                                                            {/* API Responses Table */}
                                                                            <div class='mb-3'>
                                                                                <strong>API-kall ({attempt.responses.length}):</strong>
                                                                                <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                                    <thead class='table-light'>
                                                                                        <tr>
                                                                                            <th>Tidspunkt</th>
                                                                                            <th>Type</th>
                                                                                            <th>Status</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {attempt.responses.map((resp, rIdx) => {
                                                                                            let type = 'Ukjent';
                                                                                            let status = '-';
                                                                                            let statusClass = '';

                                                                                            if (resp.tripletexCustomerQuery) {
                                                                                                // Show which matching criteria was used
                                                                                                const query = resp.tripletexCustomerQuery;
                                                                                                let matchType = '';
                                                                                                if (query.organizationNumber) {
                                                                                                    matchType = `org.nr: ${query.organizationNumber}`;
                                                                                                } else if (query.email) {
                                                                                                    matchType = `e-post: ${query.email}`;
                                                                                                } else if (query.phoneNumberMobile) {
                                                                                                    matchType = `tlf: ${query.phoneNumberMobile}`;
                                                                                                }
                                                                                                type = matchType ? `Kundesøk (${matchType})` : 'Kundesøk';
                                                                                                status = resp.tripletexCustomers?.values?.length > 0 ? `Fant ${resp.tripletexCustomers.values.length} kunde(r)` : 'Ingen treff';
                                                                                                statusClass = resp.tripletexCustomers?.values?.length > 0 ? 'text-success' : 'text-warning';
                                                                                            } else if (resp.tripletexCustomerCreated) {
                                                                                                type = 'Kundeopprettelse';
                                                                                                status = resp.tripletexCustomerCreated.value?.id ? 'OK' : 'Feil';
                                                                                                statusClass = resp.tripletexCustomerCreated.value?.id ? 'text-success' : 'text-danger';
                                                                                            } else if (resp.tripletexCustomerUpdated) {
                                                                                                type = 'Kundeoppdatering';
                                                                                                status = resp.tripletexCustomerUpdated.value?.id ? 'OK' : 'Feil';
                                                                                                statusClass = resp.tripletexCustomerUpdated.value?.id ? 'text-success' : 'text-danger';
                                                                                            } else if (resp.queryInvoiceCreate) {
                                                                                                type = 'Fakturaopprettelse';
                                                                                                status = resp.apiResponse?.value?.invoiceNumber ? `OK - #${resp.apiResponse.value.invoiceNumber}` : 'Feil';
                                                                                                statusClass = resp.apiResponse?.value?.invoiceNumber ? 'text-success' : 'text-danger';
                                                                                            } else if (resp.tripletexPaymentTypes) {
                                                                                                type = 'Betalingstyper';
                                                                                                const count = resp.tripletexPaymentTypes?.values?.length || 0;
                                                                                                status = `Fant ${count} typer`;
                                                                                                statusClass = 'text-success';
                                                                                            } else if (resp.queryPayment) {
                                                                                                type = 'Betaling';
                                                                                                const paymentType = allPaymentTypes.find(t => t.id === resp.queryPayment?.paymentTypeId);
                                                                                                const amount = resp.queryPayment?.paidAmount || 0;
                                                                                                const hasError = resp.addPayment?.error || resp.addPayment?.status === 0;
                                                                                                status = `${paymentType?.description || 'Ukjent'} - kr ${amount.toFixed(2)}`;
                                                                                                statusClass = hasError ? 'text-danger' : 'text-success';
                                                                                            } else if (resp.tripletexInvoiceResponse) {
                                                                                                type = 'Fakturastatus';
                                                                                                status = `#${resp.tripletexInvoiceResponse.invoiceNumber}`;
                                                                                                statusClass = 'text-success';
                                                                                            } else if (resp.tripletexProducts || resp.tripletexProductsFinal) {
                                                                                                type = 'Produktsøk';
                                                                                                const count = resp.tripletexProducts?.values?.length || resp.tripletexProductsFinal?.values?.length || 0;
                                                                                                status = `Fant ${count} produkt(er)`;
                                                                                                statusClass = 'text-success';
                                                                                            } else if (resp.tripletexProductCreated) {
                                                                                                type = 'Produktopprettet';
                                                                                                status = resp.tripletexProductCreated?.value?.name || 'OK';
                                                                                                statusClass = 'text-success';
                                                                                            } else if (resp.queryCreditNote) {
                                                                                                type = 'Kreditnota';
                                                                                                status = resp.apiResponse?.value?.invoiceNumber ? `OK - #${resp.apiResponse.value.invoiceNumber}` : 'Sendt';
                                                                                                statusClass = resp.apiResponse?.value?.invoiceNumber ? 'text-success' : 'text-info';
                                                                                            } else if (resp.error) {
                                                                                                // Error response from validation
                                                                                                type = resp.type === 'creditNote' ? 'Kreditnota feil' : 'Feil';
                                                                                                status = resp.error;
                                                                                                statusClass = 'text-danger';
                                                                                            }

                                                                                            return (
                                                                                                <tr key={rIdx}>
                                                                                                    <td>{resp.createdDate ? util.formatDate(resp.createdDate, { locale: 'nb-NO', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}</td>
                                                                                                    <td>{type}</td>
                                                                                                    <td class={statusClass}>{status}</td>
                                                                                                </tr>
                                                                                            );
                                                                                        })}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>

                                                                            {/* Errors */}
                                                                            {attempt.errors && attempt.errors.length > 0 && <>
                                                                                <div class='mb-3'>
                                                                                    <strong class='text-danger'>Feil ({attempt.errors.length}):</strong>
                                                                                    <ul class='mt-1 mb-0 text-danger'>
                                                                                        {attempt.errors.map((err, idx) => (
                                                                                            <li key={idx}>{err}</li>
                                                                                        ))}
                                                                                    </ul>
                                                                                </div>
                                                                            </>}
                                                                        </div>
                                                                    </details>
                                                                );
                                                            })}
                                                            </>);
                                                        })()}

                                                {isAdmin && <>
                                                    <details class='mt-3'>
                                                        <summary class='p-2 border rounded bg-secondary bg-opacity-10' style='cursor: pointer; font-size: 0.85em;'>
                                                            <i class='fa-duotone fa-code' /> Vis rå JSON data
                                                        </summary>
                                                        <div class='p-2 border border-top-0 rounded-bottom' style='max-width: 100%; overflow-x: auto; font-size: 0.8em;'>
                                                            <xmp style='white-space: pre-wrap; word-break: break-all;'>Error: {JSON.stringify(e.tripletexResponse, null, 4)}</xmp>
                                                            <xmp style='white-space: pre-wrap; word-break: break-all;'>Request: {JSON.stringify(e.tripletexRequest, null, 4)}</xmp>
                                                            <xmp style='white-space: pre-wrap; word-break: break-all;'>tripletexApiResponses: {JSON.stringify(saleDebugData[e.id]?.tripletexApiResponses || e.tripletexApiResponses, null, 4)}</xmp>
                                                            <xmp style='white-space: pre-wrap; word-break: break-all;'>tripletexDebug: {JSON.stringify(saleDebugData[e.id]?.tripletexDebug || e.tripletexDebug, null, 4)}</xmp>
                                                        </div>
                                                    </details>
                                                </>}
                                            </td>
                                        </tr>
                                    </> : <></>}
                                    {displayFikenError[e.id] ? <>
                                        <tr>
                                            <td colspan='17'>
                                                {e.fikenResponse && e.fikenResponse?.statusCode && <>
                                                    <h5>Error: {e.fikenResponse?.statusCode}</h5>
                                                    <div>
                                                        {e.fikenResponse?.error_description}
                                                    </div>
                                                </>}

                                                {e.fikenResponse && e.fikenResponse[0] && e.fikenResponse[0].message && <>
                                                    <h5>Error: {e.fikenResponse[0].message}</h5>
                                                    <div>
                                                        {e.fikenResponse[0].message}
                                                    </div>
                                                </>}

                                                {/* Fiken Debug Info - User Friendly Display */}
                                                {(() => {
                                                    const fikenApiResponsesData = saleDebugData[e.id]?.fikenApiResponses || e.fikenApiResponses;

                                                    // Loading indicator
                                                    if (loadingDebugData[e.id]) {
                                                        return (
                                                            <div class='d-flex align-items-center justify-content-center py-4'>
                                                                <i class='fa-duotone fa-spinner fa-spin me-2' />
                                                                Laster debug-data...
                                                            </div>
                                                        );
                                                    }

                                                    // Skip if no API responses data
                                                    if (!fikenApiResponsesData || !Array.isArray(fikenApiResponsesData) || fikenApiResponsesData.length === 0) {
                                                        return null;
                                                    }

                                                    // Fiken account mapping (account code -> description)
                                                    const fikenAccountMappings = {
                                                            '1900': { name: 'Kasse (kontant)', type: 'cash' },
                                                            '1910': { name: 'Kasse', type: 'cash' },
                                                            '1920': { name: 'Bank', type: 'bank' },
                                                            '1500': { name: 'Kundefordringer (ubetalt)', type: 'receivable' },
                                                        };

                                                        // Payment method to Fiken account mapping
                                                        const paymentMethodToAccount = [
                                                            { internal: 'cash', fikenAccount: '1900', fikenName: 'Kasse (kontant)' },
                                                            { internal: 'card', fikenAccount: '1920', fikenName: 'Bank' },
                                                            { internal: 'cardExternal', fikenAccount: '1920', fikenName: 'Bank' },
                                                            { internal: 'cardApp', fikenAccount: '1920', fikenName: 'Bank' },
                                                            { internal: 'vipps', fikenAccount: '1920', fikenName: 'Bank' },
                                                            { internal: 'bank', fikenAccount: '1920', fikenName: 'Bank' },
                                                            { internal: 'account', fikenAccount: '1920', fikenName: 'Bank' },
                                                            { internal: 'invoice', fikenAccount: '1500', fikenName: 'Kundefordringer (ubetalt)' },
                                                            { internal: 'giftCard', fikenAccount: '1900', fikenName: 'Kasse (kontant)' },
                                                        ];

                                                        // Get account description from account code (e.g., "1920:10001" -> "Bank")
                                                        const getAccountDescription = (accountCode) => {
                                                            if (!accountCode) return 'Ukjent';
                                                            const baseAccount = accountCode.split(':')[0];
                                                            return fikenAccountMappings[baseAccount]?.name || `Konto ${baseAccount}`;
                                                        };

                                                        // Group responses by createdDate (approximate grouping)
                                                        const attempts = [];
                                                        let currentAttempt = null;

                                                        // Sort by createdDate descending
                                                        const sortedResponses = [...fikenApiResponsesData].sort((a, b) =>
                                                            new Date(b.createdDate || 0) - new Date(a.createdDate || 0)
                                                        );

                                                        sortedResponses.forEach(resp => {
                                                            // Group responses within 60 seconds as one attempt
                                                            const respTime = new Date(resp.createdDate || 0).getTime();
                                                            if (!currentAttempt || (currentAttempt.time - respTime > 60000)) {
                                                                if (currentAttempt) attempts.push(currentAttempt);
                                                                currentAttempt = {
                                                                    createdDate: resp.createdDate,
                                                                    time: respTime,
                                                                    responses: [],
                                                                    visitor: null,
                                                                    customerCreated: false,
                                                                    customerExists: false,
                                                                    invoiceCreated: false,
                                                                    saleNumber: null,
                                                                    settled: null,
                                                                    settledDate: null,
                                                                    totalPaid: null,
                                                                    outstandingBalance: null,
                                                                    payments: [],
                                                                    paymentAccount: null,
                                                                    errors: [],
                                                                };
                                                            }
                                                            currentAttempt.responses.push(resp);

                                                            // Extract visitor info
                                                            if (resp.currentVisitor) {
                                                                currentAttempt.visitor = resp.currentVisitor;
                                                            }

                                                            // Check customer status
                                                            if (resp.fikenCustomers && Array.isArray(resp.fikenCustomers) && resp.fikenCustomers.length > 0) {
                                                                currentAttempt.customerExists = true;
                                                                currentAttempt.fikenCustomer = resp.fikenCustomers[0];
                                                            }
                                                            if (resp.fikenCustomerCreated) {
                                                                if (resp.fikenCustomerCreated.location) {
                                                                    currentAttempt.customerCreated = true;
                                                                } else if (resp.fikenCustomerCreated.statusCode >= 400) {
                                                                    currentAttempt.errors.push(`Kunde: ${resp.fikenCustomerCreated.error_description || resp.fikenCustomerCreated.message || 'Feil ved opprettelse'}`);
                                                                }
                                                            }

                                                            // Check invoice/sale creation
                                                            if (resp.apiResponse?.location || resp.fikenSaleResponse?.saleNumber) {
                                                                currentAttempt.invoiceCreated = true;
                                                            }
                                                            if (resp.apiResponse?.statusCode >= 400) {
                                                                const msg = resp.apiResponse?.error_description || resp.apiResponse?.[0]?.message || 'Feil ved faktura';
                                                                if (!currentAttempt.errors.includes(msg)) {
                                                                    currentAttempt.errors.push(msg);
                                                                }
                                                            }

                                                            // Get sale status from fikenSaleResponse
                                                            if (resp.fikenSaleResponse) {
                                                                currentAttempt.saleNumber = resp.fikenSaleResponse.saleNumber;
                                                                currentAttempt.settled = resp.fikenSaleResponse.settled;
                                                                currentAttempt.settledDate = resp.fikenSaleResponse.settledDate;
                                                                currentAttempt.totalPaid = resp.fikenSaleResponse.totalPaid;
                                                                currentAttempt.outstandingBalance = resp.fikenSaleResponse.outstandingBalance;
                                                                // Get payments from fikenSaleResponse
                                                                if (resp.fikenSaleResponse.salePayments && resp.fikenSaleResponse.salePayments.length > 0) {
                                                                    currentAttempt.payments = resp.fikenSaleResponse.salePayments;
                                                                }
                                                            }

                                                            // Extract payment info from querySaleCreate
                                                            if (resp.querySaleCreate) {
                                                                currentAttempt.paymentAccount = resp.querySaleCreate.paymentAccount;
                                                                currentAttempt.queryPaymentDate = resp.querySaleCreate.paymentDate;
                                                                currentAttempt.queryTotalPaid = resp.querySaleCreate.totalPaid;
                                                                currentAttempt.queryCash = resp.querySaleCreate.cash;
                                                            }
                                                        });
                                                        if (currentAttempt) attempts.push(currentAttempt);

                                                        // Also extract from fikenResponse if available
                                                        const fikenResponse = e.fikenResponse;
                                                        const hasValidFikenResponse = fikenResponse && fikenResponse.saleId && !fikenResponse.statusCode;

                                                        return (<>
                                                            {/* Payment Account Mapping Info - Hidden behind toggle */}
                                                            <details class='mb-3'>
                                                                <summary class='p-2 border rounded bg-light' style='cursor: pointer; font-size: 0.85em;'>
                                                                    <i class='fa-duotone fa-credit-card me-1' /> Vis betalingskontoer og mapping
                                                                </summary>
                                                                <div class='p-3 border border-top-0 rounded-bottom bg-light' style='font-size: 0.85em;'>
                                                                    {/* Fiken Account Types */}
                                                                    <div class='mb-3'>
                                                                        <strong style='font-size: 0.9em;'>Fiken kontoer:</strong>
                                                                        <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                            <thead class='table-light'>
                                                                                <tr>
                                                                                    <th>Konto</th>
                                                                                    <th>Beskrivelse</th>
                                                                                    <th>Type</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {Object.entries(fikenAccountMappings).map(([code, info], idx) => (
                                                                                    <tr key={idx}>
                                                                                        <td><code>{code}</code></td>
                                                                                        <td>{info.name}</td>
                                                                                        <td><code>{info.type}</code></td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>

                                                                    {/* Payment Method Mapping */}
                                                                    <div class='mt-2'>
                                                                        <strong style='font-size: 0.9em;'>Betalingsmetode-mapping (intern → Fiken):</strong>
                                                                        <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                            <thead class='table-light'>
                                                                                <tr>
                                                                                    <th>Intern metode</th>
                                                                                    <th>Fiken konto</th>
                                                                                    <th>Beskrivelse</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {paymentMethodToAccount.map((m, mIdx) => (
                                                                                    <tr key={mIdx}>
                                                                                        <td><code>{m.internal}</code></td>
                                                                                        <td><code>{m.fikenAccount}</code></td>
                                                                                        <td>{m.fikenName}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </details>

                                                            {/* Current Sale Status from fikenResponse */}
                                                            {hasValidFikenResponse && (
                                                                <div class='mb-3 p-3 border rounded bg-success bg-opacity-10' style='font-size: 0.85em;'>
                                                                    <strong><i class='fa-duotone fa-check-circle text-success me-1' /> Gjeldende status i Fiken:</strong>
                                                                    <div class='row mt-2'>
                                                                        <div class='col-md-6'>
                                                                            <ul class='mb-0' style='font-size: 0.9em;'>
                                                                                <li>Salgs-ID: <code>{fikenResponse.saleId}</code></li>
                                                                                <li>Dato: <code>{fikenResponse.date}</code></li>
                                                                                <li>Forfallsdato: <code>{fikenResponse.dueDate}</code></li>
                                                                                <li>Type: <code>{fikenResponse.kind}</code></li>
                                                                            </ul>
                                                                        </div>
                                                                        <div class='col-md-6'>
                                                                            <ul class='mb-0' style='font-size: 0.9em;'>
                                                                                <li>Netto: <strong>kr {((fikenResponse.netAmount || 0) / 100).toFixed(2)}</strong></li>
                                                                                <li>MVA: <strong>kr {((fikenResponse.vatAmount || 0) / 100).toFixed(2)}</strong></li>
                                                                                <li>Totalt betalt: <strong class='text-success'>kr {((fikenResponse.totalPaid || 0) / 100).toFixed(2)}</strong></li>
                                                                                <li>Utestående: <strong class={fikenResponse.outstandingBalance > 0 ? 'text-warning' : 'text-success'}>
                                                                                    kr {((fikenResponse.outstandingBalance || 0) / 100).toFixed(2)}
                                                                                </strong></li>
                                                                            </ul>
                                                                        </div>
                                                                    </div>

                                                                    {/* Payment Status */}
                                                                    <div class='mt-2'>
                                                                        <span class={`badge ${fikenResponse.settled ? 'bg-success' : 'bg-warning'} me-2`}>
                                                                            {fikenResponse.settled ? 'Betalt' : 'Ubetalt'}
                                                                        </span>
                                                                        {fikenResponse.settledDate && (
                                                                            <span class='text-muted' style='font-size: 0.9em;'>
                                                                                Oppgjort: {fikenResponse.settledDate}
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    {/* Payments registered in Fiken */}
                                                                    {fikenResponse.salePayments && fikenResponse.salePayments.length > 0 && (
                                                                        <div class='mt-3'>
                                                                            <strong style='font-size: 0.9em;'>Betalinger registrert i Fiken ({fikenResponse.salePayments.length}):</strong>
                                                                            <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                                <thead class='table-light'>
                                                                                    <tr>
                                                                                        <th>Betalings-ID</th>
                                                                                        <th>Dato</th>
                                                                                        <th>Konto</th>
                                                                                        <th>Beskrivelse</th>
                                                                                        <th>Beløp</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {fikenResponse.salePayments.map((payment, pIdx) => (
                                                                                        <tr key={pIdx}>
                                                                                            <td><code>{payment.paymentId}</code></td>
                                                                                            <td>{payment.date}</td>
                                                                                            <td><code>{payment.account}</code></td>
                                                                                            <td>{getAccountDescription(payment.account)}</td>
                                                                                            <td><strong>kr {((payment.amount || 0) / 100).toFixed(2)}</strong></td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    )}

                                                                    {/* Customer info from Fiken */}
                                                                    {fikenResponse.customer && (
                                                                        <div class='mt-3'>
                                                                            <strong style='font-size: 0.9em;'>Kunde i Fiken:</strong>
                                                                            <ul class='mb-0 mt-1' style='font-size: 0.9em;'>
                                                                                <li>Navn: <code>{fikenResponse.customer.name}</code></li>
                                                                                <li>Kundenummer: <code>{fikenResponse.customer.customerNumber}</code></li>
                                                                                <li>Kontakt-ID: <code>{fikenResponse.customer.contactId}</code></li>
                                                                                {fikenResponse.customer.organizationNumber && <li>Org.nr: <code>{fikenResponse.customer.organizationNumber}</code></li>}
                                                                                {fikenResponse.customer.email && <li>E-post: <code>{fikenResponse.customer.email}</code></li>}
                                                                            </ul>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {/* Attempts */}
                                                            {attempts.map((attempt, idx) => {
                                                            const isSuccess = attempt.invoiceCreated || attempt.saleNumber;
                                                            return (
                                                                <details key={idx} class='mt-3' open={idx === 0}>
                                                                    <summary class={`p-2 border rounded cursor-pointer ${isSuccess ? 'bg-light' : 'bg-danger bg-opacity-10'}`} style='cursor: pointer;'>
                                                                        <strong>
                                                                            {idx === 0 ? 'Latest ' : ''}Attempt #{attempts.length - idx}
                                                                        </strong>
                                                                        {' - '}
                                                                        {attempt.createdDate ? util.formatDate(attempt.createdDate, { locale: 'nb-NO', hour: '2-digit', minute: '2-digit' }) : 'Unknown date'}
                                                                        {isSuccess
                                                                            ? <span class='badge bg-success ms-2'>Success {attempt.saleNumber ? `(Invoice #${attempt.saleNumber})` : ''}</span>
                                                                            : <span class='badge bg-danger ms-2'>Failed</span>
                                                                        }
                                                                    </summary>
                                                                    <div class='p-3 border border-top-0 rounded-bottom bg-light' style='font-size: 0.85em;'>
                                                                        {/* Visitor Info */}
                                                                        {attempt.visitor && <>
                                                                            <div class='mb-3'>
                                                                                <strong>Kunde:</strong>
                                                                                <ul class='mt-1 mb-0' style='font-size: 0.9em;'>
                                                                                    <li>Navn: <code>{attempt.visitor.firstname} {attempt.visitor.lastname}</code></li>
                                                                                    {attempt.visitor.orgNumber && <li>Org.nr: <code>{attempt.visitor.orgNumber}</code></li>}
                                                                                    {attempt.visitor.email && <li>E-post: <code>{attempt.visitor.email}</code></li>}
                                                                                    {attempt.visitor.cellphone && <li>Telefon: <code>{attempt.visitor.cellphone}</code></li>}
                                                                                    {attempt.visitor.address && <li>Adresse: <code>{attempt.visitor.address}</code></li>}
                                                                                    {(attempt.visitor.postalcode || attempt.visitor.place) &&
                                                                                        <li>Sted: <code>{attempt.visitor.postalcode} {attempt.visitor.place}</code></li>
                                                                                    }
                                                                                </ul>
                                                                            </div>
                                                                        </>}

                                                                        {/* Fiken Customer Status */}
                                                                        <div class='mb-3'>
                                                                            <strong>Fiken kundestatus:</strong>
                                                                            <ul class='mt-1 mb-0' style='font-size: 0.9em;'>
                                                                                <li>Kunde funnet i Fiken: {attempt.customerExists ? <span class='text-success'>Ja</span> : <span class='text-warning'>Nei</span>}</li>
                                                                                <li>Kunde opprettet: {attempt.customerCreated ? <span class='text-success'>Ja</span> : <span class='text-muted'>Nei</span>}</li>
                                                                                {attempt.fikenCustomer && <>
                                                                                    <li>Fiken kunde-ID: <code>{attempt.fikenCustomer.contactId}</code></li>
                                                                                    <li>Fiken kundenavn: <code>{attempt.fikenCustomer.name}</code></li>
                                                                                    {attempt.fikenCustomer.organizationNumber && <li>Org.nr: <code>{attempt.fikenCustomer.organizationNumber}</code></li>}
                                                                                </>}
                                                                            </ul>
                                                                        </div>

                                                                        {/* Invoice/Sale Status */}
                                                                        <div class='mb-3'>
                                                                            <strong>Fakturastatus:</strong>
                                                                            <ul class='mt-1 mb-0' style='font-size: 0.9em;'>
                                                                                <li>Faktura opprettet: {attempt.invoiceCreated ? <span class='text-success'>Ja</span> : <span class='text-danger'>Nei</span>}</li>
                                                                                {attempt.saleNumber && <>
                                                                                    <li>Fakturanummer: <code>{attempt.saleNumber}</code></li>
                                                                                    <li>Betalt: {attempt.settled ? <span class='text-success'>Ja</span> : <span class='text-warning'>Nei</span>}</li>
                                                                                    {attempt.settledDate && <li>Oppgjort dato: <code>{attempt.settledDate}</code></li>}
                                                                                    {attempt.totalPaid !== null && <li>Totalt betalt: <code>kr {(attempt.totalPaid / 100).toFixed(2)}</code></li>}
                                                                                    {!attempt.settled && attempt.outstandingBalance !== null &&
                                                                                        <li>Utestående: <code>kr {(attempt.outstandingBalance / 100).toFixed(2)}</code></li>
                                                                                    }
                                                                                </>}
                                                                            </ul>
                                                                        </div>

                                                                        {/* Payment Info from querySaleCreate */}
                                                                        {attempt.paymentAccount && (
                                                                            <div class='mb-3'>
                                                                                <strong>Betalingsinfo sendt til Fiken:</strong>
                                                                                <ul class='mt-1 mb-0' style='font-size: 0.9em;'>
                                                                                    <li>Betalingskonto: <code>{attempt.paymentAccount}</code> ({getAccountDescription(attempt.paymentAccount)})</li>
                                                                                    {attempt.queryPaymentDate && <li>Betalingsdato: <code>{attempt.queryPaymentDate}</code></li>}
                                                                                    {attempt.queryTotalPaid !== null && <li>Beløp: <code>kr {(attempt.queryTotalPaid / 100).toFixed(2)}</code></li>}
                                                                                    <li>Kontant: {attempt.queryCash ? <span class='text-success'>Ja</span> : <span class='text-muted'>Nei</span>}</li>
                                                                                </ul>
                                                                            </div>
                                                                        )}

                                                                        {/* Payments from fikenSaleResponse */}
                                                                        {attempt.payments && attempt.payments.length > 0 && (
                                                                            <div class='mb-3'>
                                                                                <strong>Betalinger registrert ({attempt.payments.length}):</strong>
                                                                                <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                                    <thead class='table-light'>
                                                                                        <tr>
                                                                                            <th>ID</th>
                                                                                            <th>Dato</th>
                                                                                            <th>Konto</th>
                                                                                            <th>Beskrivelse</th>
                                                                                            <th>Beløp</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {attempt.payments.map((p, pIdx) => (
                                                                                            <tr key={pIdx}>
                                                                                                <td><code>{p.paymentId}</code></td>
                                                                                                <td>{p.date}</td>
                                                                                                <td><code>{p.account}</code></td>
                                                                                                <td>{getAccountDescription(p.account)}</td>
                                                                                                <td><strong>kr {((p.amount || 0) / 100).toFixed(2)}</strong></td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        )}

                                                                        {/* API Responses Table */}
                                                                        <div class='mb-3'>
                                                                            <strong>API-kall ({attempt.responses.length}):</strong>
                                                                            <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                                <thead class='table-light'>
                                                                                    <tr>
                                                                                        <th>Tidspunkt</th>
                                                                                        <th>Type</th>
                                                                                        <th>Status</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {attempt.responses.map((resp, rIdx) => {
                                                                                        let type = 'Ukjent';
                                                                                        let status = '-';
                                                                                        let statusClass = '';

                                                                                        // Use new type/status fields if available
                                                                                        if (resp.type) {
                                                                                            const typeMap = {
                                                                                                'saleCreate': 'Salgsopprettelse',
                                                                                                'invoiceCreate': 'Fakturaopprettelse',
                                                                                                'creditNote': 'Kreditnota',
                                                                                                'customerCreate': 'Kundeopprettelse',
                                                                                                'customerLookup': 'Kundesøk',
                                                                                            };
                                                                                            type = typeMap[resp.type] || resp.type;
                                                                                            if (resp.status === 'success') {
                                                                                                status = 'OK';
                                                                                                statusClass = 'text-success';
                                                                                            } else if (resp.status === 'error') {
                                                                                                status = `Feil: ${resp.statusCode || ''}`;
                                                                                                statusClass = 'text-danger';
                                                                                            } else if (resp.status === 'simulated') {
                                                                                                status = 'Simulert';
                                                                                                statusClass = 'text-info';
                                                                                            }
                                                                                        } else if (resp.fikenCustomerQuery) {
                                                                                            // Show which matching criteria was used
                                                                                            const query = resp.fikenCustomerQuery;
                                                                                            let matchType = '';
                                                                                            if (query.organizationNumber) {
                                                                                                matchType = `org.nr: ${query.organizationNumber}`;
                                                                                            } else if (query.email) {
                                                                                                matchType = `e-post: ${query.email}`;
                                                                                            } else if (query.phoneNumber) {
                                                                                                matchType = `tlf: ${query.phoneNumber}`;
                                                                                            }
                                                                                            type = matchType ? `Kundesøk (${matchType})` : 'Kundesøk';
                                                                                            status = resp.fikenCustomers?.length > 0 ? `Fant ${resp.fikenCustomers.length} kunde(r)` : 'Ingen treff';
                                                                                            statusClass = resp.fikenCustomers?.length > 0 ? 'text-success' : 'text-warning';
                                                                                        } else if (resp.fikenCustomerCreated) {
                                                                                            type = 'Kundeopprettelse';
                                                                                            status = resp.fikenCustomerCreated.location ? 'OK' : `Feil: ${resp.fikenCustomerCreated.statusCode}`;
                                                                                            statusClass = resp.fikenCustomerCreated.location ? 'text-success' : 'text-danger';
                                                                                        } else if (resp.queryInvoiceCreate) {
                                                                                            type = 'Fakturaopprettelse';
                                                                                            status = resp.apiResponse?.location ? 'OK' : `Feil: ${resp.apiResponse?.statusCode || resp.apiResponse?.[0]?.message || 'Ukjent'}`;
                                                                                            statusClass = resp.apiResponse?.location ? 'text-success' : 'text-danger';
                                                                                        } else if (resp.querySaleCreate) {
                                                                                            type = 'Salgsopprettelse';
                                                                                            status = resp.apiResponse?.location ? 'OK' : `Feil: ${resp.apiResponse?.statusCode || 'Ukjent'}`;
                                                                                            statusClass = resp.apiResponse?.location ? 'text-success' : 'text-danger';
                                                                                        } else if (resp.fikenSaleResponse) {
                                                                                            type = 'Salgsstatus';
                                                                                            status = `#${resp.fikenSaleResponse.saleNumber} - ${resp.fikenSaleResponse.settled ? 'Betalt' : 'Utestående'}`;
                                                                                            statusClass = resp.fikenSaleResponse.settled ? 'text-success' : 'text-warning';
                                                                                        }

                                                                                        return (
                                                                                            <tr key={rIdx}>
                                                                                                <td>{resp.createdDate ? util.formatDate(resp.createdDate, { locale: 'nb-NO', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}</td>
                                                                                                <td>{type}</td>
                                                                                                <td class={statusClass}>{status}</td>
                                                                                            </tr>
                                                                                        );
                                                                                    })}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>

                                                                        {/* Errors */}
                                                                        {attempt.errors && attempt.errors.length > 0 && <>
                                                                            <div class='mb-3'>
                                                                                <strong class='text-danger'>Feil ({attempt.errors.length}):</strong>
                                                                                <ul class='mt-1 mb-0 text-danger'>
                                                                                    {attempt.errors.map((err, idx) => (
                                                                                        <li key={idx}>{err}</li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>
                                                                        </>}
                                                                    </div>
                                                                </details>
                                                            );
                                                        })}
                                                        </>);
                                                })()}

                                                {isAdmin && <>
                                                    <details class='mt-3'>
                                                        <summary class='p-2 border rounded bg-secondary bg-opacity-10' style='cursor: pointer; font-size: 0.85em;'>
                                                            <i class='fa-duotone fa-code' /> Vis rå JSON data
                                                        </summary>
                                                        <div class='p-2 border border-top-0 rounded-bottom' style='max-width: 100%; overflow-x: auto; font-size: 0.8em;'>
                                                            {/* Show latest API request if available */}
                                                            {(() => {
                                                                const fikenApiResponsesRaw = saleDebugData[e.id]?.fikenApiResponses || e.fikenApiResponses;
                                                                const latestWithRequest = fikenApiResponsesRaw?.find(r => r.apiRequest);
                                                                if (latestWithRequest?.apiRequest) {
                                                                    return (
                                                                        <div class='mb-3 p-2 bg-info bg-opacity-10 rounded'>
                                                                            <strong>Siste API-forespørsel:</strong>
                                                                            <xmp style='white-space: pre-wrap; word-break: break-all;'>{JSON.stringify(latestWithRequest.apiRequest, null, 4)}</xmp>
                                                                        </div>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                            <xmp style='white-space: pre-wrap; word-break: break-all;'>fikenResponse: {JSON.stringify(e.fikenResponse, null, 4)}</xmp>
                                                            <xmp style='white-space: pre-wrap; word-break: break-all;'>fikenApiResponses: {JSON.stringify(saleDebugData[e.id]?.fikenApiResponses || e.fikenApiResponses, null, 4)}</xmp>
                                                        </div>
                                                    </details>
                                                </>}

                                            </td>
                                        </tr>
                                    </> : <></>}
                                    {powerofficeError[e.id] ? <>
                                        <tr>
                                            <td colspan='17'>
                                                {/* PowerOffice Invoice Preview - Shows what data would be sent */}
                                                {e.powerOfficeInvoicePreview && <>
                                                    <details class='mb-3' open>
                                                        <summary class={`p-2 border rounded cursor-pointer ${e.powerOfficeInvoicePreview.status === 'completed' ? 'bg-success bg-opacity-10' : e.powerOfficeInvoicePreview.status === 'failed' ? 'bg-danger bg-opacity-10' : 'bg-warning bg-opacity-10'}`} style='cursor: pointer;'>
                                                            <strong>
                                                                <i class='fa-duotone fa-file-invoice me-2' />
                                                                PowerOffice Invoice Preview
                                                            </strong>
                                                            <span class={`badge ms-2 ${e.powerOfficeInvoicePreview.status === 'completed' ? 'bg-success' : e.powerOfficeInvoicePreview.status === 'failed' ? 'bg-danger' : 'bg-warning'}`}>
                                                                {e.powerOfficeInvoicePreview.status}
                                                            </span>
                                                            {e.powerOfficeInvoicePreview.powerOfficeInvoiceId && <span class='badge bg-info ms-2'>Invoice ID: {e.powerOfficeInvoicePreview.powerOfficeInvoiceId}</span>}
                                                        </summary>
                                                        <div class='p-3 border border-top-0 rounded-bottom bg-light' style='font-size: 0.85em;'>
                                                            {/* API Calls Status */}
                                                            <div class='mb-3'>
                                                                <strong>API Calls Status:</strong>
                                                                <div class='d-flex flex-wrap gap-2 mt-1'>
                                                                    {Object.entries(e.powerOfficeInvoicePreview.apiCallsStatus || {}).map(([key, status]) => (
                                                                        <span key={key} class={`badge ${status === 'success' ? 'bg-success' : status === 'failed' ? 'bg-danger' : status === 'pending' ? 'bg-warning' : 'bg-secondary'}`}>
                                                                            {key}: {status}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {/* Errors */}
                                                            {e.powerOfficeInvoicePreview.errors && e.powerOfficeInvoicePreview.errors.length > 0 && <>
                                                                <div class='mb-3 text-danger'>
                                                                    <strong>Errors:</strong>
                                                                    <ul class='mb-0'>
                                                                        {e.powerOfficeInvoicePreview.errors.map((err, idx) => (
                                                                            <li key={idx}>{typeof err === 'string' ? err : JSON.stringify(err)}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            </>}

                                                            {/* Visitor Info */}
                                                            {e.powerOfficeInvoicePreview.visitor && <div class='mb-3'>
                                                                <strong>Customer/Visitor:</strong>
                                                                <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                    <tbody>
                                                                        <tr><td class='fw-bold' style='width: 150px;'>ID</td><td>{e.powerOfficeInvoicePreview.visitor?.id}</td></tr>
                                                                        <tr><td class='fw-bold'>Name</td><td>{e.powerOfficeInvoicePreview.visitor?.name}</td></tr>
                                                                        <tr><td class='fw-bold'>Email</td><td>{e.powerOfficeInvoicePreview.visitor?.email}</td></tr>
                                                                        <tr><td class='fw-bold'>Address</td><td>{e.powerOfficeInvoicePreview.visitor?.address}</td></tr>
                                                                        <tr><td class='fw-bold'>Postal Code</td><td>{e.powerOfficeInvoicePreview.visitor?.postalcode}</td></tr>
                                                                        <tr><td class='fw-bold'>Place</td><td>{e.powerOfficeInvoicePreview.visitor?.place}</td></tr>
                                                                        {e.powerOfficeInvoicePreview.visitor?.orgNumber && <tr><td class='fw-bold'>Org Number</td><td>{e.powerOfficeInvoicePreview.visitor?.orgNumber}</td></tr>}
                                                                    </tbody>
                                                                </table>
                                                            </div>}

                                                            {/* PowerOffice Customer */}
                                                            {e.powerOfficeInvoicePreview.powerOfficeCustomer && <>
                                                                <div class='mb-3'>
                                                                    <strong>PowerOffice Customer:</strong>
                                                                    <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                        <tbody>
                                                                            <tr><td class='fw-bold' style='width: 150px;'>PowerOffice ID</td><td>{e.powerOfficeInvoicePreview.powerOfficeCustomer?.id || e.powerOfficeInvoicePreview.powerOfficeCustomer?.Id}</td></tr>
                                                                            <tr><td class='fw-bold'>Name</td><td>{e.powerOfficeInvoicePreview.powerOfficeCustomer?.name || e.powerOfficeInvoicePreview.powerOfficeCustomer?.Name}</td></tr>
                                                                            <tr><td class='fw-bold'>Code</td><td>{e.powerOfficeInvoicePreview.powerOfficeCustomer?.code || e.powerOfficeInvoicePreview.powerOfficeCustomer?.Code}</td></tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </>}

                                                            {/* Products */}
                                                            {e.powerOfficeInvoicePreview.products && <div class='mb-3'>
                                                                <strong>Products ({e.powerOfficeInvoicePreview.products?.length || 0}):</strong>
                                                                <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                    <thead class='table-light'>
                                                                        <tr>
                                                                            <th>Product #</th>
                                                                            <th>Name</th>
                                                                            <th class='text-end'>Qty</th>
                                                                            <th class='text-end'>Price</th>
                                                                            <th class='text-end'>VAT %</th>
                                                                            <th class='text-end'>Total</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(e.powerOfficeInvoicePreview.products || []).map((prod, idx) => (
                                                                            <tr key={idx}>
                                                                                <td><code>{prod.productNumber || prod.id}</code></td>
                                                                                <td>{prod.name}</td>
                                                                                <td class='text-end'>{prod.qty}</td>
                                                                                <td class='text-end'>{util.format(prod.price, 2)}</td>
                                                                                <td class='text-end'>{prod.vat}%</td>
                                                                                <td class='text-end'>{util.format(prod.total, 2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                    {e.powerOfficeInvoicePreview.totals && <tfoot class='table-light fw-bold'>
                                                                        <tr>
                                                                            <td colspan='5'>Total</td>
                                                                            <td class='text-end'>{util.format(e.powerOfficeInvoicePreview.totals?.productsTotal, 2)}</td>
                                                                        </tr>
                                                                    </tfoot>}
                                                                </table>
                                                            </div>}

                                                            {/* Metadata */}
                                                            <div class='text-muted' style='font-size: 0.8em;'>
                                                                <small>
                                                                    Sale #{e.powerOfficeInvoicePreview.saleNumber} |
                                                                    Sale Date: {e.powerOfficeInvoicePreview.saleDate} |
                                                                    Preview Created: {e.powerOfficeInvoicePreview.createdAt ? util.formatDate(e.powerOfficeInvoicePreview.createdAt, { locale: 'nb-NO', hour: '2-digit', minute: '2-digit' }) : '-'}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </details>
                                                </>}

                                                {/* PowerOffice Response */}
                                                {e.powerOfficeResponse && <>
                                                    <div class='mb-3'>
                                                        <strong>PowerOffice Response:</strong>
                                                        {e.powerOfficeResponse.statusCode && <span class={`badge ms-2 ${e.powerOfficeResponse.statusCode === 201 ? 'bg-success' : 'bg-danger'}`}>
                                                            Status: {e.powerOfficeResponse.statusCode}
                                                        </span>}
                                                    </div>
                                                </>}

                                                {/* PowerOffice API Responses */}
                                                {e.powerOfficeApiResponses && Array.isArray(e.powerOfficeApiResponses) && e.powerOfficeApiResponses.length > 0 && <>
                                                    <details class='mb-3'>
                                                        <summary class='p-2 border rounded bg-secondary bg-opacity-10' style='cursor: pointer;'>
                                                            <strong>API Responses ({e.powerOfficeApiResponses.length})</strong>
                                                        </summary>
                                                        <div class='p-3 border border-top-0 rounded-bottom bg-light' style='font-size: 0.85em;'>
                                                            {e.powerOfficeApiResponses.map((resp, idx) => (
                                                                <div key={idx} class='mb-2 p-2 border rounded'>
                                                                    <div class='d-flex justify-content-between'>
                                                                        <span><strong>{resp.type || 'Response'}</strong></span>
                                                                        <small class='text-muted'>{resp.createdDate ? util.formatDate(resp.createdDate, { locale: 'nb-NO', hour: '2-digit', minute: '2-digit' }) : ''}</small>
                                                                    </div>
                                                                    {resp.isPaidSale && <span class='badge bg-success me-1'>Paid Sale</span>}
                                                                    {resp.actualPaidAmount && <span class='badge bg-info'>Paid: {util.format(resp.actualPaidAmount, 2)}</span>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </details>
                                                </>}

                                                {isAdmin && <>
                                                    <details class='mt-3'>
                                                        <summary class='p-2 border rounded bg-secondary bg-opacity-10' style='cursor: pointer; font-size: 0.85em;'>
                                                            <i class='fa-duotone fa-code' /> Vis rå JSON data
                                                        </summary>
                                                        <div class='p-2 border border-top-0 rounded-bottom' style='max-width: 100%; overflow-x: auto; font-size: 0.8em;'>
                                                            <xmp style='white-space: pre-wrap; word-break: break-all;'>powerOfficeResponse: {JSON.stringify(e.powerOfficeResponse, null, 4)}</xmp>
                                                            <xmp style='white-space: pre-wrap; word-break: break-all;'>powerOfficeInvoice: {JSON.stringify(e.powerOfficeInvoice, null, 4)}</xmp>
                                                            <xmp style='white-space: pre-wrap; word-break: break-all;'>powerOfficeInvoicePreview: {JSON.stringify(e.powerOfficeInvoicePreview, null, 4)}</xmp>
                                                            <xmp style='white-space: pre-wrap; word-break: break-all;'>powerOfficeApiResponses: {JSON.stringify(e.powerOfficeApiResponses, null, 4)}</xmp>
                                                        </div>
                                                    </details>
                                                </>}
                                            </td>
                                        </tr>
                                    </> : <></>}
                                    {displayDuettError[e.id] ? <>
                                        <tr>
                                            <td colspan='17'>
                                                {(() => {
                                                    const duettApiResponsesData = saleDebugData[e.id]?.duettApiResponses || e.duettApiResponses;

                                                    // Loading indicator
                                                    if (loadingDebugData[e.id]) {
                                                        return (
                                                            <div class='d-flex align-items-center justify-content-center py-4'>
                                                                <i class='fa-duotone fa-spinner fa-spin me-2' />
                                                                Laster Duett-logg...
                                                            </div>
                                                        );
                                                    }

                                                    return (<>
                                                        {/* Transfer marker - what the sale is linked to in Duett */}
                                                        {e.duettOrder?.orderId && <>
                                                            <details class='mb-3' open>
                                                                <summary class='p-2 border rounded bg-success bg-opacity-10' style='cursor: pointer;'>
                                                                    <strong>
                                                                        <i class='fa-duotone fa-file-invoice me-2' />
                                                                        Duett-overføring
                                                                    </strong>
                                                                    {!!e.duettOrder.orderNumber && <span class='badge bg-info ms-2'>Ordre #{e.duettOrder.orderNumber}</span>}
                                                                    {!!e.duettOrder.invoiceNumber && <span class='badge bg-info ms-2'>Faktura #{e.duettOrder.invoiceNumber}</span>}
                                                                    {e.duettOrder.status ? (
                                                                        <span class='badge bg-secondary ms-2' title='Ordrestatus i Duett — sier ikke om fakturaen er betalt'>{duettOrderStatusLabel(e.duettOrder.status)}</span>
                                                                    ) : null}
                                                                </summary>
                                                                <div class='p-3 border border-top-0 rounded-bottom' style='font-size: 0.85em;'>
                                                                    <table class='table table-sm table-bordered mb-0' style='font-size: 0.85em;'>
                                                                        <tbody>
                                                                            <tr><td class='fw-bold' style='width: 180px;'>Ordre-id</td><td>{e.duettOrder.orderId}</td></tr>
                                                                            <tr><td class='fw-bold'>Ordrenummer</td><td>{e.duettOrder.orderNumber || '—'}</td></tr>
                                                                            <tr><td class='fw-bold'>Fakturanummer</td><td>{e.duettOrder.invoiceNumber || '—'}{e.duettOrder.invoiceYear ? ` (${e.duettOrder.invoiceYear})` : ''}</td></tr>
                                                                            <tr><td class='fw-bold'>Status</td><td>{duettOrderStatusLabel(e.duettOrder.status) || '—'}</td></tr>
                                                                            <tr><td class='fw-bold'>Beløp (inkl. mva)</td><td>{e.duettOrder.grossAmount != null ? util.format(e.duettOrder.grossAmount, 2) : '—'}</td></tr>
                                                                            <tr><td class='fw-bold'>Overført</td><td>{e.duettOrder.transferredAt ? util.isoDate(e.duettOrder.transferredAt) : '—'}</td></tr>
                                                                            <tr><td class='fw-bold'>Utsending</td><td>{e.duettOrder.invoiceSendOption || '—'}</td></tr>
                                                                            <tr>
                                                                                <td class='fw-bold'>Bekreftet utsending</td>
                                                                                <td>
                                                                                    {e.duettOrder.dispatch ? <>
                                                                                        {e.duettOrder.dispatch.kind}
                                                                                        {e.duettOrder.dispatch.description ? ` — ${e.duettOrder.dispatch.description}` : ''}
                                                                                        {e.duettOrder.dispatch.at ? ` (${util.isoDate(e.duettOrder.dispatch.at)})` : ''}
                                                                                    </> : (e.duettInvoiceSent ? util.isoDate(e.duettInvoiceSent) : 'ikke bekreftet ennå')}
                                                                                </td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </details>
                                                        </>}

                                                        {e.duettCreditOrder?.orderId && <>
                                                            <div class='alert alert-danger py-2 mb-3' style='font-size: 0.85em;'>
                                                                <i class='fa-duotone fa-file-circle-minus me-1' />
                                                                Kreditert i Duett — kreditordre-id {e.duettCreditOrder.orderId}
                                                                {!!e.duettCreditOrder.orderNumber && <span class='ms-1'>(ordre #{e.duettCreditOrder.orderNumber})</span>}
                                                            </div>
                                                        </>}

                                                        {e.duettVoucher?.voucherId && <>
                                                            <div class='alert alert-info py-2 mb-3' style='font-size: 0.85em;'>
                                                                <i class='fa-duotone fa-file-invoice me-1' />
                                                                Inkludert i dagsoppgjør-bilag{e.duettVoucher.voucherNumber ? ` #${e.duettVoucher.voucherNumber}` : ''}
                                                                {e.duettVoucher.voucherDate ? ` (${util.isoDate(e.duettVoucher.voucherDate, false, false, true)})` : ''}
                                                            </div>
                                                        </>}

                                                        {/* Event log - one row per API call */}
                                                        {Array.isArray(duettApiResponsesData) && duettApiResponsesData.length > 0 ? (
                                                            <div class='mb-3'>
                                                                <strong>Hendelseslogg ({duettApiResponsesData.length}):</strong>
                                                                <div class='table-responsive'>
                                                                    <table class='table table-sm table-bordered mt-1 mb-0' style='font-size: 0.85em;'>
                                                                        <thead>
                                                                            <tr>
                                                                                <th style='width: 160px;'>Tid</th>
                                                                                <th style='width: 200px;'>Hendelse</th>
                                                                                <th>Resultat</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {[...duettApiResponsesData]
                                                                                .sort((a, b) => new Date(a.createdDate || 0) - new Date(b.createdDate || 0))
                                                                                .map((entry, idx) => {
                                                                                    const described = describeDuettLogEntry(entry);
                                                                                    return (
                                                                                        <tr key={idx} class={described.isError ? 'table-danger' : ''}>
                                                                                            <td><nobr>{entry.createdDate ? util.isoDate(entry.createdDate) : '—'}</nobr></td>
                                                                                            <td>{described.label}</td>
                                                                                            <td>
                                                                                                {described.isError ? (
                                                                                                    <span class='text-danger'>
                                                                                                        <i class='fa-solid fa-triangle-exclamation me-1' />
                                                                                                        {described.errors.join('; ')}
                                                                                                    </span>
                                                                                                ) : (
                                                                                                    <span>{described.outcome || '—'}</span>
                                                                                                )}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div class='text-muted mb-3'>Ingen Duett-hendelser er logget for dette salget.</div>
                                                        )}

                                                        {isAdmin && <>
                                                            <details class='mt-3'>
                                                                <summary class='p-2 border rounded bg-secondary bg-opacity-10' style='cursor: pointer; font-size: 0.85em;'>
                                                                    <i class='fa-duotone fa-code' /> Vis rå JSON data
                                                                </summary>
                                                                <div class='p-2 border border-top-0 rounded-bottom' style='max-width: 100%; overflow-x: auto; font-size: 0.8em;'>
                                                                    <xmp style='white-space: pre-wrap; word-break: break-all;'>duettOrder: {JSON.stringify(e.duettOrder, null, 4)}</xmp>
                                                                    <xmp style='white-space: pre-wrap; word-break: break-all;'>duettCreditOrder: {JSON.stringify(e.duettCreditOrder, null, 4)}</xmp>
                                                                    <xmp style='white-space: pre-wrap; word-break: break-all;'>duettVoucher: {JSON.stringify(e.duettVoucher, null, 4)}</xmp>
                                                                    <xmp style='white-space: pre-wrap; word-break: break-all;'>duettApiResponses: {JSON.stringify(duettApiResponsesData, null, 4)}</xmp>
                                                                </div>
                                                            </details>
                                                        </>}
                                                    </>);
                                                })()}
                                            </td>
                                        </tr>
                                    </> : <></>}
                                    {displayNetsError[e.id] ? <>
                                        <tr>
                                            <td colspan='17'>
                                                {isAdmin && <>
                                                    <div style='max-width: 100%; overflow-x: auto; font-size: 0.8em;'>
                                                        <xmp style='white-space: pre-wrap; word-break: break-all;'>netsResponse: {JSON.stringify(e.netsResponse, null, 4)}</xmp>
                                                        <xmp style='white-space: pre-wrap; word-break: break-all;'>netsLastWebResponse: {JSON.stringify(e.netsLastWebResponse, null, 4)}</xmp>
                                                    </div>
                                                </>}
                                            </td>
                                        </tr>
                                    </> : <></>}
                                </>);
                            })}
                            {isDebitorList && <>
                                <tr>
                                    <td colspan='12' class='text-end font-weight-bold' style='font-size: 1.5em;'><Text id='dyrejournal.total'>Total</Text></td>
                                    <td class='text-end font-weight-bold' style='font-size: 1.5em;'>
                                        <nobr>
                                            {formatting ? util.format(sumRestPayment, 2) : sumRestPayment}
                                        </nobr>
                                    </td>
                                </tr>
                            </>}
                        </tbody>
                        <tfoot class='table-light fw-bold'>
                            <tr>
                                <td colspan={footerLeftColspan} class='text-end'>
                                    {(finalSales || []).length} <Text id='dyrejournal.sales'>sales</Text>
                                </td>
                                <td class='text-center'><nobr>{util.format(footerTotalSum, 2, ',', ' ')}</nobr></td>
                                <td class='text-center'><nobr>{util.format(footerRestSum, 2, ',', ' ')}</nobr></td>
                                <td colspan={footerRightColspan}>&nbsp;</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>


            {showDhpReportConfirm && (
                <div
                    class='modal d-block'
                    tabindex='-1'
                    style='background-color: rgba(0,0,0,0.6); z-index: 1060;'
                    onClick={this.cancelReportAllDhp}
                >
                    <div class='modal-dialog modal-dialog-centered' onClick={e => e.stopPropagation()}>
                        <div class='modal-content border border-4 border-success shadow-lg' style='border-radius: 0;'>
                            <div class='modal-header bg-success text-white' style='border-radius: 0;'>
                                <h4 class='modal-title mb-0'>
                                    <i class='fa-duotone fa-cloud-arrow-up me-2' />
                                    <Text id='dyrejournal.dhp-report-confirm-title'>Report to DHP?</Text>
                                </h4>
                                <button type='button' class='btn-close btn-close-white' aria-label='Close' onClick={this.cancelReportAllDhp} />
                            </div>
                            <div class='modal-body py-4'>
                                <p class='fs-5 mb-0'>
                                    <Text id='dyrejournal.dhp-report-confirm-body' fields={{ count: dhpReportCount }}>Report {dhpReportCount} visits to DHP?</Text>
                                </p>
                            </div>
                            <div class='modal-footer'>
                                <button type='button' class='btn btn-lg btn-outline-secondary rounded-pill px-4' onClick={this.cancelReportAllDhp}>
                                    <Text id='dyrejournal.dhp-report-confirm-cancel'>Cancel</Text>
                                </button>
                                <button type='button' class='btn btn-lg btn-success rounded-pill px-4' onClick={this.confirmReportAllDhp}>
                                    <i class='fa-duotone fa-cloud-arrow-up me-2' />
                                    <Text id='dyrejournal.dhp-report-confirm-confirm'>Report to DHP</Text>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TICKET-01382: samlefaktura confirmation as a visible in-app modal (TICKET-01156 pattern) */}
            {showCollectiveInvoiceConfirm && (
                <div
                    class='modal d-block'
                    tabindex='-1'
                    style='background-color: rgba(0,0,0,0.6); z-index: 1060;'
                    onClick={this.cancelCreateCollectiveInvoice}
                >
                    <div class='modal-dialog modal-dialog-centered' onClick={e => e.stopPropagation()}>
                        <div class='modal-content border border-4 border-danger shadow-lg' style='border-radius: 0;'>
                            <div class='modal-header bg-danger text-white' style='border-radius: 0;'>
                                <h4 class='modal-title mb-0'>
                                    <i class='fa-duotone fa-file-invoice-dollar me-2' />
                                    <Text id='dyrejournal.collective-invoice-confirm-title'>Create collective invoices?</Text>
                                </h4>
                                <button type='button' class='btn-close btn-close-white' aria-label='Close' onClick={this.cancelCreateCollectiveInvoice} />
                            </div>
                            <div class='modal-body py-4'>
                                <p class='fs-5 mb-0'>
                                    <Text
                                        id='dyrejournal.collective-invoice-confirm-body'
                                        fields={{ sales: collectiveInvoiceConfirmSales, visitors: collectiveInvoiceConfirmVisitors }}
                                    >
                                        Create collective invoices for {collectiveInvoiceConfirmVisitors} customers from {collectiveInvoiceConfirmSales} visits?
                                    </Text>
                                </p>
                            </div>
                            <div class='modal-footer'>
                                <button type='button' class='btn btn-lg btn-outline-secondary rounded-pill px-4' onClick={this.cancelCreateCollectiveInvoice}>
                                    <Text id='dyrejournal.collective-invoice-confirm-cancel'>Cancel</Text>
                                </button>
                                <button type='button' class='btn btn-lg btn-danger rounded-pill px-4' onClick={() => this.confirmCreateCollectiveInvoice(COLLECTIVE_INVOICE_METHOD_INVOICE)}>
                                    <i class='fa-duotone fa-paper-plane me-2' />
                                    <Text id='dyrejournal.collective-invoice-confirm-confirm'>Create collective invoices</Text>
                                </button>
                                {/* TICKET-01715: clinics with Convene can send every samlefaktura there instead. */}
                                {canCreateCollectiveInvoiceViaConvene(this.props.stores.userStore.customer) && (
                                    <button type='button' class='btn btn-lg btn-outline-primary rounded-pill px-4' onClick={() => this.confirmCreateCollectiveInvoice(COLLECTIVE_INVOICE_METHOD_CONVENE)}>
                                        <i class='fa-duotone fa-file-invoice me-2' />
                                        <Text id='dyrejournal.collective-invoice-confirm-convene'>Create and send to Convene</Text>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isAdmin && <>
                <div class='d-flex flex-row justify-content-center bg-light rounded-lg mt-1'>
                    <small>
                        <span class='mx-1'><span class='font-weight-lighter'>animaliaRefreshTokenExpire:</span> {animaliaRefreshTokenExpire ? util.isoDate(animaliaRefreshTokenExpire) : 'n/a'}</span>
                        <span class='mx-1'><span class='font-weight-lighter'>user?.animalia?.expires_at:</span> {user?.animalia?.expires_at ? util.isoDate(user?.animalia?.expires_at) : 'n/a'}</span>
                        <span class='mx-1'><span class='font-weight-lighter'>animaliaRefreshTokenExpired:</span> {animaliaRefreshTokenExpired ? 'true' : 'false'}</span>
                        <span class='mx-1'><span class='font-weight-lighter'>user?.animalia?.error:</span> {stringify(user?.animalia?.error, null, 4)}</span>
                    </small>
                </div>
            </>}
        </>);
    }
}

export default AllVisitsTable;

